import { CartAddEvent } from '@theme/events';

const money = (cents) => `$${(cents / 100).toFixed(2)}`;
const httpsUrl = (u) => (u && u.startsWith('//') ? `https:${u}` : u || '');

/**
 * <bundle-builder> — multi-step, multi-bundle builder.
 *
 * - Mỗi step 1+ collection (tab phân loại). Sản phẩm tải lazy:
 *   Browse: /collections/<handle>?view=bb&sort_by=best-selling
 *   Search: paginate đúng collection đang chọn + lọc theo query client-side
 *           (xếp prefix-match trước), KHÔNG search toàn store.
 * - Khách dựng 1 "draft", đủ món → thành 1 bundle; có thể "ADD ANOTHER BUNDLE".
 * - Giá: fixed (combo cố định) hoặc bxgy (chỉ định step free). Tiền thật do
 *   Shopify Function tính ở checkout dựa trên thuộc tính `_bundle_*` của giỏ.
 */
class BundleBuilder extends HTMLElement {
  #abort = null;
  #io = null;

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.bundleName = this.dataset.bundleName || 'BUNDLE';
    this.bundlePriceCents = parseInt(this.dataset.bundlePriceCents, 10) || 0;
    this.addLabel = this.dataset.addLabel || 'ADD';
    this.searchingLabel = this.dataset.searchingLabel || 'Searching products…';
    this.savingsLabel = this.dataset.savingsLabel || 'You save';
    this.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    this.startedTracked = false;
    // 3 chế độ giá: fixed (tổng cố định) | bxgy (món quà FREE) | bxpct (món ưu đãi giảm %).
    this.pricingType = ['bxgy', 'bxpct'].includes(this.dataset.pricingType) ? this.dataset.pricingType : 'fixed';
    // % giảm của bxpct. Kẹp 1..100 để merchant gõ bậy không làm âm tiền.
    this.discountPct = Math.min(100, Math.max(1, parseInt(this.dataset.discountPct, 10) || 50));
    // % riêng của từng step (0 = dùng % chung ở trên) -> làm được "món 2 giảm 50%,
    // món 3 giảm 30%". Danh sách theo đúng thứ tự step nên tra thẳng bằng chỉ số.
    this.stepPcts = (this.dataset.stepPcts || '')
      .split(',')
      .map((x) => Math.min(100, Math.max(0, parseInt(x, 10) || 0)));
    this.freeSlots = (this.dataset.freeSlots || '')
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isInteger(n));

    /**
     * BXGY — món nào được tặng:
     *   'slot'     (mặc định) món ở step bật cờ "Món ưu đãi". Step đó có badge FREE,
     *              thẻ trong lưới gạch giá — khách biết trước món nào miễn phí.
     *   'cheapest' món RẺ NHẤT trong bộ, bất kể ở step nào. Giữ biên khi các step cho
     *              chọn nhiều loại giá khác nhau (tee $34.95 vs hoodie $69.95).
     * Số lượng món tặng vẫn = số step bật cờ, nên merchant chỉ đổi CÁCH CHỌN chứ không
     * đổi cấu trúc offer.
     *
     * Chế độ 'cheapest' KHÔNG cần đụng Shopify Function: theme quyết định lúc add giỏ
     * và gắn `_bundle_free = "yes"` lên đúng dòng rẻ nhất; function vẫn chỉ đọc cờ đó.
     * Bộ thiếu món thì function không giảm gì cả, nên cờ không bao giờ "cũ mà vẫn ăn".
     */
    this.freePick = this.dataset.freePick === 'cheapest' ? 'cheapest' : 'slot';

    /**
     * % giảm cho MÓN LẺ (mua kèm ngoài bộ). 0 = nguyên giá.
     * PHẢI khớp với offers/single.js trong function: theme gắn `_single_off = "<pct>"`,
     * function đọc đúng con số đó rồi cắt tiền. Đổi số ở theme mà function chưa deploy
     * bản có offers/single.js thì builder hiện giá giảm còn checkout thu đủ tiền.
     */
    this.singlePct = Math.min(100, Math.max(0, parseInt(this.dataset.singlePct, 10) || 0));

    /*
     * UPSELL SAU BỘ: danh sách loại RIÊNG cho luồng món lẻ (khác loại của step). Rỗng =
     * luồng món lẻ cũ (nút ADD 1 MORE ITEM + sheet chọn loại theo tab của step).
     * Lưới upsell mượn chính step cuối của bộ; `singleCat` là loại đang xem (-1 = không).
     */
    const csv = (v) => String(v || '').split(',').map((x) => x.trim()).filter(Boolean);
    this.upsellPats = csv(this.dataset.upsellPatterns);
    this.upsellLabels = csv(this.dataset.upsellLabels);
    this.upsellScopes = csv(this.dataset.upsellScope);
    this.upsellMode = this.upsellPats.length > 0;
    this.singleCat = -1;
    this.upsellMatch = [];
    this.otoEnabled = this.upsellMode && this.dataset.oto === '1';
    // A/B bảng upsell (2026-09-27): nhóm theo <html data-abv2>, ĐỘC LẬP với data-abv của A/B
    // nội dung nên hai thí nghiệm chạy song song không lẫn nhau (2x2). Nhóm B: không bảng bật
    // lên, không OTO — upsell chỉ còn khối nằm dưới nút CHECKOUT.
    this.popupVariant = '';
    if (this.upsellMode && this.dataset.popupAb === '1') {
      const v = document.documentElement.getAttribute('data-abv2') === 'B' ? 'B' : 'A';
      this.popupVariant = v;
      if (v === 'B') this.otoEnabled = false;
      try {
        window.__bbAb = window.__bbAb || {};
        window.__bbAb.popup = v;
        if (typeof window.clarity === 'function') {
          window.clarity('set', 'ab_popup', v);
          window.clarity('event', `bb_ab_popup_${v}`);
        }
        window.Shopify?.analytics?.publish?.('bb_content', { name: `bb_ab_popup_${v}`, path: location.pathname });
      } catch {
        /* analytics non-critical */
      }
    }
    // Gợi ý cùng design CHỈ ở loại này, mỗi món của bộ một thẻ (vd {brand}-t-shirt).
    this.upsellMatchPat = String(this.dataset.upsellMatch || '').trim();
    // Gợi ý CỐ ĐỊNH: danh sách handle mẫu (vd {brand}-hunting-cap-csc139) — thắng
    // `upsellMatchPat`. Dùng khi món gợi ý KHÔNG chia sẻ mã design với bộ (cap mã CSC).
    this.upsellMatchHandles = String(this.dataset.upsellMatchHandles || '')
      .split(',').map((x) => x.trim()).filter(Boolean);
    this.upsellMatchTag = String(this.dataset.upsellMatchTag || '').trim();
    // Tat han the goi y (va offer 1 lan khi CHECKOUT, vi no dung chinh danh sach goi y).
    this.upsellMatchOff = this.dataset.upsellMatchOff === '1';
    // 'carousel' = the goi y vuot ngang, anh to (2026-09-29). Rong = danh sach cu.
    this.upsellMatchStyle = String(this.dataset.upsellMatchStyle || '').trim();
    this.upsellMatchBadge = String(this.dataset.upsellMatchBadge || '').trim();
    this.upsellMatchBadgeN = parseInt(this.dataset.upsellMatchBadgeCount || '0', 10) || 0;

    // "Buy more, save more": bundle thứ 2 trở đi giảm THÊM % trên giá bundle đó.
    // index 1 (bundle 2) -> tiers[0], index 2 -> tiers[1], ... chốt ở mức cuối.
    this.extraTiers = (this.dataset.extraTiers || '')
      .split(',')
      .map((s) => parseFloat(s))
      .filter((n) => Number.isFinite(n) && n > 0);
    this.bxmsLabel = this.dataset.buyMoreLabel || 'Buy more, save more';
    // Note "limited time" (chỉ có khi offer bật countdown) -> gắn vào line item để cart hiện.
    this.limitedNote = this.dataset.limitedNote || '';

    // Free shipping unlock (nhử add bundle ở màn cuối). Dựa trên TỔNG phải trả >= ngưỡng (cents).
    this.fsEnabled = this.dataset.fsEnabled === '1';
    this.fsThreshold = parseInt(this.dataset.fsThreshold, 10) || 0;
    // Bảng bậc giá ship: mỗi dòng `tối_đa:giá`, vd `50:5.95`. US/GB dùng bảng nội địa,
    // nước khác dùng bảng quốc tế. Không khai bảng nào khớp -> KHÔNG in dòng nào; thà im
    // còn hơn nói một con số sai với thứ khách sẽ thấy ở checkout.
    const domestic = ['US', 'GB'].includes((this.dataset.shipCountry || '').toUpperCase());
    this.shipTiers = this.#parseShipTiers(domestic ? this.dataset.shipTiers : this.dataset.shipTiersIntl);
    this.fsLocked = this.dataset.fsLocked || '';
    this.fsUnlocked = this.dataset.fsUnlocked || '';
    // Nudge hiện trong CART khi chỉ có 1 bundle (gợi ý add bundle 2). Gắn vào line item.
    const t0 = this.extraTiers && this.extraTiers.length ? this.extraTiers[0] : 0;
    // Thang mở không có khái niệm "bundle 2" -> nhắc theo MÓN, không theo bộ.
    this.fsNudge = this.fsEnabled
      ? (this.dataset.ladder === '1'
          ? '🚚 Add 1 more item to unlock FREE shipping'
          : t0 > 0
            ? `🚚 Add bundle 2 to unlock FREE shipping + ${t0}% off`
            : '🚚 Add bundle 2 to unlock FREE shipping')
      : '';

    // THANG MỞ: không có step cố định — mỗi block là một LOẠI sản phẩm, khách thêm bao
    // nhiêu món tuỳ thích. Món hạng 1 nguyên giá, hạng 2+ giảm theo thang.
    // Xếp hạng THẬT do Shopify Function làm lúc checkout (khách sửa giỏ thì hạng đổi);
    // ở đây chỉ tính để HIỂN THỊ, hai bên dùng chung một công thức.
    this.ladderMode = this.dataset.ladder === '1';
    this.ladderBy = this.dataset.ladderBy === 'added' ? 'added' : 'price';
    this.ladderTiersRaw = this.dataset.ladderTiers || '';
    this.ladderSteps = this.#parseLadder(this.ladderTiersRaw);
    if (this.ladderMode) this.classList.add('bb-on-ladder');

    this.stepEls = [...this.querySelectorAll('[data-bb-step]')];
    this.stepCount = this.stepEls.length;
    this.storageKey = `bb:${this.sectionId}`;

    this.state = {
      bundles: [],
      cartIds: [], // song song bundles: id của bundle nếu ĐÃ có trong giỏ (null = chưa) -> chống add trùng
      draft: this.ladderMode ? [] : Array(this.stepCount).fill(null),
      // Món lẻ: mua kèm ngoài bộ, nguyên giá. Mỗi món tự mang `cartId` (null = chưa vào
      // giỏ) giống cách bundles dùng cartIds -> gỡ ở màn kết quả là gỡ được cả ở giỏ.
      singles: [],
      current: 0,
      editingBundle: null, // null = đang dựng draft; số = đang sửa bundles[n]
      // Thang mở: _bundle_id của bộ mà sheet xem lại đang "mượn" giỏ (khách CHƯA bấm đi
      // tiếp). Lưu xuống sessionStorage để tải lại trang còn kéo nó về được.
      previewId: null,
      // Thang mở: _bundle_id các bộ trong giỏ đã được MỞ LẠI vào draft (#reopenLadder). Giỏ vẫn
      // giữ dòng cũ tới lúc chốt; #checkout gỡ chúng rồi đẩy cả bộ một lần.
      reopenIds: [],
    };
    this.search = this.stepEls.map(() => null);
    // Thang mở: chỉ số bundle mà sheet xem lại vừa tự chốt vào giỏ (để nút ví ăn đúng
    // giỏ). null = không có bộ nào đang "mượn". Xem #commitLadderForExpress.
    this._autoCommitted = null;
    // KHÔNG để undefined: `undefined === false` là sai, nên lần chạy đầu của
    // #syncContentVisibility bị coi là "trạng thái vừa đổi" và nó cuộn trang xuống bộ
    // chọn đội ngay khi khách vừa mở link — che mất hero ở đầu trang.
    this._contentHidden = false;
    this.selectedBrand = ''; // brand đang chọn, nhớ & carry-over giữa các step
    this.lastOption = ''; // nhớ size khách chọn gần nhất

    // Danh sách brand khách chọn (merchant nhập). Tách theo dòng hoặc dấu phẩy.
    this.brands = [];
    try {
      const bEl = this.querySelector('[data-bb-brands]');
      if (bEl) this.brands = JSON.parse(bEl.textContent).split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    } catch {
      /* no brands */
    }

    // Mã đội -> { n: tên đầy đủ, c: màu chính, c2: màu phụ, l: logo }.
    // Tên CHỈ dùng để hiển thị trong builder; title gốc ("KC Short Sleeve Polo SC101")
    // vẫn là thứ đi vào giỏ, checkout và email xác nhận đơn.
    this.teamMeta = {};
    this.teamNames = {};
    try {
      const nEl = this.querySelector('[data-bb-teammeta]');
      if (nEl) this.teamMeta = JSON.parse(nEl.textContent) || {};
      Object.keys(this.teamMeta).forEach((k) => {
        this.teamNames[k] = this.teamMeta[k].n;
      });
    } catch {
      /* no team meta */
    }

    // Chủ đề design: key -> nhãn ("veteran" -> "Veteran"). Nhận diện trên sản phẩm bằng
    // tag `theme:<key>` (hoặc tag đúng bằng key). Không có map = không có chip, lưới
    // chạy y như trước.
    this.themeMeta = {};
    // Tiền tố mã design -> key chủ đề ("SCH" -> "hunting"). Nhờ map này hàng mới import
    // KHÔNG cần gắn tag: chủ đề đọc thẳng từ mã ở cuối tên sản phẩm.
    this.themeCodes = {};
    try {
      const thEl = this.querySelector('[data-bb-thememeta]');
      if (thEl) this.themeMeta = JSON.parse(thEl.textContent) || {};
      const tcEl = this.querySelector('[data-bb-themecodes]');
      if (tcEl) this.themeCodes = JSON.parse(tcEl.textContent) || {};
    } catch {
      /* no theme meta */
    }
    // Chủ đề khách tự bấm — dùng chung cho MỌI step (chủ đề là ý định mua, không phải
    // thuộc tính của step). Step nào không có chip đó thì tự rơi về mặc định của step.
    this.selectedTheme = '';
    // Tên đội -> mã đội, đọc từ data-slug của nút chọn đội. Handle collection đặt theo
    // MÃ (kc-t-shirt) chứ không theo tên đầy đủ, nên không có map này là dựng sai handle.
    this.brandSlugs = {};
    this.querySelectorAll('[data-bb-brandbtn][data-slug]').forEach((b) => {
      const brand = (b.dataset.brand || '').trim().toLowerCase();
      const slug = (b.dataset.slug || '').trim().toLowerCase();
      if (brand && slug) this.brandSlugs[brand] = slug;
    });

    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    this.addEventListener('click', (e) => this.#onClick(e), opts);
    // Form "Email me this bundle" trên màn xem bộ (submit không nổi lên qua click).
    this.addEventListener(
      'submit',
      (e) => {
        const form = e.target.closest('[data-bb-save-form]');
        if (!form) return;
        e.preventDefault();
        this.#saveSubmit(form);
      },
      opts,
    );
    // Bắt đầu tải ảnh to ngay khi NGÓN TAY CHẠM nút zoom, không đợi tới lúc click hoàn tất.
    // Trên điện thoại khoảng cách giữa touchstart và click là ~100-300ms — đủ để CDN vào việc.
    this.addEventListener(
      'pointerdown',
      (e) => {
        const z = e.target.closest && e.target.closest('[data-bb-zoom]');
        const card = z && z.closest('[data-bb-card]');
        const raw = card && card.dataset.zoomraw;
        if (raw) new Image().src = this.#sizeUrl(raw, this.#zoomWidth());
        // Chạm nút ĐỘI -> tải trước danh sách 24 sản phẩm đầu + ảnh hàng đầu, không đợi nhả
        // tay (2026-09-29). #fetchView dùng lại đúng promise này nên không gọi trùng.
        const brandBtn = e.target.closest && e.target.closest('[data-bb-brandbtn]');
        if (brandBtn) this.#prefetchBrand(brandBtn);
      },
      opts
    );
    this.addEventListener('change', (e) => this.#onChange(e), opts);
    this.addEventListener('input', (e) => this.#onInput(e), opts);
    // Prefetch view bbsearch lần focus ô lọc brand đầu -> Shopify compile sẵn template.
    this.searchWarmed = false;
    this.addEventListener('focusin', (e) => {
      if (e.target.matches && e.target.matches('[data-bb-brandfilter]')) this.#warmSearch(e.target.closest('[data-bb-step]'));
    }, opts);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#closeGuide();
        this.#closeZoom();
        this.#closePeek();
        if (this.singlePick) this.#cancelSingle();
        this.#closeOto();
        this.#closeUpsheet();
        this.#closeAddedSheet();
        this.#closeCelebrate();
        this.#closeReview();
      } else if (this.zoom && e.key === 'ArrowLeft') {
        this.#zoomStep(-1);
      } else if (this.zoom && e.key === 'ArrowRight') {
        this.#zoomStep(1);
      }
    }, opts);
    // Giỏ đổi từ NƠI KHÁC (vd xóa bundle ở cart drawer) -> builder tự đối chiếu lại, khỏi F5.
    document.addEventListener('cart:update', () => this.#onCartUpdated(), opts);
    this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Nút "về đầu lưới" — lưới 250 món cuộn rất dài, không có nó khách kẹt ở giữa.
    if (this.ladderMode) {
      this._onScroll = () => {
        if (this._toTopRaf) return;
        this._toTopRaf = requestAnimationFrame(() => {
          this._toTopRaf = 0;
          const btn = this.querySelector('[data-bb-totop]');
          if (!btn) return;
          const box = this.getBoundingClientRect();
          const deep = box.top < -900 && box.bottom > window.innerHeight * 0.5;
          btn.classList.toggle('bb-hide', !deep);
        });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true, signal: this.#abort.signal });
    }

    // Thanh đáy `position: fixed` neo theo LAYOUT viewport. Trên nhiều trình duyệt mobile
    // (in-app browser, Safari/Chrome lúc ẩn thanh công cụ dưới) vùng NHÌN THẤY cao hơn
    // layout viewport -> thanh đáy đứng lửng, dưới nó lộ ra lưới sản phẩm. `.bb-bar::after`
    // không cứu được vì phần vẽ dưới mép layout viewport bị compositor cắt.
    // Đo chênh lệch bằng visualViewport rồi đẩy thanh xuống đúng bằng chỗ hở.
    // Máy không có visualViewport (hoặc chênh lệch 0) thì biến = 0px, không đổi gì.
    this.#initBarAutohide();
    this.#initThemeStick();
    this.#syncViewportDrop();
    // Thanh đổi chiều cao (thêm chip, dòng nhắc xuống dòng) -> phải neo lại.
    const barEl = this.querySelector('.bb-bar');
    if (barEl && 'ResizeObserver' in window) {
      this._barRO = new ResizeObserver(() => this.#syncViewportDrop());
      this._barRO.observe(barEl);
    }
    if (window.visualViewport) {
      this._onVV = () => {
        if (this._vvRaf) return;
        this._vvRaf = requestAnimationFrame(() => { this._vvRaf = 0; this.#syncViewportDrop(); });
      };
      window.visualViewport.addEventListener('resize', this._onVV, { signal: this.#abort.signal });
      window.visualViewport.addEventListener('scroll', this._onVV, { signal: this.#abort.signal });
      window.addEventListener('orientationchange', this._onVV, { signal: this.#abort.signal });
      // iOS không bắn `visualViewport.scroll` liên tục lúc cuộn quán tính -> bám thêm scroll.
      window.addEventListener('scroll', this._onVV, { passive: true, signal: this.#abort.signal });
    }
    this.#io = new IntersectionObserver((entries) => this.#onSentinel(entries), { rootMargin: '600px' });
    this.querySelectorAll('[data-bb-sentinel]').forEach((s) => this.#io.observe(s));

    this.#restore();
    // Từ nút "Show more designs" của landing mã design: khách muốn TỰ chọn đội -> bỏ đội đã nhớ (giỏ giữ nguyên).
    if (this.dataset.lpMore && window.location.hash === '#choose-team') {
      this.selectedBrand = null;
      // #choose-team là đầu builder (còn hero) -> cuộn tiếp tới đúng bộ chọn đội. Đợi ảnh hero tải xong
      // (ảnh làm bộ chọn tụt xuống), cuộn lại một lần nữa phòng ảnh lazy nở sau.
      const go = () => {
        const pk = [...this.querySelectorAll('[data-bb-brandpicker]')].find((x) => x.offsetParent);
        // 'instant': html của theme có scroll-behavior: smooth, cuộn mượt lúc trang đang dựng bị bỏ ngang.
        if (pk) pk.scrollIntoView({ block: 'start', behavior: 'instant' });
      };
      const later = () => { go(); setTimeout(go, 600); };
      if (document.readyState === 'complete') setTimeout(later, 100);
      else window.addEventListener('load', later, { once: true });
    }
    this.#initDeeplink();
    // "Add bundle 2" từ cart (?bb_add=1) -> vào THẲNG step 1 của bundle mới, không về màn final.
    this.pendingAdd = new URLSearchParams(window.location.search).get('bb_add') === '1';
    // Express flow: khách bấm "Add bundle 2 to unlock free ship" -> dựng xong là ADD THẲNG vào giỏ,
    // không bắt kéo xuống bấm "Add to cart". Cờ này sống tới khi bundle hoàn tất rồi auto-checkout.
    this.expressAdd = this.pendingAdd;
    if (this.pendingAdd) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch {}
    }
    // Hiển thị ngay theo session (nhanh); rồi ĐỐI CHIẾU với GIỎ để sửa nếu lệch
    // (vd khách đã xóa bundle ở cart drawer -> session vẫn còn cũ).
    if (this.ladderMode) {
      // Thang mở: không có màn kết quả — luôn về lưới, giỏ dựng dở nằm ở thanh đáy.
      // Link quang cao (?design=) -> mo thang category cua mau do.
      this.#stepTo(this.deep ? this.deep.step : 0, false);
      if (this.codeLink) this.#startDesignCode();
      else if (this.deep) this.#loadDeeplink();
    } else if (this.pendingAdd) {
      this.#addAnother();
    } else if (this.state.bundles.length > 0) {
      this.#showResult(false);
    } else {
      this.#stepTo(Math.max(this.#emptySlot(this.state.draft), 0), false);
    }
    // ĐỪNG đọc số trên bong bóng giỏ ở header để đi tắt: theme vẽ lại con số đó BẤT ĐỒNG
    // BỘ, ngay sau một lượt ghi giỏ nó có thể là 0 trong khi giỏ vẫn còn hàng -> bộ đang
    // hiện bị xoá sạch và builder nhảy về step 1. (Chỉ lộ ra từ khi builder tự chốt giỏ:
    // trước đó bộ ở màn kết quả có cartId = null nên nằm trong nhánh `pending` của
    // #reconcileBundles và sống sót.) /cart.js là nguồn chuẩn duy nhất.
    this.#loadCartBundles().then(({ bundles, cartIds }) => {
      const pv = this.state.previewId; // bộ "mượn" giỏ: #restorePreviewLadder tự trả về draft
      this.#reconcileBundles(bundles, cartIds);
      this.#restorePreviewLadder();
      this.#reopenLadder(bundles, cartIds, pv);
    });

    this.#clarityTags();
    this.#initContentTracking(); this.#initTapToBuilder(); this.#initKlaviyoFormTracking();
    this.#track('BundleStarted', { bundle_name: this.bundleName, num_steps: this.stepCount });
    this.startedTracked = true;
  }

  /**
   * Khoảng hở giữa mép dưới LAYOUT viewport (nơi thanh đáy neo) và mép dưới vùng NHÌN
   * THẤY. Chặn trên 200px: chênh lớn hơn thế gần như chắc chắn là số rác (bàn phím ảo,
   * pinch-zoom) — đẩy thanh đi cả trăm pixel còn tệ hơn chỗ hở.
   */
  /**
   * Cuộn XUỐNG -> thanh đáy thu còn một dòng mảnh; cuộn LÊN (hoặc bấm vào dòng đó) -> bung
   * lại. Lúc khách đang lướt lưới 250 món thì thanh chỉ chiếm ~30px thay vì ~120px.
   *
   * Cộng dồn quãng cuộn (`acc`) rồi mới đổi trạng thái, đổi chiều thì reset — đổi ngay
   * theo từng frame thì thanh nhấp nháy liên tục trên iOS (cuộn quán tính có cả frame âm
   * lẫn dương).
   */
  /**
   * Hàng chủ đề kiểu Ô ẢNH (theme_bar_style = tiles) cao ~150px — dính nguyên khối thì
   * ăn gần nửa màn khi lướt lưới. Cuộn qua vị trí gốc của nó -> gắn `bb-themebar--stuck`
   * và CSS thu về MỘT DÒNG dropdown "DESIGN THEME / HUNTING … CHOOSE ⌄"; bấm mới xổ ô ra.
   * Đo bằng một mốc 0px đặt NGAY TRƯỚC hàng: mốc không dịch khi hàng đổi chiều cao, nên
   * không bị bật/tắt qua lại (thu gọn làm trang ngắn đi, đo trên chính hàng là dao động).
   */
  #initThemeStick() {
    const bars = [...this.querySelectorAll('.bb-themebar--tiles')];
    if (!bars.length) return;
    const pins = bars.map((bar) => {
      const pin = document.createElement('div');
      pin.className = 'bb-themebar__pin';
      pin.setAttribute('aria-hidden', 'true');
      bar.before(pin);
      return pin;
    });
    let raf = 0;
    const check = () => {
      raf = 0;
      bars.forEach((bar, i) => {
        // Hàng đang ẩn (step khác, chưa chọn đội) -> không đo được, gỡ trạng thái.
        const shown = bar.getClientRects().length > 0 && pins[i].getClientRects().length > 0;
        const top = parseFloat(getComputedStyle(bar).top) || 0;
        const stuck = shown && pins[i].getBoundingClientRect().top < top - 1;
        if (bar.classList.contains('bb-themebar--stuck') === stuck) return;
        bar.classList.toggle('bb-themebar--stuck', stuck);
        if (!stuck) {
          bar.classList.remove('bb-themebar--open');
          bar.querySelector('[data-bb-themetoggle]')?.setAttribute('aria-expanded', 'false');
        }
      });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal: this.#abort.signal });
    window.addEventListener('resize', onScroll, { passive: true, signal: this.#abort.signal });
    this._themeStickCheck = check;
  }
  /** Đang dính mà đổi chủ đề -> kéo về đầu lưới, không thì khách đứng giữa lưới đã lọc. */
  #themeStickScroll(stepEl) {
    const bar = stepEl && stepEl.querySelector('.bb-themebar--stuck');
    const pin = bar && bar.previousElementSibling;
    if (!pin || !pin.classList.contains('bb-themebar__pin')) return;
    const top = parseFloat(getComputedStyle(bar).top) || 0;
    const y = window.pageYOffset + pin.getBoundingClientRect().top - top + 1;
    window.scrollTo({ top: Math.max(0, y), behavior: 'instant' });
  }
  #initBarAutohide() {
    const bar = this.querySelector('.bb-bar');
    if (!bar) return;
    const STEP = 56; // phải cuộn liên tục 56px cùng chiều mới đổi trạng thái
    let lastY = window.pageYOffset;
    let acc = 0;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = Math.max(0, window.pageYOffset);
        const d = y - lastY;
        lastY = y;
        if (Math.abs(d) < 2) return;
        acc = acc > 0 === d > 0 ? acc + d : d;
        const doc = document.documentElement;
        // Đầu trang và cuối trang thì luôn bung: chỗ đó khách không đang lướt hàng.
        if (y < 200 || y + window.innerHeight >= doc.scrollHeight - 120) return this.#barPeek(false);
        if (acc > STEP) this.#barPeek(true);
        else if (acc < -STEP) this.#barPeek(false);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal: this.#abort.signal });
  }
  /** Bật/tắt chế độ gọn. Thanh đổi chiều cao -> neo lại theo vùng nhìn thấy. */
  #barPeek(on) {
    const bar = this.querySelector('.bb-bar');
    if (!bar || bar.classList.contains('bb-bar--peek') === on) return;
    bar.classList.toggle('bb-bar--peek', on);
    if (on) this.#barPeekText();
    this.#syncViewportDrop();
  }
  /** Chữ trên dòng gọn: đang chọn mấy món + số tiền phải trả. */
  #barPeekText() {
    const el = this.querySelector('[data-bb-peekline]');
    if (!el) return;
    const draft = (this.state && this.state.draft) || [];
    const picked = draft.filter(Boolean);
    // Cùng quy tắc với #priceUpdate: bộ chưa đủ món thì in tổng giá GỐC đã chọn.
    let pay = 0;
    try {
      const settled = this.ladderMode || this.#isFull(draft);
      pay = settled
        ? this.#calc(this.ladderMode ? picked : draft, this.#targetIndex()).pay
        : picked.reduce((a, x) => a + (x.priceCents || 0), 0);
    } catch {
      pay = 0;
    }
    const count = this.ladderMode
      ? `${picked.length} ${picked.length === 1 ? 'item' : 'items'}`
      : `${picked.length}/${this.stepCount}`;
    el.textContent = picked.length ? `${count} · ${money(pay)}` : count;
  }
  #syncViewportDrop() {
    // Neo CẢ HAI thanh cố định: `.bb-bar` (lưới) và `.bb-resultbar` (màn xem bộ). Chúng
    // loại trừ nhau theo trạng thái nên không bao giờ cùng hiện, nhưng cùng cần neo.
    const bars = [...this.querySelectorAll('.bb-bar, .bb-resultbar, .bb-upbar')];
    const vv = window.visualViewport;
    let drop = 0;
    if (vv) {
      // Chênh lệch giữa mép dưới LAYOUT viewport (nơi `position: fixed; bottom: 0` neo)
      // và mép dưới vùng NHÌN THẤY. Trình duyệt desktop và đa số mobile: 0.
      drop = Math.round(vv.offsetTop + vv.height - document.documentElement.clientHeight);
      // <8px là sai số làm tròn; >400px gần như chắc chắn là bàn phím ảo / pinch-zoom.
      if (!(drop >= 8) || drop > 400) drop = 0;
    }
    this.style.setProperty('--bb-vv-drop', `${drop}px`);
    for (const bar of bars) {
      if (drop > 0 && vv) {
        // Neo thanh theo VÙNG NHÌN THẤY, không theo layout viewport: đặt `top` = mép dưới
        // vùng nhìn thấy trừ chiều cao thanh. Tự đúng với mọi kiểu thanh công cụ co giãn
        // của in-app browser, không phải đoán bằng hằng số.
        //
        // Đừng quay lại `transform: translateY(drop)` — đo hụt/thừa một nhịp là thanh trôi
        // xuống dưới mép màn và CẮT MẤT nút (đã dính 2026-08-27).
        const h = bar.getBoundingClientRect().height;
        bar.style.bottom = 'auto';
        bar.style.top = `${Math.round(vv.offsetTop + vv.height - h)}px`;
      } else {
        bar.style.top = '';
        bar.style.bottom = '';
      }
    }
  }
  disconnectedCallback() {
    if (this._timerId) {
      clearInterval(this._timerId);
      this._timerId = 0;
    }
    this.#abort?.abort();
    this.#io?.disconnect();
    this._barRO?.disconnect();
  }

  /* ---------- helpers ---------- */

  #target() {
    return this.state.editingBundle === null ? this.state.draft : this.state.bundles[this.state.editingBundle];
  }
  #emptySlot(arr) {
    return arr.findIndex((s) => !s);
  }
  #isFull(arr) {
    return arr.length > 0 && arr.every(Boolean);
  }
  #activeHandle(stepEl) {
    // Lưới upsell: collection của loại upsell đang xem (dùng cho lọc membership khi phải
    // rơi về tìm kiếm store), không phải collection của step.
    if (this.#upsellActive()) return this.#directHandle(stepEl, stepEl.dataset.brand || this.selectedBrand) || '';
    const tab = stepEl.querySelector('[data-bb-subtab][aria-selected="true"]');
    return (tab && tab.dataset.handle) || stepEl.dataset.collectionHandle || '';
  }
  /**
   * Từ khoá scope cho TAB đang chọn. Vì scope phải khớp TÊN sản phẩm (tên collection
   * không đáng tin), merchant nhập danh sách scope ngăn cách dấu phẩy theo thứ tự tab
   * (vd "short sleeve polo, t-shirt, hawaiian shirt"). Ưu tiên:
   *   1) manual[tabIndex] (thiếu thì lấy phần tử cuối) — khi có nhập tay
   *   2) data-scope của tab (auto từ tên collection) — fallback
   *   3) scope của collection mặc định (single-collection)
   */
  #activeScope(stepEl) {
    if (this.#upsellActive()) {
      const i = this.singleCat;
      return (this.upsellScopes[i] || this.upsellLabels[i] || '').toLowerCase();
    }
    const manual = (stepEl.dataset.searchScope || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const tabs = [...stepEl.querySelectorAll('[data-bb-subtab]')];
    if (tabs.length) {
      let i = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      if (i < 0) i = 0;
      if (manual.length) return (manual[i] || manual[manual.length - 1] || '').toLowerCase();
      return (tabs[i].dataset.scope || '').toLowerCase();
    }
    return (manual[0] || stepEl.dataset.collectionScope || '').toLowerCase();
  }

  /** Slug brand giống cách Shopify sinh handle: thường hoá, ký tự đặc biệt -> '-'. */
  /** Độ sáng cảm nhận (0-1) của mã hex -> chọn chữ trắng hay chữ đen cho dễ đọc. */
  #luma(hex) {
    const h = String(hex || '').replace('#', '');
    if (h.length !== 6) return 1;
    const v = [0, 2, 4].map((i) => {
      const c = parseInt(h.slice(i, i + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
  }
  /**
   * Tô thanh "đã chọn đội" theo màu + logo của đội. Màu lấy từ metaobject `team`.
   * Đội chưa nhập màu -> gỡ biến CSS, thanh về kiểu trung tính mặc định.
   */
  #paintTeamBar(stepEl, brand) {
    const meta = this.teamMeta[this.#slugifyBrand(brand)] || {};
    const bar = stepEl.querySelector('[data-bb-brandbar]');
    const logo = bar && bar.querySelector('[data-bb-brandbar-logo]');
    if (logo) {
      if (meta.l) {
        logo.src = meta.l;
        logo.hidden = false;
      } else {
        logo.removeAttribute('src');
        logo.hidden = true;
      }
    }
    // Biến CSS đặt trên STEP chứ không phải trên thanh đội, để mọi thứ trong step
    // (thanh đội, nút ADD, thẻ đang chọn) dùng chung được.
    // Đặt lên STEP (nút ADD, thanh đội) và lên cả GỐC builder (banner, thanh tiến độ,
    // màn kết quả — những thứ nằm ngoài step). Mix & match nhiều đội thì gốc lấy đội
    // vừa chọn gần nhất.
    const targets = [stepEl, this];
    if (meta.c) {
      // Nền sáng (vd vàng của PIT) thì chữ trắng sẽ chìm -> đổi sang chữ đen.
      const ink = this.#luma(meta.c) > 0.45 ? '#101820' : '#ffffff';
      // Màu để đặt lên NỀN TỐI: lấy màu SÁNG HƠN trong hai màu của đội.
      // Không phải cứ màu phụ — ARI/ATL/CIN/NYJ có màu phụ là đen tuyền, dùng nó trên
      // nền tối là vô hình. Cả hai đều tối thì đành dùng trắng.
      const c2 = meta.c2 || meta.c;
      const lMax = Math.max(this.#luma(meta.c), this.#luma(c2));
      // Ngưỡng 0.10: vàng #9E7C0C của BAL có luma 0.215, để 0.22 là nó bị coi là 'quá tối'
      // và rơi về trắng dù trên nền đen nhìn rất rõ.
      const onDark = lMax < 0.1 ? '#ffffff' : (this.#luma(c2) > this.#luma(meta.c) ? c2 : meta.c);
      targets.forEach((el) => {
        el.style.setProperty('--bb-team', meta.c);
        el.style.setProperty('--bb-team-2', c2);
        el.style.setProperty('--bb-team-ink', ink);
        el.style.setProperty('--bb-team-on-dark', onDark);
        el.dataset.bbTeamColor = '';
      });
    } else {
      targets.forEach((el) => {
        el.style.removeProperty('--bb-team');
        el.style.removeProperty('--bb-team-2');
        el.style.removeProperty('--bb-team-ink');
        el.style.removeProperty('--bb-team-on-dark');
        delete el.dataset.bbTeamColor;
      });
    }
    // Logo đội cho nút ADD TO CART ở landing (CSS ::before). url() phải có ngoặc kép: link CDN có '&'.
    targets.forEach((el) => {
      // lc = logo đã tách nền (asset bb-lg-*.png) -> vẽ trần + viền trắng; chỉ có l (nền trắng) -> vòng tròn trắng.
      const src = meta.lc || meta.l;
      if (src) {
        el.style.setProperty('--bb-team-logo', `url("${String(src).replace(/"/g, '%22')}")`);
        el.dataset.bbTeamLogo = '';
        if (meta.lc) el.dataset.bbTeamLogoCut = ''; else delete el.dataset.bbTeamLogoCut;
      } else {
        el.style.removeProperty('--bb-team-logo');
        delete el.dataset.bbTeamLogo;
        delete el.dataset.bbTeamLogoCut;
      }
    });
  }
  /**
   * Đổi title hiển thị: "KC Short Sleeve Polo SC101" -> "Kansas City Chiefs Short Sleeve
   * Polo SC101". Chỉ đổi token đầu và chỉ khi token đó là mã đội đã biết.
   * KHÔNG đụng dữ liệu gốc — giỏ hàng/checkout/email vẫn hiện mã đội (cố ý).
   */
  #displayTitle(t) {
    const s = String(t || '');
    const i = s.indexOf(' ');
    if (i < 1) return s;
    const full = this.teamNames[s.slice(0, i).toLowerCase()];
    return full ? full + s.slice(i) : s;
  }
  #slugifyBrand(b) {
    // Nút chọn đội có data-slug (mã đội) thì ưu tiên: tên đầy đủ slug ra
    // "kansas-city-chiefs", trong khi collection tên "kc-t-shirt".
    const mapped = this.brandSlugs && this.brandSlugs[String(b || '').trim().toLowerCase()];
    if (mapped) return mapped;
    return String(b || '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
  /**
   * Pattern handle collection-theo-brand cho TAB đang chọn (căn theo tab như #activeScope).
   * Merchant nhập "{brand}-short-sleeve-polo, {brand}-t-shirt, ..." theo thứ tự tab.
   * Trả '' nếu không cấu hình.
   */
  #activePattern(stepEl) {
    if (this.#upsellActive()) return this.upsellPats[this.singleCat] || '';
    const list = (stepEl.dataset.brandPattern || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!list.length) return '';
    const tabs = [...stepEl.querySelectorAll('[data-bb-subtab]')];
    if (tabs.length) {
      let i = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      if (i < 0) i = 0;
      return list[i] || list[list.length - 1] || '';
    }
    return list[0] || '';
  }
  /**
   * Dựng handle collection trực tiếp theo brand từ pattern (vd "porsche-short-sleeve-polo").
   * Null nếu chưa cấu hình pattern -> giữ luồng search store cũ.
   */
  #directHandle(stepEl, brand) {
    const pat = this.#activePattern(stepEl);
    const slug = this.#slugifyBrand(brand);
    if (!pat || !slug) return null;
    return pat.includes('{brand}') ? pat.replace(/\{brand\}/g, slug) : `${slug}-${pat}`;
  }

  /* ---------- events ---------- */

  #onClick(e) {
    this._userActed = true;
    this.#trackClick(e);
    const tab = e.target.closest('[data-bb-subtab]');
    if (tab) return this.#switchTab(tab);
    const saveOpen = e.target.closest('[data-bb-save-open]');
    if (saveOpen) return this.#saveOpen(saveOpen);

    const themeToggle = e.target.closest('[data-bb-themetoggle]');
    if (themeToggle) {
      const bar = themeToggle.closest('[data-bb-themebar]');
      const open = bar.classList.toggle('bb-themebar--open');
      themeToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    const themeChip = e.target.closest('[data-bb-themechip]');
    if (themeChip) return this.#selectTheme(themeChip.closest('[data-bb-step]'), themeChip.dataset.theme || 'all', true);
    const themeReset = e.target.closest('[data-bb-themereset]');
    if (themeReset) {
      return this.#selectTheme(themeReset.closest('[data-bb-step]'), themeReset.dataset.theme || 'all', true);
    }

    const brandBtn = e.target.closest('[data-bb-brandbtn]');
    if (brandBtn) {
      const stepEl = brandBtn.closest('[data-bb-step]');
      this.#track('BrandSelected', {
        brand: brandBtn.dataset.brand,
        step: parseInt(stepEl.dataset.bbStep, 10) + 1,
      });
      return this.#selectBrand(stepEl, brandBtn.dataset.brand, true);
    }

    const brandChange = e.target.closest('[data-bb-brandchange]');
    if (brandChange) return this.#openPicker(brandChange.closest('[data-bb-step]'));

    const pill = e.target.closest('[data-bb-pill]');
    if (pill) return this.#selectPill(pill);

    // Trang bật `card_click_landing` (+ deeplink): bấm ẢNH hoặc TÊN thẻ trên LƯỚI -> mở landing
    // ?design=<handle> của chính trang này (giống landing mã design). Nút kính lúp vẫn phóng to;
    // thẻ mở sẵn / Complete the set không nằm trong [data-bb-grid] nên không dính.
    if (this.dataset.cardLanding && this.dataset.deeplink) {
      const hit = e.target.closest('.bb-card__media, .bb-card__title');
      const gcard = hit && !e.target.closest('.bb-card__view') && hit.closest('[data-bb-grid] [data-bb-card]');
      if (gcard && gcard.dataset.handle && !gcard.hasAttribute('data-bb-spot')) {
        this.#track('CardLandingClick', { design: gcard.dataset.handle });
        const u = new URL(window.location.href);
        ['design', 'type', 'team', 'hint'].forEach((k) => u.searchParams.delete(k));
        u.searchParams.set('design', gcard.dataset.handle);
        u.hash = '';
        window.location.href = u.href;
        return;
      }
    }

    const zoom = e.target.closest('[data-bb-zoom]');
    // Lưới: dataset nằm trên card; result: ảnh tự mang dataset -> fallback chính nó.
    if (zoom) return this.#openZoom(zoom.closest('[data-bb-card]') || zoom);
    if (e.target.closest('[data-bb-zoom-prev]')) return this.#zoomStep(-1);
    if (e.target.closest('[data-bb-zoom-next]')) return this.#zoomStep(1);
    const zthumb = e.target.closest('[data-bb-zoom-thumb]');
    if (zthumb && this.zoom) {
      this.zoom.index = parseInt(zthumb.dataset.bbZoomThumb, 10) || 0;
      return this.#renderZoom();
    }
    if (e.target.closest('[data-bb-lightbox-close]')) return this.#closeZoom();

    const addBtn = e.target.closest('[data-bb-add]');
    if (addBtn && this.dataset.lpV2 && addBtn.closest('.bb-lp') && !this.#resolveVariant(addBtn.closest('[data-bb-card]'))) {
      return this.#lpNeedSize(addBtn.closest('[data-bb-card]'));
    }
    if (addBtn && !addBtn.disabled) return this.#addItem(addBtn.closest('[data-bb-card]'));

    const slot = e.target.closest('[data-bb-slot]');
    if (slot) return this.#stepTo(parseInt(slot.dataset.bbSlot, 10), true);

    const cat = e.target.closest('[data-bb-cat]');
    if (cat) return this.#stepTo(parseInt(cat.dataset.bbCat, 10), true);

    const ladderRm = e.target.closest('[data-bb-ladder-remove]');
    if (ladderRm) return this.#removeLadderItem(parseInt(ladderRm.dataset.bbLadderRemove, 10));

    const ladderAdd = e.target.closest('[data-bb-ladder-add]');
    if (ladderAdd) {
      // Che do "chon doi": khong co bo nao de xem, chi dua khach toi bo chon doi.
      // Khong focus o tim doi — tren dien thoai no bat ban phim ao che mat luoi doi.
      if (ladderAdd.hasAttribute('data-bb-pick')) {
        const stepEl = this.stepEls[this.state.current];
        if (stepEl) this.#scrollToStep(stepEl);
        return;
      }
      return this.#openReview();
    }
    const reviewRm = e.target.closest('[data-bb-review-remove]');
    if (reviewRm) {
      // Bộ đang nằm trong giỏ -> kéo về draft trước, không thì sửa state mà giỏ giữ nguyên.
      this.#uncommitLadder();
      this.#removeLadderItem(parseInt(reviewRm.dataset.bbReviewRemove, 10));
      // Không còn món nào thì sheet rỗng vô nghĩa -> đóng, trả khách về lưới.
      if (!this.#target().filter(Boolean).length) return this.#closeReview();
      return this.#openReview();
    }
    if (e.target.closest('[data-bb-review-close]')) return this.#closeReview();
    // KEEP BUILDING: đóng màn xem lại rồi MỞ LẠI sheet chúc mừng — tức màn hình khách vừa
    // rời khỏi, chỗ có nút SWITCH CATEGORY. Chỉ đóng sheet thì khách rơi thẳng vào lưới
    // của loại đang xem và không còn lối nào để đổi loại ngoài hàng tab phía trên.
    if (e.target.closest('[data-bb-review-keep]')) {
      this.#closeReview();
      return this.#celebrate();
    }
    if (e.target.closest('[data-bb-review-checkout]')) {
      this._autoCommitted = null; // bộ này thành chính thức -> đóng sheet không kéo lại nữa
      this.#closeReview();
      return this.#commitLadder();
    }
    if (e.target.closest('[data-bb-barshow]')) return this.#barPeek(false);
    if (e.target.closest('[data-bb-totop]')) {
      const top = this.querySelector('.bb-cats') || this.stepEls[this.state.current];
      top?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Landing: nut trong popup dua khach sang LUOI (luot tiep / chon loai / doi doi) -> mo
    // builder truoc, roi de handler ben duoi lam dung viec cua no. X, nen mo va VIEW CART giu
    // nguyen landing.
    // Popup 2 nut cua landing hang crawl: nut chinh = mo builder o hang LOAI AO (khong phai luoi hoodie).
    if (e.target.closest('[data-bb-celebrate-go]') && this.querySelector('[data-bb-celebrate]')?.classList.contains('bb-celebrate--lp2')) {
      this.#closeCelebrate();
      this.#track('CelebrateAllCats', {});
      return this.#showAllCats();
    }
    if (this.landing && e.target.closest('[data-bb-celebrate-go], [data-bb-celebrate-cat], [data-bb-celebrate-team]')) {
      this.#endLanding();
    }
    const celSwitch = e.target.closest('[data-bb-celebrate-switch]');
    if (celSwitch) {
      const wrap = this.querySelector('[data-bb-celebrate-catwrap]');
      if (!wrap) return;
      const open = wrap.classList.toggle('bb-hide') === false;
      celSwitch.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
    if (e.target.closest('[data-bb-celebrate-close]')) return this.#closeCelebrate();
    const celCat = e.target.closest('[data-bb-celebrate-cat]');
    if (celCat) {
      this.#closeCelebrate();
      return this.#stepTo(parseInt(celCat.dataset.bbCelebrateCat, 10), true);
    }
    // bxgy: loại nằm TRONG step đang xem -> đổi sub-tab, không nhảy step.
    const celSub = e.target.closest('[data-bb-celebrate-subcat]');
    if (celSub) {
      this.#closeCelebrate();
      const stepEl = this.stepEls[this.state.current];
      const tab = stepEl?.querySelectorAll('[data-bb-subtab]')[parseInt(celSub.dataset.bbCelebrateSubcat, 10)];
      if (tab) this.#switchTab(tab);
      stepEl?.querySelector('[data-bb-grid]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (e.target.closest('[data-bb-celebrate-go]')) {
      this.#closeCelebrate();
      // Giữ category hiện tại, cuộn về lưới (title step đang sr-only nên không cuộn tới nó).
      this.stepEls[this.state.current]?.querySelector('[data-bb-grid]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (e.target.closest('[data-bb-celebrate-team]')) {
      this.#closeCelebrate();
      const stepEl = this.stepEls[this.state.current];
      if (!stepEl) return;
      // Cùng đường với nút "Change" trên thanh đội: xoá đội của RIÊNG step này rồi mở picker.
      this.#openPicker(stepEl);
      stepEl.querySelector('[data-bb-brandpicker]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (e.target.closest('[data-bb-celebrate-skip]')) {
      this.#closeCelebrate();
      // Vẫn KHÔNG checkout hộ — mở màn xem lại để khách tự bấm.
      // Offer theo step không có sheet xem lại (nó có màn kết quả riêng) -> mở peek.
      return this.ladderMode ? this.#openReview() : this.#openPeek();
    }

    const edit = e.target.closest('[data-bb-edit]');
    if (edit) {
      const b = edit.dataset.bbBundle;
      this.state.editingBundle = b != null && b !== '' ? parseInt(b, 10) : null;
      this.#closePeek();
      return this.#stepTo(parseInt(edit.dataset.bbEdit, 10), true);
    }

    const rm = e.target.closest('[data-bb-remove-bundle]');
    if (rm) return this.#removeBundle(parseInt(rm.dataset.bbRemoveBundle, 10));

    const matchNav = e.target.closest('[data-bb-match-nav]');
    if (matchNav) return this.#navMatch(matchNav);
    const matchAdd = e.target.closest('[data-bb-match-add]');
    if (matchAdd) return this.#addMatch(parseInt(matchAdd.dataset.bbMatchAdd, 10));
    const otoAdd = e.target.closest('[data-bb-oto-add]');
    if (otoAdd) return this.#otoAdd(parseInt(otoAdd.dataset.bbOtoAdd, 10));
    if (e.target.closest('[data-bb-oto-skip]')) return this.#otoSkip();
    if (e.target.closest('[data-bb-oto-close]')) return this.#closeOto();
    const revOpen = e.target.closest('[data-bb-reviews-open]');
    if (revOpen) return this.#openReviews(revOpen.dataset.bbReviewsOpen);
    if (e.target.closest('[data-bb-reviews-close]')) return this.#closeReviews();
    if (e.target.closest('[data-bb-upsheet-close]')) return this.#closeUpsheet();
    if (e.target.closest('[data-bb-view-order]')) return this.#viewOrder();
    if (e.target.closest('[data-bb-addedsheet-close]')) return this.#closeAddedSheet();
    if (e.target.closest('[data-bb-added-checkout]')) {
      this.#closeAddedSheet();
      return this.#checkoutClick(true);
    }
    if (e.target.closest('[data-bb-upsheet-checkout]')) return this.#upsheetCheckout();
    if (e.target.closest('[data-bb-upbar-checkout]')) return this.#checkoutClick(true);
    if (e.target.closest('[data-bb-add-another]')) return this.#addAnother();
    if (e.target.closest('[data-bb-add-single]')) return this.#startSingle();
    if (e.target.closest('[data-bb-single-cancel]')) return this.#cancelSingle();
    const singleCat = e.target.closest('[data-bb-single-cat]');
    if (singleCat) return this.#pickSingleCat(parseInt(singleCat.dataset.bbSingleCat, 10));
    const rmSingle = e.target.closest('[data-bb-remove-single]');
    if (rmSingle) return this.#removeSingle(parseInt(rmSingle.dataset.bbRemoveSingle, 10));
    if (e.target.closest('[data-bb-back]')) return this.#backToBuilder();
    if (e.target.closest('[data-bb-pickteam]')) {
      // Khong focus o tim doi: tren dien thoai ban phim ao che mat luoi 32 doi vua cuon toi.
      const stepEl = this.stepEls[this.state.current];
      if (stepEl) this.#scrollToStep(stepEl);
      return;
    }
    if (e.target.closest('[data-bb-view]')) return this.#openPeek();
    const catsToggle = e.target.closest('[data-bb-cats-toggle]');
    if (catsToggle) return this.#toggleCats(catsToggle);
    if (e.target.closest('[data-bb-peek-close]')) return this.#closePeek();
    if (e.target.closest('[data-bb-checkout]')) return this.#checkoutClick(false);
    if (e.target.closest('[data-bb-resultbar-checkout]')) return this.#checkoutClick(false);
    const sizeBtn = e.target.closest('[data-bb-size]');
    if (sizeBtn) {
      return this.#changeSize(
        sizeBtn.dataset.bbSizeScope,
        parseInt(sizeBtn.dataset.bbSizeB, 10),
        parseInt(sizeBtn.dataset.bbSizeI, 10),
        parseInt(sizeBtn.dataset.bbSize, 10)
      );
    }

    const guide = e.target.closest('[data-bb-sizeguide]');
    if (guide) {
      // Loại của nút có bảng size text trong trang -> popup text thay ảnh.
      const t = this.#chartTypeOf(guide.closest('[data-bb-step]'));
      return t ? this.#openSizeChart(t) : this.#openGuide(guide.dataset.bbSizeguide);
    }
    const chart = e.target.closest('[data-bb-sizechart]');
    if (chart) return this.#openSizeChart(chart.dataset.bbSizechart);
    if (e.target.closest('[data-bb-modal-close]')) return this.#closeGuide();
  }

  #onChange(e) {
    const card = e.target.closest('[data-bb-card]');
    if (!card) return;
    if (e.target.matches('[data-bb-variant-select]')) {
      const opt = e.target.options[e.target.selectedIndex];
      if (opt && opt.value) {
        card.dataset.selectedVariant = opt.value;
        this.lastOption = opt.textContent;
      }
      this.#refreshCard(card);
      this.#viewCard(card, 'size');
    }
  }

  #onInput(e) {
    const input = e.target.closest('[data-bb-brandfilter]');
    if (!input) return;
    const stepEl = input.closest('[data-bb-step]');
    this.#filterBrandGrid(stepEl, input.value.trim());
  }

  /* ---------- brand autocomplete ---------- */

  /** Điểm khớp: 0 = bắt đầu bằng query; 1 = có TỪ bắt đầu bằng query; 2 = chứa; 3 = không. */
  #matchScore(text, q) {
    const t = (text || '').toLowerCase();
    if (t.startsWith(q)) return 0;
    if (t.split(/[\s/&,-]+/).some((w) => w.startsWith(q))) return 1;
    return t.includes(q) ? 2 : 3;
  }

  /**
   * Tên sản phẩm có ĐÚNG brand đã chọn không? Chỉ giữ sản phẩm của brand đó
   * (Shopify search khớp OR theo scope nên trả về cả brand khác — phải lọc lại).
   * So theo TỪ đầu của brand ở mức word-boundary (vd "Dodge" -> chỉ Dodge,
   * không dính "Duramax"); brand nhiều từ ("Mercedes-Benz") khớp qua từ "mercedes".
   */
  #titleMatchesBrand(title, brand) {
    const first = (brand || '').toLowerCase().split(/[\s/&,-]+/).filter(Boolean)[0] || '';
    if (first.length < 2) return true; // brand quá ngắn -> không lọc để khỏi sót
    return this.#matchScore(title, first) <= 1; // bắt đầu hoặc có từ bắt đầu bằng brand
  }

  /**
   * Mở rộng chữ gõ partial -> tên brand đầy đủ (từ brand list), để gõ "acu" cũng
   * ra Acura (store search của Shopify khớp nguyên từ nên cần tên đầy đủ).
   * Chỉ mở rộng khi là prefix/từ-bắt-đầu của đúng brand; không khớp -> giữ nguyên.
   */
  #resolveBrand(q) {
    if (!this.brands.length || !q) return null;
    const ql = q.toLowerCase();
    const ranked = this.brands
      .map((b) => ({ b, s: this.#matchScore(b, ql) }))
      .filter((x) => x.s <= 1)
      .sort((a, b) => a.s - b.s);
    return ranked.length ? ranked[0].b : null;
  }

  /* ---------- brand selector (bắt buộc) ---------- */

  /** Vào step: auto-áp brand đã chọn (carry-over) hoặc mở picker để bắt chọn. */
  #enterStep(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    this.#prefetchGuide(stepEl);
    // Step "collection-only": bỏ chọn brand -> load thẳng collection của tab đang chọn.
    if (stepEl.dataset.collectionOnly === '1') {
      stepEl.dataset.bbHasBrand = ''; // CSS hiện grid/tabs (đã set sẵn ở Liquid, set lại cho chắc)
      if (!this.search[idx]) this.#loadCollection(stepEl);
      else this.#applyVisibility(stepEl);
      return;
    }
    const stepBrand = stepEl.dataset.brand || '';
    if (stepBrand) {
      stepEl.dataset.bbHasBrand = '';
      if (!this._teamReadyTracked) {
        this._teamReadyTracked = true;
        this.#track('TeamReady', { source: 'restored' });
      }
      if (!this.search[idx]) this.#runSearch(stepEl, stepBrand);
      else this.#applyVisibility(stepEl);
      return;
    }
    if (this.selectedBrand) {
      this.#selectBrand(stepEl, this.selectedBrand, false);
      return;
    }
    this.#openPicker(stepEl);
  }

  /** Step collection-only: load THẲNG collection của tab đang chọn (không brand, không lọc tên). */
  #loadCollection(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const handle = this.#activeHandle(stepEl);
    if (!handle) return; // merchant chưa gán collection
    const grid = stepEl.querySelector('[data-bb-grid]');
    const status = stepEl.querySelector('[data-bb-search-status]');
    // Tái dùng luồng "direct": s.direct = handle collection -> /collections/{handle}?view=bbsearch.
    // s.collectionOnly = true -> #loadSearch KHÔNG fallback sang store-search (term rỗng).
    this.search[idx] = { q: '', term: '', handle, direct: handle, collectionOnly: true, page: 0, pages: 0, more: true, loading: false, seen: new Set() };
    if (grid) grid.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
    this.#clearEmptyState(grid);
    this.#applyVisibility(stepEl);
    if (status) {
      status.classList.remove('bb-hide');
      status.dataset.loading = '';
      status.textContent = this.searchingLabel;
    }
    // 12 khung xám = phủ kín một màn mobile: lưới đang nạp thì khách thấy chỗ giữ chỗ,
    // không phải một mảng trắng (đúng triệu chứng 'step 2/3 trắng màn một lúc').
    this.#showSkeletons(grid, 12);
    this.#loadSearch(stepEl);
  }

  /**
   * @param {boolean} explicit  true = khách CHỦ ĐỘNG bấm chọn brand ở step này;
   *   false = kế thừa tự động (carry-over) từ step trước.
   * Khi chọn chủ động: cập nhật brand mặc định + reset các step KHÁC còn kế thừa
   * (chưa tự chọn) để chúng nhận brand MỚI khi vào -> fix bug carry-over cũ, vẫn
   * giữ mix-and-match cho step nào khách đã tự chọn riêng.
   */
  #selectBrand(stepEl, brand, explicit = false) {
    if (!brand) return;
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    if (!this.brandExplicit) this.brandExplicit = new Set();
    this.#warmSearch(stepEl);
    stepEl.dataset.brand = brand;
    stepEl.dataset.bbHasBrand = '';
    this.selectedBrand = brand;
    if (!this._teamReadyTracked) {
      this._teamReadyTracked = true;
      this.#track('TeamReady', { source: explicit ? 'picked' : 'restored' });
    }
    if (explicit) {
      // Chỉ ghi khi khách TỰ bấm chọn. #enterStep cũng gọi #selectBrand để kế thừa đội
      // sang step sau (explicit=false) — ghi cả trường hợp đó thì danh sách đầy đội trùng.
      this.#pushRecentTeam(brand);
      this.brandExplicit.add(idx);
      this.stepEls.forEach((el, j) => {
        // Bỏ qua step collection-only: nó không phụ thuộc brand -> đừng xóa sản phẩm đã load.
        if (j !== idx && !this.brandExplicit.has(j) && el.dataset.collectionOnly !== '1') this.#resetStepBrand(el);
      });
    }
    const nameEl = stepEl.querySelector('[data-bb-brandbar-name]');
    if (nameEl) nameEl.textContent = brand;
    this.#paintTeamBar(stepEl, brand);
    const filter = stepEl.querySelector('[data-bb-brandfilter]');
    if (filter) filter.value = '';
    this.#filterBrandGrid(stepEl, '');
    this.#persist();
    this.#runSearch(stepEl, brand);
    // `explicit` = khách TỰ bấm chọn đội. Lượt kế thừa đội sang step sau, hoặc lượt khôi
    // phục lúc tải trang, đều explicit=false -> đổi nội dung nhưng không cuộn.
    this.#syncContentVisibility(explicit);
    // Bo chon doi vua dong -> nut thanh day thoi che do "CHOOSE YOUR TEAM".
    // KHONG gioi han ladderMode: offer theo step cung co nut nay.
    this.#barUpdate();
    // Biết đội rồi mới dựng được handle -> lấy giá "from" của các loại còn lại chạy nền.
    this.#prefetchFromPrices(brand);
  }

  /** Xoá trạng thái brand của 1 step (không focus) -> lần vào sau sẽ kế thừa brand mới. */
  #resetStepBrand(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    delete stepEl.dataset.brand;
    stepEl.removeAttribute('data-bb-has-brand');
    this.search[idx] = null;
    const grid = stepEl.querySelector('[data-bb-grid]');
    if (grid) {
      grid.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
      this.#clearSkeletons(grid);
      this.#clearEmptyState(grid);
    }
    const status = stepEl.querySelector('[data-bb-search-status]');
    if (status) {
      status.classList.add('bb-hide');
      delete status.dataset.loading;
    }
    const filter = stepEl.querySelector('[data-bb-brandfilter]');
    if (filter) {
      filter.value = '';
      this.#filterBrandGrid(stepEl, '');
    }
  }

  /** Mở lại lưới chọn brand (đổi brand): xoá cờ explicit của step này + reset + focus. */
  #openPicker(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    if (this.brandExplicit) this.brandExplicit.delete(idx);
    this.#resetStepBrand(stepEl);
    this.#renderRecentTeams();
    this.#syncContentVisibility(true); // khách bấm CHANGE -> hiện nội dung trang, cuộn lại
    this.#barUpdate();
    const filter = stepEl.querySelector('[data-bb-brandfilter]');
    if (filter) filter.focus({ preventScroll: true });
  }

  /**
   * Đội khách ĐÃ CHỌN, mới nhất đứng trước. Ở localStorage nên sống qua phiên — cùng lý
   * do với `:team`: đây là sở thích của khách, không gắn với giỏ hàng của phiên.
   * Cắt còn 6: một bộ nhiều nhất 3 đội, giữ dài hơn thì hàng chip dài hơn cả hàng Popular
   * ngay bên dưới mà chẳng ai bấm tới.
   */
  #recentTeams() {
    try {
      const a = JSON.parse(localStorage.getItem(`${this.storageKey}:recent`) || '[]');
      // Cắt ở CẢ lúc đọc, không chỉ lúc ghi: dữ liệu cũ (hoặc bị sửa tay) có thể dài hơn.
      return Array.isArray(a) ? a.filter((x) => typeof x === 'string' && x).slice(0, 6) : [];
    } catch {
      return [];
    }
  }
  #pushRecentTeam(brand) {
    if (!brand) return;
    const list = this.#recentTeams().filter((b) => b !== brand);
    list.unshift(brand);
    try {
      localStorage.setItem(`${this.storageKey}:recent`, JSON.stringify(list.slice(0, 6)));
    } catch {
      /* non-critical */
    }
  }
  /** Đổ chip vào hàng "đội đã chọn" của MỌI step (mỗi step có bộ chọn đội riêng). */
  #renderRecentTeams() {
    // Đội bị xoá/đổi tên trong metaobject thì bỏ qua — không thì bấm vào là đi tìm
    // collection không tồn tại. Cùng cách kiểm với #restore().
    const list = this.#recentTeams().filter((b) => this.teamMeta[this.#slugifyBrand(b)]);
    this.querySelectorAll('[data-bb-brandrecent]').forEach((box) => {
      box.querySelectorAll('[data-bb-brandbtn]').forEach((b) => b.remove());
      list.forEach((b) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bb-brandchip';
        btn.dataset.bbBrandbtn = '';
        btn.dataset.brand = b;
        // data-slug = MÃ đội. Bỏ nó là #slugifyBrand() slugify tên đầy đủ ra
        // "kansas-city-chiefs" trong khi collection tên "kc-t-shirt".
        btn.dataset.slug = this.#slugifyBrand(b);
        // Trang bật "chỉ logo": chip lấy logo của ô cùng đội trong lưới; ô đó không có logo thì giữ chữ.
        const picker = box.closest('.bb-brandpicker--logo');
        const src = picker && [...picker.querySelectorAll('[data-bb-brandgrid] [data-bb-brandbtn]')]
          .find((g) => g.dataset.brand === b)?.dataset.img;
        if (src) {
          btn.classList.add('bb-brandchip--img');
          btn.setAttribute('aria-label', b);
          const img = document.createElement('img');
          img.src = src; img.alt = ''; img.width = 44; img.height = 44; img.decoding = 'async';
          btn.append(img);
        } else {
          btn.textContent = b;
        }
        box.append(btn);
      });
      box.classList.toggle('bb-hide', !list.length);
    });
  }

  /** Lọc + xếp hạng nút brand: prefix-match lên trước (gõ "M" -> brand bắt đầu bằng M),
   *  rồi từ-bắt-đầu, rồi chứa; ẩn nút không khớp. Rỗng = khôi phục thứ tự gốc. */
  #filterBrandGrid(stepEl, q) {
    const grid = stepEl.querySelector('[data-bb-brandgrid]');
    if (!grid) return;
    const query = (q || '').toLowerCase();
    let visible = 0;
    const ranked = [...grid.querySelectorAll('[data-bb-brandbtn]')].map((btn) => {
      const s = query ? this.#matchScore(btn.dataset.brand || '', query) : 0;
      const show = !query || s <= 2;
      btn.hidden = !show;
      if (show) visible++;
      return { btn, s, o: parseInt(btn.dataset.order, 10) || 0 };
    });
    // Xếp: hạng khớp tăng dần (0=prefix), cùng hạng giữ thứ tự gốc.
    ranked.sort((a, b) => a.s - b.s || a.o - b.o);
    ranked.forEach((x) => grid.append(x.btn));
    const none = stepEl.querySelector('[data-bb-brand-none]');
    if (none) none.classList.toggle('bb-hide', visible !== 0);
  }

  #onSentinel(entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const stepEl = entry.target.closest('[data-bb-step]');
      if (!stepEl || stepEl.classList.contains('bb-hide')) continue;
      const idx = parseInt(stepEl.dataset.bbStep, 10);
      if (this.search[idx]) this.#loadSearch(stepEl);
    }
  }

  /* ---------- size guide modal ---------- */

  #openGuide(src) {
    const modal = this.querySelector('[data-bb-modal]');
    if (!modal || !src) return;
    const img = modal.querySelector('[data-bb-modal-img]');
    if (img.getAttribute('src') !== src) {
      // Ảnh chưa tải xong -> hiện spinner, ẩn ảnh tới khi onload (đã prefetch thì gần như tức thì).
      modal.classList.add('bb-modal--loading');
      img.classList.remove('bb-modal__img--ready');
      img.onload = () => {
        modal.classList.remove('bb-modal--loading');
        img.classList.add('bb-modal__img--ready');
      };
      img.onerror = () => modal.classList.remove('bb-modal--loading');
      img.src = src;
      if (img.complete && img.naturalWidth) img.onload(); // đã có trong cache
    } else {
      img.classList.add('bb-modal__img--ready');
    }
    modal.classList.remove('bb-hide');
    modal.setAttribute('aria-hidden', 'false');
  }

  /** Tải sẵn ảnh size guide của step (chạy lúc nhàn rỗi) -> mở modal là có ngay, cả mobile. */
  #prefetchGuide(stepEl) {
    const btn = stepEl && stepEl.querySelector('[data-bb-sizeguide]');
    const url = btn && btn.dataset.bbSizeguide;
    if (!url) return;
    if (!this.guidePrefetched) this.guidePrefetched = new Set();
    if (this.guidePrefetched.has(url)) return;
    this.guidePrefetched.add(url);
    const warm = () => {
      const im = new Image();
      if ('fetchPriority' in im) im.fetchPriority = 'low';
      im.decoding = 'async';
      im.src = url;
    };
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2000 });
    else setTimeout(warm, 300);
  }
  #closeGuide() {
    const modal = this.querySelector('[data-bb-modal]');
    if (!modal) return;
    modal.classList.add('bb-hide');
    modal.setAttribute('aria-hidden', 'true');
  }

  /* ---------- product image lightbox (zoom) ---------- */

  /** Trả URL ảnh ở width yêu cầu — GHI ĐÈ tham số width có sẵn (tránh ?width=400&width=1400). */
  #sizeUrl(u, w) {
    const url = httpsUrl(u);
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.href);
      parsed.searchParams.set('width', String(w));
      return parsed.href;
    } catch {
      return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
    }
  }

  /** Width ảnh zoom theo viewport (làm tròn 100px để cache CDN tốt) -> mobile nhẹ, desktop nét. */
  /**
   * Bề rộng ảnh zoom — CHỈ HAI MỨC, cố ý.
   *
   * CDN Shopify mất ~1 giây để sinh bản resize LẦN ĐẦU của mỗi cặp (ảnh × bề rộng). Công
   * thức cũ đẻ ra 700/800/1000/1200/1600 tuỳ máy khách -> gần như ai bấm zoom cũng rơi vào
   * một bản chưa có, phải đợi CDN dựng. Gom về 2 mức thì bản đó nhanh chóng "nóng" và
   * khách sau bấm là hiện ngay.
   */
  #zoomWidth() {
    // 900 / 1200 chứ không phải 1000 / 1600: lightbox cao tối đa 80vh và rộng tối thiểu
    // 520px, nên 1200 đã dư nét trên desktop. Đo thật: 900px ≈ 142KB, 1200px ≈ 216KB.
    return (window.innerWidth || 800) < 750 ? 900 : 1200;
  }

  #resultImgWidth() {
    // Ô ảnh result bị giới hạn ~260px (CSS), serve ≥2× để nét mọi DPR.
    return (window.innerWidth || 800) < 700 ? 480 : 600;
  }

  #openZoom(card) {
    const box = this.querySelector('[data-bb-lightbox]');
    if (!box || !card) return;
    this.#track('ZoomOpened', {});
    this.#trackView(card.dataset.productId, card.dataset.title, parseInt(card.dataset.bbMinPrice, 10) || 0, 'zoom');
    const raw = card.dataset.zoomraw || '';
    const first = raw ? this.#sizeUrl(raw, this.#zoomWidth()) : card.dataset.image;
    this.zoom = {
      main: first ? [first] : [],
      thumbs: first ? [first] : [],
      index: 0,
      title: card.dataset.title || '',
      handle: card.dataset.handle || '',
      // Bản xem trước = ảnh 240 của thẻ vừa bấm, NHƯNG chỉ khi nó đã tải xong thật.
      // Thẻ nằm dưới màn hình có `loading="lazy"` nên ảnh có thể chưa về; lấy URL đó ra
      // dùng thì trình duyệt phải tải THÊM một ảnh nữa -> khung trắng, tệ hơn là không có.
      seed: (() => {
        const im = card.querySelector('img');
        return im && im.complete && im.naturalWidth ? im.currentSrc || im.src : '';
      })(),
    };
    const cap = box.querySelector('[data-bb-lightbox-caption]');
    if (cap) cap.textContent = this.zoom.title;
    this.#renderZoom();
    box.classList.remove('bb-hide');
    box.setAttribute('aria-hidden', 'false');
    if (this.zoom.handle) this.#loadZoomImages(this.zoom.handle);
  }

  /** Tải TẤT CẢ ảnh của sản phẩm khi zoom (lazy, cache theo handle) -> không phình lưới. */
  async #loadZoomImages(handle) {
    if (!this.zoomCache) this.zoomCache = new Map();
    const build = (imgs) => {
      if (!imgs || !imgs.length) return;
      if (!this.zoom || this.zoom.handle !== handle) return; // đã đóng/đổi sản phẩm
      const main = imgs.map((u) => this.#sizeUrl(u, this.#zoomWidth())).filter(Boolean);
      if (main.length < 2) return; // chỉ 1 ảnh -> giữ nguyên, không cần gallery
      this.zoom.main = main;
      this.zoom.thumbs = imgs.map((u) => this.#sizeUrl(u, 160)).filter(Boolean);
      this.zoom.index = 0;
      this.#renderZoom();
    };
    if (this.zoomCache.has(handle)) return build(this.zoomCache.get(handle));
    try {
      const data = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const imgs = Array.isArray(data.images) ? data.images : [];
      this.zoomCache.set(handle, imgs);
      build(imgs);
    } catch {
      /* giữ ảnh featured */
    }
  }

  /**
   * Đặt ảnh cho lightbox theo kiểu "nhỏ trước, to sau".
   *
   * Bản cũ gán thẳng `img.src` = ảnh to -> khung trống cho tới khi ảnh về (CDN có khi mất
   * cả giây để dựng bản resize), khách tưởng lỗi. Giờ:
   *   1. gán ngay ảnh thumbnail — nó đã nằm trong cache trình duyệt vì lưới vừa tải, nên
   *      hiện TỨC THÌ (hơi mờ vì phóng to);
   *   2. bật vòng quay chờ;
   *   3. tải ảnh to trong `new Image()`, xong mới tráo vào và tắt vòng quay.
   * Ảnh to đã có trong cache thì `complete` = true -> tráo luôn, không thấy vòng quay.
   */
  #setZoomImage(full, thumb) {
    const box = this.querySelector('[data-bb-lightbox]');
    const img = box && box.querySelector('[data-bb-lightbox-img]');
    if (!img || !full) return;
    const spin = box.querySelector('[data-bb-zoom-spin]');
    const done = () => {
      img.src = full;
      img.classList.remove('bb-lightbox__img--soft');
      if (spin) spin.hidden = true;
    };
    this._zoomWant = full; // ảnh mới nhất khách muốn xem; lượt tải cũ về sau thì bỏ qua
    const pre = new Image();
    pre.src = full;
    if (pre.complete && pre.naturalWidth) return done();
    if (thumb) {
      img.src = thumb;
      img.classList.add('bb-lightbox__img--soft');
    }
    if (spin) spin.hidden = false;
    pre.addEventListener('load', () => { if (this._zoomWant === full) done(); }, { once: true });
    pre.addEventListener('error', () => { if (spin) spin.hidden = true; }, { once: true });
  }

  #renderZoom() {
    const box = this.querySelector('[data-bb-lightbox]');
    if (!box || !this.zoom) return;
    const { main, thumbs, index, title } = this.zoom;
    const img = box.querySelector('[data-bb-lightbox-img]');
    if (img && main.length) {
      img.alt = title;
      // Bản xem trước PHẢI là ảnh nhỏ đã có trong cache. Đừng lấy `thumbs[0]` — ở lượt mở
      // đầu tiên #openZoom gán thumbs = [ảnh TO], lấy nhầm là khung vẫn trống như cũ.
      // Ảnh 1 -> dùng seed (ảnh 240 của thẻ vừa bấm); ảnh 2+ -> thumb 160 của gallery.
      const small = (index > 0 && thumbs && thumbs[index]) || this.zoom.seed || '';
      this.#setZoomImage(main[index] || main[0], small);
    }
    const multi = main.length > 1;
    box.querySelectorAll('[data-bb-zoom-prev],[data-bb-zoom-next]').forEach((b) => b.classList.toggle('bb-hide', !multi));
    const wrap = box.querySelector('[data-bb-lightbox-thumbs]');
    if (wrap) {
      wrap.classList.toggle('bb-hide', !multi);
      if (multi) {
        wrap.textContent = '';
        thumbs.forEach((src, i) => {
          const t = document.createElement('button');
          t.type = 'button';
          t.className = 'bb-lightbox__thumb';
          t.dataset.bbZoomThumb = String(i);
          t.setAttribute('aria-selected', i === index ? 'true' : 'false');
          const ti = document.createElement('img');
          ti.src = src;
          ti.alt = '';
          ti.loading = 'lazy';
          t.append(ti);
          wrap.append(t);
        });
      }
    }
  }

  #zoomStep(delta) {
    if (!this.zoom || this.zoom.main.length < 2) return;
    const n = this.zoom.main.length;
    this.zoom.index = (this.zoom.index + delta + n) % n;
    this.#renderZoom();
  }

  #closeZoom() {
    const box = this.querySelector('[data-bb-lightbox]');
    if (!box) return;
    box.classList.add('bb-hide');
    box.setAttribute('aria-hidden', 'true');
    const img = box.querySelector('[data-bb-lightbox-img]');
    if (img) img.src = '';
    this.zoom = null;
  }

  /* ---------- tabs (multi-collection) ---------- */

  #switchTab(tab) {
    const stepEl = tab.closest('[data-bb-step]');
    if (tab.getAttribute('aria-selected') === 'true') return;
    stepEl.querySelectorAll('[data-bb-subtab]').forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
    this.#syncStepCatsToggle(stepEl);
    // Collection-only: đổi tab = đổi collection -> load thẳng collection mới.
    if (stepEl.dataset.collectionOnly === '1') return this.#loadCollection(stepEl);
    const brand = stepEl.dataset.brand || '';
    // Đổi tab (collection) -> search lại brand trong collection vừa chọn.
    if (brand) this.#runSearch(stepEl, brand);
  }

  /** Ẩn/hiện card theo (đang search?, tab active, chủ đề đang lọc). 1 nguồn sự thật. */
  #applyVisibility(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const searching = !!this.search[idx];
    const active = this.#activeHandle(stepEl);
    this.#initThemeBar(stepEl);
    const theme = this.#activeTheme(stepEl);
    stepEl.querySelectorAll('[data-bb-card]').forEach((card) => {
      // The mau mo san tu link quang cao: luon hien, khong chiu loc chu de/tab.
      if (card.hasAttribute('data-bb-spot')) {
        card.removeAttribute('data-bb-hidden');
        if (this.ladderMode) this.#paintLadderCard(card);
        return;
      }
      let visible;
      if (card.hasAttribute('data-bb-search-card')) visible = searching;
      else visible = !searching && card.dataset.bbHandle === active;
      if (visible && !this.#matchesTheme(card, theme)) visible = false;
      card.toggleAttribute('data-bb-hidden', !visible);
      if (this.ladderMode) this.#paintLadderCard(card);
      // Card nạp theo batch: vẽ ngay lúc lộ ra, không thì hàng dưới vẫn hiện giá gốc.
      else if (this.singlePick) this.#paintSingleCard(card);
    });
    this.#syncThemeBar(stepEl);
    this.#syncResultCount(stepEl);
  }

  /* ---------- lọc theo chủ đề design ---------- */

  /**
   * Chủ đề của 1 sản phẩm. Ba nguồn, dừng ở nguồn đầu tiên tìm được:
   *   1. tag `theme:veteran` (hoặc tag đúng bằng key) -> ngoại lệ, và design mang 2 chủ
   *      đề (camo + cờ Mỹ) mà mã chỉ chở được 1.
   *   2. CHỮ trong title: "ARI Hunting T-shirt SC001" -> hunting. Khớp theo nhãn hoặc key
   *      của chủ đề (key có gạch nối đọc thành khoảng trắng: breast-cancer = "breast cancer").
   *   3. tiền tố mã design ở CUỐI title: "ARI T-shirt SCH001" -> "SCH" -> hunting.
   * Không ra gì -> `classic` (nếu có chủ đề đó), để hàng không bao giờ biến mất khỏi lưới.
   *
   * Title đứng TRƯỚC mã là có chủ đích: mã cũ `SC` được khai là classic, để mã chạy trước
   * thì "ARI Hunting T-shirt SC001" sẽ ra classic và chữ trong title thành vô nghĩa.
   */
  #themesOf(product) {
    const known = this.themeMeta || {};
    const out = [];
    for (const raw of product.tags || []) {
      const t = String(raw).trim().toLowerCase();
      const k = t.startsWith('theme:') ? t.slice(6).trim() : t;
      if (k && known[k] && !out.includes(k)) out.push(k);
    }
    if (out.length) return out;
    const byTitle = this.#themesFromTitle(product.title);
    if (byTitle.length) return byTitle;
    const byCode = this.#themeFromCode(product.title);
    if (byCode) return [byCode];
    return known.classic ? ['classic'] : [];
  }
  /**
   * Tìm tên chủ đề viết thẳng trong title. So theo TỪ trọn vẹn (đệm khoảng trắng hai
   * đầu) để "hunt" không ăn nhầm "hunting", và bỏ qua chủ đề `classic` — chữ "classic"
   * trong tên áo là mô tả kiểu dáng, không phải chủ đề.
   */
  #themesFromTitle(title) {
    const hay = ` ${String(title || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
    const out = [];
    for (const key of Object.keys(this.themeMeta || {})) {
      if (key === 'classic') continue;
      const terms = [this.themeMeta[key], key.replace(/-/g, ' ')];
      for (const term of terms) {
        const t = String(term || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        if (t && hay.includes(` ${t} `)) {
          if (!out.includes(key)) out.push(key);
          break;
        }
      }
    }
    return out;
  }
  /** "ARI T-shirt SCH001" -> "SCH" -> key chủ đề. Mã không khai -> '' (rơi về classic). */
  #themeFromCode(title) {
    const parts = String(title || '').trim().split(/\s+/);
    const code = (parts[parts.length - 1] || '').toUpperCase();
    // Mã dạng CHỮ + SỐ, cho phép đuôi "-2" của bản làm lại (SC100-2).
    const m = code.match(/^([A-Z]+)\d+(?:-\d+)?$/);
    if (!m) return '';
    const key = this.themeCodes[m[1]];
    return key && this.themeMeta[key] ? key : '';
  }
  #matchesTheme(card, theme) {
    if (!theme || theme === 'all') return true;
    return (card.dataset.bbThemes || '').split(' ').includes(theme);
  }
  #activeTheme(stepEl) {
    const on = stepEl.querySelector('[data-bb-themechip][aria-pressed="true"]');
    return (on && on.dataset.theme) || 'all';
  }
  /**
   * Chọn chip mặc định LẦN ĐẦU vào step. Ưu tiên chủ đề khách đã tự bấm (nếu step này
   * có chip đó), không thì `theme_default` của step — nhờ vậy mỗi step mở ra đúng chủ
   * đề merchant muốn đẩy, nhưng khách bấm một lần là ý khách thắng ở các step sau.
   */
  #initThemeBar(stepEl) {
    const bar = stepEl.querySelector('[data-bb-themebar]');
    if (!bar || bar.dataset.bbThemeInit === '1') return;
    bar.dataset.bbThemeInit = '1';
    const keys = [...bar.querySelectorAll('[data-bb-themechip]')].map((c) => c.dataset.theme);
    const fallback = bar.dataset.defaultTheme || '';
    // Step ẩn chip "All" (chạy riêng một chủ đề) -> mặc định là chip ĐẦU HÀNG, không
    // phải 'all': 'all' không có chip nào để bật thì hàng chip trông như tắt hết.
    const want =
      (this.selectedTheme && keys.includes(this.selectedTheme) && this.selectedTheme) ||
      // Chu de cua mau trong link quang cao: chi song trong luot xem nay (khong ghi localStorage).
      (this.deepTheme && keys.includes(this.deepTheme) && this.deepTheme) ||
      (fallback && keys.includes(fallback) && fallback) ||
      (keys.includes('all') ? 'all' : keys[0] || 'all');
    this.#pressChip(stepEl, want);
    this.#syncThemeToggle(stepEl);
  }
  #pressChip(stepEl, theme) {
    stepEl.querySelectorAll('[data-bb-themechip]').forEach((c) => {
      c.setAttribute('aria-pressed', c.dataset.theme === theme ? 'true' : 'false');
    });
    // Badge chủ đề trên thẻ chỉ hiện ở chế độ "All" (CSS đọc attribute này).
    stepEl.toggleAttribute('data-bb-theme-all', theme === 'all');
  }
  #selectTheme(stepEl, theme, explicit = false) {
    if (!stepEl) return;
    if (this.#activeTheme(stepEl) === theme) return;
    this.#pressChip(stepEl, theme);
    if (explicit && this.#upsellActive()) {
      this.singleTheme = theme; // chỉ nhớ cho lượt upsell, KHÔNG ghi localStorage
    } else if (explicit) {
      this.selectedTheme = theme;
      this.#persist();
      this.#track('ThemeSelected', { theme, step: parseInt(stepEl.dataset.bbStep, 10) + 1 });
    }
    this.#applyVisibility(stepEl);
    this.#syncThemeToggle(stepEl);
    if (explicit) this.#themeStickScroll(stepEl);
  }
  /**
   * Chữ trên nút thu gọn (mobile) = nhãn chip đang chọn; chọn xong thì đóng danh sách lại.
   * Cùng cách với #syncCatsToggle của hàng chọn loại.
   */
  #syncThemeToggle(stepEl) {
    const bar = stepEl && stepEl.querySelector('[data-bb-themebar]');
    if (!bar) return;
    const on = bar.querySelector('[data-bb-themechip][aria-pressed="true"]');
    const cur = bar.querySelector('[data-bb-themecurrent]');
    if (cur && on) cur.textContent = (on.childNodes[0] && on.childNodes[0].textContent || on.textContent).trim();
    bar.classList.remove('bb-themebar--open');
    const btn = bar.querySelector('[data-bb-themetoggle]');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }
  /**
   * Điền số đếm cho từng chip + ẩn chip 0 sản phẩm. Đếm trên card ĐÃ tải, nên chỉ ẩn
   * khi chắc chắn đã tải hết (`more === false`) — không thì đội nào tải dở sẽ bị mất
   * chip oan.
   */
  #syncThemeBar(stepEl) {
    const bar = stepEl.querySelector('[data-bb-themebar]');
    if (!bar) return;
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const s = this.search[idx];
    const complete = !!s && !s.more && !s.loading;
    const handle = this.#activeHandle(stepEl);
    const cards = [...stepEl.querySelectorAll('[data-bb-card]')].filter((c) =>
      c.hasAttribute('data-bb-search-card') ? !!s : c.dataset.bbHandle === handle
    );
    const active = this.#activeTheme(stepEl);
    const counts = {};
    const tiles = bar.classList.contains('bb-themebar--tiles');
    bar.querySelectorAll('[data-bb-themechip]').forEach((chip) => {
      const key = chip.dataset.theme;
      const n = key === 'all' ? cards.length : cards.filter((c) => this.#matchesTheme(c, key)).length;
      counts[key] = n;
      if (tiles) {
        // Ảnh ô = mẫu ĐẦU TIÊN của chủ đề trong lưới đã tải — không tốn request nào.
        // 'All' lấy mẫu thứ 2 để khỏi trùng ảnh với ô chủ đề đứng đầu.
        const pool = key === 'all' ? cards : cards.filter((c) => this.#matchesTheme(c, key));
        const hit = (key === 'all' && pool[1]) || pool.find((c) => c.dataset.image);
        const box = chip.querySelector('[data-bb-themeimg]');
        const url = hit?.dataset.image || '';
        if (box && url && box.dataset.src !== url) {
          box.dataset.src = url;
          box.style.backgroundImage = `url("${url}")`;
        }
      }
      const out = chip.querySelector('[data-bb-themecount]');
      // CHỈ in số khi đã tải hết collection. Trước đây in số của phần đã tải -> chủ đề
      // nằm ở trang cuối (vd Patriotic/FSC) hiện "0" trong khi thật ra có hàng, khách đọc
      // xong là bỏ qua chip đó luôn. Chưa xong thì in '…'.
      if (out) out.textContent = complete ? String(n) : cards.length ? '…' : '';
      // Chip rỗng thì giấu (chủ store 2026-10-05: kể cả chip đang chọn — chip đó bị đổi
      // sang chip khác ngay dưới đây).
      chip.hidden = complete && n === 0 && key !== 'all';
    });
    const activeCount = counts[active] || 0;
    if (complete && active !== 'all' && activeCount === 0) {
      // Chủ đề đang chọn không có mẫu cho đội này -> tự chuyển sang "All" (hoặc chip đầu
      // còn hàng). KHÔNG ghi selectedTheme: đội khác có chủ đề đó vẫn mở đúng ý khách.
      const keys = [...bar.querySelectorAll('[data-bb-themechip]')].map((c) => c.dataset.theme);
      const target = keys.includes('all') ? 'all' : keys.find((k) => counts[k] > 0);
      if (target) {
        this.#pressChip(stepEl, target);
        this.#syncThemeToggle(stepEl);
        this.#applyVisibility(stepEl); // gọi lại #syncThemeBar với chip mới (đã có hàng)
        return;
      }
    }
    const hint = bar.querySelector('[data-bb-themehint]');
    if (hint) {
      const nThemes = [...bar.querySelectorAll('[data-bb-themechip]')].filter((c) => !c.hidden && c.dataset.theme !== 'all').length;
      hint.textContent = nThemes > 1 ? (hint.dataset.tpl || '{n} styles · tap to switch').replace('{n}', nThemes) : '';
    }
    const none = bar.querySelector('[data-bb-themenone]');
    if (none) {
      const show = complete && active !== 'all' && activeCount === 0;
      none.classList.toggle('bb-hide', !show);
      if (show) {
        const label = this.themeMeta[active] || active;
        const team = this.selectedBrand ? this.#esc(this.selectedBrand) : 'This team';
        // Đích thoát: chip "All" nếu step có, không thì chip đầu tiên còn hàng — step ẩn
        // chip All mà nút vẫn trỏ 'all' thì bấm xong không chip nào sáng.
        const keys = [...bar.querySelectorAll('[data-bb-themechip]')].map((c) => c.dataset.theme);
        const target = keys.includes('all') ? 'all' : keys.find((k) => counts[k] > 0) || '';
        const esc = (x) => this.#esc(x);
        none.innerHTML = target
          ? `${team} has no ${esc(label)} designs yet. <button type="button" data-bb-themereset data-theme="${esc(target)}">See ${esc(target === 'all' ? 'all designs' : this.themeMeta[target] || target)}</button>`
          : `${team} has no ${esc(label)} designs yet.`;
      }
    }
  }

  /* ---------- card option logic ---------- */

  #variants(card) {
    try {
      return JSON.parse(card.querySelector('[data-bb-variants]').textContent);
    } catch {
      return [];
    }
  }

  /** Auto chọn variant: ưu tiên size khách đã chọn trước đó, không thì variant đầu. */
  #autoSelect(card) {
    if (card.dataset.mode === 'single') {
      const v = this.#variants(card)[0];
      if (v) card.dataset.selectedVariant = String(v[0]);
      this.#refreshCard(card);
      return;
    }
    // Pills hoặc select đều mang option [data-variant-id]/<option value>.
    const pills = [...card.querySelectorAll('[data-bb-pill]')];
    const select = card.querySelector('[data-bb-variant-select]');
    const opts = pills.length
      ? pills.map((p) => ({ id: p.dataset.variantId, label: p.dataset.label, el: p }))
      : [...(select?.options || [])].filter((o) => o.value).map((o) => ({ id: o.value, label: o.textContent, el: o }));
    const match = this.lastOption && opts.find((o) => o.label === this.lastOption);
    const chosen = match || opts[0];
    if (chosen) {
      card.dataset.selectedVariant = chosen.id;
      if (pills.length) pills.forEach((p) => p.setAttribute('aria-pressed', p === chosen.el ? 'true' : 'false'));
      else if (select) select.value = chosen.id;
    }
    this.#refreshCard(card);
  }

  #selectPill(pill) {
    const card = pill.closest('[data-bb-card]');
    if (!card) return;
    card.querySelectorAll('[data-bb-pill]').forEach((p) => p.setAttribute('aria-pressed', p === pill ? 'true' : 'false'));
    card.dataset.selectedVariant = pill.dataset.variantId;
    if (pill.dataset.label) this.lastOption = pill.dataset.label;
    this.#refreshCard(card);
    this.#viewCard(card, 'size');
  }

  /**
   * ViewContent chuan cho the mau trong luoi khi khach to y dinh RO (chon size, bam ADD) — 2026-10-05.
   * Truoc chi bat khi phong to anh -> khach chon size + ADD ma khong zoom thi tep nham lai catalog
   * khong biet mau nao. #trackView tu chan trung: moi san pham 1 lan moi luot xem trang.
   */
  #viewCard(card, source) {
    if (!card || card.hasAttribute('data-bb-spot')) return; // the landing ?design= da ban luc mo trang
    this.#trackView(card.dataset.productId, card.dataset.title, parseInt(card.dataset.bbMinPrice, 10) || 0, source);
  }

  #resolveVariant(card) {
    const variants = this.#variants(card);
    if (card.dataset.mode === 'single') return variants[0] || null;
    const id = parseInt(card.dataset.selectedVariant, 10);
    return variants.find((v) => v[0] === id) || null;
  }

  #refreshCard(card) {
    const variant = this.#resolveVariant(card);
    const priceEl = card.querySelector('[data-bb-card-price]');
    if (variant && priceEl) {
      if (card.dataset.bbFree === '1') {
        // Step ưu đãi: cập nhật giá gốc (đang gạch) và, với bxpct, cả giá sau giảm.
        const off = parseInt(card.dataset.bbOff, 10) || 100;
        const orig = priceEl.querySelector('[data-bb-card-orig]');
        if (orig) orig.textContent = money(variant[3]);
        const paid = priceEl.querySelector('.bb-card__price-free');
        if (paid && off < 100) paid.textContent = money(this.#slotPay(variant[3], off));
      } else if (this.ladderMode) {
        this.#paintLadderCard(card); // preview thang mở: giá gạch + giá giảm theo variant vừa chọn
      } else if (this.singlePick && this.singlePct > 0) {
        this.#paintSingleCard(card); // preview món lẻ: giá gạch + giá đã giảm theo size vừa chọn
      } else {
        priceEl.textContent = money(variant[3]);
      }
    }
    const addBtn = card.querySelector('[data-bb-add]');
    addBtn.disabled = !variant;
    // Landing hang crawl (chu store 2026-10-15): nut LUON den "ADD TO CART", khong gia, khong khoa;
    // chua chon size ma bam -> #addItem cuon toi hang size + nhay vien.
    const crawlLp = card.classList.contains('bb-lp--crawl');
    if (crawlLp) addBtn.disabled = false;
    if (card.hasAttribute('data-bb-spot')) {
      const lbl = this.dataset.deeplinkAddLabel || 'ADD TO MY BUNDLE';
      const lp = card.classList.contains('bb-lp');
      const has = this.ladderMode && this.#target().some(Boolean);
      const pay = variant ? (has ? this.#ladderNextPay(variant[3]) : variant[3]) : 0;
      // So luong > 1 (o QUANTITY hang crawl): nut ghi TONG cua ca N cai, tinh dung thang giam.
      const qty = Math.max(1, parseInt(card.dataset.bbQty, 10) || 1);
      const payAll = variant && qty > 1 && this.ladderMode ? this.#ladderAddPay(variant[3], qty) : pay;
      // Landing v2 (setting landing_v2): nút luôn "ADD TO CART", không kèm giá, không khoá; chưa chọn size -> #lpNeedSize.
      const v2 = lp && !!this.dataset.lpV2;
      if (v2) addBtn.disabled = false;
      addBtn.textContent = crawlLp
        ? 'ADD TO CART'
        : v2
        ? lbl
        : variant
          ? lp ? `${lbl}${qty > 1 ? ` (${qty})` : ''} · ${money(payAll)}` : lbl
          : this.dataset.deeplinkPickLabel || 'SELECT YOUR SIZE';
      if (lp) {
        const k = card.querySelector('[data-bb-lp-k]');
        const sm = card.querySelector('[data-bb-lp-small]');
        if (k) k.textContent = `${has ? 'ANOTHER' : 'THIS'} ${k.dataset.cat || 'ITEM'}`;
        if (sm) sm.textContent = has ? `${this.dataset.deeplinkAfter || '50% OFF'} — already unlocked` : 'Full price · unlocks the deal';
      }
      const on = card.querySelector('[data-bb-pill][aria-pressed="true"]');
      const sizeEl = card.querySelector('[data-bb-lp-size]');
      if (sizeEl) sizeEl.textContent = on ? on.dataset.label || on.textContent : 'Select';
      const pEl = card.querySelector('[data-bb-lp-price]');
      if (pEl && variant) pEl.textContent = money(pay);
      if (v2 && pEl) {
        // Bộ đã có món: giá gạch -> giá đã giảm, kể cả lúc chưa chọn size (trước: hiện nguyên giá).
        const base = variant ? variant[3] : parseInt(pEl.dataset.full, 10) || 0;
        const now = has ? this.#ladderNextPay(base) : base;
        pEl.innerHTML = (now < base ? `<s>${money(base)}</s> ` : '') + money(now);
      }
      if (v2) this.#syncLpStrip();
      if (card.classList.contains('bb-lp')) this.#syncSetCards();
      const fsEl = card.querySelector('[data-bb-lp-fs]');
      if (fsEl) {
        const t = money(this.fsThreshold).replace(/\.00$/, '');
        const idle = fsEl.dataset.bbLpFs === 'sub' ? 'Counts every item in your order' : `Free US shipping on orders over ${t}`;
        fsEl.textContent = has ? this.#fsPlain(this.#lpPaid()) : idle;
        fsEl.classList.toggle('is-done', has && this.#lpPaid() >= this.fsThreshold);
      }
      const sb = this.querySelector('[data-bb-lp-sticky-btn]');
      if (sb) {
        sb.textContent = crawlLp ? 'ADD TO CART' : this.dataset.lpV2 ? this.dataset.deeplinkAddLabel || 'ADD TO CART' : variant ? `ADD TO CART · ${money(payAll)}` : 'SELECT SIZE';
        const sp = this.querySelector('[data-bb-lp-sticky-p]');
        if (sp) {
          const sz = on ? (on.dataset.label || on.textContent).trim() : '';
          sp.textContent = variant ? `${sz ? `Size ${sz} · ` : ''}${money(pay)}` : `${pEl ? pEl.textContent : ''} · pick your size`;
        }
      }
    }
  }

  /* ---------- đồng hồ đếm ngược ở màn kết quả ---------- */

  /**
   * Đếm ngược tới hết Chủ nhật tuần này (giờ store) — cùng mốc với `deal_end` và banner
   * countdown, ba chỗ không được lệch nhau.
   *
   * Đây là "push nhẹ": nó đứng CHUNG hàng với dòng tiết kiệm, không chiếm hàng riêng.
   * Hết giờ thì ẩn hẳn — in "0d 0h 0m" trông như trang hỏng, và HTML trang bị cache nên
   * chuyện đó xảy ra thật vào tối Chủ nhật.
   */
  #startResultTimer() {
    const box = this.querySelector('[data-bb-result-timer]');
    const end = parseInt(this.dataset.dealEndTs || '', 10);
    if (!box || !Number.isFinite(end)) return;
    const txt = box.querySelector('[data-bb-result-timer-text]');
    const label = (this.dataset.resultTimerLabel || 'Deal ends in').trim();
    const tick = () => {
      const left = end - Math.floor(Date.now() / 1000);
      if (left <= 0) {
        box.classList.add('bb-hide');
        clearInterval(this._timerId);
        this._timerId = 0;
        return;
      }
      const d = Math.floor(left / 86400);
      const h = Math.floor((left % 86400) / 3600);
      const m = Math.floor((left % 3600) / 60);
      const sec = left % 60;
      const pad = (n) => String(n).padStart(2, '0');
      // Còn hơn một ngày thì giây chạy vô nghĩa; dưới một ngày mới đếm tới giây.
      const clock = d > 0 ? `${d}d ${h}h ${pad(m)}m` : `${pad(h)}:${pad(m)}:${pad(sec)}`;
      // `Ends on Sunday · 3d 1h 17m` — thiếu dấu ngăn thì nhãn dính liền con số
      // thành một cục chữ không đọc được ("Ends on Sunday 3d 1h 17m").
      txt.textContent = label ? `${label} · ${clock}` : clock;
      box.classList.remove('bb-hide');
    };
    tick();
    if (!this._timerId) this._timerId = setInterval(tick, 1000);
  }

  /* ---------- size đổi được ngay trên màn xem bộ ---------- */

  /**
   * Danh sách size của một thẻ, dạng `[variantId, nhãn, giá cents]`.
   *
   * Nhãn lấy từ pills/select đã render trên thẻ chứ không tự dựng lại: `labelFor` nằm
   * trong #buildCard nên không gọi được từ đây, mà chữ hiện ở màn xem bộ phải trùng
   * đúng chữ khách vừa bấm ở lưới.
   * Thẻ chỉ có một variant (`mode === 'single'`) -> mảng rỗng -> không render hàng size.
   */
  #variantOpts(card) {
    const all = this.#variants(card);
    const byId = new Map(all.map((v) => [v[0], v]));
    const opts = [];
    const pills = card.querySelectorAll('[data-bb-pill]');
    if (pills.length) pills.forEach((el) => opts.push([parseInt(el.dataset.variantId, 10), el.dataset.label || '']));
    else {
      const sel = card.querySelector('[data-bb-variant-select]');
      if (sel) [...sel.options].forEach((o) => opts.push([parseInt(o.value, 10), o.textContent]));
    }
    return opts.filter(([id]) => byId.has(id)).map(([id, lb]) => [id, lb, byId.get(id)[3]]);
  }

  /**
   * Hàng chip chọn size, dùng chung cho món trong bộ và món lẻ ở màn xem bộ.
   *
   * Vì sao phải có: size mặc định của thẻ là variant ĐẦU TIÊN = "S", trong khi khách
   * NFL Mỹ chủ yếu L/XL/2XL. Trước đây màn xem bộ chỉ IN ra chữ "S — $39.95" và muốn
   * sửa thì phải bấm nút bút chì quay lại lưới -> khách khựng lại đúng ở bước chốt.
   *
   * @param {string} scope 'b' = món trong bộ, 's' = món lẻ
   * @returns {HTMLElement|null} null khi món chỉ có một size (không có gì để chọn)
   */
  #sizeRow(sel, scope, b, i) {
    const list = sel.variants || [];
    if (list.length < 2) return null;
    const row = document.createElement('div');
    row.className = 'bb-sizes';
    const lbl = document.createElement('span');
    lbl.className = 'bb-sizes__label';
    lbl.textContent = 'Size';
    const chips = document.createElement('div');
    chips.className = 'bb-sizes__chips';
    chips.setAttribute('role', 'group');
    chips.setAttribute('aria-label', 'Choose size');
    list.forEach((v) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bb-size';
      btn.dataset.bbSize = String(v[0]);
      btn.dataset.bbSizeScope = scope;
      btn.dataset.bbSizeB = String(b);
      btn.dataset.bbSizeI = String(i);
      const on = v[0] === sel.variantId;
      if (on) btn.classList.add('bb-size--on');
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.textContent = v[1];
      chips.append(btn);
    });
    row.append(lbl, chips);
    return row;
  }

  /**
   * Đổi size của một món ĐANG Ở màn xem bộ.
   *
   * Bộ có thể đã nằm sẵn trong giỏ (từ 2026-08-27 builder tự chốt giỏ khi mở màn này để
   * nút ví có cái đúng mà thanh toán), nên đổi size phải GỠ bộ đó khỏi giỏ rồi để
   * #showResult -> #syncCartForExpress chốt lại. Bỏ bước gỡ là giỏ giữ size cũ trong khi
   * màn hiện size mới — khách trả tiền cho thứ khác với thứ đang nhìn.
   * Mọi lượt ghi giỏ đi qua #cartOp nên thứ tự gỡ-rồi-thêm được bảo đảm.
   */
  #changeSize(scope, b, i, variantId) {
    const sel = scope === 's' ? (this.state.singles || [])[i] : (this.state.bundles[b] || [])[i];
    if (!sel || sel.variantId === variantId) return;
    const v = (sel.variants || []).find((x) => x[0] === variantId);
    if (!v) return;
    this.#track('SizeChanged', { size: v[1] });
    sel.variantId = v[0];
    sel.variantLabel = v[1];
    sel.priceCents = v[2];
    this.lastOption = v[1]; // size vừa chọn thành mặc định cho món thêm tiếp theo
    this.#showExpress(false); // giỏ vừa lệch -> giấu nút ví tới khi chốt lại xong
    if (scope === 's') {
      if (sel.cartId) {
        this.#removeSingleFromCart(sel.cartId);
        sel.cartId = null;
      }
    } else if (this.state.cartIds[b]) {
      this.#removeBundleFromCart(this.state.cartIds[b]);
      this.state.cartIds[b] = null;
    }
    this.#persist();
    this.#showResult(false);
  }

  /**
   * Món dựng lại TỪ GIỎ (khách F5 / quay lại hôm sau) không có danh sách size — giỏ
   * không chở thông tin đó. Hỏi `/products/<handle>.js` một lần rồi vẽ lại màn.
   *
   * Món hỏi hụt được gán `variants = []` (KHÔNG để undefined) để lần sau nó không còn
   * bị coi là "thiếu" — nếu không, #showResult ở cuối hàm sẽ gọi lại chính hàm này và
   * thành vòng lặp vô tận.
   */
  async #ensureVariants() {
    if (this._variantsTried) return;
    const need = new Map();
    const add = (x) => {
      if (!x || x.variants || !x.handle) return;
      if (!need.has(x.handle)) need.set(x.handle, []);
      need.get(x.handle).push(x);
    };
    this.state.bundles.forEach((bn) => (bn || []).forEach(add));
    (this.state.singles || []).forEach(add);
    if (!need.size) return;
    this._variantsTried = true;
    await Promise.all(
      [...need.entries()].map(async ([handle, items]) => {
        let list = [];
        try {
          const prod = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          const vs = prod.variants || [];
          /*
            Nhãn phải giống HỆT chữ trên lưới: chỉ in những option THỰC SỰ khác nhau.
            `variant.title` của Shopify là chuỗi ghép đủ mọi option ("Short Sleeve Polo /
            XL") — in nguyên vào chip size thì chip dài bằng cả thẻ. Đây đúng là cách
            `labelFor` trong #buildCard làm, chỉ khác nguồn dữ liệu.
          */
          const nOpt = vs[0] && vs[0].options ? vs[0].options.length : 0;
          const varies = [];
          for (let k = 0; k < nOpt; k += 1) {
            if (new Set(vs.map((v) => v.options[k])).size > 1) varies.push(k);
          }
          const idx = varies.length ? varies : vs[0] && vs[0].options ? vs[0].options.map((_, k) => k) : [];
          list = vs.map((v) => {
            const parts = idx.map((k) => v.options[k]).filter((x) => x && x !== 'Default Title');
            return [v.id, parts.join(' / ') || (v.title && v.title !== 'Default Title' ? v.title : ''), v.price];
          });
        } catch {
          /* im lặng: thiếu hàng chọn size còn hơn vỡ màn kết quả */
        }
        items.forEach((x) => {
          x.variants = list;
          // Món dựng lại từ giỏ mang `variant_title` dạng dài; đồng bộ về nhãn ngắn để
          // dòng chữ dưới ảnh và chip đang chọn nói cùng một thứ.
          const hit = list.find((v) => v[0] === x.variantId);
          if (hit && hit[1]) x.variantLabel = hit[1];
        });
      })
    );
    this.#persist();
    if (this.classList.contains('bb-on-result')) this.#showResult(false);
  }

  /* ---------- bundle state ---------- */

  #addItem(card) {
    const variant = this.#resolveVariant(card);
    if (!variant) {
      // Landing hang crawl: nut ADD khong khoa -> chua chon size thi chi cho thay hang size.
      if (card.classList.contains('bb-lp--crawl')) this.#flashSizes(card);
      return;
    }
    this.#viewCard(card, 'add');
    const step = parseInt(card.dataset.step, 10);
    if (card.hasAttribute('data-bb-set-card')) {
      this.#track('SetItemAdded', { design: card.dataset.handle || '' });
      setTimeout(() => this.#syncSetCards(), 0);
    }
    if (card.hasAttribute('data-bb-spot')) {
      this.#track('DeeplinkAdded', { design: card.dataset.handle || '' });
      // Chu store chot 2026-09-24: them xong VAN O trang landing, popup chuc mung mo de len.
      // Chi roi landing khi khach bam nut dua sang luoi (xem dau #onClick).
      const n = card.querySelector('[data-bb-spot-note]');
      if (n) n.hidden = true; // mon da vao bo -> cau "mon sau giam 50%" khong con dung cho the nay
      // Mon da vao bo -> the nay gio ban voi gia MON KE TIEP (giam %). Cho push xong moi tinh.
      setTimeout(() => this.#refreshCard(card), 0);
    }
    const pill = card.querySelector('[data-bb-pill][aria-pressed="true"]');
    const select = card.querySelector('[data-bb-variant-select]');
    let label = '';
    if (pill) label = pill.dataset.label || '';
    else if (select) label = select.options[select.selectedIndex]?.textContent || '';
    if (label) this.lastOption = label;

    const item = {
      productId: parseInt(card.dataset.productId, 10),
      variantId: variant[0],
      title: card.dataset.title,
      image: card.dataset.image,            // 240px — cho slot/peek
      imageRaw: card.dataset.zoomraw || '', // URL gốc — size to ở màn result
      handle: card.dataset.handle || '',    // -> zoom gallery + link chi tiết ở result
      variantLabel: label,
      priceCents: variant[3],
      // Danh sách size đi THEO món -> khách đổi size ngay ở màn xem bộ, không phải quay
      // lại lưới. Lấy nhãn từ chính pills/select của thẻ để chữ trên hai màn giống hệt
      // nhau (labelFor là hàm cục bộ trong #buildCard, không gọi lại được từ đây).
      variants: this.#variantOpts(card),
    };
    // Đang chọn MÓN LẺ -> không đụng gì tới bộ đang dựng, chỉ nhặt đúng một món rồi
    // trả khách về màn kết quả. Đặt TRƯỚC mọi thứ khác vì nó không dùng slot nào cả.
    if (this.singlePick) {
      this.state.singles.push(item);
      this.#track('BundleSingleAdded', {
        content_id: String(variant[0]),
        value: variant[3] / 100,
        currency: this.currency,
      });
      if (this.upsellMode) {
        // Không giới hạn số món -> ở lại lưới, báo đã thêm và cho thêm tiếp. Bật về màn
        // kết quả sau MỖI món là bắt khách đi lại hai bước cho mỗi món thứ hai.
        this.#persist();
        card.classList.remove('bb-card--justadded');
        void card.offsetWidth;
        card.classList.add('bb-card--justadded');
        this.#renderUpbar();
        this.#syncCartForExpress(); // chốt giỏ nền: giỏ luôn khớp thứ thanh đáy đang ghi
        // Toast 1,6s không đủ — khách tưởng bấm hỏng. Hỏi thẳng: thanh toán hay thêm tiếp.
        this.#openAddedSheet(item);
        return;
      }
      this.#endSingle();
      this.#persist();
      return this.#showResult(true);
    }
    // Thang mở: nối vào cuối (không giới hạn số món). Có step: lấp đúng slot của step.
    if (this.ladderMode) {
      // O QUANTITY tren landing (hang crawl): N cai = N mon rieng trong bo -> giam thang nhu thuong.
      const qty = Math.max(1, Math.min(10, parseInt(card.dataset.bbQty, 10) || 1));
      for (let k = 0; k < qty; k++) this.#target().push(k ? { ...item, variants: item.variants } : item);
    } else this.#target()[step] = item;
    // Upsell lấy mốc là SẢN PHẨM ĐẦU -> có món đầu là nạp gợi ý được rồi. Nạp từ bây giờ
    // để lúc bộ đủ món, bảng chúc mừng bật lên đã có sẵn thẻ, không "nhảy" layout.
    if (this.upsellMode && (step === 0 || this.upsellMatchPat || this.upsellMatchHandles.length)) this.#loadUpsellMatch(this.#target());

    this.#track('BundleItemAdded', {
      brand: card.closest('[data-bb-step]')?.dataset.brand || this.selectedBrand || '',
      step: step + 1,
      content_id: String(variant[0]),
      product_id: String(parseInt(card.dataset.productId, 10)),
      value: variant[3] / 100,
      currency: this.currency,
    });

    if (!this.ladderMode) {
      this.#flyToSlot(card, step);
      this.#pulseSlot(step);
    }

    // Thang mở: không có "đủ món", không tự nhảy step. SAU MỖI lần thêm món đều mở sheet
    // — món 1 báo mở khoá deal, món 2+ báo đã tiết kiệm bao nhiêu + mời thêm món nữa
    // (giữ loại đang xem hay đổi loại khác). Đây là chỗ thuyết phục chính.
    if (this.ladderMode) {
      this.#persist();
      this.#barUpdate(); // render chip TRƯỚC rồi mới pulse
      this.#pulseNewestChip();
      this.#celebrate();
      return;
    }

    if (this.state.editingBundle !== null) {
      const idx = this.state.editingBundle;
      // Sửa bundle đã nằm trong giỏ -> gỡ bản cũ khỏi giỏ, đánh dấu cần add lại lúc checkout.
      if (this.state.cartIds[idx]) {
        this.#removeBundleFromCart(this.state.cartIds[idx]);
        this.state.cartIds[idx] = null;
      }
      this.state.editingBundle = null;
      this.#persist();
      return this.#showResult(true);
    }

    this.#persist();
    this.#priceUpdate();

    if (this.#isFull(this.state.draft)) {
      const c = this.#calc(this.state.draft, this.#targetIndex());
      this.#track('BundleCompleted', {
        bundle_name: this.bundleName,
        num_items: this.state.draft.length,
        value: c.pay / 100,
        currency: this.currency,
      });
      this.state.bundles.push(this.state.draft);
      this.state.cartIds.push(null); // bundle mới -> chưa trong giỏ
      this.state.draft = this.ladderMode ? [] : Array(this.stepCount).fill(null);
      this.#persist();
      this.#showResult(true);
      // Bảng chúc mừng + upsell: CHỈ ở đây (bộ vừa đủ món) — không mở khi tải lại trang,
      // đổi size, gỡ món. Luồng express sắp chuyển trang thì không mở.
      if (!this.expressAdd) this.#openUpsellSheet();
      // Express ("Add bundle 2 to unlock free ship"): hiện màn final RỒI add luôn vào giỏ,
      // khách không phải kéo xuống bấm "Add to cart" nữa. Chỉ chạy 1 lần cho lượt express.
      if (this.expressAdd) {
        this.expressAdd = false;
        this.#checkout();
      }
      return;
    }
    const next = this.#emptySlot(this.state.draft);
    this.#carryCategory(this.state.current, next);
    this.#stepTo(next, true);
    // Bộ CHƯA đủ món -> sheet nhắc còn mấy món nữa là mở khoá quà + cho đổi loại.
    // Bộ đủ món thì nhánh trên đã `return` sang màn kết quả, không bao giờ tới đây.
    this.#celebrate();
  }

  /**
   * Mang LOẠI sản phẩm đang xem sang step kế tiếp.
   *
   * Khách đang lướt HOODIE, thêm một món, rồi step 2 mở ra ở T-SHIRT (loại đầu danh sách)
   * — đứt mạch, và dòng "You're viewing …" trong sheet trông như builder tự đổi ý. Giữ
   * nguyên loại thì khách muốn đổi vẫn có hàng ô ngay trên lưới + nút trong sheet.
   *
   * Khớp theo NHÃN chứ không theo chỉ số: mỗi step có thể mở một danh sách loại khác nhau,
   * chỉ số 2 của step này không chắc là cùng loại với chỉ số 2 của step kia.
   */
  #carryCategory(fromIdx, toIdx) {
    const from = this.stepEls[fromIdx];
    const to = this.stepEls[toIdx];
    if (!from || !to || from === to) return;
    const cur = from.querySelector('[data-bb-subtab][aria-selected="true"]');
    if (!cur) return;
    const want = cur.textContent.trim().toLowerCase();
    const tabs = [...to.querySelectorAll('[data-bb-subtab]')];
    const tab = tabs.find((t) => t.textContent.trim().toLowerCase() === want);
    if (!tab || tab.getAttribute('aria-selected') === 'true') return;
    // Đặt tab TRƯỚC khi mở step: #enterStep đọc tab đang chọn để biết load collection nào,
    // nên đổi trước thì step mới tinh chỉ tốn đúng một request.
    tabs.forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
    this.#syncStepCatsToggle(to);
    // Step đã từng tải lưới của loại cũ -> #enterStep sẽ không tải lại, phải tự gọi.
    const idx = parseInt(to.dataset.bbStep, 10);
    const brand = to.dataset.brand || this.selectedBrand || '';
    if (this.search[idx] && brand) this.#runSearch(to, brand);
  }

  #pulseSlot(step) {
    const slot = this.querySelector(`[data-bb-slot="${step}"]`);
    if (!slot) return;
    slot.classList.remove('bb-bar__slot--pulse');
    void slot.offsetWidth; // restart animation
    slot.classList.add('bb-bar__slot--pulse');
  }

  /** Ảnh sản phẩm "bay" từ card vào ô slot tương ứng ở thanh đáy. */
  #flyToSlot(card, step) {
    if (this.reduceMotion) return;
    const imgEl = card.querySelector('.bb-card__media img');
    const slot = this.querySelector(`[data-bb-slot="${step}"]`);
    if (!imgEl || !slot) return;
    const from = imgEl.getBoundingClientRect();
    const to = slot.getBoundingClientRect();
    if (!from.width || !to.width) return;

    const fly = document.createElement('img');
    fly.src = imgEl.currentSrc || imgEl.src;
    fly.setAttribute('aria-hidden', 'true');
    Object.assign(fly.style, {
      position: 'fixed',
      left: `${from.left}px`,
      top: `${from.top}px`,
      width: `${from.width}px`,
      height: `${from.height}px`,
      objectFit: 'cover',
      borderRadius: '10px',
      zIndex: '2000',
      pointerEvents: 'none',
      transition: 'transform 0.55s cubic-bezier(0.5, -0.2, 0.3, 1), opacity 0.55s ease',
      willChange: 'transform, opacity',
    });
    document.body.append(fly);

    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const scale = Math.max(0.18, Math.min(0.5, to.width / from.width));
    requestAnimationFrame(() => {
      fly.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
      fly.style.opacity = '0.35';
    });
    const cleanup = () => fly.remove();
    fly.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, 800); // phòng transitionend không bắn
  }

  /** Làm ấm view bbsearch (Shopify compile template) -> search thật ra nhanh hơn. */
  #warmSearch(stepEl) {
    if (this.searchWarmed) return;
    this.searchWarmed = true;
    const scope = this.#activeScope(stepEl) || 'shirt';
    fetch(`/search?q=${encodeURIComponent(scope)}&type=product&view=bbsearch&page=1`).catch(() => {});
  }

  /**
   * MOBILE: hàng chọn loại thu về một dòng "đang xem <loại> — Switch"; bấm mới xổ ra.
   * Desktop CSS ẩn hẳn nút này nên class .bb-cats--open có bám lại cũng vô hại.
   */
  #toggleCats(btn) {
    // Mỗi step có hàng loại RIÊNG -> phải bám theo nút vừa bấm, không querySelector toàn builder.
    const cats = btn ? btn.closest('.bb-cats') : this.querySelector('.bb-cats');
    if (!cats) return;
    const open = cats.classList.toggle('bb-cats--open');
    (btn || cats.querySelector('[data-bb-cats-toggle]'))?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  /** Đồng bộ chữ trên nút thu gọn với loại đang xem, rồi đóng danh sách lại. */
  #syncCatsToggle(index) {
    // :not(.bb-cats--step) — hàng loại của từng step cũng mang class .bb-cats, không được đụng vào.
    const cats = this.querySelector('.bb-cats:not(.bb-cats--step)');
    if (!cats) return;
    const now = cats.querySelector('[data-bb-cats-current]');
    if (now) {
      const tab = this.querySelectorAll('[data-bb-cat]')[index];
      if (tab) now.textContent = tab.textContent.trim();
    }
    cats.classList.remove('bb-cats--open');
    cats.querySelector('[data-bb-cats-toggle]')?.setAttribute('aria-expanded', 'false');
  }
  /** Cùng việc trên nhưng cho hàng loại NẰM TRONG một step (data-bb-subtab). */
  #syncStepCatsToggle(stepEl) {
    const cats = stepEl?.querySelector('.bb-cats--step');
    if (!cats) return;
    const tab = cats.querySelector('[data-bb-subtab][aria-selected="true"]');
    const now = cats.querySelector('[data-bb-cats-current]');
    if (now && tab) now.textContent = tab.textContent.trim();
    cats.classList.remove('bb-cats--open');
    cats.querySelector('[data-bb-cats-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  #stepTo(index, focus) {
    if (index < 0 || index >= this.stepCount) return;
    this.state.current = index;
    this._resultTracked = false;
    this.classList.remove('bb-on-result');
    this.#showExpress(false); // rời màn xem bộ -> giỏ không còn chắc khớp
    this.querySelector('[data-bb-result]')?.classList.add('bb-hide');
    this.stepEls.forEach((el, i) => el.classList.toggle('bb-hide', i !== index));
    this.querySelectorAll('[data-bb-cat]').forEach((c, i) => c.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    this.#syncCatsToggle(index);
    this.#barUpdate();
    this.#hideError();

    const stepEl = this.stepEls[index];

    // Vào step: auto-áp brand đã chọn (carry-over) hoặc mở picker bắt chọn.
    this.#enterStep(stepEl);
    this.#syncContentVisibility(focus); // focus=false = lượt khôi phục lúc tải trang

    if (focus) {
      stepEl.querySelector('.bb-step__title')?.focus({ preventScroll: true });
      this.#scrollToStep(stepEl);
    }
  }

  /**
   * Đưa khách về ĐẦU step vừa chuyển tới.
   *
   * Hai thứ đã làm hỏng chuyện này ở bản cũ (`title.scrollIntoView({behavior:'smooth'})`):
   *   1. Đổi step là step cũ bị ẩn -> trang NGẮN lại; nếu đang cuộn sâu, trình duyệt kẹp
   *      scroll về đáy tài liệu = ngay chỗ footer. Cuộn `smooth` bắt đầu từ đó và tính
   *      quãng theo layout CŨ -> trên mobile dừng lại ở footer. Dùng cuộn TỨC THÌ.
   *   2. Lưới của step mới nạp sau vài trăm ms -> trang cao lên, vị trí vừa cuộn tới lệch.
   *      Vì vậy đánh dấu `_scrollStep` để cuộn LẠI một lần khi lô thẻ đầu tiên gắn xong.
   * Mốc cuộn: bộ chọn đội (nếu đang mở) -> hàng chọn loại -> tiêu đề step -> cả step.
   */
  #scrollToStep(stepEl) {
    if (!stepEl) return;
    // CHỈ nhận mốc đang thật sự có khung. Bộ chọn đội và hàng loại vẫn nằm trong DOM khi
    // bị ẩn; lấy nhầm một cái rect 0×0 thì Y tính ra đúng bằng vị trí hiện tại -> "cuộn"
    // mà không đi đâu cả (đã dính: bấm ADD xong vẫn đứng ở khối nội dung phía trên).
    const shown = (el) => el && el.getClientRects().length > 0;
    const anchor =
      [
        stepEl.querySelector('[data-bb-brandpicker]'),
        stepEl.querySelector('.bb-cats'),
        stepEl.querySelector('.bb-step__title'),
      ].find(shown) || stepEl;
    this._scrollStep = stepEl;
    // `scrollIntoView()` KHÔNG chốt behavior -> ăn theo `scroll-behavior: smooth` của theme.
    // Cuộn mượt bị huỷ/lệch khi lưới nạp xong làm trang cao thêm ~15.000px ngay sau đó.
    // Vì vậy tính Y rồi cuộn TỨC THÌ, và lặp lại ở frame kế cho chắc.
    const off = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-offset')) || 0;
    const go = () => {
      const top = anchor.getBoundingClientRect().top + window.pageYOffset - off;
      window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'instant' });
    };
    go();
    requestAnimationFrame(go);
  }

  /** Thang mở: chốt giỏ đang dựng thành 1 bundle rồi add thẳng vào giỏ hàng. */
  #commitLadder() {
    this.#track('CheckoutClicked', { source: 'review', items: this.#target().filter(Boolean).length });
    const done = this.#target().filter(Boolean);
    if (done.length) {
      this.#track('BundleCompleted', {
        bundle_name: this.bundleName,
        num_items: done.length,
        value: this.#calc(done, 0).pay / 100,
        currency: this.currency,
      });
    }
    this.state.previewId = null; // khách đi tiếp -> bộ này là thật, không còn là bản xem thử
    if (this.state.editingBundle !== null) {
      // Sheet xem lại đã đẩy bộ này thành bundle chính thức lúc mở (xem
      // #commitLadderForExpress) -> chỉ cần chốt hẳn, đừng đẩy lần thứ hai.
      this.state.editingBundle = null;
      this.#persist();
    } else if (this.#promoteLadderDraft() < 0) {
      return;
    }
    this.#barUpdate();
    // Review đã là bước xác nhận -> add xong đi thẳng checkout.
    this.#checkout().then(() => {
      const err = this.querySelector('[data-bb-error]');
      if (err && !err.classList.contains('bb-hide')) return; // add lỗi -> ở lại để khách thấy
      window.location.href = '/checkout';
    });
  }

  #addAnother() {
    this.state.editingBundle = null;
    this.#endSingle();
    const start = this.#emptySlot(this.state.draft);
    this.#stepTo(start === -1 ? 0 : start, true);
  }

  /* ---------- món lẻ (không thuộc bộ nào) ---------- */

  /**
   * MÓN LẺ = lối ra thứ hai sau khi xong một bộ. Khách không muốn thêm 3 món nữa vẫn
   * mua thêm được đúng một món, trả NGUYÊN GIÁ.
   *
   * Món lẻ nằm trong state chứ KHÔNG add thẳng vào giỏ: màn kết quả đang hiện tổng tiền,
   * đẩy hàng vào giỏ sau lưng thì con số đó sai. Chúng được add cùng lượt với bộ ở
   * #checkout, và KHÔNG mang thuộc tính `_bundle_*` nào -> function bỏ qua, tính đủ tiền.
   */
  /**
   * Bước 1: mở SHEET chọn loại, đè lên màn kết quả. KHÔNG rời màn kết quả ở bước này —
   * nhảy thẳng sang lưới sản phẩm nền sáng rồi mới hỏi loại là gãy mạch nhìn.
   * Sheet dùng lại đúng bộ class .bb-celebrate* nên trông y hệt sheet chúc mừng.
   */
  /**
   * Câu nhắc của luồng món lẻ, in ở CẢ sheet lẫn băng trên lưới.
   * Có giảm giá thì phải NÓI RA — để nguyên chữ "regular price" trong khi giá đã gạch
   * 20% là tự mâu thuẫn, khách đọc xong không tin con số nào nữa.
   */
  #fillSingleNotes() {
    const raw = (this.dataset.singleNote || '').trim();
    const txt = this.singlePct > 0
      ? (raw || 'Any 1 item at {pct}% off — no bundle, no minimum.').replace(/\{pct\}/g, String(this.singlePct))
      : (raw ? raw.replace(/\{pct\}/g, '0') : 'Pick 1 item to add on its own — regular price, no bundle.');
    this.querySelectorAll('[data-bb-single-note]').forEach((el) => { el.textContent = txt; });
  }
  #startSingle() {
    this.#fillSingleNotes();
    this.singlePick = true;
    // Cần biết đội + danh sách loại -> lấy theo step sẽ dùng, nhưng CHƯA chuyển màn.
    this.#renderSingleCats();
    this.#loadSingleFrom();
    const sheet = this.querySelector('[data-bb-singlesheet]');
    // Trang chỉ có MỘT loại (step khai một pattern -> không render ô nào): bước chọn
    // loại không có gì để chọn, mở ra là một sheet rỗng. Vào thẳng lưới của loại đó.
    const catCount = this.#singleStep()?.querySelectorAll('[data-bb-subtab]').length || 0;
    if (!sheet || catCount < 2) return this.#pickSingleCat(0);
    sheet.classList.remove('bb-hide');
    sheet.setAttribute('aria-hidden', 'false');
    sheet.querySelector('.bb-celebrate__heading')?.focus({ preventScroll: true });
  }
  #closeSingleSheet() {
    const sheet = this.querySelector('[data-bb-singlesheet]');
    if (!sheet) return;
    sheet.classList.add('bb-hide');
    sheet.setAttribute('aria-hidden', 'true');
  }
  #endSingle() {
    const wasUpsell = this.#upsellActive();
    this.singlePick = false;
    // Gỡ preview TRƯỚC khi đổi cờ khác, để #paintSingleCard thấy singlePick=false mà trả
    // card về giá thường. Bỏ bước này thì rời chế độ món lẻ mà lưới vẫn hiện giá giảm.
    this.#refreshSinglePreviews();
    this.classList.remove('bb-on-single');
    this.#closeSingleSheet();
    this.querySelector('[data-bb-single]')?.classList.add('bb-hide');
    const box = this.querySelector('[data-bb-single-cats]');
    if (box) box.textContent = '';
    this.querySelector('[data-bb-upbar]')?.classList.add('bb-hide');
    if (wasUpsell) {
      const stepEl = this.#singleStep();
      // Chủ đề khách bấm trong lưới upsell chỉ sống trong lượt đó — trả chip của step về
      // như cũ. Không thì bấm CHANGE một món của bộ sẽ ra lưới Aloha trong khi hàng chip
      // của step bộ đang bị giấu, khách không đổi lại được.
      if (stepEl && this._preSingleTheme != null) {
        this.#pressChip(stepEl, this._preSingleTheme);
        this.#syncThemeToggle(stepEl);
      }
      this._preSingleTheme = null;
      // Lưới của step đang là loại upsell (vd cap) -> xoá để lần sau vào step nạp lại
      // đúng loại của step (#enterStep chỉ nạp khi search[idx] rỗng).
      if (stepEl) {
        this.search[parseInt(stepEl.dataset.bbStep, 10)] = null;
        stepEl.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
      }
      this.singleCat = -1;
    }
  }
  /** Step đang dùng cho luồng món lẻ (step đang hiện). */
  #singleStep() {
    // KHÔNG mượn step của món FREE: thẻ ở đó dựng sẵn badge FREE, vào lưới món lẻ sẽ hiện
    // cả "FREE" lẫn "50% OFF" (dính khi tải lại trang: state.current về 0 = step FREE).
    const cur = this.stepEls[this.state.current];
    if (cur && !(this.#slotOff(this.state.current) > 0)) return cur;
    const paid = this.stepEls.find((_, i) => !(this.#slotOff(i) > 0));
    if (paid) return paid;
    const empty = this.#emptySlot(this.state.draft);
    return cur || this.stepEls[empty === -1 ? 0 : empty] || this.stepEls[0];
  }
  /**
   * Lưới chọn LOẠI của luồng món lẻ.
   * Ô nào mà thêm một món RẺ NHẤT của loại đó đã đủ ngưỡng free ship thì tô xanh + nói
   * thẳng. Giá lấy từ `#singleFrom` (nạp nền, xem #loadSingleFrom) — chưa biết giá thì
   * ô vẫn bấm được, chỉ là chưa có dòng giá; KHÔNG đoán bừa rồi hứa free ship hụt.
   */
  #renderSingleCats() {
    const box = this.querySelector('[data-bb-single-cats]');
    if (!box) return;
    const stepEl = this.#singleStep();
    const tabs = [...(stepEl?.querySelectorAll('[data-bb-subtab]') || [])];
    if (this.upsellMode) return this.#renderUpsellCats();
    const paid = this.#grandPay();
    const gap = this.fsEnabled && this.fsThreshold > 0 ? this.fsThreshold - paid : 0;
    box.innerHTML = tabs
      .map((t, i) => {
        const raw = this.#singleFromRaw(i);
        const from = this.#singleFromPrice(i);
        const unlocks = gap > 0 && from > 0 && from >= gap;
        // Giá gạch chỉ in khi CÓ giảm thật — gạch một con số y hệt là nói dối có khuyến mãi.
        const price = from > 0
          ? `from ${raw > from ? `<s class="bb-celebrate__catwas">${money(raw)}</s>` : ''}${money(from)}`
          : '';
        return (
          `<button type="button" class="bb-celebrate__cat bb-celebrate__cat--stack${unlocks ? ' bb-celebrate__cat--fs' : ''}" data-bb-single-cat="${i}">` +
          `<span>${this.#esc(t.textContent.trim())}</span>` +
          (price ? `<span class="bb-celebrate__catfrom">${price}</span>` : '') +
          (unlocks ? `<span class="bb-celebrate__catfs">🚚 Unlocks FREE shipping</span>` : '') +
          `</button>`
        );
      })
      .join('');
  }
  /** Chọn loại -> nạp lưới của loại đó rồi rời bước chọn loại. */
  #pickSingleCat(i) {
    const stepEl = this.#singleStep();
    if (this.upsellMode) return this.#pickUpsellCat(stepEl, i);
    this.#closeSingleSheet();
    this.classList.add('bb-on-single');
    this.querySelector('[data-bb-single]')?.classList.remove('bb-hide');
    // Rời màn kết quả sang lưới của step này rồi mới đổi loại — đổi trước khi step hiện
    // thì #enterStep có thể nạp lại loại cũ đè lên.
    this.#stepTo(parseInt(stepEl.dataset.bbStep, 10), false);
    const tab = stepEl.querySelectorAll('[data-bb-subtab]')[i];
    if (tab) this.#switchTab(tab);
    this.#refreshSinglePreviews();
    stepEl.querySelector('[data-bb-grid]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  /** Tổng phải trả hiện tại (mọi bộ + món lẻ) — cùng con số màn kết quả đang hiện. */
  #grandPay() {
    let pay = 0;
    this.state.bundles.forEach((bn, b) => { pay += this.#calc(bn, b).pay; });
    (this.state.singles || []).forEach((x) => { pay += this.#singlePay(x.priceCents); });
    return pay;
  }
  /**
   * Tiền THẬT phải trả cho một món lẻ giá `cents`. Làm tròn giống offers/single.js
   * (`r2(unit*pct/100)`) nên số builder hiện và số checkout thu luôn khớp từng cent.
   */
  #singlePay(cents) {
    // Function chỉ giảm món lẻ khi giỏ có ít nhất MỘT bộ đủ món (offers/single.js). Gỡ bộ
    // mà vẫn còn món lẻ thì builder phải về nguyên giá theo, không thì hai bên lệch tiền.
    if (!this.singlePct || !this.#hasFullBundle()) return cents;
    return Math.max(0, cents - Math.round((cents * this.singlePct) / 100));
  }
  /** Giá gốc thấp nhất của loại `i` (chưa giảm). */
  #singleFromRaw(i) {
    const key = `${this.selectedBrand}|${this.upsellMode ? 'u' : ''}${i}`;
    return (this.singleFrom && this.singleFrom[key]) || 0;
  }
  /**
   * Giá PHẢI TRẢ thấp nhất của loại `i`. Dùng cho cả phần hiện giá lẫn phép kiểm
   * "thêm món này có đủ free ship chưa" — ngưỡng free ship tính trên tiền THẬT PHẢI TRẢ,
   * lấy giá gốc là hứa free ship hụt đúng bằng phần đã giảm.
   */
  #singleFromPrice(i) {
    const raw = this.#singleFromRaw(i);
    return raw ? this.#singlePay(raw) : 0;
  }
  /**
   * Giá thấp nhất của TỪNG LOẠI cho đội đang chọn. Phải hỏi riêng từng loại: giá "from"
   * khai ở block là của cả step, không phải của từng loại.
   * Dùng `view=bb` (24 sản phẩm) cho nhẹ, và tiện thể warm template collection đó.
   */
  #loadSingleFrom() {
    const brand = this.selectedBrand;
    const stepEl = this.#singleStep();
    if (!brand || !stepEl) return;
    this.singleFrom = this.singleFrom || {};
    const pats = this.upsellMode
      ? this.upsellPats
      : (stepEl.dataset.brandPattern || '').split(',').map((x) => x.trim()).filter(Boolean);
    const slug = this.#slugifyBrand(brand);
    pats.forEach((pat, i) => {
      const key = `${brand}|${this.upsellMode ? 'u' : ''}${i}`;
      if (this.singleFrom[key]) return;
      const handle = pat.includes('{brand}') ? pat.replace(/\{brand\}/g, slug) : `${slug}-${pat}`;
      fetch(`/collections/${encodeURIComponent(handle)}?view=bb&page=1`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          let min = 0;
          (data?.results || []).forEach((pr) => {
            (pr.variants || []).forEach((v) => {
              if (v[4] && v[3] > 0 && (!min || v[3] < min)) min = v[3];
            });
          });
          if (!min) return;
          this.singleFrom[key] = min;
          // Đang mở bước chọn loại thì vẽ lại ngay, không bắt khách đóng mở lại.
          // Sheet đang mở thì vẽ lại ngay để giá "from" hiện ra, không bắt khách đóng mở lại.
          if (this.upsellMode) this.#renderUpsellCats();
          else if (!this.querySelector('[data-bb-singlesheet]')?.classList.contains('bb-hide')) this.#renderSingleCats();
        })
        .catch(() => {});
    });
  }
  #cancelSingle() {
    this.#closeAddedSheet();
    // Đang ở SHEET (chưa rời màn kết quả) -> chỉ cần đóng sheet, đừng vẽ lại màn.
    const onSheet = !this.querySelector('[data-bb-singlesheet]')?.classList.contains('bb-hide');
    this.#endSingle();
    if (!onSheet) this.#showResult(true);
  }
  #removeSingle(i) {
    this.#track('ItemRemoved', { source: 'single' });
    const [gone] = this.state.singles.splice(i, 1);
    this.#persist();
    // Đã nằm trong giỏ thì phải gỡ ở giỏ luôn, không thì khách vẫn bị tính tiền món đó.
    if (gone && gone.cartId) this.#removeSingleFromCart(gone.cartId);
    this.#showResult(false);
  }
  async #removeSingleFromCart(cartId) {
    return this.#cartOp(async () => {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const updates = {};
      (cart.items || []).forEach((it) => {
        if (it.properties && it.properties._single_id === cartId) updates[it.key] = 0;
      });
      if (Object.keys(updates).length === 0) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      this.#emitCartChanged('bundle-builder-remove', 0, true); // gỡ món trong builder -> không bung drawer
    } catch {
      /* non-critical: giỏ sẽ đồng bộ lại lần tải sau */
    }
    });
  }

  /* ---------- upsell sau bộ ---------- */

  /** Đang lướt lưới của một loại upsell. */
  #upsellActive() {
    return this.upsellMode && this.singlePick && this.singleCat >= 0;
  }
  /** Có ít nhất một bộ ĐỦ món — cùng điều kiện với offers/single.js của function. */
  #hasFullBundle() {
    return (this.state?.bundles || []).some((bn) => this.#isFull(bn));
  }
  #pctText(t) {
    return String(t || '').replace(/\{pct\}/g, String(this.singlePct));
  }
  /**
   * Dòng đếm trên lưới = số thẻ ĐANG HIỆN. Trước đây đếm thẻ đã tải, bỏ qua bộ lọc chủ
   * đề -> lưới lọc Hunting ghi "329 results" trong khi chỉ hiện 51 thẻ.
   */
  #syncResultCount(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const s = this.search[idx];
    const status = stepEl.querySelector('[data-bb-search-status]');
    if (!s || !status) return;
    const n = stepEl.querySelectorAll('[data-bb-search-card]:not([data-bb-hidden])').length;
    if (n > 0) status.textContent = `${n}${s.more ? '+' : ''} result${n === 1 ? '' : 's'}`;
  }

  /** Bấm một ô loại trong khối upsell -> lưới của loại đó (mượn step cuối của bộ). */
  #pickUpsellCat(stepEl, i) {
    if (!stepEl || !this.upsellPats[i]) return;
    this.#closeSingleSheet();
    this.#closeOto();
    this.#closeUpsheet();
    this.#closeAddedSheet();
    if (!this.singlePick) {
      this.#fillSingleNotes();
      this.singlePick = true;
    }
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    // Nhớ chip của step TRƯỚC khi đụng vào, để #endSingle trả lại đúng như cũ.
    if (this._preSingleTheme == null) this._preSingleTheme = this.#activeTheme(stepEl);
    this.singleCat = i;
    this.classList.add('bb-on-single');
    const catLbl = this.querySelector('[data-bb-single-cat-label]');
    if (catLbl) catLbl.textContent = this.#pctText(`${this.upsellLabels[i] || ''} — every design {pct}% off`);
    this.querySelector('[data-bb-single]')?.classList.remove('bb-hide');
    this.#stepTo(idx, false);
    // Chủ đề: lựa chọn của khách trong lượt upsell trước, không thì mặc định của step.
    const bar = stepEl.querySelector('[data-bb-themebar]');
    const want = this.singleTheme || (bar && bar.dataset.defaultTheme) || '';
    if (want && stepEl.querySelector(`[data-bb-themechip][data-theme="${want}"]`)) {
      this.#pressChip(stepEl, want);
      this.#syncThemeToggle(stepEl);
    }
    const brand = stepEl.dataset.brand || this.selectedBrand;
    const want2 = this.#directHandle(stepEl, this.#resolveBrand(brand) || brand);
    if (brand && this.search[idx]?.direct !== want2) this.#runSearch(stepEl, brand);
    else this.#applyVisibility(stepEl);
    this.#refreshSinglePreviews();
    this.#renderUpbar();
    const head = this.querySelector('[data-bb-single]');
    const off = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-offset')) || 0;
    const top = (head || stepEl).getBoundingClientRect().top + window.pageYOffset - off - 8;
    window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'instant' });
    this.#track('UpsellCategory', { category: this.upsellLabels[i] || String(i) });
  }

  /**
   * Nut "KEEP BROWSING {cat}" cua khoi upsell: loai khach DANG xem (loai vua xem trong
   * luoi upsell, khong thi loai cua bo). Mang data-bb-single-cat nen di chung duong voi o loai.
   */
  #upsellCurrentCat() {
    if (this.singleCat >= 0 && this.upsellPats[this.singleCat]) return this.singleCat;
    const norm = (x) => String(x || '').replace(/\s+/g, '').toLowerCase();
    for (let k = this.stepEls.length - 1; k >= 0; k--) {
      const pats = (this.stepEls[k].dataset.brandPattern || '').split(',').map(norm).filter(Boolean);
      const i = this.upsellPats.findIndex((u) => pats.includes(norm(u)));
      if (i >= 0) return i;
    }
    return 0;
  }
  #renderUpsellKeep() {
    const btns = this.querySelectorAll('[data-bb-upsell-keep]');
    if (!btns.length) return;
    const i = this.#upsellCurrentCat();
    const cat = this.upsellLabels[i] || (this.upsellPats[i] || '').replace(/\{brand\}-?/g, '').replace(/-/g, ' ');
    const txt = this.#pctText((this.dataset.upsellKeep || 'KEEP BROWSING {cat} — {pct}% OFF').replace(/\{cat\}/g, cat));
    btns.forEach((b) => {
      b.textContent = txt;
      b.dataset.bbSingleCat = String(i);
      b.classList.toggle('bb-hide', !this.upsellPats[i]);
    });
  }

  /** Ô loại của khối upsell: tên + giá "from" gốc gạch + giá đã giảm + tag −N%. */
  #renderUpsellCats() {
    const boxes = this.querySelectorAll('[data-bb-upsell-cats], [data-bb-added-cats]');
    if (!boxes.length) return;
    const paid = this.#grandPay();
    const gap = this.fsEnabled && this.fsThreshold > 0 ? this.fsThreshold - paid : 0;
    const html = this.upsellPats
      .map((_, i) => {
        const raw = this.#singleFromRaw(i);
        const from = this.#singleFromPrice(i);
        const unlocks = gap > 0 && from > 0 && from >= gap;
        const price = from > 0
          ? `from ${raw > from ? `<s class="bb-celebrate__catwas">${money(raw)}</s>` : ''}<b class="bb-celebrate__catnow">${money(from)}</b>`
          : '';
        const label = this.upsellLabels[i] || this.upsellPats[i].replace(/\{brand\}-?/g, '').replace(/-/g, ' ');
        return (
          `<button type="button" class="bb-celebrate__cat bb-celebrate__cat--stack${unlocks ? ' bb-celebrate__cat--fs' : ''}" data-bb-single-cat="${i}">` +
          `<span>${this.#esc(label)}</span>` +
          (price ? `<span class="bb-celebrate__catfrom">${price}</span>` : '') +
          (unlocks ? `<span class="bb-celebrate__catfs">🚚 Unlocks FREE shipping</span>` : '') +
          `</button>`
        );
      })
      .join('');
    boxes.forEach((b) => { b.innerHTML = html; });
    // Bảng xác nhận: đánh dấu loại đang xem.
    this.querySelectorAll('[data-bb-added-cats] [data-bb-single-cat]').forEach((b) => {
      b.setAttribute('aria-current', parseInt(b.dataset.bbSingleCat, 10) === this.singleCat ? 'true' : 'false');
    });
  }

  /**
   * Khối upsell trên màn kết quả. Chỉ hiện khi có một bộ ĐỦ món: không còn bộ thì cũng
   * không còn gì "mở khoá", nói "everything else is 50% off" lúc đó là sai.
   */
  #renderUpsell(grandPay, draftStarted) {
    const boxes = this.querySelectorAll('[data-bb-upsell]');
    if (!boxes.length) return;
    const show = this.upsellMode && this.#hasFullBundle() && !draftStarted;
    const n = (this.state.singles || []).length;
    // Cùng nội dung ở hai chỗ (màn kết quả + bảng chúc mừng) -> vẽ vào cả hai.
    boxes.forEach((box) => {
      box.classList.toggle('bb-hide', !show);
      if (!show) return;
      const head = box.querySelector('[data-bb-upsell-heading]');
      if (head) {
        head.textContent = this.#pctText(
          n > 0
            ? this.dataset.upsellMore || 'Nice pick. The next one is {pct}% off too.'
            : this.dataset.upsellHeading || 'Everything else is now {pct}% off'
        );
      }
      box.querySelectorAll('[data-bb-pct-tpl]').forEach((el) => { el.textContent = this.#pctText(el.dataset.bbPctTpl); });
      const fs = box.querySelector('[data-bb-upsell-fs]');
      if (fs) {
        const on = this.fsEnabled && this.fsThreshold > 0;
        fs.classList.toggle('bb-hide', !on);
        if (on) fs.textContent = this.#fsPlain(grandPay);
      }
    });
    this.#renderUpsheet();
    if (!show) return;
    this.#renderUpsellCats();
    this.#renderUpsellKeep();
    this.#loadSingleFrom();
    this.#renderUpsellMatch();
    // Gợi ý cùng design: nạp lúc nhàn, đừng tranh băng thông với lượt chốt giỏ + nút ví.
    const go = () => this.#loadUpsellMatch();
    if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 1500 });
    else setTimeout(go, 300);
  }

  /** Thanh đáy của lưới upsell: số món + tổng + CHECKOUT + câu free-ship. */
  #renderUpbar() {
    const bar = this.querySelector('[data-bb-upbar]');
    if (!bar) return;
    const on = this.#upsellActive();
    bar.classList.toggle('bb-hide', !on);
    if (!on) return;
    const n = this.state.bundles.reduce((k, bn) => k + (bn || []).filter(Boolean).length, 0) + (this.state.singles || []).length;
    const pay = this.#grandPay();
    const cEl = bar.querySelector('[data-bb-upbar-count]');
    const pEl = bar.querySelector('[data-bb-upbar-pay]');
    if (cEl) cEl.textContent = n === 1 ? '1 item' : `${n} items`;
    if (pEl) pEl.textContent = money(pay);
    const fs = bar.querySelector('[data-bb-upbar-fs]');
    if (fs) {
      const fsOn = this.fsEnabled && this.fsThreshold > 0;
      fs.classList.toggle('bb-hide', !fsOn);
      if (fsOn) fs.textContent = this.#fsPlain(pay);
    }
    this.#syncViewportDrop();
  }

  /**
   * Món làm MỐC cho gợi ý cùng design: món ĐƯỢC TRẢ TIỀN của bộ đủ món đầu tiên (ưu tiên
   * món không free), phải có mã design ở cuối title (vd SCH039).
   */
  #upsellRef(items) {
    return this.#upsellRefs(items)[0] || null;
  }
  /** Mốc của TỪNG món trong bộ, theo thứ tự slot (món không có mã design bị bỏ qua). */
  #upsellRefs(items) {
    const bn = items || this.state.bundles.find((b) => this.#isFull(b));
    if (!bn) return [];
    const refs = [];
    // Mốc = SẢN PHẨM ĐẦU (slot 0), chủ store chốt 2026-09-19 — không phải món trả tiền.
    for (let k = 0; k < bn.length; k += 1) {
      const it = bn[k];
      const m = String(it?.title || '').match(/\b([A-Z]{1,5}\d{1,5}(?:-\d+)?)\s*$/);
      if (!it || !m || !it.handle) continue;
      const code = m[1].toLowerCase();
      const slug = it.handle.split('-')[0];
      const stepPat = (this.stepEls[k]?.dataset.brandPattern || '').split(',')[0].trim();
      const refType = stepPat.replace(/\{brand\}-?/g, '');
      // Phần chữ giữa mã đội và loại áo trong handle, vd "hunting-" của kc-hunting-hoodie-sch039.
      let middle = it.handle.slice(slug.length + 1).replace(new RegExp(`-${code}$`), '');
      middle = refType && middle.endsWith(refType) ? middle.slice(0, -refType.length) : '';
      const themes = this.#themesOf({ title: it.title, tags: [] }).filter((t) => t !== 'classic');
      refs.push({ item: it, slot: k, free: this.#slotOff(k, bn) > 0, code: m[1], codeLc: code, slug, middle, refType, theme: themes[0] || '' });
    }
    return refs;
  }
  /** Sản phẩm CÙNG mã design với `ref` ở loại `pat` (đoán handle, mỗi lần ~11KB). */
  async #findSameDesign(ref, pat) {
    const type = pat.replace(/\{brand\}-?/g, '');
    const cands = [...new Set([
      `${ref.slug}-${ref.middle}${type}-${ref.codeLc}`,
      `${ref.slug}-${type}-${ref.codeLc}`,
      ref.theme ? `${ref.slug}-${ref.theme}-${type}-${ref.codeLc}` : '',
    ].filter(Boolean))];
    for (const h of cands) {
      try {
        // KHÔNG gửi Accept: application/json (xem #loadUpsellMatch).
        const r = await fetch(`/products/${h}.js`);
        const p = r.ok ? await r.json() : null;
        const it = p && p.id ? this.#normProduct(p, false) : null;
        if (it) return it;
      } catch {
        /* thử handle kế */
      }
    }
    return null;
  }
  /**
   * Gợi ý CỐ ĐỊNH theo handle (`upsell_match_handles`), vd `{brand}-hunting-cap-csc139`.
   * Dùng khi món gợi ý không chia sẻ mã design với bộ — cap mang mã CSC riêng nên không
   * đoán được từ mã hoodie. `{brand}` lấy từ handle của món trong bộ (mã đội).
   */
  async #loadUpsellMatchFixed(items) {
    const bn = items || this.state.bundles.find((b) => this.#isFull(b));
    if (!bn) return;
    const first = bn.find(Boolean);
    const slug = String(first?.handle || '').split('-')[0];
    if (!slug) return;
    const key = `fix|${slug}`;
    if (this._matchKey === key) return;
    this._matchKey = key;
    const catOf = (handle) => {
      let best = -1;
      let len = 0;
      this.upsellPats.forEach((pat, i) => {
        const type = pat.replace(/\{brand\}-?/g, '');
        if (type && handle.includes(`-${type}-`) && type.length > len) { best = i; len = type.length; }
      });
      return best;
    };
    const jobs = this.upsellMatchHandles.map(async (tpl) => {
      const h = tpl.replace(/\{brand\}/g, slug);
      try {
        // KHÔNG gửi Accept: application/json (xem #loadUpsellMatch).
        const r = await fetch(`/products/${h}.js`);
        const p = r.ok ? await r.json() : null;
        const it = p && p.id ? this.#normProduct(p, false) : null;
        return it ? { it, handle: h } : null;
      } catch {
        return null;
      }
    });
    const out = (await Promise.all(jobs)).filter(Boolean);
    if (this._matchKey !== key) return;
    this.upsellMatch = out.map(({ it, handle }) => {
      const v = it.variants[0];
      const code = (String(it.title).match(/\b([A-Z]{1,5}\d{1,5}(?:-\d+)?)\s*$/) || [])[1] || '';
      return {
        cat: catOf(handle),
        same: false,
        code,
        tag: (this.upsellMatchTag || 'Design {code}').replace('{code}', code),
        item: { ...it, variantId: v[0], variantLabel: v[1], priceCents: v[2] },
      };
    });
    this.#renderUpsellMatch();
  }
  /**
   * Gợi ý theo TỪNG MÓN của bộ, CHỈ ở loại `upsell_match_pattern` (chủ store chốt
   * 2026-09-19: bộ 2 hoodie -> 2 tee cùng design với từng hoodie). Hai hoodie trùng mã
   * thì thẻ thứ hai rơi về loại kế tiếp (cùng mã) để vẫn đủ 2 lựa chọn.
   */
  async #loadUpsellMatchPer(items) {
    const bn = items || this.state.bundles.find((b) => this.#isFull(b));
    if (!bn) return;
    const key = 'per|' + bn.map((x) => (x ? `${x.handle}|${x.variantLabel || ''}` : '-')).join(',');
    if (this._matchKey === key) return;
    this._matchKey = key;
    const refs = this.#upsellRefs(bn);
    if (!refs.length) return;
    const pat = this.upsellMatchPat;
    const catOf = (p) => this.upsellPats.indexOf(p);
    const seen = new Set();
    const jobs = [];
    refs.forEach((ref) => {
      if (seen.has(ref.codeLc)) return;
      seen.add(ref.codeLc);
      jobs.push(this.#findSameDesign(ref, pat).then((it) => (it ? { ref, it, cat: catOf(pat) } : null)));
    });
    const out = (await Promise.all(jobs)).filter(Boolean);
    if (out.length < Math.min(2, bn.filter(Boolean).length)) {
      const stepPats = new Set(this.stepEls.flatMap((el) => (el.dataset.brandPattern || '').split(',').map((x) => x.trim()).filter(Boolean)));
      // Mốc dự phòng = món ĐÃ khớp được một design (vd hoodie SCH), không phải luôn slot 0:
      // bộ cap + hoodie có slot 0 là cap mang mã CSC riêng, loại khác không có mã đó.
      const fb = (out[0] && out[0].ref) || refs[0];
      for (const p2 of this.upsellPats) {
        if (p2 === pat || stepPats.has(p2)) continue;
        const it = await this.#findSameDesign(fb, p2);
        if (it) {
          out.push({ ref: fb, it, cat: catOf(p2) });
          break;
        }
      }
    }
    if (this._matchKey !== key) return;
    this.upsellMatch = out.map(({ ref, it, cat }) => {
      // Size mặc định = size của CHÍNH hoodie mà thẻ này khớp.
      const want = String(ref.item.variantLabel || '').trim();
      const v = it.variants.find((x) => x[1] === want) || it.variants[0];
      // bxgy: "FREE hoodie". bxpct: món ưu đãi chỉ giảm %, gọi "FREE" là sai -> bỏ chữ thứ tự.
      const nth = ref.free
        ? (this.pricingType === 'bxgy' ? 'FREE' : '')
        : ['1st', '2nd', '3rd'][ref.slot] || `${ref.slot + 1}th`;
      return {
        cat,
        same: true,
        code: ref.code,
        tag: `Matches your ${nth ? nth + ' ' : ''}${ref.refType.replace(/-/g, ' ')} · ${ref.code}`,
        item: { ...it, variantId: v[0], variantLabel: v[1], priceCents: v[2] },
      };
    });
    this.#renderUpsellMatch();
  }
  /** Chuẩn hoá sản phẩm (từ /products/x.js hoặc JSON view=bb) về dạng món của builder. */
  #normProduct(p, fromBb) {
    let vs;
    if (fromBb) {
      vs = (p.variants || []).map((v) => ({ id: v[0], opts: [v[1], v[2]].filter((x) => x != null), price: v[3], ok: !!v[4] }));
    } else {
      vs = (p.variants || []).map((v) => ({ id: v.id, opts: v.options || [], price: v.price, ok: v.available !== false }));
    }
    vs = vs.filter((v) => v.ok);
    if (!vs.length) return null;
    const nOpt = Math.max(...vs.map((v) => v.opts.length));
    const varies = [];
    for (let k = 0; k < nOpt; k += 1) if (new Set(vs.map((v) => v.opts[k])).size > 1) varies.push(k);
    const label = (v) => varies.map((k) => v.opts[k]).filter((x) => x && x !== 'Default Title').join(' / ');
    const raw = fromBb ? p.image : p.featured_image;
    const img = raw ? httpsUrl(raw) : '';
    return {
      productId: p.id,
      title: this.#displayTitle(p.title),
      handle: p.handle || '',
      image: img ? this.#sizeUrl(raw, 240) : '',
      imageRaw: raw || '',
      variants: vs.map((v) => [v.id, label(v), v.price]),
    };
  }
  /**
   * Gợi ý "complete the set": CÙNG mã design với món của bộ, ở các loại khác. Cùng mã SC
   * là cùng một design trên mọi loại áo, nên handle đoán được từ handle của món mốc —
   * mỗi loại 1-3 request /products/x.js (~11KB) thay vì tải cả collection (~200KB).
   * Loại không có mã trùng (cap dùng mã CSC riêng) -> mẫu CÙNG CHỦ ĐỀ đầu tiên.
   */
  async #loadUpsellMatch(items) {
    if (!this.upsellMode || this.upsellMatchOff) return;
    if (this.upsellMatchHandles.length) return this.#loadUpsellMatchFixed(items);
    if (this.upsellMatchPat) return this.#loadUpsellMatchPer(items);
    const ref = this.#upsellRef(items);
    if (!ref) return;
    const key = `${ref.item.handle}|${ref.item.variantLabel || ''}`;
    if (this._matchKey === key) return;
    this._matchKey = key;
    const stepPats = new Set(this.stepEls.flatMap((el) => (el.dataset.brandPattern || '').split(',').map((x) => x.trim()).filter(Boolean)));
    // KHÔNG gửi header `Accept: application/json`: với /collections/x?view=bb Shopify sẽ
    // trả JSON GỐC của collection thay vì render template bb -> không có `results`.
    const getJson = async (url) => {
      try {
        const r = await fetch(url);
        return r.ok ? await r.json() : null;
      } catch {
        return null;
      }
    };
    const jobs = this.upsellPats.map(async (pat, i) => {
      if (stepPats.has(pat)) return null; // cùng loại với bộ -> gợi ý lại chính món đó, bỏ
      const type = pat.replace(/\{brand\}-?/g, '');
      const cands = [...new Set([
        `${ref.slug}-${ref.middle}${type}-${ref.codeLc}`,
        `${ref.slug}-${type}-${ref.codeLc}`,
        ref.theme ? `${ref.slug}-${ref.theme}-${type}-${ref.codeLc}` : '',
      ].filter(Boolean))];
      for (const h of cands) {
        const p = await getJson(`/products/${h}.js`);
        if (p && p.id) {
          const it = this.#normProduct(p, false);
          if (it) return { i, it, same: true };
        }
      }
      if (!ref.theme) return null;
      const handle = pat.includes('{brand}') ? pat.replace(/\{brand\}/g, ref.slug) : `${ref.slug}-${pat}`;
      const data = await getJson(`/collections/${encodeURIComponent(handle)}?view=bb&page=1`);
      const hit = (data?.results || []).find((r) => r.image && this.#themesOf(r).includes(ref.theme));
      const it = hit ? this.#normProduct(hit, true) : null;
      return it ? { i, it, same: false } : null;
    });
    const out = (await Promise.all(jobs)).filter(Boolean);
    if (this._matchKey !== key) return;
    // Size mặc định = size khách đã chọn cho món mốc (khách mua L thì gần như chắc cũng L).
    const want = String(ref.item.variantLabel || '').trim();
    this.upsellMatch = out
      .sort((a, b) => (b.same - a.same) || (a.i - b.i))
      .slice(0, 4)
      .map(({ i, it, same }) => {
        const v = it.variants.find((x) => x[1] === want) || it.variants[0];
        return { cat: i, same, code: ref.code, item: { ...it, variantId: v[0], variantLabel: v[1], priceCents: v[2] } };
      });
    this.#renderUpsellMatch();
  }
  #renderUpsellMatch() {
    const ms = this.upsellMatch || [];
    const carousel = this.upsellMatchStyle === 'carousel';
    const html = ms.map((m, k) => this.#matchCardHtml(m, k, 'data-bb-match-add', 'ADD', carousel)).join('');
    this.querySelectorAll('[data-bb-upsell-match]').forEach((wrap) => {
      const list = wrap.querySelector('[data-bb-upsell-matchlist]');
      if (!list) return;
      wrap.classList.toggle('bb-hide', ms.length === 0);
      wrap.classList.toggle('bb-upsell__match--carousel', carousel);
      list.classList.toggle('bb-upsell__matchlist--carousel', carousel);
      // Giu vi tri vuot khi ve lai (them mon xong the doi "Added" ma khong nhay ve dau).
      const x = list.scrollLeft;
      list.innerHTML = html;
      if (!carousel) return;
      list.scrollLeft = x;
      const hint = wrap.querySelector('[data-bb-match-count]');
      if (hint) hint.textContent = ms.length > 2 ? `${ms.length} designs · swipe →` : '';
      if (!list._bbSwipe) {
        list._bbSwipe = true;
        list.addEventListener('scroll', () => {
          if (list._bbSwiped || list.scrollLeft < 30) return;
          list._bbSwiped = true;
          this.#track('MatchSwiped', {});
        }, { passive: true });
      }
    });
  }
  /** Bảng đọc toàn bộ review — mở ĐÈ lên bảng chúc mừng, đóng lại là về đúng chỗ cũ. */
  #openReviews(source) {
    const sh = this.querySelector('[data-bb-reviews]');
    if (!sh) return;
    sh.classList.remove('bb-hide');
    sh.setAttribute('aria-hidden', 'false');
    sh.querySelector('.bb-revsheet__title')?.focus({ preventScroll: true });
    this.#track('ReviewsOpened', { source });
  }
  #closeReviews() {
    const sh = this.querySelector('[data-bb-reviews]');
    if (!sh || sh.classList.contains('bb-hide')) return;
    sh.classList.add('bb-hide');
    sh.setAttribute('aria-hidden', 'true');
  }
  /** Nut ‹ › cua carousel (desktop): cuon 1 khung nhin. */
  #navMatch(btn) {
    const list = btn.closest('[data-bb-upsell-match]')?.querySelector('[data-bb-upsell-matchlist]');
    if (!list) return;
    const dir = parseInt(btn.dataset.bbMatchNav, 10) || 1;
    list.scrollBy({ left: dir * list.clientWidth * 0.85, behavior: 'smooth' });
  }
  #matchCardHtml(m, k, attr, cta, card) {
    const it = m.item;
    if (card) {
      const paid = this.#singlePay(it.priceCents);
      const added = (this.state.singles || []).some((x) => x.productId === it.productId);
      const img = it.imageRaw ? this.#sizeUrl(it.imageRaw, 400) : it.image;
      const pct = it.priceCents ? Math.round((1 - paid / it.priceCents) * 100) : 0;
      const best = this.upsellMatchBadge && k < this.upsellMatchBadgeN;
      return (
        `<div class="bb-match bb-match--card${added ? ' bb-match--added' : ''}">` +
        `<span class="bb-match__img"><img src="${img}" alt="${this.#esc(it.title)}" loading="lazy" width="400" height="400">` +
        (best ? `<span class="bb-match__best">${this.#esc(this.upsellMatchBadge)}</span>` : '') +
        (pct > 0 ? `<span class="bb-match__pct">-${pct}%</span>` : '') +
        `</span>` +
        `<span class="bb-match__row"><span class="bb-match__code">${this.#esc(m.code || '')}</span>` +
        `<span class="bb-match__price">${paid < it.priceCents ? `<s>${money(it.priceCents)}</s> ` : ''}<b>${money(paid)}</b></span></span>` +
        (added
          ? `<span class="bb-match__done">✓ Added</span>`
          : `<button type="button" class="bb-match__add" ${attr}="${k}">ADD</button>`) +
        `</div>`
      );
    }
    const paid = this.#singlePay(it.priceCents);
    const added = (this.state.singles || []).some((x) => x.productId === it.productId);
    const cat = this.upsellLabels[m.cat] || '';
    const tag = m.tag || (m.same ? `Same design · ${m.code}` : 'Same theme');
    return (
      `<div class="bb-match${added ? ' bb-match--added' : ''}">` +
      `<span class="bb-match__thumb" style="background-image:url('${it.image}')"></span>` +
      `<span class="bb-match__meta">` +
      `<strong class="bb-match__cat">${this.#esc(cat)}</strong>` +
      `<span class="bb-match__tag">${this.#esc(tag)}</span>` +
      `<span class="bb-match__price">${paid < it.priceCents ? `<s>${money(it.priceCents)}</s> ` : ''}<b>${money(paid)}</b>` +
      (it.variantLabel ? ` <span class="bb-match__size">· ${this.#esc(it.variantLabel)}</span>` : '') +
      `</span></span>` +
      (added
        ? `<span class="bb-match__done">✓ Added</span>`
        : `<button type="button" class="bb-match__add" ${attr}="${k}">${this.#esc(cta === 'ADD' ? 'ADD' : cta.replace('{price}', money(paid)))}</button>`) +
      `</div>`
    );
  }
  /** Thêm MỘT CHẠM một món gợi ý -> món lẻ, ở lại màn kết quả, giỏ tự chốt. */
  #addMatch(k) {
    const m = (this.upsellMatch || [])[k];
    if (!m) return;
    if ((this.state.singles || []).some((x) => x.productId === m.item.productId)) return;
    this.state.singles.push({ ...m.item });
    this.#track('BundleSingleAdded', { content_id: String(m.item.variantId), value: m.item.priceCents / 100, currency: this.currency, source: 'match' });
    this.#persist();
    this.#showResult(false);
    // Đang ở bảng chúc mừng thì toast nằm DƯỚI sheet (z 55 < 70) -> khách không thấy.
    // Thẻ đã tự đổi "✓ Added" + tiêu đề đổi câu, đủ để báo.
    if (!this.#upsheetOpen()) this.#toast(this.#pctText(this.dataset.upsellAdded || 'Added ✓ — the next one is {pct}% off too'));
  }

  /** Bảng xác nhận sau khi ADD trong lưới upsell: món vừa thêm + CHECKOUT / thêm tiếp / đổi loại. */
  #openAddedSheet(item) {
    const sheet = this.querySelector('[data-bb-addedsheet]');
    if (!sheet) return this.#toast(this.#pctText(this.dataset.upsellAdded || 'Added ✓'));
    this.#closeOto();
    this.#closeUpsheet();
    const pay = this.#grandPay();
    const paid = this.#singlePay(item.priceCents);
    const put = (sel, v) => { const el = sheet.querySelector(sel); if (el) el.textContent = v; };
    const thumb = sheet.querySelector('[data-bb-added-thumb]');
    if (thumb) thumb.style.backgroundImage = item.image ? `url('${item.image}')` : '';
    put('[data-bb-added-note]', `✓ ${this.#pctText(this.dataset.addedNote || 'Added — {pct}% off applied')}`);
    put('[data-bb-added-title]', `${item.title}${item.variantLabel ? ` · ${item.variantLabel}` : ''}`);
    const pr = sheet.querySelector('[data-bb-added-price]');
    if (pr) pr.innerHTML = `${paid < item.priceCents ? `<s>${money(item.priceCents)}</s> ` : ''}<b>${money(paid)}</b>`;
    put('[data-bb-added-heading]', this.#pctText(this.dataset.upsellMore || 'Nice pick. The next one is {pct}% off too.'));
    const btn = sheet.querySelector('[data-bb-added-checkout]');
    const lbl = sheet.querySelector('[data-bb-added-checkout-label]');
    if (btn && lbl) lbl.textContent = `${btn.dataset.label || 'CHECKOUT'} — ${money(pay)}`;
    const cat = this.upsellLabels[this.singleCat] || '';
    put('[data-bb-added-keep]', (this.dataset.addedKeep || 'KEEP BROWSING {cat}').replace(/\{cat\}/g, cat).replace(/\s+/g, ' ').trim());
    this.#renderUpsellCats();
    const fs = sheet.querySelector('[data-bb-added-fs]');
    if (fs) {
      const on = this.fsEnabled && this.fsThreshold > 0;
      fs.classList.toggle('bb-hide', !on);
      if (on) fs.textContent = this.#fsPlain(pay);
    }
    sheet.classList.remove('bb-hide');
    sheet.setAttribute('aria-hidden', 'false');
    sheet.querySelector('[data-bb-added-heading]')?.focus({ preventScroll: true });
  }
  /**
   * "View my order" (bảng xác nhận, băng đầu lưới, thanh đáy): rời lưới upsell về màn final
   * và MỞ SẴN chi tiết đơn — mục đích là để khách sửa size / xoá món ngay (chủ store chốt).
   */
  #viewOrder() {
    this.#closeAddedSheet();
    if (this.singlePick) this.#endSingle();
    this.#showResult(true);
    const od = this.querySelector('[data-bb-orderdetail]');
    if (od) od.open = true;
  }
  #closeAddedSheet() {
    const sheet = this.querySelector('[data-bb-addedsheet]');
    if (!sheet || sheet.classList.contains('bb-hide')) return;
    sheet.classList.add('bb-hide');
    sheet.setAttribute('aria-hidden', 'true');
  }

  /**
   * Bảng chúc mừng + upsell. Mở là đặt luôn cờ OTO của phiên: khách vừa xem đúng lời mời
   * đó, bấm CHECKOUT sau đó mà hỏi lại lần nữa là nài (chủ store chốt 2026-09-19).
   */
  /** Bề rộng ảnh thẻ lưới: 600 khi lưới 1 cột (điện thoại), 240 khi nhiều cột. */
  #gridImgW() {
    if (this._gridImgW) return this._gridImgW;
    let w = 240;
    try { if (window.matchMedia('(max-width: 749px)').matches) w = 600; } catch { /* giữ 240 */ }
    this._gridImgW = w;
    return w;
  }

  #openUpsellSheet() {
    const sheet = this.querySelector('[data-bb-upsheet]');
    if (!sheet || !this.upsellMode || !this.#hasFullBundle()) return;
    if (this.popupVariant === 'B') return; // A/B: nhóm B không có bảng bật lên
    this.#closeOto();
    this.#closeSingleSheet();
    try { sessionStorage.setItem(`${this.storageKey}:oto`, '1'); } catch { /* không có storage -> OTO vẫn có thể hiện, chấp nhận */ }
    sheet.classList.remove('bb-hide');
    sheet.setAttribute('aria-hidden', 'false');
    this.#renderUpsheet();
    sheet.querySelector('.bb-upsheet__ready')?.focus({ preventScroll: true });
    this.#track('UpsellPopupShown', {});
  }
  #upsheetOpen() {
    const sheet = this.querySelector('[data-bb-upsheet]');
    return !!sheet && !sheet.classList.contains('bb-hide');
  }
  #closeUpsheet() {
    const sheet = this.querySelector('[data-bb-upsheet]');
    if (!sheet || sheet.classList.contains('bb-hide')) return;
    this.#track('UpsellPopupClosed', { singles: (this.state.singles || []).length });
    sheet.classList.add('bb-hide');
    sheet.setAttribute('aria-hidden', 'true');
  }
  /** Dòng "You save" + tổng trên nút CHECKOUT của bảng — cập nhật sau mỗi lần ADD. */
  #renderUpsheet() {
    const sheet = this.querySelector('[data-bb-upsheet]');
    if (!sheet || sheet.classList.contains('bb-hide')) return;
    let total = 0;
    this.state.bundles.forEach((bn, b) => { total += this.#calc(bn, b).total; });
    (this.state.singles || []).forEach((x) => { total += x.priceCents; });
    const pay = this.#grandPay();
    const save = Math.max(0, total - pay);
    const saveEl = sheet.querySelector('[data-bb-upsheet-save]');
    if (saveEl) {
      saveEl.textContent = save > 0 ? this.#savingsText(save, total) : '';
      saveEl.classList.toggle('bb-hide', save <= 0);
    }
    const btn = sheet.querySelector('[data-bb-upsheet-checkout]');
    const lbl = sheet.querySelector('[data-bb-upsheet-checkout-label]');
    if (btn && lbl) lbl.textContent = `${btn.dataset.label || 'CHECKOUT'} — ${money(pay)}`;
  }
  async #upsheetCheckout() {
    this.#closeUpsheet();
    await this.#waitSync();
    return this.#checkout();
  }

  /** Đợi lượt tự chốt giỏ đang chạy xong, không thì bấm CHECKOUT giữa chừng là add trùng. */
  #waitSync() {
    return new Promise((res) => {
      const t0 = Date.now();
      const tick = () => {
        if (!this._expressSyncing || Date.now() - t0 > 8000) return res();
        setTimeout(tick, 120);
      };
      tick();
    });
  }
  /** Mọi nút CHECKOUT đi qua đây: offer một lần (nếu có) -> đợi giỏ chốt xong -> thanh toán. */
  async #checkoutClick(fromUpbar) {
    this.#track('CheckoutClicked', { source: fromUpbar ? 'upbar' : 'result', singles: (this.state.singles || []).length });
    if (fromUpbar) {
      this.#endSingle();
      this.#showResult(false);
    }
    if (this.#maybeOto()) return;
    await this.#waitSync();
    return this.#checkout();
  }

  /**
   * OFFER MỘT LẦN khi bấm CHECKOUT: đủ bộ, chưa thêm món upsell nào, đã có gợi ý cùng
   * design, và CHƯA hiện lần nào trong phiên. Chưa có gợi ý thì bỏ qua — không bắt khách
   * chờ tải chỉ để xem một lời mời.
   */
  #maybeOto() {
    if (!this.otoEnabled || (this.state.singles || []).length || !this.#hasFullBundle()) return false;
    const list = (this.upsellMatch || []).slice(0, 3);
    if (!list.length) return false;
    const k = `${this.storageKey}:oto`;
    try {
      if (sessionStorage.getItem(k)) return false;
      sessionStorage.setItem(k, '1');
    } catch {
      return false;
    }
    const sheet = this.querySelector('[data-bb-oto]');
    if (!sheet) return false;
    const code = (list.find((m) => m.same) || list[0]).code || '';
    const h = sheet.querySelector('[data-bb-oto-heading]');
    const sub = sheet.querySelector('[data-bb-oto-sub]');
    if (h) h.textContent = this.#pctText(this.dataset.otoHeading || 'Wait — one more at {pct}% off?');
    if (sub) sub.textContent = this.#pctText(this.dataset.otoSub || 'Same {code} design, half price.').replace(/\{code\}/g, code);
    const box = sheet.querySelector('[data-bb-oto-list]');
    if (box) box.innerHTML = list.map((m, i) => this.#matchCardHtml(m, i, 'data-bb-oto-add', 'ADD & CHECKOUT — {price}')).join('');
    sheet.classList.remove('bb-hide');
    sheet.setAttribute('aria-hidden', 'false');
    h?.focus({ preventScroll: true });
    this.#track('UpsellOtoShown', {});
    return true;
  }
  #closeOto() {
    const sheet = this.querySelector('[data-bb-oto]');
    if (!sheet || sheet.classList.contains('bb-hide')) return;
    sheet.classList.add('bb-hide');
    sheet.setAttribute('aria-hidden', 'true');
  }
  async #otoAdd(k) {
    const m = (this.upsellMatch || [])[k];
    this.#closeOto();
    if (m && !(this.state.singles || []).some((x) => x.productId === m.item.productId)) {
      this.state.singles.push({ ...m.item });
      this.#track('BundleSingleAdded', { content_id: String(m.item.variantId), value: m.item.priceCents / 100, currency: this.currency, source: 'oto' });
      this.#persist();
      this.#showResult(false);
    }
    await this.#waitSync();
    return this.#checkout();
  }
  async #otoSkip() {
    this.#closeOto();
    await this.#waitSync();
    return this.#checkout();
  }

  #backToBuilder() {
    this.state.editingBundle = null;
    const start = this.#emptySlot(this.state.draft);
    this.#stepTo(start === -1 ? 0 : start, true);
  }

  #removeBundle(b) {
    this.#track('ItemRemoved', { source: 'bundle' });
    const cartId = this.state.cartIds[b];
    this.state.bundles.splice(b, 1);
    this.state.cartIds.splice(b, 1);
    this.#persist();
    if (cartId) this.#removeBundleFromCart(cartId); // bundle đã trong giỏ -> gỡ luôn khỏi giỏ
    if (this.state.bundles.length === 0 && (this.state.singles || []).length === 0) {
      const start = this.#emptySlot(this.state.draft);
      this.#stepTo(start === -1 ? 0 : start, true);
    } else {
      this.#showResult(false);
    }
  }

  /** Gỡ tất cả line item cùng _bundle_id ra khỏi giỏ (set qty 0). */
  async #removeBundleFromCart(cartId) {
    return this.#cartOp(async () => {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const updates = {};
      (cart.items || []).forEach((it) => {
        if (it.properties && it.properties._bundle_id === cartId) updates[it.key] = 0;
      });
      if (Object.keys(updates).length === 0) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      this.#emitCartChanged('bundle-builder-remove', 0, true); // gỡ món trong builder -> không bung drawer
    } catch {
      /* non-critical: giỏ sẽ đồng bộ lại lần tải sau */
    }
    });
  }

  /**
   * Đối chiếu state với GIỎ (nguồn chuẩn cho bundle đã checkout):
   * - committed bundle = đúng những gì CÒN trong giỏ (xóa ở cart -> tự biến mất ở builder).
   * - giữ lại bundle đang dựng dở (cartId null, chưa vào giỏ).
   */
  #reconcileBundles(cartBundles, cartIds) {
    if (this.state.draft.some(Boolean) || this.state.editingBundle !== null) return; // đừng phá thao tác đang dở
    const pendingAdd = this.pendingAdd; // đến từ "Add bundle 2" -> đang ở step 1, đừng nhảy về final
    this.pendingAdd = false;
    const pending = [];
    this.state.bundles.forEach((bn, i) => {
      if (!this.state.cartIds[i]) pending.push(bn);
    });
    const newBundles = cartBundles.concat(pending);
    const newCartIds = cartIds.concat(pending.map(() => null));
    const unchanged =
      newBundles.length === this.state.bundles.length &&
      this.state.cartIds.every((id, i) => id === newCartIds[i]);
    if (unchanged) return; // khớp rồi -> không re-render thừa
    this.state.bundles = newBundles;
    this.state.cartIds = newCartIds;
    this.#persist();
    if (pendingAdd) return; // giữ ở step 1 của bundle mới
    if (this.ladderMode) this.#barUpdate();
    // Còn bộ HOẶC còn món lẻ -> vẫn có cái để xem, đừng đá khách về lưới chọn áo.
    else if (this.state.bundles.length > 0 || (this.state.singles || []).length > 0) this.#showResult(false);
    else this.#stepTo(Math.max(this.#emptySlot(this.state.draft), 0), false);
  }

  /** Giỏ thay đổi từ ngoài builder (cart drawer/page) -> đối chiếu lại để màn final khớp giỏ (khỏi F5). */
  #onCartUpdated() {
    const result = this.querySelector('[data-bb-result]');
    const onResult = result && !result.classList.contains('bb-hide');
    if (!onResult) return; // chỉ cần đồng bộ khi đang hiện màn final
    if (this.state.draft.some(Boolean) || this.state.editingBundle !== null) return;
    if (this._selfCartOp) return; // builder tự ghi giỏ -> khỏi đối chiếu
    this.#loadCartBundles().then(({ bundles, cartIds }) => this.#reconcileBundles(bundles, cartIds));
  }

  /** Đọc nhanh số lượng giỏ từ DOM (cart bubble). Trả null nếu không chắc -> vẫn fetch để an toàn. */

  /** Đọc giỏ -> dựng lại các bundle ĐỦ món của builder này (để hiện màn final khi quay lại). */
  async #loadCartBundles() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      // Giỏ KHÔNG chở danh sách size, mà #reconcileBundles thay nguyên state.bundles bằng
      // bộ dựng từ giỏ -> hàng chọn size sẽ biến mất sau mỗi lần đối chiếu. Nhặt lại từ
      // state hiện có theo productId; món nào vẫn thiếu thì #ensureVariants() đi hỏi.
      const known = new Map();
      const remember = (x) => {
        if (x && x.productId && x.variants) known.set(x.productId, x.variants);
      };
      this.state.bundles.forEach((bn) => (bn || []).forEach(remember));
      (this.state.singles || []).forEach(remember);
      (this.state.draft || []).forEach(remember);
      const groups = new Map();
      (cart.items || []).forEach((it) => {
        const p = it.properties || {};
        if (p._bundle !== this.bundleName || !p._bundle_id) return; // chỉ bundle của builder này
        const slot = parseInt(p._bundle_slot || '0', 10) || 0;
        if (!groups.has(p._bundle_id)) groups.set(p._bundle_id, []);
        const rawImg = (it.featured_image && it.featured_image.url) || it.image || '';
        groups.get(p._bundle_id)[slot] = {
          productId: it.product_id,
          variantId: it.variant_id,
          title: it.product_title || it.title || '',
          image: this.#sizeUrl(rawImg, 240),
          imageRaw: rawImg,
          handle: it.handle || '',
          variantLabel: it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '',
          priceCents: it.original_price != null ? it.original_price : it.price,
          variants: known.get(it.product_id) || undefined,
        };
      });
      const bundles = [];
      const cartIds = [];
      // Sort theo _bundle_id (khớp cách function xếp hạng) -> index = hạng.
      [...groups.keys()].sort().forEach((id) => {
        const arr = groups.get(id);
        if (this.ladderMode) {
          // Thang mở: số món TUỲ Ý. Ép về đúng stepCount (số block loại) thì bộ 2 món bị
          // coi là thiếu -> biến mất khỏi state sau mỗi lần đối chiếu, trong khi giỏ vẫn
          // còn hàng. Chỉ lộ ra từ khi sheet xem lại tự chốt giỏ.
          const flat = arr.filter(Boolean);
          if (flat.length) {
            bundles.push(flat);
            cartIds.push(id);
          }
          return;
        }
        const norm = Array(this.stepCount).fill(null);
        for (let i = 0; i < this.stepCount; i++) norm[i] = arr[i] || null;
        if (norm.every(Boolean)) {
          bundles.push(norm);
          cartIds.push(id);
        }
      });
      return { bundles, cartIds };
    } catch {
      return { bundles: [], cartIds: [] };
    }
  }

  /* ---------- pricing math ---------- */

  /** % giảm THÊM cho bundle ở vị trí `index` (0-based). Bundle 1 (0) = 0%. */
  #tierPct(index) {
    if (!Number.isInteger(index) || index < 1) return 0;
    const tiers = this.extraTiers;
    if (!tiers || !tiers.length) return 0;
    return tiers[Math.min(index - 1, tiers.length - 1)];
  }

  /** Vị trí mà target hiện tại chiếm trong bundles -> quyết định tier. */
  #targetIndex() {
    return this.state.editingBundle != null ? this.state.editingBundle : this.state.bundles.length;
  }

  /**
   * {total, pay, savings, basePay, extraPct, extraSavings} cho 1 mảng selections (cents).
   * `index` = vị trí bundle (0-based) -> áp ưu đãi "buy more save more" cho bundle 2+.
   */
  #calc(sels, index = 0) {
    const total = sels.reduce((s, x) => s + (x ? x.priceCents : 0), 0);
    let pay;
    if (this.ladderMode) {
      // Thang mở: xếp hạng rồi giảm theo hạng. KHÔNG cộng thêm "buy more save more"
      // (hai thang chồng nhau thì không ai đoán được giá) -> trả về luôn.
      const pay0 = this.#ladderRows(sels).reduce((sum, r) => sum + r.pay, 0);
      return { total, pay: pay0, savings: Math.max(0, total - pay0), basePay: pay0, extraPct: 0, extraSavings: 0 };
    }
    if (this.pricingType === 'bxgy' || this.pricingType === 'bxpct') {
      // Trừ theo % của TỪNG slot ưu đãi (bxgy = 100%). Làm tròn từng dòng để khớp
      // cách Shopify Function áp percentage lên từng cart line.
      const off = sels.reduce((s, x, i) => {
        const pct = this.#slotOff(i, sels);
        return s + (x && pct > 0 ? x.priceCents - this.#slotPay(x.priceCents, pct) : 0);
      }, 0);
      pay = total - off;
    } else {
      pay = this.bundlePriceCents;
    }
    const basePay = Math.max(0, pay);
    // "Buy more, save more" áp trên phần PHẢI TRẢ (bxgy: đã trừ quà free; fixed: giá cố định).
    const extraPct = this.#tierPct(index);
    // Làm tròn GIỐNG function: target = round(base*(1-pct/100)) -> khớp giá charge, không lệch cent.
    pay = extraPct > 0 ? Math.max(0, Math.round((basePay * (100 - extraPct)) / 100)) : basePay;
    const extraSavings = basePay - pay;
    const savings = Math.max(0, total - pay);
    return { total, pay, savings, basePay, extraPct, extraSavings };
  }

  /**
   * "2:50, 3:30, 4+:30" -> [{n,pct,plus}]. Chuỗi hỏng -> [] -> không giảm gì.
   * PHẢI khớp `parseLadder` trong cart_lines_discounts_generate_run.js.
   */
  #parseLadder(str) {
    const out = [];
    for (const part of String(str || '').split(',')) {
      const m = part.trim().match(/^(\d+)(\+?)\s*:\s*(\d+(?:\.\d+)?)$/);
      if (!m) continue;
      const n = parseInt(m[1], 10);
      const pct = Math.min(100, Math.max(0, parseFloat(m[3])));
      if (n >= 1 && pct > 0) out.push({ n, pct, plus: m[2] === '+' });
    }
    return out.sort((a, b) => a.n - b.n);
  }
  /** % cho món hạng `rank` (1-based): khớp chính xác trước, rồi tới mức "n+" gần nhất. */
  #ladderPct(rank) {
    let pct = 0;
    for (const st of this.ladderSteps) {
      if (st.n === rank) return st.pct;
      if (st.plus && st.n <= rank) pct = st.pct;
    }
    return pct;
  }
  /** Danh sách món đã chọn kèm % của từng món, đã xếp hạng đúng như function sẽ làm. */
  #ladderRows(sels) {
    const items = sels.map((x, i) => ({ sel: x, added: i })).filter((r) => r.sel);
    items.sort((a, b) => (this.ladderBy === 'added' ? a.added - b.added : b.sel.priceCents - a.sel.priceCents));
    return items.map((r, i) => {
      const pct = this.#ladderPct(i + 1);
      return { ...r, pct, pay: this.#slotPay(r.sel.priceCents, pct) };
    });
  }

  /**
   * % giảm của slot thứ `i`: 100 = quà FREE (bxgy), 1..99 = giảm một phần (bxpct),
   * 0 = trả nguyên giá. Một chỗ tính duy nhất -> thẻ, màn kết quả, peek và giỏ không lệch nhau.
   */
  /**
   * Tập chỉ số slot được TẶNG cho một bộ cụ thể.
   *   'slot'     -> đúng các step bật cờ (không phụ thuộc hàng đã chọn)
   *   'cheapest' -> N slot rẻ nhất đang có hàng, N = số step bật cờ.
   * Ràng buộc: PHẢI trả cùng kết quả ở mọi chỗ gọi (preview, màn kết quả, props giỏ)
   * nên chỉ nhận đúng một đầu vào là mảng đã chọn. Bằng giá thì lấy slot ĐỨNG TRƯỚC
   * để kết quả ổn định giữa các lần vẽ lại.
   */
  #freeIdx(sels) {
    if (this.freePick !== 'cheapest') return this.freeSlots;
    const n = this.freeSlots.length;
    if (!n || !Array.isArray(sels)) return [];
    // Bộ CHƯA đủ món -> chưa có quà. Khớp với function: nhánh tiered bỏ qua bộ thiếu món
    // (g.lines.length < g.count) nên giỏ chưa đủ cũng không được giảm gì.
    // Không có dòng này thì món ĐẦU TIÊN thêm vào luôn là món rẻ nhất -> hiện $0.00.
    if (sels.filter(Boolean).length < sels.length) return [];
    return sels
      .map((x, i) => ({ i, price: x ? x.priceCents : Infinity, has: !!x }))
      .filter((r) => r.has)
      .sort((a, b) => a.price - b.price || a.i - b.i)
      .slice(0, n)
      .map((r) => r.i);
  }
  #slotOff(i, sels) {
    // Chế độ 'cheapest' chưa biết bộ hàng (vd lúc dựng thẻ trong lưới) -> chưa có món
    // nào là quà, trả 0. Đừng đoán bừa: gắn badge FREE lên thẻ rồi tính tiền khác là sai.
    if (this.freePick === 'cheapest' && !Array.isArray(sels)) return 0;
    if (!this.#freeIdx(sels).includes(i)) return 0;
    if (this.pricingType === 'bxgy') return 100;
    // (this.stepPcts || []): phòng ca push thiếu file — section cũ không có
    // data-step-pcts mà JS mới đã lên, hoặc ngược lại.
    if (this.pricingType === 'bxpct') {
      const own = (this.stepPcts || [])[i] || 0;
      return own > 0 ? own : this.discountPct;
    }
    return 0;
  }
  /** Tiền phải trả cho 1 món sau khi trừ % của slot. Làm tròn cent GIỐNG mọi chỗ khác. */
  #slotPay(cents, off) {
    if (off <= 0) return cents;
    if (off >= 100) return 0;
    return Math.max(0, cents - Math.round((cents * off) / 100));
  }
  #offBadge(off) {
    return off >= 100 ? 'FREE' : `-${off}%`;
  }

  /** "You save $X (Y%)" — kèm phần trăm tiết kiệm. */
  #savingsText(savings, total) {
    const pct = total > 0 ? Math.round((savings / total) * 100) : 0;
    return `${this.savingsLabel} ${money(savings)}${pct > 0 ? ` (${pct}%)` : ''}`;
  }

  /**
   * Bắn event phễu cho Meta Pixel (+ dataLayer cho GTM nếu có). Non-blocking,
   * fail-safe: không có fbq/dataLayer thì bỏ qua, không bao giờ làm vỡ luồng.
   * standard=true -> 'track' (event chuẩn FB như AddToCart); false -> 'trackCustom'.
   */
  /**
   * Klaviyo: event "Built Bundle" khi khach dung xong bo (man xem bo / sheet xem lai).
   * MOT event cho moi trang offer -> 1 flow vot gio, nut CTA tro {{ event.OfferURL }}
   * (kem ?team= de mo san dung doi, ke ca tren thiet bi khac). Khach chua dinh danh voi
   * Klaviyo (chua mo email / dien form) thi Klaviyo tu bo qua, khong loi gi.
   */
  #klaviyoBundle(source) {
    try {
      const sets = this.ladderMode ? [this.#target().filter(Boolean)] : this.state.bundles;
      const items = [];
      let total = 0;
      let pay = 0;
      sets.forEach((b, i) => {
        const list = (b || []).filter(Boolean);
        if (!list.length) return;
        const c = this.#calc(b, this.ladderMode ? 0 : i);
        total += c.total;
        pay += c.pay;
        list.forEach((it) => items.push(it));
      });
      (this.state.singles || []).forEach((it) => {
        if (!it || !it.priceCents) return;
        total += it.priceCents;
        pay += this.#singlePay(it.priceCents);
        items.push(it);
      });
      if (!items.length) return;
      // Cung mot bo thi khong ban lai (doi qua lai giua cac man, F5...).
      const key = items.map((it) => it.variantId).join(',');
      if (this._kvLast === key) return;
      this._kvLast = key;

      const code = this.selectedBrand ? String(this.#slugifyBrand(this.selectedBrand)) : '';
      const meta = code && this.teamMeta[code];
      const url = new URL(window.location.pathname, window.location.origin);
      if (meta) url.searchParams.set('team', code.toUpperCase());
      const big = (u) => (u ? this.#sizeUrl(u, 600) : '');
      // Email dung ten doi day du (chu store chot 2026-09-29) -> gui nguyen title cua card.
      const props = {
        OfferName: this.bundleName,
        OfferType: this.ladderMode ? 'ladder' : this.pricingType,
        OfferURL: url.href,
        TeamCode: meta ? code.toUpperCase() : '',
        TeamName: meta && meta.n ? meta.n : '',
        ItemCount: items.length,
        ItemNames: items.map((it) => String(it.title || '')),
        Items: items.map((it) => ({
          ProductName: String(it.title || ''),
          ImageURL: big(it.image),
          Size: it.variantLabel || '',
          Price: (it.priceCents || 0) / 100,
        })),
        ImageURL: big(items[0].image),
        OriginalTotal: total / 100,
        Savings: Math.max(0, total - pay) / 100,
        Source: source,
        $value: pay / 100,
      };
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(['track', 'Built Bundle', props]);
      if (window.__bbDebug) console.log('[bb] klaviyo Built Bundle', props);
    } catch {
      /* khong de tracking lam hong builder */
    }
  }

  /**
   * "Email me this bundle" (2026-10-07): thay popup Klaviyo tren trang offer. Khach da duoc
   * Klaviyo nhan dien (co email) thi giau ca khoi — flow vot bo da chay duoc cho ho roi.
   */
  #syncSave() {
    const box = this.querySelector('[data-bb-save]');
    if (!box || this._saveDone) return;
    const k = window.klaviyo;
    if (k && typeof k.isIdentified === 'function') {
      Promise.resolve(k.isIdentified())
        .then((known) => box.classList.toggle('bb-hide', !!known && !window.__bbSaveForce))
        .catch(() => box.classList.remove('bb-hide'));
    } else {
      box.classList.remove('bb-hide');
    }
  }

  #saveOpen(btn) {
    const box = btn.closest('[data-bb-save]');
    const form = box && box.querySelector('[data-bb-save-form]');
    if (!form) return;
    const open = form.classList.toggle('bb-hide') === false;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      this.#track('SaveBundleOpen');
      // Khong focus tren dien thoai: ban phim ao bat len che mat tong tien + nut CHECKOUT.
      if (window.matchMedia('(hover: hover)').matches) form.querySelector('input').focus();
    }
  }

  #saveSubmit(form) {
    const box = form.closest('[data-bb-save]');
    const input = form.querySelector('input[type="email"]');
    const err = form.querySelector('[data-bb-save-err]');
    const email = String(input.value || '').trim().toLowerCase();
    const ok = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email);
    err.classList.toggle('bb-hide', ok);
    if (!ok) return input.focus();
    form.querySelector('button[type="submit"]').disabled = true;
    try {
      // identify XONG roi moi ban lai Built Bundle (bo khoa chong lap) -> event gan dung profile
      // vua nhap, flow TV235P chay. Ban song song thi request events di truoc profiles (da gap
      // khi test 2026-10-07) va event bi ghi cho danh tinh cu cua trinh duyet.
      const send = () => {
        this._kvLast = '';
        this.#klaviyoBundle('save');
      };
      const who = { email, BundleSaveSource: this.bundleName };
      const k = window.klaviyo;
      if (k && typeof k.identify === 'function') {
        Promise.resolve(k.identify(who)).then(send, send);
      } else {
        window.klaviyo = k || [];
        window.klaviyo.push(['identify', who]);
        send();
      }
    } catch {
      /* khong de Klaviyo lam hong builder */
    }
    this.#track('SaveBundleSubmit');
    this._saveDone = true;
    form.classList.add('bb-hide');
    box.querySelector('[data-bb-save-open]').classList.add('bb-hide');
    box.querySelector('[data-bb-save-done]').classList.remove('bb-hide');
  }

  #track(name, data = {}, standard = false, metaName = '', eventID = '') {
    try {
      // 1) Shopify Customer Events bridge (store dùng web pixel sandbox) -> tên snake_case.
      const snake = `bb_${name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()}`;
      const pub = window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish;
      if (typeof pub === 'function') window.Shopify.analytics.publish(snake, data);

      // Event hanh vi chi dung de phan tich -> khong day sang Meta (nhieu event tuy bien
      // chi lam roi Events Manager) va dataLayer.
      const analyticsOnly = /^(TeamReady|ResultShown|CheckoutClicked|ReviewOpened|UpsellPopupClosed|SizeChanged|ItemRemoved|ZoomOpened|DeeplinkShown|DeeplinkAdded|DesignCodeShown|DesignCodeTeam|LandingStickyClick|SetShown|SetItemAdded|SetPopupClick|OtherTeamClick|CardLandingClick|ExploreShown|ExploreClick|ExploreAllClick|LadderReopened|MatchSwiped|ReviewsOpened|SaveBundleOpen|SaveBundleSubmit)$/.test(name);

      // 2) Fallback: Meta Pixel trực tiếp (nếu fbq được nạp thẳng vào theme).
      if (!analyticsOnly && typeof window.fbq === 'function') {
        if (eventID) window.fbq(standard ? 'track' : 'trackCustom', metaName || name, data, { eventID });
        else window.fbq(standard ? 'track' : 'trackCustom', metaName || name, data);
      }

      // 3) Fallback: GTM dataLayer.
      if (!analyticsOnly && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: snake, ...data });
      }

      // 4) Microsoft Clarity: event khong mang du lieu, nen tach bien the theo step /
      //    so mon de dung phieu. Tag gan vao phien de loc heatmap + recording theo offer.
      if (window.__bbDebug === undefined) {
        try {
          window.__bbDebug = !!localStorage.getItem('bb:debug');
        } catch {
          window.__bbDebug = false;
        }
      }
      if (window.__bbDebug) console.log('[bb]', snake, data);
      if (typeof window.clarity === 'function') {
        window.clarity('event', snake);
        if (data.step) window.clarity('event', `${snake}_s${data.step}`);
        if (data.source) window.clarity('event', `${snake}_${data.source}`);
        if (name === 'BundleItemAdded') {
          const n = this.#target().filter(Boolean).length;
          window.clarity('event', `bb_items_${n >= 5 ? '5plus' : n}`);
        }
        if (name === 'BrandSelected' && data.brand) window.clarity('set', 'bb_team', String(data.brand));
      }
    } catch {
      /* analytics non-critical */
    }
  }

  /**
   * Do khach co doc phan noi dung quanh builder khong (chi gui Clarity):
   *   bb_seen_<khoi>  khoi hien tren man hinh (>=40% khoi hoac >=40% man hinh)
   *   bb_read_<khoi>  khoi nam tren man hinh >=3 giay lien -> "dung lai doc"
   *   bb_faq_open + bb_faq_<muc>  mo mot muc <details> (shipping, returns, sizing...)
   *   bb_reviews_nav, bb_cta_<...>  bam nut trong khoi noi dung
   * Khoi = con truc tiep cua goc khoi HTML (class usp-*), ten lay tu class dau tien bo
   * tien to (u-head -> head, h-trust -> trust) roi doi sang ten de doc (head -> headline).
   * Them section banner (hero) va chinh builder. Chay MOT lan moi trang.
   */
  /** Gửi một event phân tích (Clarity + GA4 qua bb_content). Không gửi Meta. */
  #ev(name) {
    try {
      if (window.__bbDebug) console.log('[bb]', name);
      if (typeof window.clarity === 'function') window.clarity('event', name);
      window.Shopify?.analytics?.publish?.('bb_content', { name, path: location.pathname });
    } catch {
      /* analytics non-critical */
    }
  }

  /**
   * Banner hero + nhãn nhỏ trên tiêu đề (`[class*="eyebrow"]`) cuộn xuống builder khi bấm
   * (2026-09-27). Clarity: 139 + 183 lượt bấm vào đúng 2 chỗ này trong 7 ngày mà trước đó
   * không có gì xảy ra -> khách tưởng trang hỏng. Link/nút thật bên trong vẫn chạy như cũ.
   */
  #initTapToBuilder() {
    if (window.__bbTapInit) return;
    window.__bbTapInit = true;
    const main = document.querySelector('main') || document.body;
    const sel = '.shopify-section[id*="__hero"], [class*="usp-"] [class*="eyebrow"]';
    main.querySelectorAll(sel).forEach((el) => el.classList.add('bb-tap-to-builder'));
    main.addEventListener('click', (e) => {
      const hit = e.target.closest(sel);
      if (!hit || e.target.closest('a, button, input, select, bundle-builder')) return;
      // Dùng lại #scrollToStep: nó tự chọn mốc ĐANG HIỆN (bộ chọn đội / hàng loại / tiêu đề step).
      const st = (this.stepEls || []).find((el) => el.getClientRects().length > 0) || this;
      this.#scrollToStep(st);
      this.#ev(hit.matches('.shopify-section') ? 'bb_tap_hero' : 'bb_tap_eyebrow');
    });
  }

  /**
   * Đo popup đăng ký của Klaviyo (2026-09-27): mở / đóng / gửi. Để trả lời câu hỏi "khách đóng
   * popup xong có thoát luôn không" — so phễu bb_kl_form_close -> bb_team_ready trên GA4.
   * Klaviyo bắn window event `klaviyoForms` với e.detail.type = open|embedOpen|close|submit|...
   */
  #initKlaviyoFormTracking() {
    if (window.__bbKlInit) return;
    window.__bbKlInit = true;
    window.addEventListener('klaviyoForms', (e) => {
      const t = String(e?.detail?.type || '');
      if (t === 'open' || t === 'close' || t === 'submit' || t === 'stepSubmit') {
        this.#ev(`bb_kl_form_${t === 'stepSubmit' ? 'submit' : t}`);
      }
    });
  }

  #initContentTracking() {
    try {
      if (window.__bbContentTracked || typeof window.clarity !== 'function') return;
      if (!('IntersectionObserver' in window)) return;
      window.__bbContentTracked = true;
      const ev = (n) => {
        try {
          if (window.__bbDebug) console.log('[bb]', n);
          window.clarity('event', n);
          // Pixel chi subscribe duoc ten co dinh -> gom vao bb_content {name}.
          window.Shopify?.analytics?.publish?.('bb_content', { name: n, path: location.pathname });
        } catch {
          /* analytics non-critical */
        }
      };
      const alias = {
        head: 'headline', dealhead: 'pricing_head', grid: 'pricing', math: 'pricing', table: 'pricing',
        guar: 'guarantee', acc: 'faq', rev: 'reviews', how: 'howto', cta: 'cta', trust: 'trust',
      };
      const clean = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30);
      const blocks = new Map(); // element -> name
      const main = document.querySelector('main') || document.body;
      main.querySelectorAll('.shopify-section').forEach((sec) => {
        if (sec.querySelector('bundle-builder')) return;
        const id = sec.id.split('__')[1] || '';
        if (/^hero/.test(id)) {
          blocks.set(sec, 'hero');
          return;
        }
        const root = sec.querySelector('[class*="usp-"]');
        if (!root) return;
        const used = new Set();
        Array.from(root.children).forEach((c) => {
          if (/^(STYLE|SCRIPT|LINK)$/.test(c.tagName)) return;
          // Khoi cua nhom A/B kia bi an -> khong dem, khong chiem ten.
          const cv = c.getAttribute('data-ab-variant');
          if (cv && cv !== document.documentElement.getAttribute('data-abv')) return;
          const cls = String(c.className || '').split(/\s+/)[0].replace(/^[a-z0-9]+-/, '');
          let name = alias[cls] || clean(cls) || '';
          if (!name || used.has(name)) return; // 2 khoi cung ten (vd dealhead+grid) -> dem khoi dau
          used.add(name);
          blocks.set(c, name);
        });
      });
      blocks.set(this, 'builder');

      // A/B: moi phan tu [data-ab="<ten>"][data-ab-variant="A|B"] thuoc mot thi nghiem.
      const abv = document.documentElement.getAttribute('data-abv');
      const exps = new Set();
      main.querySelectorAll('[data-ab]').forEach((el) => exps.add(clean(el.getAttribute('data-ab'))));
      if (abv && exps.size) {
        window.__bbAb = window.__bbAb || {};
        exps.forEach((x) => {
          if (!x) return;
          window.__bbAb[x] = abv;
          window.clarity('set', `ab_${x}`, abv);
          ev(`bb_ab_${x}_${abv}`);
        });
      }

      const seen = new Set();
      const timers = new Map();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const name = blocks.get(en.target);
          if (!name) return;
          const vh = window.innerHeight || 1;
          const on = en.isIntersecting && (en.intersectionRatio >= 0.4 || en.intersectionRect.height >= vh * 0.4);
          if (on) {
            if (!seen.has(name)) {
              seen.add(name);
              ev(`bb_seen_${name}`);
            }
            if (!timers.has(en.target) && !seen.has(`read:${name}`)) {
              timers.set(en.target, setTimeout(() => {
                seen.add(`read:${name}`);
                ev(`bb_read_${name}`);
                io.unobserve(en.target);
              }, 3000));
            }
          } else if (timers.has(en.target)) {
            clearTimeout(timers.get(en.target));
            timers.delete(en.target);
          }
        });
      }, { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] });
      blocks.forEach((_, el) => io.observe(el));

      // Muc thu gon (<details>) ngoai builder: FAQ, shipping, returns, description...
      const faqKey = (t) => {
        const x = String(t || '').toLowerCase();
        const map = [
          [/ship|deliver/, 'shipping'], [/return|refund|warrant|exchange/, 'returns'], [/siz|fit/, 'sizing'],
          [/made of|material|fabric/, 'material'], [/peel|crack|fade|print/, 'print'], [/track/, 'tracking'],
          [/care|wash/, 'care'], [/team/, 'teams'], [/design/, 'designs'], [/descri/, 'description'],
        ];
        const hit = map.find(([re]) => re.test(x));
        return hit ? hit[1] : clean(x.split(/\s+/).slice(0, 3).join(' ')) || 'other';
      };
      main.querySelectorAll('details').forEach((d) => {
        if (d.closest('bundle-builder')) return;
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          ev('bb_faq_open');
          ev(`bb_faq_${faqKey(d.querySelector('summary')?.textContent)}`);
        });
      });

      // Nut trong khoi noi dung (ngoai builder): chuyen review, CTA cuon xuong, link sang trang khac.
      main.addEventListener('click', (e) => {
        const b = e.target.closest('button, a');
        if (!b || b.closest('bundle-builder') || !blocks.size) return;
        if (b.closest('[class*="rev"]') && b.matches('button')) return ev('bb_reviews_nav');
        if (b.hasAttribute('data-usp-scroll')) return ev('bb_cta_scroll');
        if (b.closest('.shopify-section') && !b.closest('header, footer')) {
          const blk = Array.from(blocks.keys()).find((k) => k.contains(b));
          if (blk) ev(`bb_click_${blocks.get(blk)}`);
        }
      }, true);
    } catch {
      /* analytics non-critical */
    }
  }

  /**
   * Ghi nhom A/B vao thuoc tinh gio `_ab` (vd "headline:B") -> Shopify chep sang don hang,
   * nen tinh duoc ti le ra don cua tung nhom. Dau `_` de khong hien ra o trang thanh toan.
   * Chay mot lan moi luot xem trang, truoc khi day hang vao gio.
   */
  async #syncAbAttr() {
    try {
      const ab = window.__bbAb;
      if (!ab || this._abSynced) return;
      const val = Object.entries(ab).map(([k, v]) => `${k}:${v}`).join(',');
      if (!val) return;
      this._abSynced = true;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _ab: val } }),
      });
    } catch {
      /* analytics non-critical */
    }
  }

  /** Gan nhan offer vao phien Clarity (loc heatmap/recording theo tung trang offer). */
  #clarityTags() {
    try {
      if (typeof window.clarity !== 'function') return;
      window.clarity('set', 'bb_offer', String(this.bundleName || ''));
      window.clarity('set', 'bb_type', this.ladderMode ? 'ladder' : this.pricingType);
    } catch {
      /* analytics non-critical */
    }
  }

  /**
   * Moi cu bam vao mot nut cua builder -> event Clarity `bbc_<ten attribute>`
   * (vd bbc_checkout, bbc_resultbar_checkout, bbc_oto_skip). Lay attribute data-bb-*
   * dau tien cua nut nen nut moi them sau nay tu duoc dem, khong phai khai them.
   */
  #trackClick(e) {
    try {
      if (typeof window.clarity !== 'function') return;
      const el = e.target.closest('button, a, [role="button"], [role="tab"], summary');
      if (!el || !this.contains(el)) return;
      const attr = Array.from(el.attributes)
        .map((a) => a.name)
        .find((n) => n.startsWith('data-bb-') && !/^data-bb-(hidden|img|seen|themes|step|slug|brand)$/.test(n));
      if (!attr) return;
      const n = `bbc_${attr.slice(8).replace(/-/g, '_')}`;
      if (window.__bbDebug) console.log('[bb]', n);
      window.clarity('event', n);
      window.Shopify?.analytics?.publish?.('bb_click', { name: n, path: location.pathname });
    } catch {
      /* analytics non-critical */
    }
  }

  /* ---------- sticky bar ---------- */

  #barUpdate() {
    this.#barPeekText();
    const resultEl = this.querySelector('[data-bb-result]');
    const onResult = !!resultEl && !resultEl.classList.contains('bb-hide');
    const target = this.#target();
    if (this.ladderMode) {
      this.querySelector('[data-bb-bar]')?.classList.remove('bb-hide');
      // Cờ cho CSS: chưa có món nào thì giấu khối giá (đang là "$0.00 / $0.00").
      this.classList.toggle('bb-has-items', target.some(Boolean));
      this.#renderLadderBar(target);
      this.#renderFsBar();
      // Draft đổi -> % của "món kế tiếp" đổi -> vẽ lại giá gạch trên mọi card.
      this.#refreshLadderPreviews();
      // The landing (?design=): gia "THIS/ANOTHER", nut ADD, dong free ship, thanh dinh day phu thuoc
      // bo dang co -> xoa mon thi phai ve lai, khong thi con ghi "already unlocked" khi bo da trong.
      this.querySelectorAll('[data-bb-spot]').forEach((c) => this.#refreshCard(c));
      this.#priceUpdate();
      return;
    }
    this.querySelectorAll('[data-bb-slot]').forEach((slot, i) => {
      const sel = target[i];
      let s = '';
      if (sel) s = 'filled';
      else if (!onResult && i === this.state.current) s = 'active';
      slot.dataset.state = s;
      const thumb = slot.querySelector('[data-bb-slot-thumb]');
      if (thumb) thumb.style.backgroundImage = sel && sel.image ? `url("${sel.image}")` : '';
    });

    this.querySelector('[data-bb-bar]')?.classList.toggle('bb-hide', onResult);
    const hasAny = target.some(Boolean);
    this.querySelector('[data-bb-view]')?.classList.toggle('bb-hide', !(hasAny && !onResult));

    // Chua chon doi + chua co mon nao -> thanh day chi con MOT nut "CHOOSE YOUR TEAM".
    // Giau luon khoi gia (dang la "$0.00") vi luc nay chua co gi de noi ve tien.
    const pickStep = !hasAny && !onResult && this.#pickerVisible();
    this.querySelector('[data-bb-pickteam]')?.classList.toggle('bb-hide', !pickStep);
    this.querySelector('[data-bb-bar]')?.classList.toggle('bb-bar--pick', pickStep);
    // Chua chon mon nao thi khoi gia dang la "$0.00" - khong noi len dieu gi, giau di.
    // Thang mo da lam viec nay bang .bb-has-items tu truoc.
    this.querySelector('[data-bb-bar]')?.classList.toggle('bb-bar--empty', !hasAny && !onResult);

    // Nhắc tiến độ.
    const nudge = this.querySelector('[data-bb-nudge]');
    if (nudge) {
      const left = target.filter((x) => !x).length;
      nudge.textContent = left > 0 ? `${left} more item${left === 1 ? '' : 's'} to go` : '';
      nudge.classList.toggle('bb-hide', left === 0);
    }
    this.#priceUpdate();
  }

  /**
   * Danh sách món đã chọn ở thanh đáy + câu nhử mức kế tiếp.
   * Câu nhử nói rõ món NÀO được giảm ("món rẻ nhất trong bộ") vì với thang xếp theo giá,
   * món vừa thêm chưa chắc là món bị giảm — không nói rõ là khách thấy sai lời hứa.
   */
  #renderLadderBar(target) {
    const wrap = this.querySelector('[data-bb-ladder-items]');
    const next = this.querySelector('[data-bb-ladder-next]');
    const addBtn = this.querySelector('[data-bb-ladder-add]');
    if (!wrap) return;
    const rows = this.#ladderRows(target);
    wrap.textContent = '';
    rows.forEach((r) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-ladderbar__item';
      b.dataset.bbLadderRemove = String(r.added);
      b.setAttribute('aria-label', `Remove ${r.sel.title}`);
      const th = document.createElement('span');
      th.className = 'bb-ladderbar__thumb';
      if (r.sel.image) th.style.backgroundImage = `url("${r.sel.image}")`;
      // Loại áo + mã design (2 token cuối của title) -> khách biết X sẽ gỡ đúng món nào.
      // Chỉ lấy mã là chưa đủ: cùng một mã SC/SHW có ở MỌI loại áo nên 2 chip trông y hệt.
      const codeTxt = String(r.sel.title || '').trim().split(/\s+/).slice(-2).join(' ');
      const code = document.createElement('span');
      code.className = 'bb-ladderbar__code';
      code.textContent = codeTxt;
      const tx = document.createElement('span');
      tx.textContent = r.pct > 0 ? `-${r.pct}%` : money(r.sel.priceCents);
      const x = document.createElement('span');
      x.className = 'bb-ladderbar__x';
      x.textContent = '×';
      b.append(th, code, tx, x);
      wrap.append(b);
    });
    // Chua chon doi + chua co mon nao -> nut doi vai thanh "CHOOSE YOUR TEAM".
    // De nguyen "VIEW CART & CHECKOUT" mo mo o day la nut duy nhat cuoi man hinh lai tro
    // toi mot cai gio rong, trong khi viec dau tien khach phai lam la chon doi.
    if (addBtn) {
      const pick = rows.length === 0 && this.#pickerVisible();
      const lbl = addBtn.querySelector('span');
      if (lbl) {
        if (this._ladderAddLabel == null) this._ladderAddLabel = lbl.textContent;
        lbl.textContent = pick
          ? addBtn.dataset.bbPickLabel || 'CHOOSE YOUR TEAM'
          : this._ladderAddLabel;
      }
      addBtn.toggleAttribute('data-bb-pick', pick);
      addBtn.disabled = pick ? false : rows.length === 0;
    }
    if (next) {
      const pct = this.#ladderPct(rows.length + 1);
      if (!rows.length) {
        const first = this.#ladderPct(2);
        next.textContent = first ? `Add 2 items — the 2nd one is ${first}% off` : '';
      } else if (pct > 0) {
        next.textContent =
          this.ladderBy === 'added'
            ? `Add 1 more — get ${pct}% off it`
            : `Add 1 more — get ${pct}% off the cheapest item in your bundle`;
      } else {
        next.textContent = '';
      }
      next.classList.toggle('bb-hide', !next.textContent);
    }
  }

  /** Bỏ 1 món khỏi giỏ đang dựng (thang mở) — chỉ số theo THỨ TỰ THÊM. */
  #removeLadderItem(addedIndex) {
    const target = this.#target();
    if (addedIndex < 0 || addedIndex >= target.length) return;
    this.#track('ItemRemoved', { source: 'ladder' });
    target.splice(addedIndex, 1);
    this.#persist();
    this.#barUpdate();
  }

  /* ---------- thang mở: sheet chúc mừng + fs bar + preview giá ---------- */

  /**
   * Giá GỐC thấp nhất của loại `i` (cents). Ba nguồn, theo thứ tự:
   *   1. `from_price` merchant nhập  -> biết NGAY khi vào trang, không cần request
   *   2. card đã tải trong lưới của loại đó
   *   3. giá do prefetch nền lấy về (xem #prefetchFromPrices)
   * 0 = chưa biết -> chỗ hiển thị tự ẩn thay vì ghi bậy.
   */
  #stepMinPrice(i) {
    const step = this.stepEls[i];
    if (!step) return 0;
    const declared = parseInt(step.dataset.fromPrice, 10) || 0;
    if (declared > 0) return declared;
    let min = 0;
    step.querySelectorAll('[data-bb-card]').forEach((c) => {
      const v = parseInt(c.dataset.bbMinPrice, 10) || 0;
      if (v > 0 && (!min || v < min)) min = v;
    });
    if (min) return min;
    return (this.fromPriceCache && this.fromPriceCache[i]) || 0;
  }
  /** Giá phải trả cho món kế tiếp của loại `i` (đã trừ % theo hạng). 0 = chưa biết giá. */
  #stepFromPay(i) {
    const min = this.#stepMinPrice(i);
    if (!min) return 0;
    // Chỉ thang mở mới giảm theo hạng món. bxgy: món tiếp theo trả nguyên giá (quà là
    // món rẻ nhất, chỉ chốt khi bộ đủ món) -> trả thẳng giá gốc thấp nhất.
    return this.ladderMode ? this.#ladderNextPay(min) : min;
  }
  /**
   * Lấy giá thấp nhất của các loại CHƯA biết giá, chạy nền một lần mỗi phiên.
   * Dùng `view=bb` (24 sản phẩm/trang) chứ không phải bbsearch (250) cho nhẹ; đồng thời
   * warm luôn template của collection đó nên lúc khách bấm sang loại ấy sẽ mở nhanh hơn.
   */
  #prefetchFromPrices(brand) {
    if (!this.ladderMode || this.fromPrefetched || !brand) return;
    this.fromPrefetched = true;
    this.fromPriceCache = this.fromPriceCache || {};
    const run = () => {
      this.stepEls.forEach((step, i) => {
        if (this.#stepMinPrice(i) > 0) return; // đã biết giá -> khỏi gọi
        const handle = this.#directHandle(step, brand);
        if (!handle) return;
        fetch(`/collections/${encodeURIComponent(handle)}?view=bb&page=1`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            let min = 0;
            (data?.results || []).forEach((p) => {
              (p.variants || []).forEach((v) => {
                const price = v[3];
                if (v[4] && price > 0 && (!min || price < min)) min = price;
              });
            });
            if (!min) return;
            this.fromPriceCache[i] = min;
            // Sheet đang mở thì cập nhật ngay, không bắt khách đóng mở lại.
            const el = this.querySelector('[data-bb-celebrate]');
            if (el && !el.classList.contains('bb-hide')) this.#celebrate();
          })
          .catch(() => {});
      });
    };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 1500);
  }
  /** Giá "from" rẻ nhất trong MỌI loại đã tải -> dùng để ước lượng còn mấy món nữa thì free ship. */
  #cheapestNextPay() {
    let best = 0;
    this.stepEls.forEach((_, i) => {
      const p = this.#stepFromPay(i);
      if (p > 0 && (!best || p < best)) best = p;
    });
    return best;
  }

  /**
   * Sheet chúc mừng — mở SAU MỖI lần thêm món, nội dung đổi theo số món:
   *   1 món  -> "mở khoá": món kế tiếp giảm bao nhiêu %
   *   2 món+ -> đã tiết kiệm bao nhiêu + mời thêm món nữa (kèm giá "from" sau giảm)
   * Luôn kèm: nút giữ nguyên loại đang xem / đổi loại khác, và tiến độ free shipping.
   */
  #celebrate() {
    const el = this.querySelector('[data-bb-celebrate]');
    if (!el) return;
    const items = this.#target().filter(Boolean);
    const count = items.length;
    if (!count) return;
    // Còn thiếu mấy món nữa mới đủ bộ. Thang mở không có "đủ bộ" -> luôn 0.
    const left = this.ladderMode ? 0 : Math.max(0, this.stepCount - count);
    const pct = this.ladderMode ? this.#ladderPct(count + 1) : left === 1 ? 100 : 0;
    const { total, pay } = this.#calc(this.#target(), 0);
    const saved = Math.max(0, total - pay);
    const curIdx = this.state.current;
    // Thang mở: loại = step (hàng .bb-cats chung). Offer theo step: loại = sub-tab đang
    // chọn TRONG step đang xem. Hai chỗ khác nhau nên phải đọc khác nhau.
    const curCat = this.ladderMode
      ? (this.querySelectorAll('[data-bb-cat]')[curIdx]?.textContent || '').trim()
      : (this.stepEls[curIdx]?.querySelector('[data-bb-subtab][aria-selected="true"]')?.textContent || '').trim();
    const fromPay = this.#stepFromPay(curIdx) || this.#cheapestNextPay();
    const fill = (tpl) =>
      String(tpl || '')
        .replace(/\{pct\}/g, String(pct))
        .replace(/\{n\}/g, String(count))
        .replace(/\{left\}/g, String(left))
        .replace(/\{total\}/g, String(this.stepCount))
        .replace(/\{saved\}/g, money(saved))
        .replace(/\{from\}/g, fromPay ? money(fromPay) : '—')
        .replace(/\{cat\}/g, curCat || 'ITEM');
    const put = (sel, tpl) => {
      const n = el.querySelector(sel);
      if (n) n.textContent = fill(tpl);
    };
    // Fallback wording nằm ở JS chứ không phải filter default của Liquid — chuỗi chứa
    // {pct} bên trong {{ ... | default: '...' }} làm Liquid vỡ parse.
    if (!this.ladderMode) {
      /*
       * BUY X GET Y — cả bộ mới có quà, nên câu chữ xoay quanh SỐ MÓN CÒN THIẾU chứ không
       * phải số tiền đã tiết kiệm (lúc này luôn = 0, quà chỉ chốt khi bộ đủ món).
       * Món áp chót thì đổi giọng: "thêm một món nữa là món rẻ nhất free".
       */
      const one = left === 1;
      put(
        '[data-bb-celebrate-heading]',
        this.dataset.celebrateHeading ||
          (one
            ? '🎉 One more item — and the cheapest one is FREE'
            : '✓ Added — {left} more to unlock your FREE item')
      );
      put(
        '[data-bb-celebrate-sub]',
        this.dataset.celebrateSub ||
          "You're viewing {cat}. Mix any category you like — we always take the cheapest item off your bill."
      );
      put('[data-bb-celebrate-golabel]', this.dataset.celebrateCta || 'KEEP BROWSING {cat}');
    } else if (count === 1) {
      put('[data-bb-celebrate-heading]', this.dataset.celebrateHeading || '🎉 Unlocked: your next item is {pct}% OFF');
      put('[data-bb-celebrate-sub]', pct > 0
        ? (this.dataset.celebrateSub || 'Mix any category — the discount comes off automatically at checkout.')
        : 'Your bundle is ready whenever you are.');
      put('[data-bb-celebrate-golabel]', this.dataset.celebrateCta || 'PICK MY {pct}% OFF {cat}');
      put('[data-bb-celebrate-skip]', this.dataset.celebrateSkip || 'VIEW CART & CHECKOUT');
    } else {
      put('[data-bb-celebrate-heading]', this.dataset.celebrateMoreHeading || '🎉 {n} items · you saved {saved}');
      put('[data-bb-celebrate-sub]', pct > 0
        ? (this.dataset.celebrateMoreSub || 'Add one more and get {pct}% off it — from {from}.')
        : 'That is every discount unlocked — nice work.');
      put('[data-bb-celebrate-golabel]', curCat ? (this.dataset.celebrateKeep || 'KEEP BROWSING {cat}') : 'KEEP BROWSING');
      put('[data-bb-celebrate-skip]', `VIEW CART & CHECKOUT (${count})`);
    }
    // Ảnh món VỪA thêm (không phải món đầu) -> khách thấy đúng thứ mình vừa bấm.
    /*
     * Từ món thứ 2 trở đi, nút CHÍNH đổi thành "xem bộ & thêm giỏ": lúc đó khách đã có
     * một bộ thật và đã thấy tiền tiết kiệm, đẩy mua mới có nghĩa. Ở món 1 thì ngược lại
     * — chưa có gì để mua, nút chính phải là "lướt tiếp" để họ mở khoá ưu đãi đã.
     * CSS lo phần đổi vị trí + kiểu (xem .bb-celebrate--sell).
     */
    el.classList.toggle('bb-celebrate--sell', count >= 2 && saved > 0);
    // Landing hang crawl (chu store 2026-10-15): CHI 2 nut — "xem moi loai 50% off" + "xem gio &
    // thanh toan". Bo "PICK MY 50% OFF HOODIE" / "or" / "SWITCH CATEGORY" (CSS an theo class).
    const lp2 = this.landing && this.#lpV2() && this.ladderMode;
    el.classList.toggle('bb-celebrate--lp2', lp2);
    if (lp2) {
      put('[data-bb-celebrate-golabel]', `SEE ALL CATEGORIES · ${pct > 0 ? `${pct}% OFF` : 'KEEP SHOPPING'}`);
      put('[data-bb-celebrate-skip]', count > 1 ? `VIEW CART & CHECKOUT (${count})` : 'VIEW CART & CHECKOUT');
    }
    this.#celebrateSet(el);

    // Dòng thúc mua: chỉ nói THẬT — tiền đã tiết kiệm và hạn của deal.
    const urg = el.querySelector('[data-bb-celebrate-urgency]');
    if (urg) {
      const end = (this.dataset.dealEnd || '').trim();
      const tpl =
        this.dataset.celebrateUrgency ||
        (end ? '🔒 {saved} saved — applied at checkout · deal ends {date}' : '🔒 {saved} saved — applied automatically at checkout');
      urg.textContent = fill(tpl).replace(/\{date\}/g, end);
      urg.classList.toggle('bb-hide', saved <= 0);
    }

    // "Lướt tiếp loại này" cũng KHÔNG mất ưu đãi. Chỉ ghi khi nút đó đang là nút PHỤ
    // (từ món thứ 2): ở món 1 nó là nút chính và nhãn đã có sẵn "% OFF" rồi.
    const goNote = el.querySelector('[data-bb-celebrate-gonote]');
    if (goNote) {
      const note = this.ladderMode && count >= 2 && pct > 0 && !lp2 ? `still ${pct}% off` : '';
      goNote.textContent = note;
      goNote.classList.toggle('bb-hide', !note);
    }
    // Đổi loại KHÔNG mất ưu đãi — nói thẳng ngay trên nút, đừng bắt khách suy ra.
    const swNote = el.querySelector('[data-bb-celebrate-switchnote]');
    if (swNote) {
      // bxgy: đổi loại không đụng gì tới quà (quà = món rẻ nhất của cả bộ) -> nói thẳng.
      const note = this.ladderMode ? (pct > 0 ? `still ${pct}% off` : '') : 'cheapest is still free';
      swNote.textContent = note;
      swNote.classList.toggle('bb-hide', !note);
    }
    // Mỗi lần mở lại sheet thì lưới loại đóng — không thì lần sau nó bung sẵn, rối lại.
    el.querySelector('[data-bb-celebrate-catwrap]')?.classList.add('bb-hide');
    el.querySelector('[data-bb-celebrate-switch]')?.setAttribute('aria-expanded', 'false');
    const th = el.querySelector('[data-bb-celebrate-thumb]');
    const last = items[count - 1];
    if (th && last && last.image) th.src = last.image;
    const added = el.querySelector('.bb-celebrate__added');
    if (added) {
      if (!this.ladderMode) {
        added.textContent = `✓ Added — ${count} of ${this.stepCount}`;
      } else {
        const row = this.#ladderRows(this.#target()).find((r) => r.added === count - 1);
        added.textContent = row && row.pct > 0 ? `✓ Added — ${row.pct}% off applied` : '✓ Added at full price';
      }
    }
    // Nút loại sản phẩm: đánh dấu loại đang xem + kèm giá "from" nếu đã tải card.
    if (this.ladderMode) {
      el.querySelectorAll('[data-bb-celebrate-cat]').forEach((btn) => {
        const i = parseInt(btn.dataset.bbCelebrateCat, 10);
        const name = (this.querySelectorAll('[data-bb-cat]')[i]?.textContent || '').trim();
        const p = this.#stepFromPay(i);
        btn.setAttribute('aria-current', i === curIdx ? 'true' : 'false');
        btn.innerHTML =
          `<span>${this.#esc(name)}</span>` +
          (p > 0 ? `<span class="bb-celebrate__catfrom">from ${money(p)}</span>` : '');
      });
    } else {
      this.#celebrateSubcats(el, curIdx);
    }
    // "Còn ~N món nữa là free ship" — cụ thể hơn nhiều so với chỉ nói còn thiếu bao nhiêu tiền.
    const hint = el.querySelector('[data-bb-celebrate-fshint]');
    if (hint) {
      const cheapest = this.#cheapestNextPay();
      const remain = this.fsThreshold - pay;
      const show = this.fsEnabled && this.fsThreshold > 0 && remain > 0 && cheapest > 0;
      if (show) {
        const n = Math.ceil(remain / cheapest);
        // Đứng CHUNG hàng với "🚚 $X away from FREE shipping" nên bỏ đuôi "and shipping
        // is on us" — nói lại đúng thứ dòng bên cạnh vừa nói.
        hint.textContent = `≈ ${n} more item${n > 1 ? 's' : ''}`;
      }
      hint.classList.toggle('bb-hide', !show);
    }
    this.#renderFsBar();
    /*
     * BUY X GET Y bỏ hai thứ khỏi sheet (thang mở vẫn giữ):
     *   - nút "xem bộ": bộ chỉ thành hình khi đủ món, bấm giữa chừng chỉ thấy bộ dở dang
     *   - dải free-ship + vạch tiến độ: sheet này đang đếm SỐ MÓN còn thiếu, thêm một
     *     thanh tiến độ đếm theo TIỀN là hai đồng hồ chạy song song, khách đọc nhầm nhau
     * #renderFsBar() chạy trước và bật dải free-ship ở MỌI [data-bb-fsbar], nên phải ẩn
     * sau nó — đừng đảo thứ tự hai đoạn này.
     */
    const stepMode = !this.ladderMode;
    el.querySelector('[data-bb-celebrate-skip]')?.classList.toggle('bb-hide', stepMode);
    const teamNote = el.querySelector('[data-bb-celebrate-teamnote]');
    if (teamNote) {
      const team = this.stepEls[curIdx]?.dataset.brand || this.selectedBrand || '';
      teamNote.textContent = team ? `now ${team}` : '';
      teamNote.classList.toggle('bb-hide', !team);
    }
    if (stepMode) {
      el.querySelector('[data-bb-fsbar]')?.classList.add('bb-hide');
      el.querySelector('[data-bb-celebrate-fshint]')?.classList.add('bb-hide');
    }
    el.classList.remove('bb-hide');
    el.setAttribute('aria-hidden', 'false');
    el.querySelector('[data-bb-celebrate-heading]')?.focus({ preventScroll: true });
    this.#track('LadderCelebrate', { pct, items: count });
  }
  /**
   * Lưới loại trong sheet cho offer THEO STEP: đọc sub-tab của step đang xem rồi dựng lại
   * mỗi lần mở, vì mỗi step có thể mở một danh sách loại khác nhau (Liquid không biết
   * trước nên để container rỗng).
   * KHÔNG hiện giá "from": giá đó là của cả step chứ không phải của từng loại — in ra là sai.
   * Step chỉ có một loại -> giấu luôn cả nút "đổi loại" lẫn lưới, không thì bấm vào rỗng.
   */
  #celebrateSubcats(el, stepIdx) {
    const box = el.querySelector('[data-bb-celebrate-cats]');
    const tabs = [...(this.stepEls[stepIdx]?.querySelectorAll('[data-bb-subtab]') || [])];
    const many = tabs.length > 1;
    // Nút phụ "xem bộ" đã bị bỏ ở offer theo step, nên "or" chỉ còn ngăn giữa nút chính
    // và nút đổi loại — không còn loại nào để đổi thì giấu cả hai.
    el.querySelector('[data-bb-celebrate-switch]')?.classList.toggle('bb-hide', !many);
    el.querySelector('.bb-celebrate__or')?.classList.toggle('bb-hide', !many);
    if (!box) return;
    box.innerHTML = many
      ? tabs
          .map(
            (t, i) =>
              `<button type="button" class="bb-celebrate__cat" data-bb-celebrate-subcat="${i}" aria-current="${
                t.getAttribute('aria-selected') === 'true' ? 'true' : 'false'
              }"><span>${this.#esc(t.textContent.trim())}</span></button>`
          )
          .join('')
      : '';
  }
  /** Sheet xem lại bộ đồ trước khi sang checkout (chưa add gì vào giỏ). */
  #openReview() {
    const el = this.querySelector('[data-bb-review]');
    const items = this.#target().filter(Boolean);
    if (!el || !items.length) return;
    this.#track('ReviewOpened', { items: items.length });
    this.#klaviyoBundle('review');
    const rows = this.#ladderRows(this.#target());
    const { total, pay } = this.#calc(this.#target(), 0);
    const saved = Math.max(0, total - pay);
    const head = el.querySelector('[data-bb-review-heading]');
    if (head) head.textContent = `Your bundle · ${items.length} item${items.length > 1 ? 's' : ''}`;
    const wrap = el.querySelector('[data-bb-review-items]');
    if (wrap) {
      wrap.innerHTML = rows
        .map((r) => {
          const price = r.pct > 0
            ? `<s>${money(r.sel.priceCents)}</s>${money(r.pay)}`
            : money(r.sel.priceCents);
          return (
            '<div class="bb-review__row">' +
            `<span class="bb-review__thumb"${r.sel.image ? ` style="background-image:url('${this.#esc(r.sel.image)}')"` : ''}></span>` +
            `<span class="bb-review__name">${this.#esc(r.sel.title)}${r.sel.variantLabel ? ` · ${this.#esc(r.sel.variantLabel)}` : ''}</span>` +
            `<span class="bb-review__price">${price}</span>` +
            // Gỡ ngay tại sheet: bắt khách đóng sheet, tìm lại chip ở thanh đáy rồi mới
            // xoá được là thừa một bước.
            `<button type="button" class="bb-review__x" data-bb-review-remove="${r.added}" aria-label="Remove ${this.#esc(r.sel.title)}">&times;</button>` +
            '</div>'
          );
        })
        .join('');
    }
    const t = el.querySelector('[data-bb-review-total]');
    const p = el.querySelector('[data-bb-review-pay]');
    const sv = el.querySelector('[data-bb-review-save]');
    if (t) t.textContent = saved > 0 ? money(total) : '';
    if (p) p.textContent = money(pay);
    if (sv) {
      sv.textContent = saved > 0 ? `🎉 You save ${money(saved)}` : '';
      sv.classList.toggle('bb-hide', saved <= 0);
    }
    this.#renderShipNote(pay);
    this.#renderFsBar();
    el.classList.remove('bb-hide');
    el.setAttribute('aria-hidden', 'false');
    head?.focus({ preventScroll: true });
    // Chốt bộ vào giỏ SAU khi đã vẽ xong sheet: #promoteLadderDraft() dọn sạch draft, vẽ
    // trước thì danh sách món trong sheet rỗng.
    this.#commitLadderForExpress();
  }
  #closeReview() {
    const el = this.querySelector('[data-bb-review]');
    if (!el || el.classList.contains('bb-hide')) return;
    el.classList.add('bb-hide');
    el.setAttribute('aria-hidden', 'true');
    // Đóng sheet mà không mua -> trả bộ về trạng thái đang dựng và gỡ khỏi giỏ.
    // (#commitLadder xoá cờ TRƯỚC khi gọi hàm này nên đi checkout thì không bị kéo lại.)
    this.#uncommitLadder();
  }
  #closeCelebrate() {
    const el = this.querySelector('[data-bb-celebrate]');
    if (!el || el.classList.contains('bb-hide')) return;
    el.classList.add('bb-hide');
    el.setAttribute('aria-hidden', 'true');
  }

  /** Toast nhỏ tự tắt — xác nhận "đã thêm" cho món 2+ (thang mở không có fly-to-slot). */
  #toast(msg) {
    const t = this.querySelector('[data-bb-toast]');
    if (!t) return;
    t.textContent = msg;
    t.classList.remove('bb-hide');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => t.classList.add('bb-hide'), 1600);
  }
  /** Chip theo THỨ TỰ THÊM mới nhất nháy một nhịp (chips xếp theo hạng, không theo lúc thêm). */
  #pulseNewestChip() {
    const idx = this.#target().length - 1;
    const chip = this.querySelector(`[data-bb-ladder-remove="${idx}"]`);
    if (!chip) return;
    chip.classList.remove('bb-ladderbar__item--pulse');
    void chip.offsetWidth;
    chip.classList.add('bb-ladderbar__item--pulse');
  }

  /** Free-ship progress: cập nhật MỌI [data-bb-fsbar] (thanh đáy + sheet chúc mừng). */
  /**
   * Câu nhử free-ship. Có bảng giá ship thì nói luôn SỐ TIỀN SHIP tiết kiệm được, chứ
   * không chỉ khoảng cách còn thiếu.
   *
   * Vì sao đáng: bảng giá của store có chỗ mua thêm mà TRẢ ÍT HƠN. Ở mức $109.90 khách
   * trả $11.95 ship -> tổng $121.85; thêm $10.10 nữa là chạm $120, ship free -> tổng
   * $120. Không in con số ra thì không ai tự tính, và đây là lý do thật chứ không phải
   * lời nhử suông.
   */
  /** #fsText bỏ emoji đầu câu — khối upsell giữ đúng bảng màu đen/trắng/màu đội (chủ store chốt). */
  #fsPlain(payCents) {
    return this.#fsText(payCents).replace(/^[^\w$]+\s*/u, '');
  }
  #fsText(payCents) {
    if (payCents >= this.fsThreshold) return '🎉 FREE shipping unlocked!';
    const gap = this.fsThreshold - payCents;
    const ship = this.#shipCents(payCents); // null = chưa khai bảng -> quay về câu cũ
    // CHỈ nói "thêm X, tiết kiệm Y" khi phần còn thiếu RẺ HƠN tiền ship đang phải trả —
    // lúc đó mua thêm mà tổng tiền LẠI ÍT HƠN, câu đó tự nó thuyết phục.
    // Ngược lại (thiếu $40, ship $15.95) nó đọc thành "tiêu $40 để tiết kiệm $16" — làm
    // yếu đi chính lời nhử. Xa thì cứ nói khoảng cách như cũ.
    if (ship > 0 && gap <= ship) return `🚚 Add ${money(gap)} → save ${money(ship)} in shipping`;
    return `🚚 ${money(gap)} away from FREE shipping`;
  }

  #renderFsBar() {
    const bars = this.querySelectorAll('[data-bb-fsbar]');
    if (!bars.length) return;
    const target = this.#target().filter(Boolean);
    const show = this.fsEnabled && this.fsThreshold > 0 && target.length > 0;
    const pay = show ? this.#calc(this.#target(), 0).pay : 0;
    bars.forEach((bar) => {
      bar.classList.toggle('bb-hide', !show);
      if (!show) return;
      const fill = bar.querySelector('[data-bb-fsbar-fill]');
      const text = bar.querySelector('[data-bb-fsbar-text]');
      const pct = Math.min(100, Math.round((pay / this.fsThreshold) * 100));
      if (fill) fill.style.width = `${pct}%`;
      if (text) {
        text.textContent = this.#fsText(pay);
      }
    });
  }

  /**
   * Tiền THẬT phải trả thêm nếu thêm 1 món giá `cents` vào draft hiện tại.
   * Tính bằng chênh lệch pay(draft + món) − pay(draft) qua #ladderRows -> đúng cho cả
   * ladder_order 'added' lẫn 'price' (giảm rơi vào món rẻ nhất), khớp làm tròn Function.
   */
  #ladderNextPay(cents) {
    return this.#ladderAddPay(cents, 1);
  }
  /** Tien phai tra them khi them `qty` mon gia `cents` vao bo dang co (cung cong thuc voi gio). */
  #ladderAddPay(cents, qty) {
    const cur = this.#target().filter(Boolean);
    const pay = (arr) => this.#ladderRows(arr).reduce((sum, r) => sum + r.pay, 0);
    const more = Array.from({ length: qty }, () => ({ priceCents: cents }));
    return Math.max(0, pay([...cur, ...more]) - pay(cur));
  }
  /** Giá cơ sở hiển thị của card: variant đã chọn, chưa chọn thì giá min. */
  #ladderCardCents(card) {
    const v = this.#resolveVariant(card);
    if (v) return v[3];
    return parseInt(card.dataset.bbMinPrice, 10) || 0;
  }
  /**
   * Bật/tắt preview giảm giá trên 1 card (badge -N% + giá gạch + giá sau giảm).
   * Cờ riêng `bbLadderPrev` — KHÔNG dùng bbFree (cờ đó tĩnh theo step cho bxgy/bxpct,
   * còn preview này phải gỡ được khi khách xoá hết món).
   */
  #paintLadderCard(card) {
    if (!this.ladderMode || card.dataset.bbFree === '1') return;
    const priceEl = card.querySelector('[data-bb-card-price]');
    if (!priceEl) return;
    const cents = this.#ladderCardCents(card);
    const pfx = card.dataset.bbPriceVaries === '1' && !this.#resolveVariant(card) ? 'From ' : '';
    const nextPay = this.#target().some(Boolean) ? this.#ladderNextPay(cents) : cents;
    const off = cents > 0 ? Math.round(((cents - nextPay) / cents) * 100) : 0;
    if (off <= 0) {
      // Không còn giảm (draft rỗng / hết bậc thang) -> trả card về giá thường.
      if (card.dataset.bbLadderPrev !== '1') return;
      delete card.dataset.bbLadderPrev;
      card.querySelector('[data-bb-ladder-badge]')?.remove();
      priceEl.classList.remove('bb-card__price--free');
      priceEl.textContent = pfx + money(cents);
      return;
    }
    card.dataset.bbLadderPrev = '1';
    priceEl.classList.add('bb-card__price--free');
    priceEl.innerHTML =
      `<s class="bb-card__price-orig" data-bb-card-orig>${pfx}${money(cents)}</s>` +
      `<span class="bb-card__price-free">${money(nextPay)}</span>`;
    let badge = card.querySelector('[data-bb-ladder-badge]');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'bb-card__free';
      badge.dataset.bbLadderBadge = '';
      card.querySelector('.bb-card__media')?.append(badge);
    }
    badge.textContent = `-${off}%`;
  }
  /**
   * Preview giảm giá của MÓN LẺ trên 1 card: badge -20%, giá gốc gạch, giá phải trả.
   *
   * Dùng lại đúng bộ class của preview thang mở (`.bb-card__price--free`,
   * `.bb-card__price-orig`, `.bb-card__price-free`, badge `.bb-card__free`) — cùng một
   * chuyện "món này đang được giảm" thì phải nhìn giống nhau ở mọi chế độ.
   *
   * Cờ riêng `bbSinglePrev` để gỡ sạch khi thoát chế độ món lẻ. KHÔNG dùng `bbFree`:
   * cờ đó là của step ưu đãi bxgy/bxpct, đụng vào là hỏng badge FREE.
   */
  #paintSingleCard(card) {
    const priceEl = card.querySelector('[data-bb-card-price]');
    if (!priceEl) return;
    const cents = this.#ladderCardCents(card);
    const pay = this.#singlePay(cents);
    const on = this.singlePick && this.singlePct > 0 && cents > 0 && pay < cents;
    if (!on) {
      if (card.dataset.bbSinglePrev !== '1') return;
      delete card.dataset.bbSinglePrev;
      card.querySelector('[data-bb-single-badge]')?.remove();
      priceEl.classList.remove('bb-card__price--free');
      priceEl.textContent = (card.dataset.bbPriceVaries === '1' && !this.#resolveVariant(card) ? 'From ' : '') + money(cents);
      return;
    }
    const pfx = card.dataset.bbPriceVaries === '1' && !this.#resolveVariant(card) ? 'From ' : '';
    card.dataset.bbSinglePrev = '1';
    priceEl.classList.add('bb-card__price--free');
    priceEl.innerHTML =
      `<s class="bb-card__price-orig" data-bb-card-orig>${pfx}${money(cents)}</s>` +
      `<span class="bb-card__price-free">${money(pay)}</span>`;
    let badge = card.querySelector('[data-bb-single-badge]');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'bb-card__free';
      badge.dataset.bbSingleBadge = '';
      card.querySelector('.bb-card__media')?.append(badge);
    }
    badge.textContent = `${this.singlePct}% OFF`;
  }
  /** Vẽ (hoặc gỡ) preview món lẻ trên MỌI card. Gọi lúc vào và lúc thoát chế độ. */
  #refreshSinglePreviews() {
    if (this.ladderMode) return;
    this.querySelectorAll('[data-bb-card]').forEach((c) => this.#paintSingleCard(c));
  }

  /** Vẽ lại preview trên MỌI card (draft đổi là % món kế tiếp đổi). */
  #refreshLadderPreviews() {
    if (!this.ladderMode) return;
    this.querySelectorAll('[data-bb-card]').forEach((c) => this.#paintLadderCard(c));
  }

  #priceUpdate() {
    const target = this.#target();
    const { total, pay, savings } = this.#calc(target, this.#targetIndex());
    const orig = this.querySelector('[data-bb-bar-original]');
    const bundleEl = this.querySelector('[data-bb-bar-bundle]');
    const saveEl = this.querySelector('[data-bb-bar-savings]');
    /*
      Bộ CHƯA đủ món: chỉ hiện TỔNG GIÁ GỐC của những món đã chọn, không hiện giá gạch
      cũng không hiện "You save".

      Lý do: món quà (step bật cờ) được tính giảm 100% ngay từ lúc chọn, nên thêm món đầu
      tiên là thanh đáy ghi `$39.95` gạch, `$0.00`, "You save $39.95 (100%)" trong khi vẫn
      còn "2 more items to go". Con số đúng về mặt tính toán nhưng đọc như đã xong việc —
      chủ store bắt đúng chỗ này 2026-08-27. Ưu đãi chỉ có thật khi bộ đủ món (function
      cũng bỏ qua bộ thiếu món), nên trước đó chỉ nói con số chắc chắn đúng.
    */
    const settled = this.ladderMode || this.#isFull(target);
    const picked = (target || []).filter(Boolean);
    const running = picked.reduce((a, x) => a + (x.priceCents || 0), 0);
    if (orig) {
      this.#animateMoney(orig, total);
      // Gạch ngang một con số y hệt con số phải trả là nói dối có khuyến mãi.
      orig.classList.toggle('bb-hide', !settled || total <= pay);
    }
    if (bundleEl) this.#animateMoney(bundleEl, settled ? pay : running);
    if (saveEl) {
      const show = settled && savings > 0;
      saveEl.textContent = show ? this.#savingsText(savings, total) : '';
      saveEl.classList.toggle('bb-hide', !show);
    }
  }

  /** Tween số tiền hiển thị từ giá trị cũ -> mới (count-up). */
  #animateMoney(el, toCents) {
    const fromCents = parseInt(el.dataset.cents, 10);
    el.dataset.cents = String(toCents);
    // Tab ẩn/không được vẽ thì requestAnimationFrame không chạy -> count-up kẹt giữa
    // chừng và hiện SỐ TIỀN SAI. Chốt thẳng giá trị cuối trong trường hợp đó.
    if (this.reduceMotion || document.hidden || !Number.isInteger(fromCents) || fromCents === toCents) {
      el.textContent = money(toCents);
      return;
    }
    if (el._raf) cancelAnimationFrame(el._raf);
    const dur = 380;
    let start = null;
    const tick = (ts) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = money(Math.round(fromCents + (toCents - fromCents) * eased));
      if (p < 1) el._raf = requestAnimationFrame(tick);
    };
    el._raf = requestAnimationFrame(tick);
  }

  /* ---------- result screen ---------- */

  #showResult(focus) {
    this.stepEls.forEach((el) => el.classList.add('bb-hide'));
    const result = this.querySelector('[data-bb-result]');
    if (!result) return; // thang mở: không có màn kết quả, thanh đáy lo phần này
    result.classList.remove('bb-hide');
    this.classList.add('bb-on-result');
    // Chi dem lan VAO man ket qua, khong dem moi lan ve lai (doi size, go mon...).
    if (!this._resultTracked && this._userActed) {
      this._resultTracked = true;
      this.#track('ResultShown', { bundles: this.state.bundles.length });
      this.#klaviyoBundle('result');
    }
    this.#syncSave();
    this.#showExpress(false); // giỏ chưa chắc khớp -> giấu nút ví tới khi #syncCartForExpress xong

    const wrap = this.querySelector('[data-bb-result-bundles]');
    wrap.textContent = '';
    let grandTotal = 0;
    let grandPay = 0;
    let grandExtra = 0;
    this.state.bundles.forEach((bundle, b) => {
      const { total, pay, savings, extraPct, extraSavings } = this.#calc(bundle, b);
      grandTotal += total;
      grandPay += pay;
      grandExtra += extraSavings;
      wrap.append(this.#renderBundleCard(bundle, b, total, pay, savings, extraPct, extraSavings));
    });

    // Món lẻ: nguyên giá, cộng thẳng vào cả hai vế nên không sinh ra "tiết kiệm" ảo.
    const singles = this.state.singles || [];
    const singlesWrap = this.querySelector('[data-bb-result-singles]');
    if (singlesWrap) {
      singlesWrap.textContent = '';
      singles.forEach((sel, i) => {
        const row = document.createElement('div');
        row.className = 'bb-single-row';
        const paid = this.#singlePay(sel.priceCents);
        const off = sel.priceCents > paid;
        row.innerHTML =
          `<span class="bb-single-row__thumb" style="background-image:url('${sel.image}')"></span>` +
          `<span class="bb-single-row__meta"><strong>${this.#esc(sel.title)}</strong>` +
          `<span>${sel.variantLabel ? this.#esc(sel.variantLabel) + ' — ' : ''}` +
          (off ? `<s class="bb-single-row__was">${money(sel.priceCents)}</s>` : '') +
          `${money(paid)}` +
          (off ? `<span class="bb-single-row__off">−${this.singlePct}%</span>` : '') +
          `</span></span>` +
          `<button type="button" class="bb-single-row__rm" data-bb-remove-single="${i}" aria-label="Remove">&times;</button>`;
        const sizes = this.#sizeRow(sel, 's', 0, i);
        if (sizes) row.querySelector('.bb-single-row__meta').append(sizes);
        singlesWrap.append(row);
      });
      singlesWrap.classList.toggle('bb-hide', singles.length === 0);
    }
    // Giá gốc vào grandTotal, giá đã giảm vào grandPay -> phần chênh tự hiện ở "You save".
    grandTotal += singles.reduce((n, x) => n + x.priceCents, 0);
    grandPay += singles.reduce((n, x) => n + this.#singlePay(x.priceCents), 0);

    // Tổng hợp + nút.
    const sumSave = Math.max(0, grandTotal - grandPay);
    const totalEl = this.querySelector('[data-bb-grand-total]');
    const payEl = this.querySelector('[data-bb-grand-pay]');
    const saveEl = this.querySelector('[data-bb-grand-savings]');
    if (totalEl) {
      totalEl.textContent = money(grandTotal);
      // Gạch một con số y hệt số phải trả là nói dối có khuyến mãi (vd gỡ bộ, món lẻ về giá gốc).
      totalEl.classList.toggle('bb-hide', grandTotal <= grandPay);
    }
    if (payEl) payEl.textContent = money(grandPay);
    if (saveEl) {
      saveEl.textContent = sumSave > 0 ? this.#savingsText(sumSave, grandTotal) : '';
      saveEl.classList.toggle('bb-hide', sumSave <= 0);
    }

    // Nút thêm bundle: ghi rõ bundle kế tiếp + ưu đãi thêm ngay trên nút.
    const draftStarted = this.state.draft.some(Boolean);
    const nextIdx = this.state.bundles.length; // bundle kế tiếp sẽ chiếm index này
    const nextPct = this.#tierPct(nextIdx);
    // Free shipping unlock: dựa trên TỔNG phải trả (grandPay) >= ngưỡng.
    const fsActive = this.fsEnabled && this.fsThreshold > 0;
    const fsUnlocked = fsActive && grandPay >= this.fsThreshold;
    const fsUnlocking = fsActive && !fsUnlocked && this.state.bundles.length > 0; // thêm 1 bundle nữa -> mở free-ship
    const addAnother = this.querySelector('[data-bb-add-another]');
    if (addAnother) {
      const lbl = addAnother.querySelector('[data-bb-add-another-label]') || addAnother;
      // Nhãn 2 dòng: hành động (main) + lợi ích (sub) -> gọn, không nhồi nhét.
      /*
       * Nhãn 3 dòng: HÀNH ĐỘNG / ƯU ĐÃI CỦA OFFER / thưởng free-ship (xanh).
       * Mỗi dòng đúng một việc — nhồi "buy 2 get 1 free + unlock free shipping" vào cùng
       * một dòng thì không đọc nổi, mà bỏ dòng offer đi thì khách không có gì để so với
       * nút món lẻ bên cạnh.
       */
      // Dòng ưu đãi tô XANH giống nút món lẻ — hai nút đứng cạnh nhau, một bên xanh một
      // bên trắng thì ưu đãi yếu hơn (giảm 20%) lại nổi hơn ưu đãi mạnh hơn (tặng 1 món).
      const setLbl = (main, offer, fs) => {
        lbl.innerHTML =
          `<span class="bb-addlabel__main">${this.#esc(main)}</span>` +
          (offer ? `<span class="bb-addlabel__fs">${this.#esc(offer)}</span>` : '') +
          (fs ? `<span class="bb-addlabel__fs">${this.#esc(fs)}</span>` : '');
      };
      const offerLine = addAnother.dataset.addAnotherOffer || this.dataset.addAnotherOffer || '';
      if (draftStarted) {
        setLbl(addAnother.dataset.continueLabel || 'CONTINUE YOUR BUNDLE', '', '');
      } else {
        const main = `Add bundle ${nextIdx + 1}`;
        const offer = nextPct > 0 ? `${offerLine ? offerLine + ' · ' : ''}extra ${nextPct}% off` : offerLine;
        // 🚚 giống thanh tiến độ free-ship và ô loại trong sheet — cùng một chuyện thì
        // dùng cùng một biểu tượng, khách nhận ra ngay không cần đọc chữ.
        // Kèm số tiền ship tiết kiệm được — "Unlock FREE shipping" không cho biết là bao nhiêu.
        const fsShip = this.#shipCents(grandPay);
        const fsLbl = fsShip > 0 ? `🚚 Unlock FREE shipping — save ${money(fsShip)}` : '🚚 Unlock FREE shipping';
        setLbl(main, offer, fsUnlocking ? fsLbl : '');
      }
    }

    // Nút "thêm một món lẻ" — lối ra cho khách không muốn thêm nguyên một bộ nữa.
    const single = this.querySelector('[data-bb-add-single]');
    if (single) {
      const lbl = single.querySelector('[data-bb-add-single-label]') || single;
      const gap = fsActive ? this.fsThreshold - grandPay : 0;
      // Còn thiếu ít tiền là free ship -> nói CON SỐ, đó là lý do thật để thêm một món.
      // Chỉ nói khi khoảng thiếu nằm trong tầm một món (dựa trên giá "from" rẻ nhất đã
      // biết); xa quá mà vẫn nhử thì một món không cứu nổi, hoá ra hứa suông.
      // reach = món RẺ NHẤT sau khi đã giảm -> "còn thiếu bấy nhiêu" mới đúng là trong tầm.
      const reach = this.#singlePay(this.#cheapestNextPay());
      const fsClose = fsActive && !fsUnlocked && gap > 0 && reach > 0 && gap <= reach * 1.35;
      const pctTxt = (t) => String(t || '').replace(/\{pct\}/g, String(this.singlePct));
      const offer = this.singlePct > 0
        ? pctTxt(this.dataset.addSingleSub || 'SAVE {pct}% — no bundle needed')
        : 'No bundle needed — regular price';
      // Token {ship} = tiền ship đang phải trả ở mức hiện tại. Không biết giá ship thì rơi
      // về câu chỉ có khoảng thiếu.
      const shipNow = this.#shipCents(grandPay);
      // Cùng quy tắc với #fsText: chỉ khoe con số tiết kiệm khi thêm hàng làm tổng tiền
      // GIẢM xuống, không thì câu đó phản tác dụng.
      const shipWin = shipNow > 0 && gap <= shipNow;
      // Ô cài đặt mặc định có {ship}; lúc không khoe được thì phải BỎ cả mẫu đó, không thì
      // ra "…more → save  shipping" (chỗ trống). Rơi về câu chỉ có khoảng cách.
      let fsTpl = this.dataset.addSingleFs || '';
      if (!shipWin && fsTpl.indexOf('{ship}') !== -1) fsTpl = '';
      if (!fsTpl) fsTpl = shipWin ? '{amount} more → save {ship} shipping' : '{amount} to FREE shipping';
      const fsLine = fsClose
        ? pctTxt(fsTpl).replace(/\{amount\}/g, money(gap)).replace(/\{ship\}/g, shipWin ? money(shipNow) : '')
        : '';
      // Dòng ưu đãi tô XANH khi CÓ giảm thật (chủ store chốt 2026-08-21). Không giảm thì
      // để chữ thường — tô xanh một câu "nguyên giá" là bịa ra khuyến mãi không có.
      lbl.innerHTML =
        `<span class="bb-addlabel__main">${this.#esc(this.dataset.addSingleLabel || 'ADD 1 MORE ITEM')}</span>` +
        `<span class="${this.singlePct > 0 ? 'bb-addlabel__fs' : 'bb-addlabel__sub'}">${this.#esc(offer)}</span>` +
        (fsLine ? `<span class="bb-addlabel__fs">${this.#esc(fsLine)}</span>` : '');
      // Đang dựng dở một bộ thì mời thêm món lẻ là cắt ngang việc đang làm.
      single.classList.toggle('bb-hide', draftStarted);
    }

    this.#renderUpsell(grandPay, draftStarted);

    // "Buy more, save more" + BẢNG BẬC chi tiết (Bundle 2: −10%, Bundle 3: −15%, …) cho khách rõ.
    const bxmsTag = this.querySelector('[data-bb-bxms-hint]');
    if (bxmsTag) {
      const tiers = this.extraTiers || [];
      const show = tiers.length > 0 && !draftStarted;
      if (show) {
        const tierHtml = tiers
          .map((pct, i) => {
            const n = i + 2; // bundle thứ mấy (tier[0] = bundle 2)
            const cap = i === tiers.length - 1 ? '+' : '';
            return `<span class="bb-bxms-note__tier">Bundle ${n}${cap}: <b>−${pct}%</b></span>`;
          })
          .join('');
        bxmsTag.innerHTML =
          `<span class="bb-bxms-note__h">${this.#esc(this.bxmsLabel)}</span>` +
          `<span class="bb-bxms-note__tiers">${tierHtml}</span>`;
      } else {
        bxmsTag.innerHTML = '';
      }
      bxmsTag.classList.toggle('bb-hide', !show);
    }

    /*
      Tiến độ free-ship ở ĐẦU màn kết quả, thay cho dòng nhử "thêm bộ 2 để được free ship"
      nằm cuối. Vạch tiến độ nói được cả hai chuyện (còn bao xa / đã mở khoá) mà không ép
      khách vào đúng một cách duy nhất là mua thêm nguyên một bộ — giờ đã có món lẻ.
      Tính theo grandPay (mọi bộ + món lẻ), KHÁC #renderFsBar() vốn tính theo bộ đang dựng.
    */
    const rfs = this.querySelector('[data-bb-resultfs]');
    if (rfs) {
      const show = fsActive && (this.state.bundles.length > 0 || singles.length > 0);
      rfs.classList.toggle('bb-hide', !show);
      if (show) {
        const pct = Math.min(100, Math.round((grandPay / this.fsThreshold) * 100));
        rfs.querySelector('[data-bb-resultfs-fill]').style.width = `${pct}%`;
        rfs.querySelector('[data-bb-resultfs-text]').textContent = this.#fsText(grandPay);
        rfs.classList.toggle('bb-fsbar--done', fsUnlocked);
      }
    }

    const checkoutBtn = this.querySelector('[data-bb-checkout]');
    if (checkoutBtn) checkoutBtn.disabled = this.state.bundles.length === 0 && singles.length === 0;

    // Chi tiết đơn thu gọn ở đầu màn: "· 3 items" + tối đa 4 ảnh nhỏ.
    const od = this.querySelector('[data-bb-orderdetail]');
    if (od) {
      const all = [...this.state.bundles.flat().filter(Boolean), ...singles];
      const cEl = od.querySelector('[data-bb-orderdetail-count]');
      const tEl = od.querySelector('[data-bb-orderdetail-thumbs]');
      if (cEl) cEl.textContent = `· ${all.length} ${all.length === 1 ? 'item' : 'items'}`;
      if (tEl) {
        tEl.innerHTML =
          all.slice(0, 4).map((x) => `<span class="bb-orderdetail__thumb" style="background-image:url('${x.image}')"></span>`).join('') +
          (all.length > 4 ? `<span class="bb-orderdetail__more">+${all.length - 4}</span>` : '');
      }
    }

    /*
      Thanh CHECKOUT dính đáy — CSS chỉ cho nó hiện ở <=749px (xem .bb-resultbar).
      Trên mobile nút CHECKOUT thật nằm 1,7 màn hình bên dưới vì 3 ảnh sản phẩm xếp dọc,
      mà `.bb-bar` thì đã ẩn ở màn này -> không có nút trả tiền nào trong tầm mắt.
      Hiện/ẩn theo `.bb-on-result` là việc của CSS, ở đây chỉ đổ nội dung.
    */
    const rbar = this.querySelector('[data-bb-resultbar]');
    if (rbar) {
      const nItems = this.state.bundles.reduce((k, bn) => k + (bn || []).filter(Boolean).length, 0) + singles.length;
      rbar.classList.toggle('bb-hide', nItems === 0);
      const cEl = rbar.querySelector('[data-bb-resultbar-count]');
      const pEl = rbar.querySelector('[data-bb-resultbar-pay]');
      if (cEl) cEl.textContent = nItems === 1 ? '1 item' : `${nItems} items`;
      if (pEl) pEl.textContent = money(grandPay);
      this.#syncViewportDrop(); // thanh vừa đổi chiều cao/hiện ra -> neo lại
    }

    // Món dựng lại từ giỏ chưa có danh sách size -> hỏi rồi vẽ lại (chạy đúng một lần).
    this.#ensureVariants();
    this.#startResultTimer();

    this.#renderShipNote(grandPay);
    this.#barUpdate();
    // Màn kết quả là bước CHỐT: giấu mọi khối nội dung marketing của trang, chỉ còn nó.
    this._contentHidden = true;
    this.#toggleContentSections(true);
    // Màn này là bước xác nhận -> chốt giỏ luôn, nút ví mới có cái đúng để thanh toán.
    this.#syncCartForExpress();
    if (focus) {
      const heading = result.querySelector('.bb-result__heading');
      heading?.focus({ preventScroll: true });
      // `block: 'center'` đưa panel ra GIỮA màn -> khách nhìn thấy nửa trên là khối nội
      // dung phía trên, tưởng chưa xong. Đưa MÉP TRÊN panel lên đầu khung nhìn, và cuộn
      // TỨC THÌ (cuộn mượt tính quãng theo layout cũ, mà layout vừa đổi hẳn vì các khối
      // nội dung vừa bị giấu — cùng bẫy với #scrollToStep).
      const off = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-offset')) || 0;
      const go = () => {
        const top = result.getBoundingClientRect().top + window.pageYOffset - off - 8;
        window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'instant' });
      };
      go();
      requestAnimationFrame(go);
    }
  }

  /**
   * Giấu/hiện MỌI section nội dung của trang khi vào/ra màn kết quả — hero, khối HTML
   * marketing, review… — để bước chốt chiếm trọn màn hình.
   *
   * Phạm vi: chỉ các section BÊN TRONG `<main>` và không chứa builder. Header/footer nằm
   * ngoài `<main>` nên không bị đụng (khách vẫn thấy logo, giỏ hàng).
   */
  /**
   * Nội dung trang (hero + khối HTML) CHỈ hiện ở bước chọn đội.
   *
   * Chọn đội xong là khách đã vào việc — mọi thứ marketing phía trên chỉ làm lưới bị đẩy
   * xuống và khiến thanh cuộn dài vô nghĩa. Bấm CHANGE để mở lại bộ chọn đội thì nội dung
   * quay lại.
   *
   * Giấu làm trang NGẮN đi hàng chục nghìn pixel -> vị trí cuộn hiện tại thành vô nghĩa,
   * nên mỗi lần trạng thái ĐỔI thì kéo luôn về đầu step (cùng lý do với #scrollToStep).
   */
  /**
   * @param {boolean} userAction  true = khách vừa bấm gì đó. Chỉ khi đó mới cuộn.
   *   Lúc TẢI TRANG hàm này cũng chạy (khôi phục phiên, kế thừa đội sang step) — cuộn ở
   *   đó là tự dưng kéo khách xuống giữa trang ngay khi vừa mở link.
   */
  /**
   * Bo chon doi cua step dang xem co dang HIEN khong.
   * Do bang getClientRects thay vi doc this.selectedBrand: khach quay lai co san doi trong
   * localStorage nhung step van co the dang mo picker (bam CHANGE / SWITCH TEAM).
   */
  #pickerVisible() {
    const stepEl = this.stepEls[this.state.current];
    const picker = stepEl && stepEl.querySelector('[data-bb-brandpicker]');
    return !!picker && picker.getClientRects().length > 0;
  }

  #syncContentVisibility(userAction = false) {
    const onResult = this.classList.contains('bb-on-result');
    const stepEl = this.stepEls[this.state.current];
    const hide = !this.landing && (onResult || !this.#pickerVisible());
    if (this._contentHidden === hide) return;
    this._contentHidden = hide;
    this.#toggleContentSections(hide);
    if (userAction && !onResult && stepEl) this.#scrollToStep(stepEl);
  }
  #toggleContentSections(hide) {
    const main = this.closest('main') || document.querySelector('main');
    if (!main) return;
    main.querySelectorAll('.shopify-section').forEach((sec) => {
      if (sec.contains(this)) return;
      sec.classList.toggle('bb-content-off', hide);
    });
  }

  #renderBundleCard(bundle, b, total, pay, savings, extraPct = 0, extraSavings = 0) {
    const card = document.createElement('div');
    card.className = 'bb-result__bundle-card';

    const head = document.createElement('div');
    head.className = 'bb-result__bundle-head';
    const label = document.createElement('span');
    label.className = 'bb-result__bundle-label';
    label.textContent = `BUNDLE ${b + 1}`;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'bb-result__bundle-remove';
    remove.dataset.bbRemoveBundle = String(b);
    remove.setAttribute('aria-label', `Remove bundle ${b + 1}`);
    remove.textContent = '×';
    head.append(label, remove);

    const items = document.createElement('div');
    items.className = 'bb-result__items';
    bundle.forEach((sel, i) => {
      if (!sel) return;
      const item = document.createElement('div');
      item.className = 'bb-result-item';

      const img = document.createElement('img');
      img.className = 'bb-result-item__img';
      img.src = (sel.imageRaw ? this.#sizeUrl(sel.imageRaw, this.#resultImgWidth()) : '') || sel.image;
      img.alt = sel.title;
      img.loading = 'lazy';
      // Bấm ảnh -> phóng to (tái dùng lightbox của lưới).
      img.dataset.bbZoom = '';
      img.dataset.zoomraw = sel.imageRaw || '';
      img.dataset.image = sel.image || '';
      img.dataset.title = sel.title || '';
      img.dataset.handle = sel.handle || '';

      const zoomHint = document.createElement('span');
      zoomHint.className = 'bb-result-item__zoomhint';
      zoomHint.setAttribute('aria-hidden', 'true');
      zoomHint.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/><path d="M11 8v6M8 11h6"/></svg>';

      const off = this.#slotOff(i, bundle);
      if (off > 0) {
        const badge = document.createElement('span');
        badge.className = 'bb-result-item__free';
        badge.textContent = this.#offBadge(off);
        item.append(badge);
      }

      const meta = document.createElement('div');
      meta.className = 'bb-result-item__meta';
      const name = document.createElement('strong');
      name.textContent = sel.title;
      const priceTxt = off >= 100 ? 'FREE' : off > 0 ? money(this.#slotPay(sel.priceCents, off)) : money(sel.priceCents);
      const detail = sel.variantLabel ? `${sel.variantLabel} — ${priceTxt}` : priceTxt;
      meta.append(name, document.createTextNode(detail));

      const editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'bb-result-item__edit';
      editBtn.dataset.bbBundle = String(b);
      editBtn.dataset.bbEdit = String(i);
      editBtn.setAttribute('aria-label', `Edit item ${i + 1} of bundle ${b + 1}`);
      editBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>';

      const sizes = this.#sizeRow(sel, 'b', b, i);
      if (sizes) meta.append(sizes);

      item.append(img, zoomHint, editBtn, meta);
      items.append(item);
    });

    const prices = document.createElement('div');
    prices.className = 'bb-result__bundle-prices';
    prices.innerHTML =
      `<s class="bb-result__original">${money(total)}</s>` +
      `<span class="bb-result__bundle">${money(pay)}</span>` +
      (savings > 0 ? `<span class="bb-result__save">${this.#savingsText(savings, total)}</span>` : '');

    card.append(head, items, prices);

    // "Buy more, save more" — ưu đãi thêm cho bundle 2+ (ghi rõ lý do).
    if (extraPct > 0 && extraSavings > 0) {
      const note = document.createElement('div');
      note.className = 'bb-result__bxms';
      const badge = document.createElement('span');
      badge.className = 'bb-result__bxms-badge';
      badge.textContent = `−${extraPct}%`;
      const txt = document.createElement('span');
      txt.textContent = `${this.bxmsLabel} · extra ${money(extraSavings)} off`;
      note.append(badge, txt);
      card.append(note);
    }
    return card;
  }

  /* ---------- peek (xem nhanh bundle đang dựng) ---------- */

  #openPeek() {
    const peek = this.querySelector('[data-bb-peek]');
    if (!peek) return;
    this.#renderPeek();
    peek.classList.remove('bb-hide');
    peek.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => peek.classList.add('bb-peek--open'));
  }

  #closePeek() {
    const peek = this.querySelector('[data-bb-peek]');
    if (!peek || peek.classList.contains('bb-hide')) return;
    peek.classList.remove('bb-peek--open');
    peek.setAttribute('aria-hidden', 'true');
    const hide = () => peek.classList.add('bb-hide');
    if (this.reduceMotion) hide();
    else setTimeout(hide, 260);
  }

  #renderPeek() {
    const target = this.#target();
    const wrap = this.querySelector('[data-bb-peek-items]');
    if (wrap) {
      wrap.textContent = '';
      this.stepEls.forEach((stepEl, i) => {
        const sel = target[i];
        const label = stepEl.querySelector('.bb-step__title')?.lastChild?.textContent?.trim() || `Step ${i + 1}`;
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'bb-peek__item' + (sel ? '' : ' bb-peek__item--empty');
        row.dataset.bbEdit = String(i); // bấm = sửa slot này (draft)
        if (sel) {
          row.innerHTML =
            `<span class="bb-peek__thumb" style="background-image:url('${sel.image}')"></span>` +
            `<span class="bb-peek__meta"><strong>${this.#esc(sel.title)}</strong>` +
            `<span>${sel.variantLabel ? this.#esc(sel.variantLabel) + ' — ' : ''}${money(sel.priceCents)}</span></span>` +
            `<span class="bb-peek__edit" aria-hidden="true">Change</span>`;
        } else {
          row.innerHTML =
            `<span class="bb-peek__thumb bb-peek__thumb--empty" aria-hidden="true">${i + 1}</span>` +
            `<span class="bb-peek__meta"><strong>${this.#esc(label)}</strong><span>Tap to choose</span></span>` +
            `<span class="bb-peek__edit" aria-hidden="true">+ Add</span>`;
        }
        wrap.append(row);
      });
    }
    const { total, pay, savings } = this.#calc(target, this.#targetIndex());
    const set = (sel, txt) => { const el = this.querySelector(sel); if (el) el.textContent = txt; };
    set('[data-bb-peek-total]', money(total));
    // Cùng lý do với thanh đáy: chưa có chênh thì không gạch.
    this.querySelector('[data-bb-peek-total]')?.classList.toggle('bb-hide', total <= pay);
    set('[data-bb-peek-pay]', money(pay));
    const saveEl = this.querySelector('[data-bb-peek-savings]');
    if (saveEl) {
      saveEl.textContent = savings > 0 ? this.#savingsText(savings, total) : '';
      saveEl.classList.toggle('bb-hide', savings <= 0);
    }
  }

  #esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* ---------- data loading (browse + search) ---------- */

  async #fetchView(url) {
    // Lượt tải trước lúc chạm nút đội (#prefetchBrand): dùng lại nếu đúng URL và còn mới.
    const pre = this._brandPrefetch;
    if (pre && pre.url === url && Date.now() - pre.t < 20000) {
      this._brandPrefetch = null;
      try { return await pre.p; } catch { /* lỗi thì gọi lại bình thường bên dưới */ }
    }
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }
  /**
   * Chạm nút đội (pointerdown) -> gọi luôn `view=bb` của collection sẽ mở, và khi có
   * danh sách thì tải trước ảnh hàng đầu ĐÚNG URL mà thẻ sẽ dùng (#sizeUrl + #gridImgW),
   * để thẻ dựng xong là ảnh đã về hoặc đang về. Khoảng chạm -> click ~100-300ms.
   * Chỉ step đang chưa có đội; tối đa 3 ảnh trên điện thoại (ảnh 600, ~50KB) / 8 trên
   * desktop, để lỡ chạm khi đang vuốt qua lưới đội thì cũng không tốn nhiều.
   */
  #prefetchBrand(btn) {
    try {
      const stepEl = btn.closest('[data-bb-step]');
      if (!stepEl || this.#upsellActive()) return;
      const handle = this.#directHandle(stepEl, btn.dataset.brand);
      if (!handle) return;
      const url = `/collections/${encodeURIComponent(handle)}?view=bb&page=1`;
      const pre = this._brandPrefetch;
      if (pre && pre.url === url && Date.now() - pre.t < 20000) return;
      const p = fetch(url).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });
      p.catch(() => {});
      this._brandPrefetch = { url, t: Date.now(), p };
      const w = this.#gridImgW();
      const n = w > 300 ? 3 : 8;
      const theme = this.#activeTheme(stepEl);
      p.then((data) => {
        let rows = (data.results || []).filter((r) => r.image);
        // Chủ đề đang bật (vd Hunting) -> tải trước đúng những mẫu sẽ hiện.
        if (theme && theme !== 'all') {
          const hit = rows.filter((r) => (this.#themesOf(r) || []).includes(theme));
          if (hit.length) rows = hit;
        }
        rows.slice(0, n).forEach((r) => {
          const im = new Image();
          im.fetchPriority = 'high';
          im.src = this.#sizeUrl(r.image, w);
        });
      }).catch(() => {});
    } catch { /* tải trước là phụ, không được làm hỏng lượt chọn đội */ }
  }

  async #runSearch(stepEl, query) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const grid = stepEl.querySelector('[data-bb-grid]');
    const status = stepEl.querySelector('[data-bb-search-status]');
    const loadingEl = stepEl.querySelector('[data-bb-loading]');

    if (!query) {
      this.search[idx] = null;
      this.#clearSkeletons(grid);
      this.#clearEmptyState(grid);
      grid.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
      status.classList.add('bb-hide');
      delete status.dataset.loading;
      loadingEl.classList.add('bb-hide');
      return;
    }

    // Search store đã-index theo "brand + từ loại (scope)" cho NHANH, rồi LỌC về
    // đúng collection của step (membership) -> không phụ thuộc kích cỡ collection.
    // Mở rộng partial -> brand đầy đủ (gõ "acu" => search "Acura").
    const scope = this.#activeScope(stepEl);
    const effective = this.#resolveBrand(query) || query;
    this.search[idx] = {
      q: effective.toLowerCase(),
      term: scope ? `${effective} ${scope}` : effective,
      handle: this.#activeHandle(stepEl),
      direct: this.#directHandle(stepEl, effective),
      page: 0,
      pages: 0,
      more: true,
      loading: false,
      seen: new Set(),
    };
    grid.querySelectorAll('[data-bb-search-card]').forEach((c) => c.remove());
    this.#clearEmptyState(grid);
    this.#applyVisibility(stepEl); // ẩn browse
    status.classList.remove('bb-hide');
    status.dataset.loading = '';
    status.textContent = this.searchingLabel;
    this.#showSkeletons(grid, 8);
    this.#loadSearch(stepEl);
  }

  #showSkeletons(grid, n) {
    for (let i = 0; i < n; i++) {
      const sk = document.createElement('div');
      sk.className = 'bb-skeleton';
      sk.dataset.bbSkeleton = '';
      sk.innerHTML = '<div class="bb-skeleton__media"></div><div class="bb-skeleton__line"></div><div class="bb-skeleton__line"></div>';
      grid.append(sk);
    }
  }
  #clearSkeletons(grid) {
    grid.querySelectorAll('[data-bb-skeleton]').forEach((s) => s.remove());
  }

  /** Trống: collection-only -> báo chung; brand-mode -> mời chọn brand khác. */
  #renderEmptyState(grid) {
    grid.querySelectorAll('[data-bb-empty]').forEach((e) => e.remove());
    const box = document.createElement('div');
    box.className = 'bb-empty';
    box.dataset.bbEmpty = '';
    if (grid.closest('[data-bb-step]')?.dataset.collectionOnly === '1') {
      box.innerHTML = '<p class="bb-empty__title">No products available.</p>';
    } else {
      const brand = this.selectedBrand ? this.#esc(this.selectedBrand) : 'this brand';
      box.innerHTML =
        `<p class="bb-empty__title">No ${brand} items in this category.</p>` +
        '<p class="bb-empty__hint">Pick another brand to continue.</p>' +
        '<div class="bb-empty__chips"><button type="button" class="bb-chip" data-bb-brandchange>Change brand</button></div>';
    }
    grid.append(box);
  }
  #clearEmptyState(grid) {
    grid.querySelectorAll('[data-bb-empty]').forEach((e) => e.remove());
  }

  async #loadSearch(stepEl) {
    const idx = parseInt(stepEl.dataset.bbStep, 10);
    const s = this.search[idx];
    if (!s || s.loading || !s.more) return;
    s.loading = true;
    const grid = stepEl.querySelector('[data-bb-grid]');
    const status = stepEl.querySelector('[data-bb-search-status]');
    const stale = () => this.search[idx] !== s;
    status.dataset.loading = '';

    try {
      let added = 0;
      // s.direct: trỏ THẲNG collection-theo-brand (1 request, đúng tập, không lọc lại).
      //   -> nhanh hơn nhiều store-wide search. Hỏng/rỗng thì TỰ fallback về search cũ.
      // Ngược lại: store search (đã index, 250/trang) theo brand+scope rồi LỌC về
      //   collection của step (membership). Lặp tới khi có >=1 match hoặc hết kết quả.
      while (s.more && added === 0 && !stale()) {
        // LƯỢT ĐẦU của một collection: gọi `view=bb` (24 sản phẩm) thay vì `view=bbsearch`
        // (250). Trang 250 phải render 250 sản phẩm kèm toàn bộ variant ở phía Shopify —
        // đo thật là ~1–3s, trong lúc đó lưới trắng trơn và KHÔNG có ảnh nào bắt đầu tải.
        // Bản 24 sản phẩm về sau vài trăm ms -> hai hàng đầu có ảnh gần như tức thì, phần
        // còn lại nạp tiếp ở lượt sau (trùng id thì `s.seen` đã lọc).
        const fast = Boolean(s.direct) && !s.fastDone;
        const url = fast
          ? `/collections/${encodeURIComponent(s.direct)}?view=bb&page=1`
          : s.direct
            ? `/collections/${encodeURIComponent(s.direct)}?view=bbsearch&page=${s.page + 1}`
            : `/search?q=${encodeURIComponent(s.term)}&type=product&view=bbsearch&sort_by=best-selling&page=${s.page + 1}`;
        let data;
        try {
          data = await this.#fetchView(url);
        } catch (err) {
          // Collection direct không tồn tại (404)/lỗi mạng -> quay về search store cũ.
          // (collection-only: term rỗng nên KHÔNG fallback search store — để lỗi nổi lên.)
          if (s.direct && !s.collectionOnly) {
            s.direct = null;
            s.page = 0;
            s.more = true;
            s.seen.clear();
            s.loading = false;
            return this.#loadSearch(stepEl);
          }
          throw err;
        }
        if (stale()) return;
        if (fast) {
          // KHÔNG tăng s.page: lượt sau bbsearch vẫn phải bắt đầu từ trang 1 để lấy đủ
          // 250 sản phẩm đầu (24 cái vừa hiện nằm trong đó, `s.seen` lọc trùng).
          s.fastDone = true;
        } else {
          s.page += 1;
        }
        s.pages += 1;
        // Bỏ qua sản phẩm thiếu ảnh -> tránh card trống xấu UX.
        // Direct: collection đã đúng tập brand -> không cần membership/title-match/sort.
        const batch = s.direct
          ? (data.results || []).filter((r) => !s.seen.has(String(r.id)) && r.image).map((r) => ({ r }))
          : (data.results || [])
              .filter(
                (r) =>
                  !s.seen.has(String(r.id)) &&
                  r.image &&
                  (r.collections || []).includes(s.handle) &&
                  this.#titleMatchesBrand(r.title, s.q)
              )
              .map((r) => ({ r, sc: this.#matchScore(r.title, s.q) }))
              .sort((a, b) => a.sc - b.sc); // brand prefix trước
        if (batch.length) {
          this.#clearSkeletons(grid);
          for (const { r } of batch) {
            s.seen.add(String(r.id));
            // `card_size_pills` (section): số size tối đa còn hiện nút một chạm thay cho ô xổ.
            // Hoodie có 8 size (S–5XL) -> mặc định 6 đẩy nó về ô xổ; trang nào khai 8 thì thành nút.
            const cardEl = this.#buildCard(r, idx, null, parseInt(this.dataset.cardPills, 10) || 6);
            cardEl.dataset.bbSearchCard = '';
            grid.append(cardEl);
            this.#autoSelect(cardEl);
          }
          added += batch.length;
          this.#applyVisibility(stepEl);
          // Lượt nhanh chỉ có 24 món -> phải nạp tiếp, nhưng ĐỪNG nạp ngay: trang
          // `view=bbsearch` nặng ~225KB, chạy song song sẽ giành băng thông đúng lúc 8 ảnh
          // đầu đang tải. Chờ lúc nhàn (tối đa 1,5s) rồi mới kéo phần còn lại.
          //
          // KHÔNG chỉ kéo một trang: hàng chip chủ đề đếm trên card ĐÃ tải, mà chủ đề ít
          // hàng thường nằm ở trang cuối (FSC nằm trang 2 của 323 polo). Nên kéo tiếp cho
          // tới hết, tối đa 3 trang bbsearch (~750 sản phẩm) để không ôm collection khổng lồ.
          if (!fast && s.more && s.pages <= 3) {
            const more = () => this.#loadSearch(stepEl);
            if ('requestIdleCallback' in window) requestIdleCallback(more, { timeout: 2500 });
            else setTimeout(more, 900);
          }
          if (fast && s.more) {
            const rest = () => this.#loadSearch(stepEl);
            if ('requestIdleCallback' in window) requestIdleCallback(rest, { timeout: 1500 });
            else setTimeout(rest, 600);
          }
          // Lưới vừa cao lên -> chỉnh lại vị trí đã cuộn tới lúc đổi step (một lần).
          if (this._scrollStep === stepEl) {
            this._scrollStep = null;
            this.#scrollToStep(stepEl);
            this._scrollStep = null;
          }
        }
        s.more = Boolean(data.more);
      }

      // Direct tồn tại nhưng rỗng (0 sản phẩm) -> fallback search cho chắc.
      // (collection-only: collection rỗng thì hiện empty-state, KHÔNG fallback.)
      if (s.direct && !s.collectionOnly && added === 0 && grid.querySelectorAll('[data-bb-search-card]').length === 0) {
        s.direct = null;
        s.page = 0;
        s.more = true;
        s.seen.clear();
        s.loading = false;
        return this.#loadSearch(stepEl);
      }

      const count = grid.querySelectorAll('[data-bb-search-card]').length;
      if (count === 0) {
        this.#clearSkeletons(grid);
        if (s.more) {
          status.textContent = this.searchingLabel;
        } else {
          status.textContent = 'No matches in this collection.';
          this.#renderEmptyState(grid);
        }
      } else {
        this.#syncResultCount(stepEl);
      }
    } catch {
      /* thử lại lần sau */
    } finally {
      if (!stale()) {
        s.loading = false;
        delete status.dataset.loading;
        if (grid.querySelectorAll('[data-bb-search-card]').length > 0) this.#clearSkeletons(grid);        // Lượt tải CUỐI đặt s.more = false sau khi #applyVisibility đã chạy -> đếm lại
        // ở đây, không thì số mẫu trên ô chủ đề kẹt ở '…' mãi.
        this.#syncThemeBar(stepEl);
      }
    }
  }

  #buildCard(product, stepIdx, handle, maxPills = 6) {
    const variants = (product.variants || []).filter((v) => v[4]).map((v) => [v[0], v[1], v[2], v[3]]);
    const mode = variants.length <= 1 ? 'single' : 'variant-list';
    const img = httpsUrl(product.image);
    const sized = (w) => (img ? this.#sizeUrl(product.image, w) : '');
    // Buy X get Y: món ở step ưu đãi -> gạch giá gốc + badge FREE (bxgy) hoặc -N% (bxpct).
    const off = this.#slotOff(stepIdx);
    const isFree = off > 0;

    const card = document.createElement('div');
    card.className = 'bb-card';
    card.dataset.bbCard = '';
    card.dataset.mode = mode;
    if (isFree) {
      card.dataset.bbFree = '1';
      card.dataset.bbOff = String(off);
    }
    card.dataset.productId = String(product.id);
    // Đổi tên hiển thị NGAY tại đây: mọi chỗ đọc dataset.title (ô đã chọn, peek,
    // zoom) tự động hiện tên đầy đủ, không phải sửa từng chỗ.
    card.dataset.title = this.#displayTitle(product.title);
    card.dataset.handle = product.handle || '';
    // Cùng 300 với ảnh lưới: chip ở thanh đáy / peek dùng lại đúng file đã tải, không
    // bắt CDN sinh thêm một bản 240 chỉ để hiện thumbnail 42px.
    // 240 thay vì 300: trên điện thoại thẻ chỉ rộng ~165px nên 240 vẫn dư nét, mà nhẹ hơn
    // ~35%. Đây cũng là ảnh dùng lại cho chip thanh đáy, peek và bản xem trước của lightbox.
    // Điện thoại (2026-09-27): lưới 1 cột, ảnh rộng ~343px -> cần bản 600 cho màn DPR 2-3.
    // Desktop giữ 240. MỘT bề rộng cho mỗi khổ (không srcset) — lý do ở ghi chú ảnh bên dưới.
    const gridW = this.#gridImgW();
    card.dataset.image = sized(gridW) || img;
    card.dataset.zoomraw = product.image || ''; // URL gốc -> sẽ size theo màn lúc mở zoom
    card.dataset.step = String(stepIdx);
    const themes = this.#themesOf(product);
    if (themes.length) card.dataset.bbThemes = themes.join(' ');
    if (handle != null) card.dataset.bbHandle = handle;

    const media = document.createElement('div');
    media.className = 'bb-card__media';
    media.dataset.bbZoom = ''; // bấm ảnh -> phóng to
    if (img) {
      const im = document.createElement('img');
      im.decoding = 'async';

      im.width = 400;
      im.height = 400;
      im.alt = this.#displayTitle(product.title);
      // Lazy load GỐC của trình duyệt. Đã thử thay bằng hàng đợi tự viết (8 ảnh/lượt, ưu
      // tiên từ trên xuống) rồi bỏ: trình duyệt vốn đã chỉ tải ảnh quanh khung nhìn, tự
      // xếp ưu tiên theo vị trí và tự điều tiết theo tốc độ mạng — phần thêm được rất
      // mỏng, còn code tự viết phải tự lo mọi ca (thẻ chưa gắn DOM, thẻ đang ẩn, ảnh
      // không bắn load/error) và sai MỘT ca là ảnh không bao giờ tải (đã dính 2026-08-27).
      //
      // Hai thứ thật sự làm ảnh nhanh lên nằm chỗ khác, đã sửa riêng:
      //   - lượt đầu gọi `view=bb` (24 sp, ~470ms) thay vì `view=bbsearch` (250 sp, 225KB)
      //   - mỗi ảnh chỉ dùng MỘT bề rộng 300, không srcset -> CDN chỉ phải sinh một bản
      //     (sinh bản mới mất ~1s bất kể to nhỏ) nên tỉ lệ trúng cache cao hơn hẳn.
      //
      // GÁN `loading` TRƯỚC `src`: đặt src trên <img> chưa gắn DOM là Chrome tải NGAY, cờ
      // lazy gán sau vô tác dụng -> cả lô 250 ảnh tranh băng thông (bẫy cũ 2026-08-20).
      const gridEl = this.stepEls[stepIdx] && this.stepEls[stepIdx].querySelector('[data-bb-grid]');
      // 1 cột trên điện thoại -> màn đầu chỉ thấy ~2 thẻ, eager 3 là đủ (ảnh 600 nặng hơn).
      const firstRows = gridEl ? gridEl.querySelectorAll('[data-bb-card]').length < (gridW > 300 ? 3 : 8) : false;
      im.loading = firstRows ? 'eager' : 'lazy';
      if (firstRows) im.setAttribute('fetchpriority', 'high');
      // Shimmer chạy tới khi ảnh về; ảnh lỗi thì tắt shimmer (đừng hứa cái không tới).
      media.dataset.bbImg = 'loading';
      const imgDone = (ok) => { media.dataset.bbImg = ok ? 'done' : 'error'; };
      im.addEventListener('load', () => imgDone(true), { once: true });
      im.addEventListener('error', () => imgDone(false), { once: true });
      im.src = sized(gridW) || img;
      if (im.complete && im.naturalWidth) imgDone(true);
      media.append(im);
    }
    const view = document.createElement('button');
    view.type = 'button';
    view.className = 'bb-card__view';
    view.dataset.bbZoom = '';
    view.setAttribute('aria-label', `Zoom ${this.#displayTitle(product.title)}`);
    view.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>';
    media.append(view);

    if (themes.length && this.themeMeta[themes[0]]) {
      const themeBadge = document.createElement('span');
      themeBadge.className = 'bb-card__theme';
      themeBadge.textContent = this.themeMeta[themes[0]];
      media.append(themeBadge);
    }

    if (isFree) {
      const freeBadge = document.createElement('span');
      freeBadge.className = 'bb-card__free';
      freeBadge.textContent = this.#offBadge(off);
      media.append(freeBadge);
    }

    const content = document.createElement('div');
    content.className = 'bb-card__content';

    const title = document.createElement('p');
    title.className = 'bb-card__title';
    title.textContent = this.#displayTitle(product.title);

    const price = document.createElement('p');
    price.className = 'bb-card__price';
    price.dataset.bbCardPrice = '';
    const prices = variants.map((v) => v[3]);
    const min = prices.length ? Math.min(...prices) : 0;
    const priceLabel = prices.length && Math.max(...prices) !== min ? `From ${money(min)}` : money(min);
    // Cho preview thang mở: giá cơ sở khi chưa chọn variant + cờ "giá tuỳ size".
    card.dataset.bbMinPrice = String(min);
    if (prices.length && Math.max(...prices) !== min) card.dataset.bbPriceVaries = '1';
    if (isFree) {
      price.classList.add('bb-card__price--free');
      const orig = document.createElement('s');
      orig.className = 'bb-card__price-orig';
      orig.dataset.bbCardOrig = '';
      orig.textContent = priceLabel;
      const freeTxt = document.createElement('span');
      freeTxt.className = 'bb-card__price-free';
      freeTxt.textContent = off >= 100 ? 'FREE' : money(this.#slotPay(min, off));
      price.append(orig, freeTxt);
    } else {
      price.textContent = priceLabel;
    }
    content.append(title, price);

    const actions = document.createElement('div');
    actions.className = 'bb-card__actions';

    if (mode === 'variant-list') {
      const o1Varies = new Set(variants.map((v) => v[1])).size > 1;
      const o2Varies = new Set(variants.map((v) => v[2])).size > 1;
      const labelFor = (v) => {
        const parts = [];
        if (o1Varies && v[1] && v[1] !== 'Default Title') parts.push(v[1]);
        if (o2Varies && v[2] && v[2] !== 'Default Title') parts.push(v[2]);
        return parts.join(' / ') || [v[1], v[2]].filter((x) => x && x !== 'Default Title').join(' / ') || 'Default';
      };
      // Ít option (<=6) -> pills bấm 1 chạm; nhiều -> dropdown gọn.
      if (variants.length <= maxPills) {
        const pills = document.createElement('div');
        // >6 nút (hoodie S–5XL) -> lưới 4 cột, ô to bấm dễ; ít nút thì vẫn hàng tự xuống dòng như cũ.
        pills.className = variants.length > 6 ? 'bb-card__pills bb-card__pills--grid' : 'bb-card__pills';
        pills.setAttribute('role', 'group');
        pills.setAttribute('aria-label', 'Choose option');
        for (const v of variants) {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'bb-card__pill';
          pill.dataset.bbPill = '';
          pill.dataset.variantId = String(v[0]);
          pill.dataset.label = labelFor(v);
          pill.setAttribute('aria-pressed', 'false');
          pill.textContent = labelFor(v);
          pills.append(pill);
        }
        content.append(pills);
      } else {
        const select = document.createElement('select');
        select.className = 'bb-card__select';
        select.dataset.bbVariantSelect = '';
        select.setAttribute('aria-label', 'Choose option');
        for (const v of variants) {
          const opt = document.createElement('option');
          opt.value = String(v[0]);
          opt.textContent = labelFor(v);
          select.append(opt);
        }
        actions.append(select);
      }
    }

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'bb-card__add';
    add.dataset.bbAdd = '';
    add.disabled = true;
    add.textContent = this.addLabel;
    actions.append(add);
    content.append(actions);

    const json = document.createElement('script');
    json.type = 'application/json';
    json.dataset.bbVariants = '';
    json.textContent = JSON.stringify(variants);

    card.append(media, content, json);
    if (this.ladderMode) this.#paintLadderCard(card); // draft đã có món -> card mới sinh ra đã mang giá giảm
    return card;
  }

  /* ---------- checkout ---------- */

  /**
   * EXPRESS CHECKOUT — nút ví (Shop Pay / Apple Pay / Google Pay).
   *
   * Nút do Shopify render trong iframe của họ và đọc GIỎ THẬT, không đọc state của
   * builder. Mình KHÔNG chèn được `/cart/add.js` vào lúc khách bấm: Apple Pay bắt buộc
   * mở payment sheet trong cùng một cú chạm, một lượt fetch ở giữa là trình duyệt chặn.
   *
   * Vì vậy đảo thứ tự: **mở màn xem bộ = chốt giỏ luôn**. Chỉ khi giỏ đã khớp đúng thứ
   * đang hiện trên màn thì nút ví mới được bỏ `bb-hide`. Add lỗi -> nút ví ở nguyên trong
   * bóng tối, khách vẫn còn nút thường (nút đó sẽ báo lỗi tử tế khi bấm).
   */
  /**
   * Báo cho theme biết giỏ vừa đổi.
   *
   * `silent` = đừng để cart drawer tự trượt ra. `cart-drawer.js` mở drawer với MỌI
   * CartAddEvent khi nó có attribute `auto-open`; trước đây không ai thấy vì add xong là
   * redirect thẳng sang /checkout. Giờ builder ở lại trang (chốt giỏ cho nút ví, gỡ món
   * khỏi giỏ ở màn xem bộ) nên drawer sẽ bung ra đè lên đúng màn khách đang đọc.
   * Gỡ attribute quanh lượt dispatch — dispatchEvent chạy đồng bộ nên trả lại ngay được.
   */
  /**
   * Nối tiếp MỌI lượt ghi giỏ của builder vào một hàng đợi.
   *
   * Cả `#removeBundleFromCart` lẫn `#removeSingleFromCart` đều làm hai nhịp
   * (GET /cart.js để tra `key` của dòng -> POST /cart/update.js). Hai lượt chạy chồng
   * nhau thì lượt sau tra `key` trên ảnh chụp giỏ CŨ. Trước đây không với tới được vì bộ
   * ở màn kết quả chưa nằm trong giỏ; giờ builder tự chốt giỏ nên gỡ bộ là có ghi thật.
   */
  #cartOp(fn) {
    this._cartChain = (this._cartChain || Promise.resolve()).then(fn).catch(() => {});
    return this._cartChain;
  }

  #emitCartChanged(source, itemCount, silent) {
    const drawer = silent ? document.querySelector('cart-drawer-component[auto-open]') : null;
    drawer?.removeAttribute('auto-open');
    // `#onCartUpdated` sinh ra để bắt thay đổi từ NGOÀI builder (cart drawer / trang giỏ).
    // Lượt này là của chính builder, state đã đúng rồi -> đối chiếu lại chỉ có hại.
    this._selfCartOp = true;
    try {
      document.dispatchEvent(new CartAddEvent({}, this.sectionId, { source, itemCount }));
    } finally {
      this._selfCartOp = false; // dispatchEvent chạy đồng bộ nên trả cờ ngay được
    }
    drawer?.setAttribute('auto-open', '');
  }

  /** `50:5.95, 65:7.95` -> [{max: 5000, cents: 595}, ...] đã sắp tăng dần. */
  #parseShipTiers(raw) {
    return String(raw || '')
      .split(/[,\r\n]/)
      .map((row) => row.trim())
      .filter(Boolean)
      .map((row) => {
        const [max, price] = row.split(':').map((x) => parseFloat(String(x).trim()));
        return Number.isFinite(max) && Number.isFinite(price)
          ? { max: Math.round(max * 100), cents: Math.round(price * 100) }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.max - b.max);
  }

  /**
   * Giá ship cho một tổng tiền. Trả `0` = free, số cents = phải trả, `null` = không biết
   * (chưa khai bảng, hoặc tổng vượt bậc cuối mà chưa tới ngưỡng free).
   *
   * Ngưỡng free lấy từ `free_ship_threshold` — CÙNG con số với rule trong admin và với
   * announcement bar. Đổi bên admin thì phải đổi cả ở đây.
   */
  #shipCents(payCents) {
    if (this.fsThreshold > 0 && payCents >= this.fsThreshold) return 0;
    if (!this.shipTiers || !this.shipTiers.length) return null;
    const hit = this.shipTiers.find((t) => payCents <= t.max);
    return hit ? hit.cents : null;
  }

  /**
   * Dòng nhỏ cạnh giá: `+ $9.95 shipping` hoặc `+ FREE Shipping` (xanh).
   *
   * 2026-08-28: markup `[data-bb-shipnote]` ĐÃ GỠ khỏi cả màn kết quả lẫn sheet xem lại
   * -> vòng lặp bên dưới không chạy nữa. GIỮ NGUYÊN HÀM: nó còn đặt `_displayPay`, thứ
   * `#syncPayTerms()` đọc để tính trả góp Shop Pay sau khi chốt giỏ. Xoá hàm là hỏng
   * dòng trả góp. Muốn bật lại dòng giá ship thì chỉ cần thêm lại markup.
   */
  #renderShipNote(payCents) {
    this._displayPay = payCents; // #syncPayTerms dùng lại con số này sau khi chốt giỏ xong
    const els = this.querySelectorAll('[data-bb-shipnote]');
    if (!els.length) return;
    const cents = payCents > 0 ? this.#shipCents(payCents) : null;
    els.forEach((el) => {
      el.classList.toggle('bb-hide', cents === null);
      el.classList.toggle('bb-shipnote--free', cents === 0);
      el.textContent = cents === null ? '' : cents === 0 ? '+ FREE Shipping' : `+ ${money(cents)} shipping`;
    });
  }

  /**
   * Trả góp Shop Pay đọc số tiền từ `[data-cart-subtotal]`, mà Liquid render nó lúc tải
   * trang (giỏ còn rỗng). Cập nhật theo tổng đang hiện rồi mới bỏ `bb-hide` — hiện sớm là
   * khách thấy "4 x $0.00" một nhịp.
   */
  async #syncPayTerms(payCents) {
    const box = this.querySelector('[data-bb-installments]');
    if (!box) return;
    if (!(payCents > 0)) {
      box.classList.add('bb-hide');
      this._payTermsFor = 0;
      return;
    }
    if (this._payTermsFor === payCents) return; // số tiền không đổi -> khỏi gọi lại
    this._payTermsFor = payCents;
    try {
      // Section này chỉ có mỗi khối trả góp (~2KB) và đọc GIỎ HIỆN TẠI, nên phải gọi SAU
      // khi builder đã chốt giỏ. Shopify tự quyết hiện hay không (ngoài khoảng cho trả
      // góp, hoặc khách ở nước không hỗ trợ) — mình không tự vẽ, chữ trả góp có ràng buộc
      // pháp lý phải do Shopify in ra.
      const html = await fetch('/?section_id=bb-installments').then((r) => r.text());
      const el = new DOMParser().parseFromString(html, 'text/html').querySelector('shopify-payment-terms');
      box.textContent = '';
      if (el) box.append(el);
      box.classList.toggle('bb-hide', !el);
    } catch {
      box.classList.add('bb-hide');
    }
  }

  #showExpress(on) {
    this.querySelector('[data-bb-express]')?.classList.toggle('bb-hide', !on);
  }

  /** Chốt giỏ cho khớp màn đang hiện, rồi mới mở nút ví. Không redirect, không báo lỗi. */
  #syncCartForExpress() {
    if (!this.querySelector('[data-bb-express]')) return; // store chưa bật ví -> khỏi gọi giỏ
    // Lượt sau chờ lượt trước rồi CHẠY LẠI, không bị nuốt: khách đổi size trong lúc
    // lượt chốt giỏ trước còn dở thì giỏ phải được chốt lại theo size mới, không thì nút
    // ví thanh toán đúng cái size cũ.
    if (this._expressSyncing) {
      this._expressDirty = true;
      return;
    }
    this._expressSyncing = true;
    this.#checkout(true).finally(() => {
      this._expressSyncing = false;
      if (this._autoCommitted !== null && this._autoCommitted !== undefined) {
        this.state.previewId = this.state.cartIds[this._autoCommitted] || null;
        this.#persist();
      }
      // Trả góp đọc GIỎ -> chỉ đúng sau khi lượt chốt giỏ ở trên xong.
      this.#syncPayTerms(this._displayPay || 0);
      if (this._expressDirty) {
        this._expressDirty = false;
        this.#syncCartForExpress();
      }
    });
  }

  /**
   * Thang mở: đẩy bộ đang dựng thành bundle chính thức (CHƯA gọi giỏ). Trả về chỉ số
   * bundle mới, hoặc -1 nếu chẳng có gì để đẩy.
   */
  #promoteLadderDraft() {
    const draft = this.state.draft.filter(Boolean);
    if (!draft.length) return -1;
    // KHÔNG bắn BundleCompleted ở đây. Hàm này giờ chạy mỗi lần MỞ sheet xem lại (để chốt
    // giỏ cho nút ví), mà khách mở/đóng mấy lần cũng được -> bắn ở đây là thổi phồng pixel.
    // Sự kiện nằm ở #commitLadder, lúc khách thật sự đi tiếp.
    this.state.bundles.push(draft);
    this.state.cartIds.push(null);
    this.state.draft = [];
    this.#persist();
    return this.state.bundles.length - 1;
  }

  /**
   * Thang mở: mở sheet xem lại là chốt bộ vào giỏ luôn (để nút ví ăn đúng giỏ).
   *
   * Đánh dấu bằng `state.editingBundle` chứ không phải một cờ riêng — nhờ vậy `#target()`
   * vẫn trả về đúng bộ đó, nên thanh đáy và danh sách trong sheet không bị rỗng đi trong
   * lúc sheet đang mở.
   */
  #commitLadderForExpress() {
    if (this._autoCommitted !== null && this._autoCommitted !== undefined) return this.#syncCartForExpress();
    const idx = this.#promoteLadderDraft();
    if (idx < 0) return;
    this._autoCommitted = idx;
    this.state.editingBundle = idx;
    this.#persist();
    this.#barUpdate();
    this.#syncCartForExpress();
  }

  /**
   * Đóng sheet mà chưa mua -> KÉO BỘ ĐÓ RA KHỎI GIỎ và trả về trạng thái đang dựng dở.
   * Thiếu bước này thì bấm KEEP BUILDING xong giỏ vẫn còn hàng, khách thêm tiếp là nhân đôi.
   */
  #uncommitLadder() {
    const b = this._autoCommitted;
    if (b === null || b === undefined) return false;
    this._autoCommitted = null;
    const bundle = this.state.bundles[b];
    if (!bundle) {
      // Index cũ không còn (giỏ bị sửa từ nơi khác) -> vẫn phải trả editingBundle về null,
      // không thì #target() trả undefined và mọi thứ đọc nó đều ném lỗi.
      this.state.editingBundle = null;
      return false;
    }
    const cartId = this.state.cartIds[b];
    this.state.bundles.splice(b, 1);
    this.state.cartIds.splice(b, 1);
    this.state.editingBundle = null;
    this.state.draft = bundle; // trả nguyên bộ về chỗ đang dựng
    this.state.previewId = null;
    this.#persist();
    if (cartId) this.#removeBundleFromCart(cartId);
    this.#showExpress(false);
    this.#barUpdate();
    return true;
  }

  /**
   * @param {boolean} auto  true = tự chốt giỏ khi mở màn xem bộ (cho nút ví), KHÔNG
   *                        redirect và KHÔNG bung băng lỗi — khách chưa bấm gì cả.
   */
  /**
   * Bắn AddToCart cho một tập bộ + món lẻ.
   *
   * CHỈ gọi khi khách THẬT SỰ đi tiếp, không gọi ở lượt tự chốt giỏ. Lượt tự chốt chạy mỗi
   * lần mở màn xem bộ; khách mở/đóng 3 lần là 3 sự kiện AddToCart cho một bộ họ còn chưa
   * mua — pixel đó là tín hiệu Meta dùng để tối ưu quảng cáo, thổi phồng là dạy sai model.
   */
  #trackAddToCart(bundleIdxs, singles) {
    let payCents = 0;
    const contentIds = [];
    const contents = [];
    bundleIdxs.forEach((b) => {
      const bundle = this.state.bundles[b];
      if (!bundle) return;
      payCents += this.#calc(bundle, b).pay;
      bundle.forEach((sel) => {
        contentIds.push(String(sel.variantId));
        contents.push({ id: String(sel.variantId), quantity: 1 });
      });
    });
    (singles || []).forEach((sg) => {
      payCents += this.#singlePay(sg.priceCents);
      contentIds.push(String(sg.variantId));
      contents.push({ id: String(sg.variantId), quantity: 1 });
    });
    if (!contentIds.length) return;
    this.#track(
      'AddToCart',
      {
        content_type: 'product',
        content_ids: contentIds,
        contents,
        num_items: contentIds.length,
        value: Number((payCents / 100).toFixed(2)),
        currency: this.currency,
      },
      false,
      'BundleCheckout'
    );
  }

  /**
   * ViewContent CHUAN cho quang cao catalog: khach xem MOT mau (landing tu quang cao, phong to anh
   * trong luoi). Cung dinh dang voi pixel cua app Shopify (kiem 2026-09-24): content_type
   * 'product_group' + content_ids = PRODUCT id -> khop item_group_id trong catalog. Moi san pham
   * ban mot lan moi luot xem trang. Khong co fbq (khach chua dong y cookie) -> #track tu bo qua.
   */
  #trackView(productId, title, priceCents, source) {
    const pid = String(productId || '');
    if (!pid) return;
    this._viewed = this._viewed || new Set();
    if (this._viewed.has(pid)) return;
    this._viewed.add(pid);
    // fbq do app Shopify nap SAU khi trang chay (va chi khi khach dong y cookie). ViewContent o
    // landing ban ngay luc mo trang -> doi fbq toi da 10s, khong thi su kien mat truoc khi pixel san.
    let tries = 0;
    const send = () => {
      if (typeof window.fbq !== 'function' && tries++ < 20) return setTimeout(send, 500);
      this.#sendView(pid, title, priceCents, source);
    };
    send();
  }
  #sendView(pid, title, priceCents, source) {
    this.#track(
      'ViewContent',
      {
        content_type: 'product_group',
        content_ids: [pid],
        content_name: title || '',
        value: Number(((priceCents || 0) / 100).toFixed(2)),
        currency: this.currency,
        source,
      },
      true,
      '',
      `bb-vc-${pid}-${Date.now()}`
    );
  }

  async #checkout(auto = false) {
    // Thang mở: bộ đã mở lại từ giỏ -> gỡ dòng cũ (hàng đợi #cartOp; lượt add bên dưới chờ nó)
    // để cả bộ vào giỏ chung MỘT _bundle_id, function xếp hạng nối tiếp.
    if (this.ladderMode && (this.state.reopenIds || []).length) {
      const ids = this.state.reopenIds;
      this.state.reopenIds = [];
      this.#persist();
      ids.forEach((id) => this.#removeBundleFromCart(id));
    }
    if (this.state.bundles.length === 0 && (this.state.singles || []).length === 0) {
      if (auto) return;
      return this.#showError('Please complete at least one bundle.');
    }
    await this.#syncAbAttr();
    // Chỉ add những bundle ĐỦ món mà CHƯA có trong giỏ (cartId null) -> tránh add trùng khi quay lại.
    const newOnes = [];
    this.state.bundles.forEach((bn, b) => {
      if (this.#isFull(bn) && !this.state.cartIds[b]) newOnes.push(b);
    });
    // Món lẻ chưa vào giỏ (cartId null) cũng phải được add ở lượt này.
    const newSingles = (this.state.singles || []).filter((x) => !x.cartId);
    // Bundle LẪN món lẻ đều đã nằm trong giỏ -> đi thẳng tới trang thanh toán.
    if (newOnes.length === 0 && newSingles.length === 0) {
      if (auto) return this.#showExpress(true); // giỏ đã khớp sẵn -> mở nút ví
      // Giỏ đã được lượt tự chốt đẩy lên từ trước, nhưng ĐÂY mới là lúc khách bấm đi tiếp
      // -> pixel bắn ở đây, đúng một lần cho một ý định mua.
      this.#trackAddToCart(this.state.bundles.map((_, i) => i), this.state.singles);
      window.location.href = '/checkout';
      return;
    }
    // Nút bấm KHÔNG cố định một chỗ: 3 offer theo step dùng nút ở màn kết quả, thang mở
    // không render màn đó nên nút nằm ở sheet xem lại / thanh đáy. Lấy cái nào có.
    const button =
      this.querySelector('[data-bb-checkout]') ||
      this.querySelector('[data-bb-review-checkout]') ||
      this.querySelector('[data-bb-ladder-add]');
    if (button) button.disabled = true;
    this.#hideError();
    try {
      const items = [];
      const newIds = {}; // index bundle -> _bundle_id vừa tạo
      const isBxgy = this.pricingType === 'bxgy';
      const isBxpct = this.pricingType === 'bxpct';
      const tiersStr = (this.extraTiers || []).join(',');
      newOnes.forEach((b) => {
        const bundle = this.state.bundles[b];
        const bundleId = `${Date.now()}-${b}-${Math.floor(Math.random() * 1e6)}`;
        newIds[b] = bundleId;
        const count = String(bundle.length);
        // _bundle_base + _bundle_tiers -> function tự tính ưu đãi theo SỐ bundle trong giỏ.
        const baseStr = (this.#calc(bundle, b).basePay / 100).toFixed(2);
        bundle.forEach((s, i) => {
          const props = {
            _bundle: this.bundleName,
            _bundle_id: bundleId,
            _bundle_count: count,
            _bundle_slot: String(i),
            _bundle_type: this.ladderMode ? 'ladder' : isBxgy ? 'bxgy' : isBxpct ? 'bxpct' : 'fixed',
            _bundle_price: baseStr,
            // bxgy: base = tổng phần TÍNH TIỀN (đã trừ quà free, do #calc tính); fixed: giá cố định.
            _bundle_base: baseStr,
            _bundle_tiers: tiersStr,
          };
          // Đánh dấu slot ưu đãi -> function giảm đúng slot đó; phần còn lại vẫn "buy more save more".
          //   bxgy  -> _bundle_free = "yes"  (100% off)
          //   bxpct -> _bundle_off  = "50"   (giảm %)
          if (this.ladderMode) {
            // Function tự xếp hạng lúc checkout -> KHÔNG gửi % theo dòng. Khách xoá bớt
            // một dòng trong giỏ thì hạng đổi, chỉ function mới thấy được điều đó.
            props._bundle_ladder = this.ladderTiersRaw;
            props._bundle_ladder_by = this.ladderBy;
            delete props._bundle_base; // thang mở không dùng "buy more save more"
            delete props._bundle_tiers;
          } else {
            const slotOff = this.#slotOff(i, bundle);
            if (slotOff >= 100) props._bundle_free = 'yes';
            else if (slotOff > 0) props._bundle_off = String(slotOff);
          }
          // Chỉ offer bật countdown mới có note -> cart hiện "limited time ends ...".
          if (this.limitedNote) props._bundle_ends = this.limitedNote;
          // Trang nguồn -> cart "Add bundle 2" redirect đúng offer. + nudge free-ship khi chỉ 1 bundle.
          props._bundle_url = window.location.pathname;
          if (this.fsNudge) {
            props._bundle_fs = this.fsNudge;
            props._bundle_fs_min = String(this.fsThreshold);
          }
          items.push({ id: s.variantId, quantity: 1, properties: props });
        });
      });

      // MÓN LẺ: không mang bất kỳ `_bundle_*` nào -> function bỏ qua, thu đủ tiền.
      // `_single_id` chỉ để builder tìm lại đúng dòng khi khách bấm gỡ.
      newSingles.forEach((sg, i) => {
        const sid = `s-${Date.now()}-${i}-${Math.floor(Math.random() * 1e6)}`;
        sg.pendingCartId = sid;
        const props = { _single_id: sid, _bundle_url: window.location.pathname };
        // offers/single.js đọc đúng thuộc tính này. Không gắn = checkout thu nguyên giá.
        if (this.singlePct > 0) props._single_off = String(this.singlePct);
        items.push({ id: sg.variantId, quantity: 1, properties: props });
      });

      await (this._cartChain || Promise.resolve()); // đợi lượt gỡ đang dở, đừng ghi chồng
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add items to cart.');

      // Đánh dấu các bundle vừa add là đã trong giỏ -> không add lại lần sau.
      newOnes.forEach((b) => { this.state.cartIds[b] = newIds[b]; });
      newSingles.forEach((sg) => { sg.cartId = sg.pendingCartId; delete sg.pendingCartId; });
      // Giỏ đã khớp đúng thứ đang hiện trên màn -> giờ mới cho nút ví xuất hiện.
      if (auto) this.#showExpress(true);

      // Lượt TỰ chốt giỏ không phải hành động mua -> không bắn pixel (xem #trackAddToCart).
      if (!auto) this.#trackAddToCart(newOnes, newSingles);

      this.#persist(); // GIỮ state (gồm cartIds) -> quay lại trang vẫn ở màn final
      // auto = builder tự chốt giỏ, khách chưa bấm gì -> đừng bung drawer đè lên màn.
      this.#emitCartChanged('bundle-builder-component', items.length, auto);
    } catch (error) {
      // auto = khách chưa bấm gì -> không bung băng lỗi giữa màn. Nút ví ở yên trong bóng
      // tối, khách bấm nút thường thì mới thấy thông báo.
      if (!auto) this.#showError(error.message);
    } finally {
      if (button) button.disabled = false;
    }
  }

  /* ---------- persistence & errors ---------- */

  /**
   * Thang mở: giỏ ĐÃ có bộ của trang này (khách quay lại từ checkout / giỏ) -> đưa các món đó về
   * draft để món thêm sau tính hạng NỐI TIẾP (vẫn 50%) và thanh free ship tính cả tiền đã có.
   * KHÔNG đụng giỏ ở đây: khách chỉ xem rồi đi thì giỏ y nguyên; #checkout mới gỡ dòng cũ.
   */
  #reopenLadder(cartBundles, cartIds, skipId) {
    if (!this.ladderMode || this.state.editingBundle !== null) return;
    if (this._autoCommitted !== null && this._autoCommitted !== undefined) return;
    const ids = [];
    const items = [];
    (cartBundles || []).forEach((bn, i) => {
      const id = cartIds[i];
      if (!id || id === skipId || (this.state.reopenIds || []).includes(id)) return;
      ids.push(id);
      items.push(...(bn || []).filter(Boolean));
    });
    if (!items.length) return;
    const keep = this.state.bundles.map((bn, i) => [bn, this.state.cartIds[i]]).filter(([, id]) => !ids.includes(id));
    this.state.bundles = keep.map(([bn]) => bn);
    this.state.cartIds = keep.map(([, id]) => id);
    this.state.draft = [...items, ...this.state.draft.filter(Boolean)];
    this.state.reopenIds = [...(this.state.reopenIds || []), ...ids];
    this.#persist();
    this.#barUpdate();
    this.#track('LadderReopened', { items: items.length });
  }

  #persist() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({ bundles: this.state.bundles, cartIds: this.state.cartIds, draft: this.state.draft, singles: this.state.singles, brand: this.selectedBrand, previewId: this.state.previewId || null, reopenIds: this.state.reopenIds || [] }));
    } catch {
      /* non-critical */
    }
    // Đội đã chọn lưu RIÊNG sang localStorage -> khách quay lại hôm sau vẫn vào thẳng
    // lưới sản phẩm của đội mình. Chỉ mỗi tên đội; bundle đang dựng dở vẫn ở
    // sessionStorage vì nó gắn với giỏ hàng của phiên, sống dai sẽ lệch với giỏ thật.
    try {
      if (this.selectedBrand) localStorage.setItem(`${this.storageKey}:team`, this.selectedBrand);
      if (this.selectedTheme) localStorage.setItem(`${this.storageKey}:theme`, this.selectedTheme);
    } catch {
      /* non-critical */
    }
  }

  /**
   * Thang mở: bộ mà sheet xem lại đã "mượn" giỏ nhưng khách CHƯA bấm đi tiếp. Tải lại
   * trang giữa chừng thì kéo nó khỏi giỏ và trả về chỗ đang dựng.
   *
   * Không làm thì thanh đáy trống trơn trong khi giỏ vẫn còn hàng — khách tưởng mất bộ
   * đang dựng, làm lại từ đầu và thành ra mua gấp đôi.
   */
  #restorePreviewLadder() {
    if (!this.ladderMode || !this.state.previewId) return;
    const idx = this.state.cartIds.indexOf(this.state.previewId);
    // Không còn trong giỏ, hoặc khách đã bắt đầu dựng bộ khác -> chỉ dọn cờ.
    if (idx < 0 || this.state.draft.filter(Boolean).length) {
      this.state.previewId = null;
      this.#persist();
      return;
    }
    this._autoCommitted = idx;
    this.#uncommitLadder();
  }

  /**
   * LINK QUANG CAO CATALOG: ?design=<handle san pham>.
   * Khach bam quang cao vi thich DUNG mot mau -> builder mo ra voi mau do nam san tren
   * luoi (khoi "spot"), khoi phai luot 300+ mau de tim lai. Chi chay khi section bat
   * `enable_deeplink` (host co data-deeplink) — cac trang dang ban khong doi gi.
   * Doi lay tu token dau cua handle (kc-...) va THANG doi da luu: quang cao hien ao KC
   * thi phai mo luoi KC. Step = category co pattern khop loai ao trong handle (khop DAI
   * nhat, de "cap" khong an nham handle co chu khac).
   */
  #initDeeplink() {
    this.deep = null;
    if (!this.dataset.deeplink) return;
    let h = '';
    try {
      h = (new URLSearchParams(window.location.search).get('design') || '').toLowerCase().trim();
    } catch {
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]{2,150}$/.test(h)) return;
    // Link theo MA DESIGN (?design=SCH002[&type=hoodie]): chua biet doi -> man chon doi truoc.
    // Ma CR-... = hang crawl ve de test (E:\Spy, /crawl-design): loai ao tu do, khong can step rieng.
    if (/^(?:cr-)?[a-z]{2,6}\d{2,5}(?:-\d+)?$/.test(h)) return this.#initDesignCode(h);
    const meta = this.teamMeta && this.teamMeta[h.split('-')[0]];
    if (!meta || !meta.n) return;
    let step = -1;
    let best = 0;
    let type = '';
    this.stepEls.forEach((el, i) => {
      (el.dataset.brandPattern || '').split(',').forEach((p) => {
        const slug = p.trim().replace(/\{brand\}-?/g, '');
        if (slug && slug.length > best && (h.includes(`-${slug}-`) || h.endsWith(`-${slug}`))) {
          best = slug.length;
          step = i;
          type = slug;
        }
      });
    });
    if (step < 0) return;
    // Loai ao cua mau -> <html data-bb-lp-type>: khoi "The fabric" dung loai trong noi dung
    // trang tu hien (CSS cua khoi do doc attribute nay). Gan som, truoc khi ve.
    document.documentElement.dataset.bbLpType = type;
    this.selectedBrand = meta.n;
    this.deep = { handle: h, step };
    // LANDING: khach tu quang cao -> trang chi hien khoi san pham (anh, size, ADD) va noi dung
    // thuyet phuc ben duoi; builder (luoi, chon loai, thanh day) an toi khi khach roi landing
    // (#endLanding). Chu store 2026-10-15: bo DA CO MON (khach quay lai, bam ad catalog khac) cung
    // ra landing — truoc day roi thang vao builder (luoi, chip chu de). Mon cu van giu o thanh day.
    this.landing = true;
    this.classList.add('bb-landing');
    const shell = document.createElement('div');
    shell.className = 'bb-spot bb-spot--loading';
    shell.dataset.bbSpotWrap = '';
    shell.innerHTML = '<div class="bb-spot__skel"></div>';
    this.prepend(shell);
    // Back cua trinh duyet: dong man chon loai / tu builder ve lai the san pham (#showAllCats, #endLanding).
    window.addEventListener(
      'popstate',
      () => {
        if (this._typOpen) return this.#closeTypePicker();
        if (!this.landing && this._lpWrap) this.#restoreLanding();
      },
      { signal: this.#abort.signal }
    );
  }

  /**
   * Landing "v2" (popup 2 nut, man chon loai, nut quay ve san pham): hang crawl `?design=CR-...` va
   * link catalog `?design=<handle>` (chu store 2026-10-15). Link ma design thuong (SCH002) giu ban cu.
   */
  #lpV2() {
    return !!this.codeLink?.crawl || (!!this.deep && !this.codeLink);
  }

  /** Ngu canh cho man chon loai (#showAllCats) + nut quay ve san pham (#restoreLanding). */
  #setLpCtx(card, p) {
    const team = this.teamMeta[String(p.handle || '').split('-')[0]];
    if (!team) return;
    const types = this.stepEls
      .map((el, i) => ({ el, i, label: (this.querySelector(`[data-bb-cat="${i}"]`)?.textContent || '').trim() }))
      .filter((t) => t.label && this.#directHandle(t.el, team.n));
    if (!types.length) return;
    this._lpCtx = {
      team,
      types,
      mainCents: Math.min(...(p.variants || []).map((v) => v.price)),
      title: (card.querySelector('.bb-card__title')?.textContent || '').trim(),
    };
  }

  /**
   * Giao dien trang LANDING cho khoi mau (ban Claude Design "Option 1"): gallery vuot duoc,
   * badge, so dem anh + day thumbnail (CHI KHI co tu 2 anh — polo/tee chi co 1 anh, cap co
   * nhieu), sao + link review, hop so gia "THIS <LOAI> / EVERYTHING AFTER", hang SIZE + size
   * guide, ghi chu size, 3 dau tick cam ket. Van dung lai the tu #buildCard nen chon size /
   * ADD / gia di dung duong cu — o day chi them phan tu va doi cho.
   */
  #buildLandingHero(card, p, stepEl, stepIdx) {
    card.classList.add('bb-lp');
    const crawl = !!this.codeLink?.crawl;
    card.classList.toggle('bb-lp--crawl', crawl);
    if (crawl) this.#crawlPageContent();
    const w = window.innerWidth < 750 ? 900 : 1200;
    const imgs = [...new Set((p.images || []).map((u) => httpsUrl(u)).filter(Boolean))];
    if (!imgs.length) imgs.push(httpsUrl(p.featured_image || ''));
    const many = imgs.length > 1;
    card.classList.toggle('bb-lp--single', !many);
    const title = card.dataset.title || '';

    // --- gallery ---
    const media = card.querySelector('.bb-card__media');
    let track = null;
    if (media) {
      media.querySelector('img')?.remove();
      media.querySelector('.bb-card__view')?.remove();
      media.dataset.bbImg = 'done';
      track = document.createElement('div');
      track.className = 'bb-lp__track';
      imgs.forEach((u, i) => {
        const s = document.createElement('div');
        s.className = 'bb-lp__slide';
        const im = document.createElement('img');
        im.decoding = 'async';
        im.loading = i === 0 ? 'eager' : 'lazy'; // loading TRUOC src (bay cu 2026-08-20)
        if (i === 0) im.setAttribute('fetchpriority', 'high');
        im.width = 1200;
        im.height = 1200;
        im.alt = many ? `${title} — image ${i + 1} of ${imgs.length}` : title;
        im.src = this.#sizeUrl(u, w) || u;
        s.append(im);
        track.append(s);
      });
      media.prepend(track);
      const badge = document.createElement('span');
      badge.className = 'bb-lp__badge';
      badge.textContent = this.dataset.deeplinkHeading || 'The design you picked';
      media.append(badge);
    }
    if (many && media && track) {
      const count = document.createElement('span');
      count.className = 'bb-lp__count';
      count.textContent = `1 / ${imgs.length}`;
      media.append(count);
      const thumbs = document.createElement('div');
      thumbs.className = 'bb-lp__thumbs';
      imgs.forEach((u, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bb-lp__thumb';
        b.setAttribute('aria-label', `Show image ${i + 1}`);
        if (i === 0) b.setAttribute('aria-current', 'true');
        const ti = document.createElement('img');
        ti.loading = 'lazy';
        ti.alt = '';
        ti.src = this.#sizeUrl(u, 200) || u;
        b.append(ti);
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          // Cap nhat so dem + vien NGAY, khong cho su kien scroll: tab khong ve frame (an/nen)
          // thi scroll event va cuon muot deu dung — bay cung ho voi requestAnimationFrame.
          setActive(i);
          track.scrollTo({ left: i * track.clientWidth, behavior: document.hidden ? 'instant' : 'smooth' });
        });
        thumbs.append(b);
      });
      media.after(thumbs);
      function setActive(i) {
        count.textContent = `${i + 1} / ${imgs.length}`;
        thumbs.querySelectorAll('.bb-lp__thumb').forEach((t, j) => t.toggleAttribute('aria-current', j === i));
      }
      let raf = 0;
      track.addEventListener(
        'scroll',
        () => {
          if (raf) return;
          raf = setTimeout(() => {
            raf = 0;
            const i = Math.max(0, Math.min(imgs.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
            setActive(i);
          }, 60);
        },
        { passive: true }
      );
    }

    // --- noi dung ---
    const titleEl = card.querySelector('.bb-card__title');
    if (titleEl) {
      // Tu co gach ngang (T-shirt, Quarter-zip, CR-SCX001) khong bi be doi o dau "-" cuoi dong.
      const words = (titleEl.textContent || '').trim().split(/\s+/);
      if (words.length > 1) {
        titleEl.innerHTML = words
          .map((w) => (w.includes('-') ? `<span class="bb-lp__code">${this.#esc(w)}</span>` : this.#esc(w)))
          .join(' ');
      }
      const stars = document.createElement('a');
      stars.className = 'bb-lp__stars';
      stars.href = '#lp-reviews';
      // So review dem tu CHINH khoi review tren trang (khong bia so).
      const nRev = document.querySelectorAll('#lp-reviews .lp-r').length;
      stars.innerHTML =
        '<span aria-hidden="true">&#9733;&#9733;&#9733;&#9733;&#9733;</span> ' +
        (nRev ? `${nRev} verified buyer reviews` : 'Verified buyer reviews');
      titleEl.before(stars);
      const crawlType = this.codeLink?.crawl ? String(p.title || '').trim().split(/\s+/).slice(1, -1).pop() : '';
      const cat = crawlType || (this.querySelector(`[data-bb-cat="${stepIdx}"]`)?.textContent || '').trim().split(/\s+/)[0] || 'ITEM';
      const prices = (p.variants || []).map((v) => v.price);
      // "2nd polo from $19.97": gia mon thu 2 CUNG loai, cung cong thuc thang voi gio — truoc day
      // ghi cung "Hoodies from $34.97" ca tren landing polo/tee.
      const minP = prices.length ? Math.min(...prices) : 0;
      const half = minP ? this.#ladderRows([{ priceCents: minP }, { priceCents: minP }]).reduce((a, x) => a + x.pay, 0) - minP : 0;
      // Setting cua section (mac dinh "Hoodies from $34.97") chi dung khi khong tinh duoc.
      // Hang crawl: noi ve CAC LOAI KHAC (hoodie, tee, polo...) — khach thay store co nhieu loai,
      // khong chi them 1 cai cardigan nua (chu store 2026-10-15).
      let afterSub = crawl
        ? `On ${this.#typesBlurb()} — mix any`
        : half > 0 && half < minP
          ? `2nd ${cat.toLowerCase()} from ${money(half)}`
          : this.dataset.deeplinkAfterSub || 'Every item after your first';
      // Ô EVERYTHING AFTER nêu các LOẠI của trang (chủ store 2026-10-22: "nói rõ các category, không chỉ cardigan"):
      // loại đang xem đứng đầu, thêm tối đa 3 loại khác theo thứ tự tab, rồi "& more".
      const PLURAL = { 'T-SHIRT': 'tees', TSHIRT: 'tees', HOODIE: 'hoodies', 'QUARTER-ZIP': 'quarter-zips', POLO: 'polos',
        HAWAIIAN: 'Hawaiian shirts', SWEATPANTS: 'sweatpants', CAP: 'caps', CARDIGAN: 'cardigans', JERSEY: 'jerseys' };
      const plural = (c) => PLURAL[c] || `${c.toLowerCase()}s`;
      const cats = [...new Set([...this.querySelectorAll('[data-bb-cat]')].map((x) => x.textContent.trim().split(/\s+/)[0].toUpperCase()).filter(Boolean))];
      if (!crawl && cats.length > 1) {
        // Loại khác: ưu tiên loại bán chạy / dễ hiểu trước (hoodie, tee, polo, cap), còn lại theo thứ tự tab.
        const PRI = ['HOODIE', 'T-SHIRT', 'TSHIRT', 'POLO', 'CAP', 'QUARTER-ZIP', 'CARDIGAN', 'HAWAIIAN', 'JERSEY', 'SWEATPANTS'];
        const rank = (c) => (PRI.indexOf(c) < 0 ? 99 : PRI.indexOf(c));
        const others = cats.filter((c) => c !== cat.toUpperCase()).sort((a, b) => rank(a) - rank(b));
        const list = [cat.toUpperCase(), ...others].slice(0, 4).map(plural);
        list[0] = list[0].charAt(0).toUpperCase() + list[0].slice(1);
        afterSub = cats.length > 4 ? `${list.join(', ')} & more` : `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
      }
      const deal = document.createElement('div');
      deal.className = 'bb-lp__deal';
      deal.innerHTML =
        `<div><span class="bb-lp__k" data-bb-lp-k data-cat="${this.#esc(cat.toUpperCase())}">THIS ${this.#esc(cat.toUpperCase())}</span>` +
        `<b data-bb-lp-price data-full="${minP}">${money(prices.length ? Math.min(...prices) : 0)}</b>` +
        '<small data-bb-lp-small>Full price &middot; unlocks the deal</small></div>' +
        `<div class="bb-lp__on"><span class="bb-lp__k">EVERYTHING AFTER</span><b>${this.#esc(this.dataset.deeplinkAfter || '50% OFF')}</b>` +
        `<small>${this.#esc(afterSub)}</small></div>`;
      titleEl.after(deal);
    }
    // Dong chat vai ngan duoi hop so gia — lay tu CHINH khoi "The fabric" cua loai nay trong noi
    // dung trang (mot nguon), kem link nhay xuong khoi day du. Loai chua co noi dung -> bo qua.
    const fab = document.querySelector(`.lp-fab[data-lp-type~="${document.documentElement.dataset.bbLpType || '-'}"]`);
    const fabShort = fab?.querySelector('[data-lp-fabric-short]')?.innerHTML;
    const dealEl = card.querySelector('.bb-lp__deal');
    if (fab && fabShort && dealEl) {
      const f = document.createElement('p');
      f.className = 'bb-lp__fabric';
      // Không còn link "More" (kéo khách khỏi khu mua); khối "The fabric" đầy đủ vẫn ở dưới trang.
      f.innerHTML = `<span class="bb-lp__speck">Fabric</span><span>${fabShort}</span>`;
      dealEl.after(f);
    }
    // Hang crawl: mo ta lay tu CHINH san pham (noi dung trang chi viet cho 7 loai ao cua store).
    if (this.codeLink?.crawl && p.description && dealEl) {
      const desc = document.createElement('details');
      desc.className = 'bb-lp__desc';
      desc.open = true;
      desc.innerHTML = `<summary>Product details</summary><div class="bb-lp__desc-body">${p.description}</div>`;
      (card.querySelector('.bb-card__actions') || dealEl).after(desc);
    }
    this.#buildTeamStrip(card, p);
    const picker = card.querySelector('.bb-card__pills, [data-bb-variant-select]');
    let guide = card.querySelector('.bb-spot__guide');
    const chartType = document.documentElement.dataset.bbLpType || '';
    if (chartType && document.querySelector(`template[data-bb-sizechart~="${chartType}"]`)) {
      // Có bảng size text cho loại này -> nút mở popup text (bỏ ảnh của step nếu có).
      if (!guide) {
        guide = document.createElement('button');
        guide.type = 'button';
        guide.className = 'bb-spot__guide';
        guide.textContent = 'Size guide';
      }
      delete guide.dataset.bbSizeguide;
      guide.dataset.bbSizechart = chartType;
    }
    if (picker && card.dataset.mode !== 'single') {
      const head = document.createElement('div');
      head.className = 'bb-lp__sizehead';
      head.innerHTML = '<span>SIZE: <em data-bb-lp-size>Select</em></span>';
      if (guide) head.append(guide);
      picker.before(head);
      // Hang crawl: ghi chu "Unisex fit..." la cua hoodie (step muon) -> thay bang cho xem bang size
      // THAT cua san pham (anh cuoi + khoi Product details; da kiem 32/32 sp CR-SCX001 2026-10-15).
      const desc = card.querySelector('.bb-lp__desc');
      if (crawl) {
        const fit = document.createElement('p');
        fit.className = 'bb-lp__fit bb-lp__fit--chart';
        fit.innerHTML =
          '<span aria-hidden="true">&#128207;</span> Size chart is in the product photos' +
          (desc ? ' and in <a href="#" data-bb-lp-chart>Product details</a> below.' : '.');
        fit.querySelector('[data-bb-lp-chart]')?.addEventListener('click', (e) => {
          e.preventDefault();
          desc.open = true;
          (desc.querySelector('img') || desc).scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
        (card.querySelector('.bb-card__pills') || picker).after(fit);
      }
      const fitTxt = crawl ? '' : (stepEl.querySelector('.bb-stepnote, .bb-step__note')?.textContent || '').replace(/\s+/g, ' ').trim();
      if (fitTxt) {
        const fit = document.createElement('p');
        fit.className = 'bb-lp__fit';
        fit.textContent = fitTxt;
        (card.querySelector('.bb-card__pills') || picker).after(fit);
      }
    } else if (guide) guide.remove(); // mot size (cap) -> khong co gi de chon, bo luon size guide
    const actions = card.querySelector('.bb-card__actions');
    // O SO LUONG (hang crawl, chu store 2026-10-15): mua 2-3 cai cung mau (qua tang / ca nha).
    // Moi cai la 1 mon trong bo -> cai thu 2 tro di giam 50% nhu mon khac (#addItem nhan so luong).
    if (crawl && actions && card.dataset.mode !== 'single') {
      const q = document.createElement('div');
      q.className = 'bb-lp__qty';
      q.innerHTML =
        '<span>QUANTITY</span><div class="bb-lp__qtybox">' +
        '<button type="button" data-bb-qty-step="-1" aria-label="Decrease quantity">&minus;</button>' +
        '<input type="number" inputmode="numeric" min="1" max="10" value="1" aria-label="Quantity" data-bb-qty>' +
        '<button type="button" data-bb-qty-step="1" aria-label="Increase quantity">+</button></div>';
      const input = q.querySelector('[data-bb-qty]');
      const set = (n) => {
        const v = Math.max(1, Math.min(10, parseInt(n, 10) || 1));
        input.value = String(v);
        card.dataset.bbQty = String(v);
        this.#refreshCard(card);
      };
      q.addEventListener('click', (e) => {
        const b = e.target.closest('[data-bb-qty-step]');
        if (!b) return;
        e.stopPropagation();
        set((parseInt(input.value, 10) || 1) + parseInt(b.dataset.bbQtyStep, 10));
      });
      input.addEventListener('change', () => set(input.value));
      card.dataset.bbQty = '1';
      actions.before(q);
    }
    if (actions) {
      const trust = document.createElement('div');
      trust.className = 'bb-lp__trust';
      // Mon mot size (cap): khong co gi de doi size -> dau tick dau noi ve size co dinh.
      const oneSize = card.dataset.mode === 'single';
      const claims = [oneSize ? 'One size, adjustable strap' : '180-day size exchange', "Print that can't peel", '100% refund if damaged'];
      // Hang crawl (chat lieu chua kiem): khong hua ve ban in.
      if (this.codeLink?.crawl) claims.splice(1, 1);
      trust.innerHTML = claims
        .map((t) => `<div><i aria-hidden="true">&#10003;</i>${t}</div>`)
        .join('');
      const ship = document.createElement('p');
      ship.className = 'bb-lp__ship';
      const fsOn = this.fsEnabled && this.fsThreshold > 0;
      ship.textContent =
        // Free ship da co dong rieng (.bb-lp__fs) ngay duoi hop so gia -> khong nhac lai o day.
        'Production 5–7 business days · US shipping 5–7 business days';
      // Logo the: muon dung khoi .bb-paylogos Liquid da ve san trong sheet xem lai.
      const payTpl = document.querySelector('template[data-bb-lp-paylogos]');
      const pay = payTpl?.content.firstElementChild || this.querySelector('.bb-paylogos');
      if (this.dataset.lpV2) {
        // Landing v2: một khung danh sách cam kết + dòng thanh toán an toàn (thay 3 ô tick + dòng ship + logo rời).
        const ic = (d) => `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
        // Free ship nằm TRONG khung (chủ store 2026-10-22), dòng phụ do #syncLanding đổi theo bộ ([data-bb-lp-fs="sub"]).
        const fsRow = this.fsEnabled && this.fsThreshold > 0
          ? [[ic('<path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/>'),
              `Free US shipping over ${money(this.fsThreshold).replace(/\.00$/, '')}`, '<span data-bb-lp-fs="sub">Counts every item in your order</span>']]
          : [];
        const rows = [
          ...fsRow,
          // Chữ giao hàng theo setting section (lp_ship_title / lp_ship_sub) — mỗi loại hàng mỗi thời gian.
          [ic('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>'),
            this.dataset.lpShipTitle || 'Arrives in 10–15 business days', this.dataset.lpShipSub || 'Production 5–7 days + US shipping 5–7 days'],
          [ic('<path d="M4 9h13l-3-3M20 15H7l3 3"/>'),
            oneSize ? 'One size fits most' : 'Free size exchange', oneSize ? 'Adjustable strap' : 'Within 180 days of delivery'],
          [ic('<path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z"/><path d="M9 12l2 2 4-4"/>'),
            '100% refund if damaged', 'Or a free replacement — no forms'],
        ];
        const box = document.createElement('ul');
        box.className = 'bb-lp__assure';
        box.setAttribute('role', 'list');
        box.innerHTML = rows.map(([i, b, s]) => `<li>${i}<div><b>${b}</b>${s.startsWith('<span') ? s : `<span>${s}</span>`}</div></li>`).join('');
        const sec = document.createElement('div');
        sec.className = 'bb-lp__secure';
        sec.innerHTML = `<p>${ic('<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>')}Secure checkout</p>`;
        if (pay) {
          const pc = pay.cloneNode(true);
          pc.classList.add('bb-lp__pay');
          sec.append(pc);
        }
        actions.after(box, sec);
      } else if (pay) {
        const pc = pay.cloneNode(true);
        pc.classList.add('bb-lp__pay');
        // Hang crawl: cam ket + giao hang la 1 khoi lien -> logo the xuong duoi khoi.
        if (crawl) actions.after(trust, ship, pc);
        else actions.after(trust, pc, ship);
      } else actions.after(trust, ship);
    }
    // Free ship: noi ngay duoi hop so gia — khach biet nguong TRUOC khi add (truoc day chi thay sau).
    if (this.fsEnabled && this.fsThreshold > 0 && !this.dataset.lpV2) {
      const fs = document.createElement('p');
      fs.className = 'bb-lp__fs';
      fs.dataset.bbLpFs = '';
      (card.querySelector('.bb-lp__fabric') || card.querySelector('.bb-lp__deal'))?.after(fs);
    }
    this.#buildLandingSticky(card);
    // Hang 2 nut phu DUOI HAN khoi mua (sau ADD + cam ket + logo the + dong giao hang): "Same design /
    // Other team" va "More <doi> <loai>" (#buildExplore them vao). Chu store 2026-10-06: khong dat
    // giua size va ADD de khach khong bi phan tam.
    const alt = document.createElement('div');
    alt.className = 'bb-lp__alt';
    alt.dataset.bbLpAlt = '';
    const altAt = card.querySelector('.bb-lp__ship') || card.querySelector('.bb-lp__secure') || card.querySelector('.bb-lp__pay') || card.querySelector('.bb-card__actions');
    altAt?.after(alt);
    // Mua lam qua / ca nha moi nguoi mot doi: quay ve man chon doi cua CUNG mau, bo dang co giu nguyen.
    const code = (String(p.title || '').trim().split(/\s+/).pop() || '').toLowerCase();
    const type = document.documentElement.dataset.bbLpType || '';
    if (alt.isConnected && type && /^(?:cr-)?[a-z]+\d+(?:-\d+)?$/.test(code) && !card.querySelector('[data-bb-lp-teams]')) {
      const a = document.createElement('a');
      a.className = 'bb-lp__altbtn';
      const u = new URL(window.location.href);
      u.searchParams.set('design', code.toUpperCase());
      u.searchParams.set('type', type);
      u.searchParams.delete('team');
      a.href = u.pathname + u.search;
      a.innerHTML = `<small>Same design</small><b>Other ${this.dataset.brandNoun || 'team'} &rarr;</b>`;
      a.addEventListener('click', () => this.#track('OtherTeamClick', { design: code.toUpperCase() }));
      alt.append(a);
    }
  }

  /**
   * "SELECT TEAM" kiểu foco (chủ store 2026-10-26): hàng logo đội của CÙNG mã design, ngay trên hàng size.
   * Đội đang xem đứng đầu (viền màu đội), rồi đội khách chọn gần đây, rồi A-Z. Mobile vuốt ngang, desktop có
   * 2 nút mũi tên. Bấm logo -> #findDesign (cache) -> link mã design: mở landing đội đó tại chỗ (pushState &team,
   * Back về đội cũ); link handle: chuyển sang link mã design của đội đó. Đội không có mẫu -> ô mờ + dòng báo.
   */
  #buildTeamStrip(card, p) {
    const code = (String(p.title || '').trim().split(/\s+/).pop() || '').toLowerCase();
    const type = document.documentElement.dataset.bbLpType || '';
    const cur = String(p.handle || '').split('-')[0];
    const anchor = card.querySelector('.bb-card__pills, [data-bb-variant-select]') || card.querySelector('.bb-card__actions');
    if (!type || !anchor || !this.teamMeta[cur] || !/^[a-z]+\d+(?:-\d+)?$/.test(code)) return;
    const all = Object.keys(this.teamMeta).sort((a, b) => this.teamMeta[a].n.localeCompare(this.teamMeta[b].n));
    if (all.length < 2) return;
    const recent = this.#recentTeams().map((n) => this.#slugifyBrand(n)).filter((k) => this.teamMeta[k]);
    const order = [...new Set([cur, ...recent, ...all])];
    const noun = this.dataset.brandNoun || 'team';
    const box = document.createElement('div');
    box.className = 'bb-lp__teams';
    box.dataset.bbLpTeams = '';
    box.innerHTML =
      `<div class="bb-lp__teamshead"><span>SELECT ${this.#esc(noun.toUpperCase())}: <em>${this.#esc(this.teamMeta[cur].n)}</em></span>` +
      `<button type="button" class="bb-lp__teamall" data-bb-lp-teamall aria-haspopup="dialog">See all ${this.#esc(noun)}s</button></div>` +
      '<div class="bb-lp__teamwrap">' +
      `<button type="button" class="bb-lp__teamnav bb-lp__teamnav--prev" data-bb-lp-teamnav="-1" aria-label="Previous ${this.#esc(noun)}s" hidden>&lsaquo;</button>` +
      '<div class="bb-lp__teamrow" role="list">' +
      order
        .map((k, i) => {
          const t = this.teamMeta[k];
          const logo = t.lc || t.l;
          return (
            `<button type="button" class="bb-lp__team" role="listitem" data-bb-lp-team="${this.#esc(k)}" aria-label="${this.#esc(t.n)}"` +
            ` aria-pressed="${k === cur ? 'true' : 'false'}"${t.c ? ` style="--tp-c:${this.#esc(t.c)}"` : ''}>` +
            (logo
              ? `<img src="${this.#esc(logo)}" alt="" width="48" height="48" loading="${i < 8 ? 'eager' : 'lazy'}" decoding="async">`
              : `<span>${this.#esc(k.toUpperCase())}</span>`) +
            '</button>'
          );
        })
        .join('') +
      '</div>' +
      `<button type="button" class="bb-lp__teamnav bb-lp__teamnav--next" data-bb-lp-teamnav="1" aria-label="More ${this.#esc(noun)}s" hidden>&rsaquo;</button>` +
      '</div>' +
      '<p class="bb-lp__teammsg" role="status" hidden></p>';
    anchor.before(box);
    const row = box.querySelector('.bb-lp__teamrow');
    const msg = box.querySelector('.bb-lp__teammsg');
    const prev = box.querySelector('[data-bb-lp-teamnav="-1"]');
    const next = box.querySelector('[data-bb-lp-teamnav="1"]');
    const sync = () => {
      const max = row.scrollWidth - row.clientWidth;
      prev.hidden = row.scrollLeft < 4;
      next.hidden = row.scrollLeft > max - 4;
      row.classList.toggle('is-end', row.scrollLeft > max - 4);
    };
    row.addEventListener('scroll', sync, { passive: true });
    requestAnimationFrame(sync);
    window.addEventListener('resize', sync, { passive: true, signal: this.#abort.signal });
    box.addEventListener('click', async (e) => {
      if (e.target.closest('[data-bb-lp-teamall]')) {
        this.#openTeamSheet(row, noun);
        return;
      }
      const nav = e.target.closest('[data-bb-lp-teamnav]');
      if (nav) {
        row.scrollBy({ left: Number(nav.dataset.bbLpTeamnav) * row.clientWidth * 0.8, behavior: 'smooth' });
        return;
      }
      const b = e.target.closest('[data-bb-lp-team]');
      if (!b || b.disabled || b.getAttribute('aria-pressed') === 'true' || box.dataset.busy) return;
      const k = b.dataset.bbLpTeam;
      box.dataset.busy = '1';
      b.classList.add('is-loading');
      msg.hidden = true;
      const r = await this.#findDesign(k, type, code);
      delete box.dataset.busy;
      b.classList.remove('is-loading');
      if (!r) {
        msg.textContent = `This design isn't available for ${this.teamMeta[k].n} right now. Please pick another ${noun}.`;
        msg.hidden = false;
        b.disabled = true;
        return;
      }
      this.#track('LandingTeamSwitch', { design: code.toUpperCase(), team: k.toUpperCase() });
      this.#pushRecentTeam(this.teamMeta[k].n);
      const L = this.codeLink;
      if (L && L.code === code && L.type === type) {
        try {
          const u = new URL(window.location.href);
          u.searchParams.set('team', k.toUpperCase());
          window.history.pushState({ bbTeam: k }, '', u);
        } catch {}
        return this.#openDesignCode(k, r);
      }
      const u = new URL(window.location.href);
      u.searchParams.set('design', code.toUpperCase());
      u.searchParams.set('type', type);
      u.searchParams.set('team', k.toUpperCase());
      window.location.href = u.pathname + u.search;
    });
  }

  /**
   * "See all teams" (chủ store 2026-10-26): lưới đủ đội FULL MÀN, ô to (mobile 4/hàng) cho khỏi bấm nhầm.
   * Ô = bản sao ô của hàng SELECT TEAM; bấm ô -> đóng lưới + bấm hộ ô đó ở hàng (dùng chung luồng đổi đội, báo lỗi).
   * Đóng: nút ×, bấm nền tối (desktop), Esc.
   */
  #openTeamSheet(row, noun) {
    const m = document.createElement('div');
    m.className = 'bb-lp-tsheet';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-label', `Select ${noun}`);
    m.innerHTML =
      '<div class="bb-lp-tsheet__panel">' +
      `<div class="bb-lp-tsheet__head"><b>SELECT ${this.#esc(noun.toUpperCase())}</b>` +
      '<button type="button" class="bb-lp-tsheet__x" data-bb-tsheet-x aria-label="Close">&times;</button></div>' +
      '<div class="bb-lp-tsheet__grid" role="list"></div></div>';
    const grid = m.querySelector('.bb-lp-tsheet__grid');
    row.querySelectorAll('[data-bb-lp-team]').forEach((b) => {
      const c = b.cloneNode(true);
      c.className = 'bb-lp-tsheet__team';
      c.querySelector('img')?.setAttribute('loading', 'eager');
      grid.append(c);
    });
    const root = document.documentElement;
    const prev = root.style.overflow;
    const onKey = (e) => e.key === 'Escape' && close();
    const close = () => {
      m.remove();
      root.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
    m.addEventListener('click', (e) => {
      if (e.target === m || e.target.closest('[data-bb-tsheet-x]')) return close();
      const t = e.target.closest('[data-bb-lp-team]');
      if (!t || t.disabled) return;
      close();
      if (t.getAttribute('aria-pressed') === 'true') return;
      this.#track('LandingTeamSheetPick', { team: t.dataset.bbLpTeam.toUpperCase() });
      row.querySelector(`[data-bb-lp-team="${CSS.escape(t.dataset.bbLpTeam)}"]`)?.click();
    });
    document.addEventListener('keydown', onKey);
    root.style.overflow = 'hidden';
    document.body.append(m);
    m.querySelector('[data-bb-tsheet-x]').focus({ preventScroll: true });
    this.#track('LandingTeamSeeAll', {});
  }

  /**
   * "COMPLETE THE SET": cung MA DESIGN + cung doi, o cac loai ao khac co tren trang (tee, polo,
   * quarter-zip...). Khach den vi thich MAU nay -> mon thu 2 hop ly nhat la bo do cung mau.
   * The dung #buildCard nhu luoi (chon size, ADD, gia thang tu ve lai) -> khong co luong them mon moi.
   * Loai nao khong co mau nay thi bo qua, khong co loai nao thi khong ve gi.
   */
  async #buildDesignSet(card, p, mainStep) {
    const code = (String(p.title || '').trim().split(/\s+/).pop() || '').toLowerCase();
    const slug = String(p.handle || '').split('-')[0];
    if (!/^[a-z]+\d+(?:-\d+)?$/.test(code) || !this.teamMeta[slug]) return;
    const cats = this.querySelectorAll('[data-bb-cat]');
    const mainType = (this.stepEls[mainStep]?.dataset.brandPattern || '').split(',')[0].trim().replace(/\{brand\}-?/g, '');
    const jobs = this.stepEls.map((el, i) => {
      if (i === mainStep) return null;
      const type = (el.dataset.brandPattern || '').split(',')[0].trim().replace(/\{brand\}-?/g, '');
      if (!type) return null;
      // Cung khuon handle voi san pham chinh: kc-hunting-hoodie-sch002 -> kc-hunting-polo...-sch002.
      const exact = mainType && p.handle.includes(`-${mainType}-`) ? p.handle.replace(`-${mainType}-`, `-${type}-`) : '';
      return this.#findDesign(slug, type, code, exact).then((r) => r && { r, i });
    });
    const found = (await Promise.all(jobs.filter(Boolean))).filter(Boolean);
    const wrap = card.closest('[data-bb-spot-wrap]');
    if (!found.length || !wrap || !wrap.isConnected) return;
    const mainCents = Math.min(...(p.variants || []).map((v) => v.price));
    const sec = document.createElement('section');
    sec.className = 'bb-set';
    sec.dataset.bbSet = '';
    sec.innerHTML =
      `<div class="bb-set__head"><h3>${this.#esc(this.dataset.setHeading || 'Complete the set — same design')}</h3>` +
      `<p>${this.#esc(this.dataset.setSub || 'Every piece after your first is 50% off. Mix and match.')}</p></div>` +
      '<div class="bb-set__row"></div>';
    const row = sec.querySelector('.bb-set__row');
    found.forEach(({ r, i }) => {
      const variants = (r.p.variants || []).map((v) => [v.id, v.option1, v.option2, v.price, v.available]);
      const product = { id: r.p.id, title: r.p.title, handle: r.p.handle, tags: r.p.tags || [], image: httpsUrl(r.p.featured_image || ''), variants };
      const c = this.#buildCard(product, i, null);
      c.dataset.bbSetCard = '';
      c.classList.add('bb-card--set');
      const cat = (cats[i]?.textContent || '').trim();
      const min = Math.min(...variants.filter((v) => v[4]).map((v) => v[3]));
      // Gia du kien khi la mon thu 2 (bo dang trong) — cung cong thuc thang voi gio hang.
      const half = this.#ladderRows([{ priceCents: mainCents }, { priceCents: min }]).reduce((a, x) => a + x.pay, 0) - mainCents;
      const tag = document.createElement('p');
      tag.className = 'bb-set__tag';
      tag.dataset.bbSetTag = '';
      tag.innerHTML = `<b>${this.#esc(cat)}</b>` + (half > 0 && half < min ? `<span>${money(half)} as your 2nd item</span>` : '');
      const content = c.querySelector('.bb-card__content');
      if (content) content.prepend(tag);
      else c.prepend(tag);
      // O size bat dau o "Size" (chua chon) — truoc day hien "S" ma nut ADD van khoa, de hieu nham.
      const sel = c.querySelector('[data-bb-variant-select]');
      if (sel) {
        sel.prepend(new Option('Size', ''));
        sel.value = '';
      }
      row.append(c);
      this.#refreshCard(c);
    });
    // Khoi bo dung TRUOC khoi "More <doi> <loai>" (khoi do dung ngay luc mo trang, khoi bo dung sau).
    const after = wrap.querySelector('[data-bb-exp]') || wrap.querySelector('[data-bb-lp-sticky]');
    if (after) after.before(sec);
    else wrap.append(sec);
    this.#syncSetCards();
    this.#track('SetShown', { design: code.toUpperCase(), n: found.length });
  }

  /** Mon cua bo da co trong bo -> danh dau tren the set; bo co mon -> bo dong "as your 2nd item". */
  #syncSetCards() {
    const has = this.#target().some(Boolean);
    const inIds = new Set(this.#target().filter(Boolean).map((x) => String(x.productId)));
    this.querySelectorAll('[data-bb-set-card]').forEach((c) => {
      // Khach da chon size o mon chinh -> dien SAN cung size cho the bo (chi khi the chua chon,
      // va chi khi loai do co dung size ay) -> them polo cung bo con mot cham.
      if (this.lastOption && !c.dataset.selectedVariant) {
        const sel = c.querySelector('[data-bb-variant-select]');
        const opt = sel && [...sel.options].find((o) => o.value && o.textContent === this.lastOption);
        if (opt) {
          sel.value = opt.value;
          c.dataset.selectedVariant = opt.value;
          this.#refreshCard(c);
        }
      }
      c.classList.toggle('is-added', inIds.has(c.dataset.productId));
      const sp = c.querySelector('[data-bb-set-tag] span');
      if (sp) sp.hidden = has;
    });
    this.querySelectorAll('[data-bb-exp-2nd]').forEach((b) => (b.hidden = has));
  }

  /**
   * Popup chuc mung tren LANDING: hang "Same design" (toi da 3 loai chua co trong bo) ngay
   * duoi cau chao. Bam -> dong popup, cuon toi dung the do trong hang Complete the set.
   */
  #celebrateSet(el) {
    el.querySelector('[data-bb-celebrate-set]')?.remove();
    if (!this.landing) return;
    const inIds = new Set(this.#target().filter(Boolean).map((x) => String(x.productId)));
    const cards = [...this.querySelectorAll('[data-bb-set-card]')].filter((c) => !inIds.has(c.dataset.productId)).slice(0, 3);
    const sub = el.querySelector('[data-bb-celebrate-sub]');
    if (!cards.length || !sub) return;
    const box = document.createElement('div');
    box.className = 'bb-celset';
    box.dataset.bbCelebrateSet = '';
    box.innerHTML = '<p class="bb-celset__h">Same design — complete the set</p><div class="bb-celset__row"></div>';
    const row = box.querySelector('.bb-celset__row');
    cards.forEach((c) => {
      const cents = this.#ladderCardCents(c);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bb-celset__item';
      const cat = c.querySelector('[data-bb-set-tag] b')?.textContent || '';
      b.innerHTML =
        `<img src="${this.#esc(c.dataset.image || '')}" alt="" width="72" height="72">` +
        `<span>${this.#esc(cat)}</span><b>${money(this.#ladderNextPay(cents))}</b>`;
      b.addEventListener('click', () => {
        this.#track('SetPopupClick', { design: c.dataset.handle || '' });
        this.#closeCelebrate();
        c.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
        c.classList.remove('bb-lp-flash');
        void c.offsetWidth;
        c.classList.add('bb-lp-flash');
      });
      row.append(b);
    });
    sub.after(box);
  }

  /** Tong khach dang phai tra cho cac mon da co trong bo (gia thang, cents). */
  #lpPaid() {
    const cur = this.#target().filter(Boolean);
    return this.#ladderRows(cur).reduce((sum, r) => sum + r.pay, 0);
  }

  /**
   * "MORE DAL HOODIES": mau KHAC cung loai + cung doi -> khach khong ung mau quang cao thi luot
   * tiep thay vi thoat. Thiet ke: Claude Design 2026-10-06. 1 request `view=bb` (trang 1, 24
   * mau), chi goi khi khoi sap vao man. Bam mau -> landing cua mau do (bo dang dung giu trong
   * session); "See all" -> builder ngay tren trang, dung step + doi, chip chu de "All".
   * Duoi 2 mau / loi -> giau ca khoi lan link duoi tieu de. Khong in tong so mau (khong biet chinh xac).
   */
  #buildExplore(card, p, stepIdx) {
    const wrap = card.closest('[data-bb-spot-wrap]');
    const stepEl = this.stepEls[stepIdx];
    const slug = String(p.handle || '').split('-')[0];
    const team = this.teamMeta[slug];
    if (!wrap || !stepEl || !team) return;
    const handle = this.#directHandle(stepEl, team.n);
    if (!handle) return;
    const raw = (this.querySelector(`[data-bb-cat="${stepIdx}"]`)?.textContent || '').trim().toUpperCase();
    if (!raw) return;
    const plural = /HAWAIIAN/.test(raw) ? 'HAWAIIAN SHIRTS' : /S$/.test(raw) ? raw : `${raw}S`;
    // Ten doi DAY DU ("MORE DALLAS COWBOYS HOODIES") — chu store 2026-10-06; khoi nam trong builder nen dung ten doi duoc.
    const label = `${team.n.toUpperCase()} ${plural}`;
    const sec = document.createElement('section');
    sec.className = 'bb-exp is-loading';
    sec.dataset.bbExp = '';
    const skel = '<div class="bb-exp-card"><span class="bb-exp-card__media"></span><span class="bb-exp-card__body"><i></i><i></i></span></div>';
    sec.innerHTML =
      `<div class="bb-exp__head"><div><h3>More ${this.#esc(label)}</h3><p>Not your design? Pick another. Same 50% off deal.</p></div>` +
      '<button type="button" class="bb-exp__all-top" data-bb-exp-all>See all</button></div>' +
      `<div class="bb-exp__row">${skel.repeat(4)}</div>` +
      `<button type="button" class="bb-exp__all" data-bb-exp-all>See all ${this.#esc(label)}</button>`;
    const sticky = wrap.querySelector('[data-bb-lp-sticky]');
    if (sticky) sticky.before(sec);
    else wrap.append(sec);
    // Link nhac duoi tieu de (canh "Same design for another team") -> cuon toi khoi.
    const jump = document.createElement('a');
    jump.className = 'bb-lp__altbtn';
    jump.href = '#';
    // Hien ngay (khong cho du lieu): nut nam canh cho chon size, hien muon thi hang nut nhay.
    // Khoi bi bo (duoi 2 mau / loi) thi drop() go luon nut.
    jump.innerHTML = `<small>${this.#esc(team.n)}</small><b>More ${this.#esc(plural.toLowerCase())} &darr;</b>`;
    jump.addEventListener('click', (e) => {
      e.preventDefault();
      sec.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    const altRow = card.querySelector('[data-bb-lp-alt]');
    if (altRow) altRow.append(jump);
    else card.querySelector('.bb-card__actions')?.before(jump);
    const drop = () => {
      sec.remove();
      jump.remove();
    };
    sec.addEventListener('click', (e) => {
      if (e.target.closest('[data-bb-exp-all]')) return this.#exploreAll(stepIdx, team.n, label);
      const a = e.target.closest('[data-bb-exp-card][href]');
      if (a) this.#track('ExploreClick', { from: p.handle, design: a.dataset.handle || '' });
    });
    const load = async () => {
      let rows;
      try {
        const r = await fetch(`/collections/${encodeURIComponent(handle)}?view=bb&page=1`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        rows = ((await r.json()).results || []).filter(
          (x) => x.image && x.handle !== p.handle && (x.variants || []).some((v) => v[4])
        );
      } catch {
        return drop();
      }
      if (rows.length < 2 || !sec.isConnected) return drop();
      // Cung chu de voi mau dang xem truoc, xen ke: 2 cung chu de, 1 khac.
      const theme = (this.#themesOf({ title: p.title, tags: p.tags || [] }) || [])[0] || '';
      const same = rows.filter((x) => (this.#themesOf(x) || [])[0] === theme);
      const diff = rows.filter((x) => !same.includes(x));
      const list = [];
      while (list.length < 8 && (same.length || diff.length)) {
        for (let k = 0; k < 2 && same.length && list.length < 8; k++) list.push(same.shift());
        if (diff.length && list.length < 8) list.push(diff.shift());
      }
      const mainCents = Math.min(...(p.variants || []).map((v) => v.price));
      const base = new URL(window.location.href);
      ['type', 'team', 'hint'].forEach((k) => base.searchParams.delete(k));
      const row = sec.querySelector('.bb-exp__row');
      row.innerHTML =
        list
          .map((x) => {
            const min = Math.min(...x.variants.filter((v) => v[4]).map((v) => v[3]));
            // Gia khi la mon thu 2 — cung cong thuc thang voi gio (nhu khoi Complete the set).
            const half = this.#ladderRows([{ priceCents: mainCents }, { priceCents: min }]).reduce((a, y) => a + y.pay, 0) - mainCents;
            const th = (this.#themesOf(x) || [])[0];
            const tag = th && this.themeMeta[th] ? this.themeMeta[th] : '';
            const code = String(x.title || '').trim().split(/\s+/).pop() || '';
            base.searchParams.set('design', x.handle);
            const img = httpsUrl(x.image);
            return (
              `<a class="bb-exp-card" data-bb-exp-card data-handle="${this.#esc(x.handle)}" href="${this.#esc(base.pathname + base.search)}">` +
              `<span class="bb-exp-card__media"><img src="${this.#esc(this.#sizeUrl(img, 400) || img)}" alt="${this.#esc(x.title || '')}" width="400" height="400" loading="lazy" decoding="async"></span>` +
              '<span class="bb-exp-card__body">' +
              `<span class="bb-exp-card__tag">${this.#esc(tag)}</span><span class="bb-exp-card__code">${this.#esc(code)}</span>` +
              `<span class="bb-exp-card__price">${money(min)}` +
              (half > 0 && half < min ? `<b data-bb-exp-2nd> &middot; ${money(half)} as 2nd</b>` : '') +
              '</span></span></a>'
            );
          })
          .join('') +
        `<button type="button" class="bb-exp-more" data-bb-exp-all>See all ${this.#esc(label)}<span aria-hidden="true">&rarr;</span></button>`;
      sec.classList.remove('is-loading');
      this.#syncSetCards();
      this.#track('ExploreShown', { design: p.handle, n: list.length });
    };
    if (!('IntersectionObserver' in window)) return load();
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        load();
      },
      { rootMargin: '600px 0px' }
    );
    io.observe(sec);
  }

  /** Ten so nhieu cua loai ao tren trang ("HOODIE" -> "hoodies", "T-SHIRT" -> "tees"). */
  #typePlural(label) {
    const k = String(label || '').trim().toLowerCase();
    const map = { 't-shirt': 'tees', hawaiian: 'Hawaiian shirts', sweatpants: 'sweatpants', 'v-neck': 'V-necks' };
    return map[k] || (k.endsWith('s') ? k : `${k}s`);
  }

  /** "hoodies, tees, polos & more" — tu chinh hang loai ao cua trang (them loai la tu cap nhat). */
  #typesBlurb() {
    const names = [...this.querySelectorAll('[data-bb-cat]')].map((el) => this.#typePlural(el.textContent));
    const pick = ['hoodies', 'tees', 'polos', 'caps'].filter((n) => names.includes(n));
    const list = (pick.length >= 2 ? pick : names).slice(0, 4);
    if (!list.length) return 'every item';
    return `${list.join(', ')} & more`;
  }

  /**
   * Landing v2: bộ đã có món -> dải trên thành "YOUR 50% OFF IS UNLOCKED" + nút xem bộ (đếm món),
   * vì món đang xem chỉ còn nửa giá. Gọi lúc dựng landing và mỗi lần vẽ lại thẻ (thêm/gỡ món).
   */
  #syncLpStrip() {
    const strip = this.querySelector('[data-bb-spot-wrap] .bb-lp__strip');
    const n = this.#target().filter(Boolean).length;
    if (!strip || !n || strip.dataset.n === String(n)) return;
    strip.dataset.n = String(n);
    const view = `<button type="button" class="bb-lp__strip-btn" data-bb-ladder-add>View my bundle (${n}) &rarr;</button>`;
    strip.classList.add('bb-lp__strip--up');
    strip.innerHTML =
      '<b>&#10003; YOUR 50% OFF IS UNLOCKED</b>' +
      `<span class="bb-lp__strip-m">This item is half price &middot; ${view}</span>` +
      `<span class="bb-lp__strip-d">&middot; this item is half price &middot; ${view}</span>`;
  }

  /** Chưa chọn size: cuộn tới hàng size + nháy viền xanh. */
  #lpNeedSize(card) {
    const head = card.querySelector('.bb-lp__sizehead') || card.querySelector('.bb-card__pills');
    head?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const pills = card.querySelector('.bb-card__pills');
    if (pills) {
      pills.classList.remove('bb-lp-flash');
      void pills.offsetWidth; // chay lai animation moi lan bam
      pills.classList.add('bb-lp-flash');
    }
    const sizeEl = card.querySelector('[data-bb-lp-size]');
    if (sizeEl) sizeEl.textContent = 'Please select a size';
    this.#track('LandingNeedSize', {});
  }

  /** Loại (slug pattern) đầu tiên của step có bảng size text trong trang; '' nếu không có. */
  #chartTypeOf(stepEl) {
    if (!stepEl) return '';
    const slugs = (stepEl.dataset.brandPattern || '').split(',').map((x) => x.trim().replace(/\{brand\}-?/g, '')).filter(Boolean);
    return slugs.find((t) => document.querySelector(`template[data-bb-sizechart~="${t}"]`)) || '';
  }

  /** Popup bảng size HTML: nội dung từ <template data-bb-sizechart="<loại>"> trong trang. */
  #openSizeChart(type) {
    const tpl = document.querySelector(`template[data-bb-sizechart~="${type}"]`);
    if (!tpl) return;
    let m = document.querySelector('[data-bb-sc]');
    if (!m) {
      m = document.createElement('div');
      m.className = 'bb-sc';
      m.dataset.bbSc = '';
      m.setAttribute('role', 'dialog');
      m.setAttribute('aria-modal', 'true');
      m.setAttribute('aria-label', 'Size guide');
      m.innerHTML =
        '<div class="bb-sc__bg" data-bb-sc-close></div><div class="bb-sc__panel">' +
        '<button type="button" class="bb-sc__x" data-bb-sc-close aria-label="Close">&times;</button>' +
        '<div class="bb-sc__body"></div></div>';
      document.body.append(m);
      const close = () => {
        m.classList.remove('is-open');
        document.documentElement.classList.remove('bb-sc-lock');
      };
      m.addEventListener('click', (e) => {
        if (e.target.closest('[data-bb-sc-close]')) return close();
        const u = e.target.closest('[data-sc-unit]');
        if (u) {
          m.querySelectorAll('[data-sc-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b === u)));
          m.querySelector('.bb-sc__body').dataset.unit = u.dataset.scUnit;
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && m.classList.contains('is-open')) close();
      });
    }
    const body = m.querySelector('.bb-sc__body');
    body.replaceChildren(tpl.content.cloneNode(true));
    body.dataset.unit = 'in';
    m.classList.add('is-open');
    document.documentElement.classList.add('bb-sc-lock');
    this.#track('SizeChartOpen', { type });
  }

  /**
   * Hang crawl: khoi "EXPLORE ALL <DOI> PRODUCTS" thay cho "More <doi> hoodies" — moi LOAI ao cua
   * doi tren trang (hoodie, tee, polo, V-neck, cap...), the = anh mau dau collection {doi}-{loai} +
   * gia "as 2nd". Bam -> builder dung loai + doi (nhu "See all"). Chu store 2026-10-15: cho khach
   * thay store co nhieu loai, khong chi hoodie. Tai anh khi khoi sap vao man (1 request / loai).
   */
  #buildTeamTypes(card, p) {
    const wrap = card.closest('[data-bb-spot-wrap]');
    const team = this.teamMeta[String(p.handle || '').split('-')[0]];
    if (!wrap || !team) return;
    const types = this.stepEls
      .map((el, i) => ({ el, i, label: (this.querySelector(`[data-bb-cat="${i}"]`)?.textContent || '').trim() }))
      .filter((t) => t.label && this.#directHandle(t.el, team.n));
    if (!types.length) return;
    const label = `${team.n.toUpperCase()} PRODUCTS`;
    const sec = document.createElement('section');
    sec.className = 'bb-exp bb-exp--types is-loading';
    sec.dataset.bbExp = '';
    const skel = '<div class="bb-exp-card"><span class="bb-exp-card__media"></span><span class="bb-exp-card__body"><i></i><i></i></span></div>';
    sec.innerHTML =
      `<div class="bb-exp__head"><div><h3>Explore all ${this.#esc(label)}</h3>` +
      `<p>${types.length} product types &middot; every item after your first is 50% off.</p></div></div>` +
      `<div class="bb-exp__row">${skel.repeat(Math.min(4, types.length))}</div>` +
      '<button type="button" class="bb-exp__all" data-bb-exp-cats>See all categories &middot; 50% off</button>';
    const sticky = wrap.querySelector('[data-bb-lp-sticky]');
    if (sticky) sticky.before(sec);
    else wrap.append(sec);
    const jump = document.createElement('a');
    jump.className = 'bb-lp__altbtn bb-lp__altbtn--go';
    jump.href = '#';
    jump.innerHTML = `<small>${this.#esc(team.n)}</small><b>Explore all products &darr;</b>`;
    jump.addEventListener('click', (e) => {
      e.preventDefault();
      this.#track('TeamTypesJump', { team: team.n });
      sec.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    card.querySelector('[data-bb-lp-alt]')?.append(jump);
    sec.addEventListener('click', (e) => {
      if (e.target.closest('[data-bb-exp-cats]')) {
        this.#track('TeamTypesAll', { team: team.n });
        return this.#showAllCats();
      }
      const t = e.target.closest('[data-bb-type-step]');
      if (!t) return;
      const i = parseInt(t.dataset.bbTypeStep, 10);
      this.#track('TeamTypesClick', { team: team.n, type: t.dataset.label || '' });
      this.#exploreAll(i, team.n, t.dataset.label || '');
    });
    const load = async () => {
      const list = await this.#teamTypeRows();
      if (!list.length || !sec.isConnected) {
        sec.remove();
        jump.remove();
        return;
      }
      sec.querySelector('.bb-exp__head p').innerHTML =
        `${list.length} product types &middot; every item after your first is 50% off.`;
      sec.querySelector('.bb-exp__row').innerHTML = this.#typeCardsHtml(list);
      sec.classList.remove('is-loading');
      this.#syncSetCards();
      this.#track('TeamTypesShown', { team: team.n, n: list.length });
    };
    if (!('IntersectionObserver' in window)) return load();
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        load();
      },
      { rootMargin: '600px 0px' }
    );
    io.observe(sec);
  }

  /** Mau dau tien con hang cua tung loai ao (collection {doi}-{loai}) — 1 request/loai, nho theo phien trang. */
  #teamTypeRows() {
    const C = this._lpCtx;
    if (!C) return Promise.resolve([]);
    if (!C.rows) {
      C.rows = Promise.all(
        C.types.map(async (t) => {
          try {
            const r = await fetch(`/collections/${encodeURIComponent(this.#directHandle(t.el, C.team.n))}?view=bb&page=1`);
            if (!r.ok) return null;
            const x = ((await r.json()).results || []).find((y) => y.image && (y.variants || []).some((v) => v[4]));
            return x ? { t, x } : null;
          } catch {
            return null;
          }
        })
      ).then((rows) => rows.filter(Boolean));
    }
    return C.rows;
  }

  /** The loai ao (anh + "from $X · $Y as 2nd"); bam -> builder dung loai + doi. */
  #typeCardsHtml(list) {
    const { team, mainCents } = this._lpCtx;
    return list
      .map(({ t, x }) => {
        const min = Math.min(...x.variants.filter((v) => v[4]).map((v) => v[3]));
        const half = this.#ladderRows([{ priceCents: mainCents }, { priceCents: min }]).reduce((a, y) => a + y.pay, 0) - mainCents;
        const img = httpsUrl(x.image);
        return (
          `<button type="button" class="bb-exp-card" data-bb-type-step="${t.i}" data-label="${this.#esc(t.label)}">` +
          `<span class="bb-exp-card__media"><img src="${this.#esc(this.#sizeUrl(img, 400) || img)}" alt="${this.#esc(`${team.n} ${t.label}`)}" width="400" height="400" loading="lazy" decoding="async"></span>` +
          '<span class="bb-exp-card__body">' +
          `<span class="bb-exp-card__code">${this.#esc(this.#typePlural(t.label).toUpperCase())}</span>` +
          `<span class="bb-exp-card__price">from ${money(min)}` +
          (half > 0 && half < min ? `<b data-bb-exp-2nd> &middot; ${money(half)} as 2nd</b>` : '') +
          '</span></span></button>'
        );
      })
      .join('');
  }

  /**
   * "SEE ALL CATEGORIES": MAN CHON LOAI AO truoc (chu store 2026-10-15 — truoc day nhay thang vao
   * luoi hoodie). Van o trong khung landing: an the san pham, hien luoi loai + nut quay lai san pham.
   * Chon loai -> builder dung loai + doi. Nut Back cua trinh duyet cung dong man nay.
   */
  #showAllCats() {
    const C = this._lpCtx;
    const wrap = this.querySelector('[data-bb-spot-wrap]');
    if (!C || !this.landing || !wrap) {
      if (this.landing) this.#endLanding();
      const top = this.querySelector('.bb-cats') || this.stepEls[this.state.current];
      setTimeout(() => top?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
      return;
    }
    wrap.querySelector('[data-bb-typ]')?.remove();
    const box = document.createElement('section');
    box.className = 'bb-typ';
    box.dataset.bbTyp = '';
    const skel = '<div class="bb-exp-card"><span class="bb-exp-card__media"></span><span class="bb-exp-card__body"><i></i><i></i></span></div>';
    box.innerHTML =
      `<button type="button" class="bb-lpback" data-bb-typ-back>&larr; Back to ${this.#esc(C.title || 'your design')}</button>` +
      `<h2 class="bb-typ__title">Shop all ${this.#esc(C.team.n)} products</h2>` +
      '<p class="bb-typ__sub">Pick a product type. Every item after your first is 50% off &mdash; mix any.</p>' +
      `<div class="bb-typ__grid">${skel.repeat(C.types.length)}</div>`;
    wrap.classList.add('is-typ');
    wrap.append(box);
    window.scrollTo({ top: 0, behavior: 'instant' });
    try {
      window.history.pushState({ bbTyp: 1 }, '', window.location.href);
    } catch {}
    this._typOpen = true;
    box.addEventListener('click', (e) => {
      if (e.target.closest('[data-bb-typ-back]')) {
        this.#track('TypePickBack', {});
        return window.history.state?.bbTyp ? window.history.back() : this.#closeTypePicker();
      }
      const t = e.target.closest('[data-bb-type-step]');
      if (!t) return;
      this.#track('TypePickClick', { team: C.team.n, type: t.dataset.label || '' });
      this.#closeTypePicker();
      this.#exploreAll(parseInt(t.dataset.bbTypeStep, 10), C.team.n, t.dataset.label || '');
    });
    this.#teamTypeRows().then((list) => {
      if (!box.isConnected) return;
      box.querySelector('.bb-typ__grid').innerHTML = this.#typeCardsHtml(list);
      this.#syncSetCards();
    });
    this.#track('TypePickShown', { team: C.team.n });
  }

  /** Dong man chon loai, tra lai the san pham (giu nguyen size/so luong khach da chon). */
  #closeTypePicker() {
    this._typOpen = false;
    const wrap = this.querySelector('[data-bb-spot-wrap]');
    wrap?.querySelector('[data-bb-typ]')?.remove();
    wrap?.classList.remove('is-typ');
  }

  /**
   * Nut "Back to <san pham>" o dau builder sau khi roi landing (hang crawl): dung lai DUNG khung
   * landing cu (the san pham con nguyen, size/so luong da chon), bo da co mon van giu.
   */
  #restoreLanding() {
    const wrap = this._lpWrap;
    if (!wrap || this.landing) return;
    this.querySelector('[data-bb-lpback-bar]')?.remove();
    this.landing = true;
    this.classList.add('bb-landing');
    this.prepend(wrap);
    this.#closeTypePicker();
    this.#syncContentVisibility(false);
    const card = wrap.querySelector('[data-bb-spot]');
    if (card) this.#refreshCard(card);
    this.#barUpdate();
    window.scrollTo({ top: 0, behavior: 'instant' });
    this.#track('LandingRestore', {});
  }

  /** "See all DAL hoodies": roi landing, mo builder dung step + doi, chu de "All". */
  #exploreAll(stepIdx, teamName, label) {
    this.#track('ExploreAllClick', { label });
    const stepEl = this.stepEls[stepIdx];
    if (!stepEl) return;
    if (this.state.current !== stepIdx) this.#stepTo(stepIdx, false);
    if (stepEl.dataset.brand !== teamName) this.#selectBrand(stepEl, teamName, false);
    if (stepEl.querySelector('[data-bb-themechip][data-theme="all"]')) this.#selectTheme(stepEl, 'all', false);
    if (this.landing) this.#endLanding();
    else this.#scrollToStep(stepEl);
  }

  /**
   * Thanh mua DINH DAY tren mobile cho trang landing: trang dai (chat vai, bang gia, ~40 review,
   * FAQ) -> qua man dau la mat nut ADD. Chi hien khi nut ADD cua the da troi LEN khoi man hinh;
   * bo da co mon thi CSS giau (thanh day .bb-bar cua builder thay cho).
   * Bam: chua chon size -> cuon ve hang size + nhay vien; da chon -> bam dung nut ADD cua the.
   */
  /** Chua chon size: cuon toi hang size + nhay vien xanh (dung chung cho thanh day va nut ADD hang crawl). */
  #flashSizes(card) {
    const head = card.querySelector('.bb-lp__sizehead') || card.querySelector('.bb-card__pills');
    head?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const pills = card.querySelector('.bb-card__pills');
    if (pills) {
      pills.classList.remove('bb-lp-flash');
      void pills.offsetWidth; // chay lai animation moi lan bam
      pills.classList.add('bb-lp-flash');
    }
  }

  /**
   * Noi dung trang ben duoi (Liquid, viet cho 7 loai ao cua store) khop voi hang crawl: an khoi
   * "Size guide" (bang size hoodie — sai loai), bo FAQ ve ban in (chua kiem), viet lai FAQ size theo
   * bang size cua chinh san pham; nut cuoi trang = "ADD TO CART" nhu nut chinh. Chu store 2026-10-15.
   */
  #crawlPageContent() {
    document.documentElement.dataset.bbLpCrawl = '';
    document.querySelectorAll('.lp-faq details').forEach((d) => {
      const q = (d.querySelector('summary')?.textContent || '').toLowerCase();
      if (/peel|crack/.test(q)) d.remove();
      else if (/sizing/.test(q)) {
        const p = d.querySelector('p');
        if (p)
          p.textContent =
            'Sizes S–5XL — check the size chart in the product photos and in Product details above before you order. ' +
            'Production 5–7 business days, US shipping 5–7 business days, free over $120. Size exchanges within 180 days; ' +
            'damaged or missing items get a full refund or free replacement.';
      }
    });
    document.querySelectorAll('.lp-cta [data-usp-scroll]').forEach((b) => {
      b.innerHTML = 'ADD TO CART &uarr;';
    });
  }

  #buildLandingSticky(card) {
    const wrap = card.closest('[data-bb-spot-wrap]');
    const actions = card.querySelector('.bb-card__actions');
    if (!wrap || !actions || !('IntersectionObserver' in window)) return;
    const bar = document.createElement('div');
    bar.className = 'bb-lp-sticky';
    bar.dataset.bbLpSticky = '';
    const img = card.querySelector('.bb-lp__slide img');
    const title = (card.querySelector('.bb-card__title')?.textContent || '').trim();
    bar.innerHTML =
      (img ? `<img src="${this.#esc(this.#sizeUrl(img.src, 200) || img.src)}" alt="" width="44" height="44">` : '') +
      `<div class="bb-lp-sticky__t"><b>${this.#esc(title)}</b><span data-bb-lp-sticky-p></span></div>` +
      '<button type="button" class="bb-lp-sticky__btn" data-bb-lp-sticky-btn></button>';
    wrap.append(bar);
    bar.querySelector('[data-bb-lp-sticky-btn]').addEventListener('click', () => {
      const ready = !!this.#resolveVariant(card);
      this.#track('LandingStickyClick', { ready: ready ? 1 : 0 });
      if (ready) return card.querySelector('[data-bb-add]')?.click();
      this.#flashSizes(card);
    });
    const io = new IntersectionObserver(([e]) => {
      // Hien moi khi nut ADD khong nam trong man: da troi len, HOAC chua cuon toi (man dau mobile
      // chi thay anh + tieu de, hang size o duoi) -> luc nao cung co nut mua trong tam tay.
      bar.classList.toggle('is-on', !e.isIntersecting);
    });
    io.observe(actions);
  }

  /**
   * LINK THEO MA DESIGN: ?design=SCH002 (+ &type=<loai>, mac dinh hoodie).
   * Quang cao chay theo MAU, khong theo doi -> khach vao la thay ngay luoi 32 doi; bam doi
   * nao thi mo trang landing san pham (y het link ?design=<handle>) cua mau do, doi do.
   * Loai ao = step co pattern {brand}-<type> (khop DUNG truoc, roi khop mot phan: "polo").
   * Them &team=KC -> bo qua man chon doi (link chia se / F5 sau khi da chon).
   */
  #initDesignCode(code) {
    let want = 'hoodie';
    try {
      want = (new URLSearchParams(window.location.search).get('type') || 'hoodie').toLowerCase().trim();
    } catch {}
    const slugs = this.stepEls.map((el) =>
      (el.dataset.brandPattern || '').split(',').map((x) => x.trim().replace(/\{brand\}-?/g, '')).filter(Boolean)
    );
    const crawl = code.startsWith('cr-');
    let step = slugs.findIndex((list) => list.includes(want));
    if (step < 0) step = slugs.findIndex((list) => list.some((x) => x.includes(want)));
    let type = step < 0 ? '' : slugs[step].find((x) => x === want) || slugs[step].find((x) => x.includes(want));
    if (step < 0 && crawl && /^[a-z0-9-]{2,60}$/.test(want)) {
      // Hang crawl loai moi (cardigan, sweater...): handle {doi}-{loai}-{ma}; the dat o step hoodie.
      step = Math.max(0, slugs.findIndex((list) => list.includes('hoodie')));
      type = want;
    }
    if (step < 0 || !type) return;
    document.documentElement.dataset.bbLpType = type;
    this.codeLink = { code, type, step, cache: {}, crawl };
    this.deep = { handle: '', step };
    // Luon o che do landing khi dang chon doi: chi hien man chon doi + noi dung ben duoi.
    this.landing = true;
    this.classList.add('bb-landing');
    const shell = document.createElement('div');
    shell.className = 'bb-spot bb-spot--loading';
    shell.dataset.bbSpotWrap = '';
    shell.innerHTML = '<div class="bb-spot__skel"></div>';
    this.prepend(shell);
  }

  /** Handle cua mau cho 1 doi. Handle co/khong co chu de (kc-hunting-hoodie-sch002) -> thu lan luot. */
  async #resolveDesignCode(slug) {
    const L = this.codeLink;
    if (L.cache[slug] !== undefined) return L.cache[slug];
    const out = await this.#findDesign(slug, L.type, L.code);
    L.cache[slug] = out;
    return out;
  }

  /** San pham cua (doi, loai ao, ma design); null neu khong co / het hang. Co cache theo phien trang. */
  async #findDesign(slug, type, code, exact = '') {
    if (!this._designCache) this._designCache = {};
    const key = `${slug}|${type}|${code}`;
    if (key in this._designCache) return this._designCache[key];
    const m = code.toUpperCase().match(/^([A-Z]+)/);
    let theme = (m && this.themeCodes[m[1]]) || '';
    if (!theme) {
      // Mã chưa gắn chủ đề (vd CSC153 hunting cap: handle kc-hunting-cap-csc153) -> thử chủ đề mặc định
      // (theme_default) của bước loại áo đó trên trang. Thêm 2026-10-21 cho landing v2 trang hunting cap.
      const stepEl = this.stepEls.find((el) =>
        (el.dataset.brandPattern || '').split(',').some((x) => x.trim().replace(/\{brand\}-?/g, '') === type)
      );
      const def = stepEl?.querySelector('[data-bb-themebar]')?.dataset.defaultTheme || '';
      if (def && def !== 'all') theme = def;
    }
    // `exact`: handle doan chac (cung khuon voi san pham chinh) -> chi thu dung 1 lan, khong do
    // them: loai khong co mau nay (vd sweatpants SCH002) chi ton 1 request 404 thay vi 3.
    const tries = exact ? [exact] : [...new Set([
      theme && `${slug}-${theme}-${type}-${code}`,
      `${slug}-${type}-${code}`,
      theme && `${slug}-${type}-${theme}-${code}`,
    ].filter(Boolean))];
    let out = null;
    for (const h of tries) {
      try {
        // KHONG gui Accept: application/json (xem #loadUpsellMatch).
        const r = await fetch(`/products/${h}.js`);
        if (!r.ok) continue;
        const p = await r.json();
        if ((p.variants || []).some((v) => v.available)) out = { handle: p.handle || h, p };
        break;
      } catch {
        /* thu handle ke */
      }
    }
    this._designCache[key] = out;
    return out;
  }

  /** Vao trang: co &team hop le -> mo thang landing; khong thi man chon doi. */
  async #startDesignCode() {
    const L = this.codeLink;
    // Back tu landing ve man chon doi (pushState) -> dung lai trang tu URL.
    // CHI khi doi tren URL doi (Back tu landing ve man chon doi). Link neo trong trang (#lp-fabric-...,
    // #lp-reviews) cung ban popstate -> truoc day bam "More" o dong Fabric la tai lai ca trang.
    window.addEventListener(
      'popstate',
      () => {
        // Hang crawl: Back tu man chon loai -> ve the san pham; Back tu builder -> ve lai landing.
        if (this._typOpen) return this.#closeTypePicker();
        if (!this.landing && this._lpWrap) return this.#restoreLanding();
        let t = '';
        try {
          t = (new URLSearchParams(window.location.search).get('team') || '').toLowerCase();
        } catch {}
        if (t !== (L.openTeam || '')) window.location.reload();
      },
      { signal: this.#abort.signal }
    );
    let q = '';
    try {
      q = (new URLSearchParams(window.location.search).get('team') || '').toLowerCase();
    } catch {}
    if (q && this.teamMeta[q]) {
      const r = await this.#resolveDesignCode(q);
      if (r) {
        this.#pushRecentTeam(this.teamMeta[q].n);
        return this.#openDesignCode(q, r);
      }
    }
    this.#showTeamPick();
    this.#track('DesignCodeShown', { design: L.code, type: L.type });
  }

  #showTeamPick() {
    const L = this.codeLink;
    const recent = this.#recentTeams().map((n) => this.#slugifyBrand(n)).filter((k) => this.teamMeta[k]);
    const all = Object.keys(this.teamMeta).sort((a, b) => this.teamMeta[a].n.localeCompare(this.teamMeta[b].n));
    // &hint=DAL: doi trong anh quang cao -> lam anh mau + dung dau (khach van phai tu bam chon).
    let hint = '';
    try {
      hint = (new URLSearchParams(window.location.search).get('hint') || '').toLowerCase();
    } catch {}
    const order = [...new Set([...(this.teamMeta[hint] ? [hint] : []), ...recent, ...all])];
    const label = (!L.crawl && this.querySelectorAll('[data-bb-cat]')[L.step]?.textContent.trim()) || L.type.replace(/-/g, ' ').toUpperCase();
    const logoOnly = !!this.querySelector('.bb-brandpicker--logo');
    // "team" / "school" (college) — data-brand-noun do core đặt theo team_source.
    const noun = this.dataset.brandNoun || 'team';
    const wrap = document.createElement('div');
    wrap.className = 'bb-spot bb-tp';
    wrap.dataset.bbSpotWrap = '';
    wrap.innerHTML =
      `<div class="bb-lp__strip"><b>${this.#esc(this.dataset.deeplinkStrip || 'BUY 1, GET 50% OFF EVERY ITEM AFTER')}</b>` +
      '<span class="bb-lp__strip-m">Auto-applied at checkout &mdash; no code</span>' +
      '<span class="bb-lp__strip-d">&middot; applied automatically at checkout &mdash; no code</span></div>' +
      '<div class="bb-tp__head">' +
      '<div class="bb-tp__media"><img alt="" width="600" height="600" hidden><span class="bb-tp__shown" hidden></span></div>' +
      '<div class="bb-tp__intro">' +
      `<p class="bb-tp__kicker">${this.#esc(label)} &middot; ${this.#esc(L.code.toUpperCase())}</p>` +
      `<h1 class="bb-tp__title">${this.#esc(this.dataset.designcodeTitle || `Choose your ${noun}`)}</h1>` +
      `<p class="bb-tp__sub">${this.#esc(this.dataset.designcodeSub || `Made for every ${noun}. Tap yours to see this design in your colors.`)}</p>` +
      // lp_more_designs: về bộ chọn đội của trang (không ?design) -> chọn đội -> lưới mọi design của loại này.
      (this.dataset.lpMore
        ? `<a class="bb-tp__more" href="${this.#esc(window.location.pathname)}#choose-team" data-bb-tp-more>Show more ${this.#esc(label.toLowerCase())} designs &rarr;</a>`
        : '') +
      '</div></div>' +
      '<p class="bb-tp__msg" role="status" hidden></p>' +
      `<div class="bb-tp__find"><input type="search" placeholder="Search your ${noun}" aria-label="Search your ${noun}" autocomplete="off"` +
      ' role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="bb-tp-sug" data-bb-tp-find>' +
      '<div class="bb-tp__sug" id="bb-tp-sug" role="listbox" hidden></div></div>' +
      '<div class="bb-tp__grid" role="list">' +
      order
        .map((k, i) => {
          const t = this.teamMeta[k];
          // Trang bật "chỉ logo" (picker_logo_only): ô có logo thì ẩn tên (vẫn còn trong aria-label + data-q cho ô tìm).
          const lo = logoOnly && t.l;
          return (
            `<button type="button" class="bb-tp__team${lo ? ' bb-tp__team--logo' : ''}" role="listitem" data-bb-tp-team="${this.#esc(k)}" data-q="${this.#esc(`${k} ${t.n}`.toLowerCase())}"` +
            (lo ? ` aria-label="${this.#esc(t.n)}"` : '') +
            (t.c ? ` style="--tp-c:${this.#esc(t.c)}"` : '') +
            '>' +
            // 12 o dau nam trong man dau mobile -> tai ngay (lazy de o trong mot nhip).
            (t.l ? `<img src="${this.#esc(t.l)}" alt="" width="64" height="64" loading="${i < 12 ? 'eager' : 'lazy'}" decoding="async">` : '') +
            `<span>${this.#esc(t.n)}</span></button>`
          );
        })
        .join('') +
      '</div>';
    const shell = this.querySelector('[data-bb-spot-wrap]');
    if (shell) shell.replaceWith(wrap);
    else this.prepend(wrap);
    // Co man chon doi -> noi dung trang ("Pick your size", nut "PICK MY SIZE") doi chu theo (CSS trong template).
    document.documentElement.dataset.bbTp = '';
    wrap.addEventListener('click', (e) => {
      const b = e.target.closest('[data-bb-tp-team]');
      if (b) this.#pickDesignTeam(b);
    });
    this.#teamSuggest(wrap, order);
    // Anh mau: doi khach chon gan nhat, khong co thi doi dau danh sach.
    const sample = order[0];
    this.#resolveDesignCode(sample).then((r) => {
      if (!r || !wrap.isConnected) return;
      const img = wrap.querySelector('.bb-tp__media img');
      const src = this.#sizeUrl(httpsUrl(r.p.featured_image || ''), window.innerWidth < 750 ? 900 : 1200);
      if (!src) return;
      img.src = src;
      img.hidden = false;
      const shown = wrap.querySelector('.bb-tp__shown');
      shown.textContent = `Shown: ${this.teamMeta[sample].n}`;
      shown.hidden = false;
      wrap.querySelector('.bb-tp__head').classList.add('bb-tp__head--img');
    });
  }

  /**
   * O "Search your team": go la hien DROPDOWN goi y ngay duoi o (chu store 2026-10-06), luoi 32 doi
   * giu nguyen. Khop dau tu truoc (ma "kc", "dal"; tu dau ten "kan", "cow"), roi khop giua chu.
   * Toi da 6 dong; mui ten len/xuong + Enter; Esc / bam ra ngoai de dong. Bam dong = bam o doi.
   */
  #teamSuggest(wrap, order) {
    const find = wrap.querySelector('[data-bb-tp-find]');
    const box = wrap.querySelector('.bb-tp__sug');
    if (!find || !box) return;
    let cur = -1;
    const close = () => {
      box.hidden = true;
      find.setAttribute('aria-expanded', 'false');
      cur = -1;
    };
    const mark = (i) => {
      const items = box.querySelectorAll('[data-bb-tp-sug]');
      cur = items.length ? (i + items.length) % items.length : -1;
      items.forEach((b, j) => b.setAttribute('aria-selected', j === cur ? 'true' : 'false'));
      items[cur]?.scrollIntoView({ block: 'nearest' });
    };
    const pick = (k) => {
      const btn = wrap.querySelector(`[data-bb-tp-team="${CSS.escape(k)}"]`);
      close();
      if (btn) this.#pickDesignTeam(btn);
    };
    const render = () => {
      const q = find.value.trim().toLowerCase();
      if (!q) return close();
      const score = (k) => {
        const n = this.teamMeta[k].n.toLowerCase();
        if (k === q) return 0;
        if (k.startsWith(q) || n.startsWith(q)) return 1;
        if (n.split(/\s+/).some((w) => w.startsWith(q))) return 2;
        return n.includes(q) ? 3 : 9;
      };
      const hits = order.map((k) => [k, score(k)]).filter((x) => x[1] < 9).sort((a, b) => a[1] - b[1]).slice(0, 6);
      box.innerHTML = hits.length
        ? hits
            .map(([k]) => {
              const t = this.teamMeta[k];
              return (
                `<button type="button" role="option" aria-selected="false" class="bb-tp__sugitem" data-bb-tp-sug="${this.#esc(k)}">` +
                (t.l ? `<img src="${this.#esc(t.l)}" alt="" width="32" height="32">` : '<i></i>') +
                `<span>${this.#esc(t.n)}</span><em>${this.#esc(k.toUpperCase())}</em></button>`
              );
            })
            .join('')
        : this.dataset.brandNoun === 'school'
          ? '<p class="bb-tp__sugnone">No school matches that. Try a school name or code (e.g. ALA, TENN).</p>'
          : '<p class="bb-tp__sugnone">No team matches that. Try a city or team code (e.g. KC, DAL).</p>';
      box.hidden = false;
      find.setAttribute('aria-expanded', 'true');
      mark(hits.length ? 0 : -1);
    };
    find.addEventListener('input', render);
    find.addEventListener('focus', () => find.value.trim() && render());
    find.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') return close();
      if (box.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        mark(cur + (e.key === 'ArrowDown' ? 1 : -1));
      } else if (e.key === 'Enter') {
        const it = box.querySelectorAll('[data-bb-tp-sug]')[cur];
        if (it) {
          e.preventDefault();
          pick(it.dataset.bbTpSug);
        }
      }
    });
    // pointerdown: chon TRUOC khi o mat focus (blur tren mobile dong ban phim lam dropdown nhay).
    box.addEventListener('pointerdown', (e) => {
      const it = e.target.closest('[data-bb-tp-sug]');
      if (!it) return;
      e.preventDefault();
      pick(it.dataset.bbTpSug);
    });
    document.addEventListener(
      'pointerdown',
      (e) => {
        if (!box.hidden && !e.target.closest('.bb-tp__find')) close();
      },
      { signal: this.#abort.signal }
    );
  }

  async #pickDesignTeam(btn) {
    const L = this.codeLink;
    if (L.busy) return;
    const slug = btn.dataset.bbTpTeam;
    const wrap = btn.closest('[data-bb-spot-wrap]');
    const msg = wrap.querySelector('.bb-tp__msg');
    L.busy = true;
    btn.classList.add('is-loading');
    msg.hidden = true;
    const r = await this.#resolveDesignCode(slug);
    L.busy = false;
    btn.classList.remove('is-loading');
    if (!r) {
      msg.textContent = `This design isn't available for ${this.teamMeta[slug].n} right now. Please pick another ${this.dataset.brandNoun || 'team'}.`;
      msg.hidden = false;
      btn.disabled = true;
      return;
    }
    this.#track('DesignCodeTeam', { design: L.code, team: slug.toUpperCase() });
    this.#pushRecentTeam(this.teamMeta[slug].n);
    // URL co &team -> F5 / chia se mo dung doi; nut Back cua trinh duyet ve man chon doi.
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('team', slug.toUpperCase());
      window.history.pushState({ bbTeam: slug }, '', u);
    } catch {}
    this.#openDesignCode(slug, r);
  }

  /** Da co doi -> chay dung luong landing cua link ?design=<handle>. */
  #openDesignCode(slug, r) {
    const L = this.codeLink;
    const name = this.teamMeta[slug].n;
    L.openTeam = slug;
    delete document.documentElement.dataset.bbTp;
    this.deep = { handle: r.handle, step: L.step };
    this._deepPrefetch = r;
    this.selectedBrand = name;
    const stepEl = this.stepEls[L.step];
    if (stepEl && stepEl.dataset.brand !== name) this.#selectBrand(stepEl, name, false);
    // LUON la trang landing, ke ca khi bo da co mon (khach Back / "Other team" roi chon doi khac):
    // truoc day bo co mon -> roi vao builder (khoi mau trong step, chip chu de, luoi) — chu store
    // 2026-10-15: doi doi nao cung phai ra dung trang san pham nhu lan dau. Mon da co van giu
    // (thanh day hien vi .bb-has-items), the gia tu doi sang "ANOTHER <LOAI> · 50% OFF".
    const old = this.querySelector('[data-bb-spot-wrap]');
    this.landing = true;
    this.classList.add('bb-landing');
    this.#syncContentVisibility(false);
    const shell = document.createElement('div');
    shell.className = 'bb-spot bb-spot--loading';
    shell.dataset.bbSpotWrap = '';
    shell.innerHTML = '<div class="bb-spot__skel"></div>';
    if (old) old.replaceWith(shell);
    else this.prepend(shell);
    window.scrollTo({ top: 0, behavior: 'instant' });
    this.#loadDeeplink();
  }

  /** Link hong / mau het hang: bo khung cho, tra ve builder binh thuong. */
  #dropDeeplink() {
    this.querySelector('[data-bb-spot-wrap]')?.remove();
    this.#endLanding();
  }

  /** Khach vua ADD mon tu trang landing -> mo builder day du, dua khoi mau ve step cua no. */
  #endLanding() {
    if (!this.landing) return;
    this.landing = false;
    this.classList.remove('bb-landing');
    const wrap = this.querySelector('[data-bb-spot-wrap]');
    const stepEl = this.deep && this.stepEls[this.deep.step];
    // Mon da vao bo (hien o thanh day) -> go khoi anh to de khach vao thang luoi nhu trang
    // offer thuong. Go NGAY, TRUOC khi cuon: khoi cao ~1 man, go sau khi da tinh vi tri
    // cuon thi ca trang don len va khach roi vao khoang trang. #addItem van doc duoc the
    // da tach khoi trang (dataset, nut size van con tren phan tu).
    if (wrap) wrap.remove();
    // Hang crawl: giu khung landing de quay lai duoc (nut "Back to <san pham>" + nut Back trinh duyet).
    if (wrap && this.#lpV2()) {
      this._lpWrap = wrap;
      this.#addLandingBack();
      try {
        if (!window.history.state?.bbTyp) window.history.pushState({ bbLp: 1 }, '', window.location.href);
      } catch {}
    }
    this.#syncContentVisibility(false);
    if (stepEl) this.#scrollToStep(stepEl);
    this.#barUpdate();
  }

  /** Thanh "← Back to <san pham>" o dau builder (chi sau khi roi landing hang crawl). */
  #addLandingBack() {
    this.querySelector('[data-bb-lpback-bar]')?.remove();
    const title = this._lpCtx?.title || (this._lpWrap?.querySelector('.bb-card__title')?.textContent || '').trim();
    const bar = document.createElement('div');
    bar.className = 'bb-lpback-bar';
    bar.dataset.bbLpbackBar = '';
    bar.innerHTML = `<button type="button" class="bb-lpback">&larr; Back to ${this.#esc(title || 'your design')}</button>`;
    bar.querySelector('button').addEventListener('click', () => this.#restoreLanding());
    // Trong thanh "PRODUCT TYPE" (sticky) -> cuon toi dau van thay nut; khong co thanh thi dat dau builder.
    const cats = this.querySelector('.bb-cats');
    if (cats) cats.prepend(bar);
    else this.prepend(bar);
  }

  /** Tai dung san pham trong link, dung the "spot" dat tren luoi cua category do. */
  async #loadDeeplink() {
    const d = this.deep;
    const stepEl = d && this.stepEls[d.step];
    if (!stepEl) return this.#endLanding();
    let p;
    // Link ma design: san pham da tai luc tim handle -> khoi tai lai.
    if (this._deepPrefetch && this._deepPrefetch.handle === d.handle) p = this._deepPrefetch.p;
    else try {
      // KHONG gui Accept: application/json (xem #loadUpsellMatch).
      const r = await fetch(`/products/${d.handle}.js`);
      if (!r.ok) return this.#dropDeeplink();
      p = await r.json();
    } catch {
      return this.#dropDeeplink(); // mau da xoa / loi mang -> luoi binh thuong, khong bao loi
    }
    const variants = (p.variants || []).map((v) => [v.id, v.option1, v.option2, v.price, v.available]);
    const image = httpsUrl(p.featured_image || '');
    if (!image || !variants.some((v) => v[4])) return this.#dropDeeplink();
    const product = { id: p.id, title: p.title, handle: p.handle, tags: p.tags || [], image, variants };
    // 12 = hien du 8 size thanh nut bam, khong dung dropdown nhu the trong luoi.
    const card = this.#buildCard(product, d.step, null, 12);
    card.dataset.bbSpot = '';
    card.classList.add('bb-card--spot');
    const wrap = document.createElement('div');
    wrap.className = 'bb-spot';
    wrap.dataset.bbSpotWrap = '';
    const head = document.createElement('p');
    head.className = 'bb-spot__head';
    head.textContent = this.dataset.deeplinkHeading || 'The design you picked';
    wrap.append(head, card);
    const grid = stepEl.querySelector('[data-bb-grid]');
    if (!grid) return this.#dropDeeplink();
    // Size guide cua category ngay trong khoi: o landing, nut size guide cua step dang bi an.
    // Hang crawl muon step hoodie: bang size cua hoodie KHONG dung cho loai nay — bang that nam o
    // anh + mo ta san pham (chu store 2026-10-15).
    const guideSrc = !this.codeLink?.crawl && stepEl.querySelector('[data-bb-sizeguide]')?.dataset.bbSizeguide;
    if (guideSrc) {
      const g = document.createElement('button');
      g.type = 'button';
      g.className = 'bb-spot__guide';
      g.dataset.bbSizeguide = guideSrc;
      g.textContent = 'Size guide';
      card.querySelector('.bb-card__pills, [data-bb-variant-select]')?.before(g);
    }
    const shell = this.querySelector('[data-bb-spot-wrap]');
    if (this.landing) {
      // Dai offer xanh tren cung (banner offer cua builder dang an o landing).
      const strip = document.createElement('div');
      strip.className = 'bb-lp__strip';
      strip.innerHTML =
        `<b>${this.#esc(this.dataset.deeplinkStrip || 'BUY 1, GET 50% OFF EVERY ITEM AFTER')}</b>` +
        '<span class="bb-lp__strip-m">Auto-applied at checkout &mdash; no code</span>' +
        '<span class="bb-lp__strip-d">&middot; applied automatically at checkout &mdash; no code</span>';
      wrap.prepend(strip);
      if (this.dataset.lpV2) this.#syncLpStrip();
      if (shell) shell.replaceWith(wrap);
      else this.prepend(wrap);
    } else {
      shell?.remove();
      stepEl.querySelector('[data-bb-spot-wrap]')?.remove();
      // Dat TREN hang chip chu de: chip loc luoi ben duoi, khong loc the nay.
      (stepEl.querySelector('[data-bb-themebar]') || grid).before(wrap);
    }
    // Anh LON: the luoi dung ban 240px, o day anh chiem nua khung (desktop) / het be
    // ngang may (mobile). Dung dung 2 be rong cua lightbox zoom (900/1200) de trung cache CDN.
    const im = card.querySelector('.bb-card__media img');
    if (im) {
      im.loading = 'eager';
      im.setAttribute('fetchpriority', 'high');
      im.width = 1200;
      im.height = 1200;
      im.src = this.#sizeUrl(product.image, window.innerWidth < 750 ? 900 : 1200) || im.src;
    }
    // Dong nhac offer ngay duoi gia: chi khi bo dang trong (mon nay la mon nguyen gia).
    const note = document.createElement('p');
    note.className = 'bb-spot__note';
    note.dataset.bbSpotNote = '';
    note.textContent = this.dataset.deeplinkNote || 'Every item you add after this one is 50% OFF';
    note.hidden = this.#target().length > 0;
    card.querySelector('[data-bb-card-price]')?.after(note);
    if (this.landing) {
      this.#buildLandingHero(card, p, stepEl, d.step);
      if (this.#lpV2()) this.#setLpCtx(card, p);
      // Hang crawl: khoi "More <doi> hoodies" -> "Explore all <doi> products" (moi loai ao cua doi).
      if (this.codeLink?.crawl) this.#buildTeamTypes(card, p);
      else this.#buildExplore(card, p, d.step);
      this.#buildDesignSet(card, p, d.step);
    }
    // KHONG chon san size: #autoSelect lay size khach bam lan truoc (hoac S) -> de mua nham
    // size. Khach phai tu bam; nut ADD ghi "SELECT YOUR SIZE" toi luc do (#refreshCard).
    if (card.dataset.mode === 'single') this.#autoSelect(card);
    else this.#refreshCard(card);
    // Luoi loc theo DUNG chu de cua mau -> "mau tuong tu" nam ngay ben duoi. Khong ghi
    // localStorage (explicit=false): day la y cua quang cao, khong phai khach tu bam.
    const theme = (card.dataset.bbThemes || '').split(' ')[0];
    // Mang chu de sang cac category khac: khach den vi mau Hunting thi tab HOODIE cung mo o Hunting.
    this.deepTheme = theme || '';
    this.#initThemeBar(stepEl);
    if (theme && stepEl.querySelector(`[data-bb-themechip][data-theme="${theme}"]`)) this.#selectTheme(stepEl, theme, false);
    else this.#applyVisibility(stepEl);
    this.#track('DeeplinkShown', { design: d.handle });
    this.#trackView(p.id, p.title, Math.min(...variants.map((v) => v[3])), 'landing');
    // Mobile: phan tren (tieu de, chon loai, size guide, thanh doi) an het man dau -> khach
    // tu quang cao chi thay mep anh. Cuon toi dung mau, CHI khi khach chua tu cuon.
    // Desktop khong cuon: mau da nam trong man dau, cuon la mat dong offer phia tren.
    if (!this.landing && window.innerWidth < 750 && window.pageYOffset < 80) {
      const off = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-offset')) || 0;
      const go = () => {
        if (window.pageYOffset > 400) return;
        // Hang chon loai dinh tren dau khi cuon (sticky) -> tru ca chieu cao cua no, khong thi no de len tieu de + mep anh.
        const cats = this.querySelector(':scope > .bb-cats');
        const catsH = cats && getComputedStyle(cats).position === 'sticky' ? cats.offsetHeight : 0;
        const top = wrap.getBoundingClientRect().top + window.pageYOffset - off - catsH - 8;
        window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'instant' });
      };
      go();
      requestAnimationFrame(go);
    }
  }

  #restore() {
    this.#restoreSession();
    // Phiên mới thì sessionStorage rỗng -> lấy lại đội từ localStorage.
    // PHẢI nằm ngoài #restoreSession(): hàm đó `return` sớm khi sessionStorage rỗng,
    // để chung một hàm thì đúng trường hợp cần fallback lại là trường hợp bị bỏ qua.
    if (!this.selectedBrand) {
      try {
        const theme = localStorage.getItem(`${this.storageKey}:theme`);
        if (theme && (theme === 'all' || this.themeMeta[theme])) this.selectedTheme = theme;
        // Link tu email (?team=KC) thang doi luu o localStorage. Bo dang dung trong
        // phien (sessionStorage) van thang ca hai vi khoi nay chi chay khi phien rong.
        const q = new URLSearchParams(window.location.search).get('team');
        const qm = q && this.teamMeta[q.toLowerCase()];
        const team = (qm && qm.n) || localStorage.getItem(`${this.storageKey}:team`);
        // Chỉ nhận nếu đội đó còn trong danh sách hiện tại (đội bị xoá/đổi tên trong
        // metaobject thì bỏ qua, không thì builder đi tìm collection không tồn tại).
        if (team && this.teamMeta[this.#slugifyBrand(team)]) this.selectedBrand = team;
      } catch {
        /* start fresh */
      }
    }
  }

  #restoreSession() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return;
      // Thang mở: số món tuỳ ý -> chỉ cần phần tử hợp lệ. Có step: phải đúng số step.
      // Để nguyên điều kiện cũ thì F5 một cái là mất sạch giỏ đang dựng ở chế độ thang.
      const okArr = (a) =>
        Array.isArray(a) && (this.ladderMode ? a.every(Boolean) : a.length === this.stepCount);
      if (Array.isArray(saved.bundles) && saved.bundles.every((bn) => okArr(bn) && bn.length > 0 && bn.every(Boolean))) {
        this.state.bundles = saved.bundles;
        // cartIds song song bundles (id nếu đã trong giỏ); chuẩn hoá độ dài.
        const ids = Array.isArray(saved.cartIds) ? saved.cartIds : [];
        this.state.cartIds = this.state.bundles.map((_, i) => ids[i] || null);
      }
      if (okArr(saved.draft)) this.state.draft = saved.draft;
      // Món lẻ: mảng phẳng, không ràng buộc số step như draft/bundles.
      if (Array.isArray(saved.singles)) this.state.singles = saved.singles.filter((x) => x && x.variantId);
      if (typeof saved.brand === 'string') this.selectedBrand = saved.brand;
      if (typeof saved.previewId === 'string') this.state.previewId = saved.previewId;
      if (Array.isArray(saved.reopenIds)) this.state.reopenIds = saved.reopenIds.filter((x) => typeof x === 'string');
    } catch {
      /* start fresh */
    }
  }

  #showError(message) {
    const el = this.querySelector('[data-bb-error]');
    el.textContent = message;
    el.classList.remove('bb-hide');
  }
  #hideError() {
    this.querySelector('[data-bb-error]')?.classList.add('bb-hide');
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
